import "./atmosphere.css";
import {
  DEFAULT_ROOM_ATMOSPHERE,
  ROOM_ATMOSPHERE_OPTIONS,
  loadRoomAtmosphere,
  resetRoomAtmosphere,
  saveRoomAtmosphere,
  type RoomAtmosphereID,
} from "./atmosphere";
import {
  CloudflareWebAuth,
  WEB_AUTH_MESSAGES,
  consumeMagicLinkFragment,
  type AccountSession,
} from "./auth";
import {
  CloudflareBookmarkRepository,
  CloudflareConversationSessionRepository,
  CloudflareLibraryRepository,
  CloudflareRepositoryError,
} from "./cloudflare-repository";
import {
  MAX_QUICK_CAPTURE_CONTENT_LENGTH,
  MAX_QUICK_CAPTURE_NOTE_LENGTH,
  parseQuickCapture,
  type QuickCaptureDraft,
} from "./capture";
import {
  NewCardDraftStore,
} from "./new-card-draft-store";
import {
  LocalNewCardMediaStore,
  NEW_CARD_IMAGE_MIME_TYPES,
  type NewCardMediaStore,
} from "./new-card-media-store";
import {
  MAX_NEW_CARD_MEDIA_BYTES,
  MAX_NEW_CARD_PRIVATE_NOTES_LENGTH,
  MAX_NEW_CARD_PRIVATE_SUMMARY_LENGTH,
  MAX_NEW_CARD_SHARED_NOTES_LENGTH,
  MAX_NEW_CARD_SOURCE_LABEL_LENGTH,
  MAX_NEW_CARD_TITLE_LENGTH,
  NEW_CARD_DRAFT_ID_PREFIX,
  checkpointNewCardDraft,
  createNewCardDraft,
  newCardBookmark,
  seedNewCardPhoto,
  validateNewCardDraft,
  type NewCardAudience,
  type NewCardDraft,
  type NewCardPhotoMetadata,
  type NewCardPlacement,
  type NewCardSourceKind,
} from "./new-card";
import {
  evaluateBulkCleanupPatch,
  saveBulkCleanup,
  type BulkCleanupPatch,
} from "./bulk-edit";
import {
  AUTH_COMPLETE_ROUTE,
  appRoute,
  PRIVACY_COPY,
  PRODUCT,
  WEB_ROUTES,
} from "./config";
import {
  DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
  NATIVE_CONVERSATION_THEMES,
  allowedLibraryStatusTransitions,
  applyBookmarkEdits,
  conversationDeck,
  fingerprintPrivateSummary,
  filterConversationCards,
  filterLibrary,
  filterRecipientLibrary,
  humanizeConversationTheme,
  parseTerms,
  safeExternalURL,
  safeSameOriginMediaURL,
  toLibraryRecipientEntry,
  updateConversationState,
  validateAudienceAssignments,
  validateConversationTheme,
  validateOptionalExternalURL,
  validateOwnerTitle,
  validatePrivateCardMedia,
  validatePublicationAlignment,
  validatePrivateSummaryTransition,
  validateSharedSourceLabel,
  validateSharedTitle,
  validateVideoDurationSeconds,
  type AudienceAssignment,
  type Bookmark,
  type BookmarkEdits,
  type ConversationCard,
  type ConversationDateFilter,
  type ConversationState,
  type LibraryFilters,
  type LibraryFormat,
  type LibraryLane,
  type LibraryReceipt,
  type LibraryRecipientEntry,
  type LibraryStatus,
  type LibraryTriageConfidence,
  type LibraryTriageDecision,
} from "./domain";
import {
  demoAudiences,
  demoBookmarks,
  demoLibraryReceipts,
  demoUserLabels,
  demoViewerUserID,
} from "./fixtures";
import {
  mountFilterMenu,
  type FilterMenuHandle,
  type FilterMenuOption,
} from "./filter-menu";
import {
  mountSelectMenu,
  type SelectMenuHandle,
} from "./select-menu";
import {
  normalizeSemanticScoreMap,
  rankLibraryBookmarks,
  type SemanticScoreMap,
} from "./search";
import {
  CARD_TURN_SPRING,
  cardTurnPose,
  decayReleaseVelocity,
  pointerTilt,
  releaseDirection,
  resistedDrag,
  springIsAtRest,
  stepSpring,
  type CardTurnBounds,
  type CardTurnDirection,
  type MotionState,
  type Tilt,
} from "./motion";
import {
  CONVERSATION_DECK_PREFERENCES_STORAGE_KEY,
  conversationDeckPreferenceSummary,
  conversationDeckSourceOptions,
  defaultConversationDeckPreferences,
  loadConversationDeckPreferences,
  ownerPreparationBookmarks,
  ownerPreparationCounts,
  ownerSourceID,
  ownerSourceLabel,
  ownerSourceOptions,
  presentConversationDeck,
  serializeConversationDeckPreferences,
  type ConversationDeckPreferences,
  type OwnerDestinationFilter,
  type OwnerPreparationSort,
  type OwnerReviewFilter,
  type OwnerSourceFilterMode,
} from "./owner-prep";
import {
  MAX_PRIVATE_KEYWORDS,
  MAX_PRIVATE_NOTES_LENGTH,
  MAX_PRIVATE_SUMMARY_LENGTH,
  applyPrivateQuickEdit,
  privateQuickEditChangesBookmark,
  validatePrivateQuickEdit,
  type PrivateQuickEditInput,
} from "./quick-edit";
import {
  classifyLibraryTriage,
  reviewedLibraryTriage,
} from "./triage";
import {
  continuityCodeForCard,
  generateContinuityManifest,
  resolveContinuityManifest,
  type ResolvedContinuityManifest,
} from "./continuity";
import {
  ScreenAwakeController,
  browserScreenWakeLockProvider,
  presentScreenAwake,
} from "./screen-awake";
import {
  VersionSentinel,
  isVersionNavigationSafe,
  type VersionSentinelState,
} from "./version-sentinel";
import {
  LocalContinuityManifestRepository,
  LocalFixtureConversationSessionRepository,
  LocalFixtureBookmarkRepository,
  LocalFixtureCardBackPhotoRepository,
  LocalFixturePrivateMediaRepository,
  MAX_SYNTHETIC_ENDED_SESSIONS,
  MAX_SYNTHETIC_CARD_BACK_PHOTOS,
  MAX_SYNTHETIC_IMAGE_BYTES,
  MemoryFixtureCardBackPhotoRepository,
  MemoryFixturePrivateMediaRepository,
  MemoryStringStorage,
  PRIVATE_CONTINUITY_MANIFEST_KEY,
  SYNTHETIC_MEDIA_REFERENCE_PREFIX,
  isAllowedSyntheticImageType,
  isSyntheticCardBackReference,
  isSyntheticMediaReference,
  syntheticWorkspace,
  type BookmarkRepository,
  supportsBookmarkCreate,
  type AuthoritativeConversationCard,
  type AuthoritativeConversationEvent,
  type AuthoritativeConversationMutation,
  type AuthoritativeConversationRepository,
  type AuthoritativeConversationSession,
  type AuthoritativeConversationSessions,
  type CardBackPhoto,
  type CardBackPhotoRepository,
  type ConversationOutcomeRepository,
  type ContinuityManifestRepository,
  type ConversationSessionRepository,
  type ConversationSessionSnapshot,
  type EndedConversationSessionSnapshot,
  type LibraryReceiptRepository,
  type RecipientLibraryRepository,
  type PrivateMediaRepository,
  type ResettableBookmarkRepository,
  type SummaryEnrichmentJob,
} from "./repository";

type AppMode = "library" | "conversation";
type ConversationLayout = "stack" | "hand" | "table" | "all";
type HistoryFilter = "all" | "discussed" | "postponed";
type VisibleVectorState = "ready" | "stale" | "pending" | "queued" | "failed";
type NewCardRecoveryNotice =
  | "recovered"
  | "invalid"
  | "expired"
  | "owner_mismatch"
  | null;
type ContinuityLoadState =
  | "loading"
  | "ready"
  | "missing"
  | "invalid"
  | "failed";

interface ConversationEvent {
  id: string;
  bookmarkID: string;
  audienceId: string;
  action: "revealed" | "discussed" | "postponed";
  occurredAt: Date;
}

interface LibraryViewCandidate {
  bookmark: Bookmark | null;
  assignment: AudienceAssignment;
  entry: LibraryRecipientEntry;
}

interface ConversationOutcomeUndo {
  id: string;
  audienceId: string;
  bookmarkBefore: Bookmark;
  deckOrderBefore: string[];
  outcome: Exclude<ConversationState, "available">;
  outcomeEventID: string;
  authoritativeAssignmentID?: string;
  cardBackPhotoOffsetBefore: number;
  outcomePersistence: Promise<void>;
}

interface ToastAction {
  label: string;
  keyboardShortcut?: string;
  onActivate: () => void;
  onDismiss?: () => void;
}

interface ConversationHistoryEntry {
  id: string;
  card: ConversationCard;
  event: ConversationEvent;
  feedIndex: number;
}

interface ConversationHistoryGroup {
  sessionID: string;
  startedAt: Date;
  endedAt: Date | null;
  active: boolean;
  entries: ConversationHistoryEntry[];
}

interface StagedPrivateMedia {
  reference: string;
  image: File;
  objectURL: string;
}

const laneLabels: Record<LibraryLane, string> = {
  read: "Read",
  listen: "Listen",
  watch: "Watch",
};

const laneSymbols: Record<LibraryLane, string> = {
  read: "Aa",
  listen: "◖",
  watch: "▶",
};

const formatLabels: Record<LibraryFormat, string> = {
  text: "Text",
  audio: "Audio",
  video: "Video",
  unknown: "Unknown",
};

const vectorLabels: Record<Bookmark["vectorStatus"], string> = {
  ready: "Search index current",
  pending: "Private re-index pending",
  failed: "Re-index needs attention",
};

const cardBackSymbols = ["✦", "◌", "⌁", "✺", "◇", "∿"];
const cardCountWords = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
] as const;
const mobileConversationQuery = "(max-width: 640px)";
const compactConversationQuery = "(max-width: 820px)";
const librarySearchWindowSize = 20;
const revokedWorkspaceMessage =
  "This browser’s private access has expired. Return to sign in and send yourself a new email link.";

function tableCardCountHeading(
  count: number,
  hasRevealedCard: boolean,
): string {
  const label = cardCountWords[count] ?? String(count);
  if (hasRevealedCard) {
    return count === 1
      ? `${label} card. One question in the room.`
      : `${label} cards. One question in the room.`;
  }
  return count === 1
    ? `${label} card, no spoilers.`
    : `${label} cards, no spoilers.`;
}

function initialAppMode(parameters: URLSearchParams): AppMode {
  const requestedView = parameters.get("view");
  if (requestedView === "library") return "library";
  if (requestedView === "cards") return "conversation";
  return window.matchMedia(mobileConversationQuery).matches
    ? "conversation"
    : "library";
}

function defaultSessionMetricsVisible(): boolean {
  return !window.matchMedia(mobileConversationQuery).matches;
}

function normalizedLibraryQuery(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{Mark}+/gu, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("en");
}

function humanizeCluster(value: string): string {
  return value
    .split("-")
    .filter(Boolean)
    .map((word, index) => (
      index === 0
        ? `${word.charAt(0).toLocaleUpperCase("en")}${word.slice(1)}`
        : word
    ))
    .join(" ");
}

const dateFilterLabels: Record<ConversationDateFilter, string> = {
  all: "All time",
  week: "Last 7 days",
  two_weeks: "Last 2 weeks",
  month: "Last month",
  custom: "Custom range",
};

const demoSemanticScoreFixtures: Record<string, Record<string, number>> = {
  listening: {
    "demo-listen-maya-02": 0.96,
    "demo-read-maya-01": 0.42,
    "demo-listen-maya-09": 0.28,
  },
  home: {
    "demo-watch-maya-03": 0.94,
    "demo-read-me-04": 0.24,
  },
  creativity: {
    "demo-read-phelim-07": 0.95,
    "demo-watch-phelim-08": 0.68,
  },
  aurora: {
    "demo-read-maya-01": 1,
    "demo-listen-maya-02": 0.6,
  },
};

export class LibraryApp {
  private readonly auth = new CloudflareWebAuth();
  private repository: BookmarkRepository | null = null;
  private receiptRepository: LibraryReceiptRepository | null = null;
  private recipientLibraryRepository: RecipientLibraryRepository | null = null;
  private conversationOutcomeRepository: ConversationOutcomeRepository | null =
    null;
  private conversationSessionRepository: ConversationSessionRepository | null =
    null;
  private authoritativeConversationRepository:
    AuthoritativeConversationRepository | null = null;
  private authoritativeConversationState:
    AuthoritativeConversationSessions | null = null;
  private readonly authoritativeConversationCards =
    new Map<string, AuthoritativeConversationCard>();
  private authoritativeConversationRefreshRequired = false;
  private authoritativeConversationRefreshInFlight = false;
  private continuityRepository: ContinuityManifestRepository | null = null;
  private resolvedContinuity: ResolvedContinuityManifest | null = null;
  private continuityLoadState: ContinuityLoadState = "loading";
  private continuityOperationInFlight = false;
  private cardBackPhotoRepository: CardBackPhotoRepository | null = null;
  private privateMediaRepository: PrivateMediaRepository | null = null;
  private resettableRepository: ResettableBookmarkRepository | null = null;
  private bookmarks: Bookmark[] = [];
  private receipts: LibraryReceipt[] = [];
  private recipientEntries: LibraryRecipientEntry[] = [];
  private viewerUserID = demoViewerUserID;
  private readonly audienceLabels = new Map<string, string>();
  private enrichmentJobs: SummaryEnrichmentJob[] = [];
  private readonly newCardDraftStore = new NewCardDraftStore(
    window.localStorage,
  );
  private readonly newCardMediaStore: NewCardMediaStore | null =
    window.indexedDB
      ? new LocalNewCardMediaStore(window.indexedDB)
      : null;
  private newCardDraft: NewCardDraft | null = null;
  private newCardPhotoObjectURL: string | null = null;
  private newCardPhotoStaging = false;
  private newCardMediaGeneration = 0;
  private newCardSaveInProgress = false;
  private readonly newBookmarkIDs = new Set<string>();
  private selectedID: string | null = null;
  private selectedPublicationID: string | null = null;
  private libraryDetailMode: "edit" | "read" = "edit";
  private mode: AppMode = "library";
  private demo = false;
  private returnToConversation = false;
  private filters: LibraryFilters = {
    audienceId: "all",
    senderID: "all",
    lane: "all",
    status: "all",
    watchMode: "longer_than_threshold",
    longVideoThresholdSeconds: DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
  };
  private libraryFiltersOpen = false;
  private libraryFilterToggleFocused = false;
  private readonly libraryFilterMenus: FilterMenuHandle[] = [];
  private watchModeFilterMenu: FilterMenuHandle | null = null;
  private readonly bookmarkSelectMenus = new Map<string, SelectMenuHandle>();
  private libraryQuery = "";
  private librarySearchVisibleCount = librarySearchWindowSize;
  private ownerManagerQuery = "";
  private ownerManagerAudienceID = "all";
  private ownerManagerDestination: OwnerDestinationFilter = "all";
  private ownerManagerCluster = "all";
  private ownerManagerReview: OwnerReviewFilter = "all";
  private readonly ownerManagerIncludedSources = new Set<string>();
  private readonly ownerManagerExcludedSources = new Set<string>();
  private ownerManagerSort: OwnerPreparationSort = "newest";
  private ownerManagerShuffleSeed = crypto.randomUUID();
  private readonly ownerManagerSelection = new Set<string>();
  private ownerBulkPreviewSignature: string | null = null;
  private ownerBulkSaveInProgress = false;
  private ownerManagerWorkspaceChanged = false;
  private ownerQuickEditBookmarkID: string | null = null;
  private conversationAudienceID = "maya";
  private conversationDeckSort: OwnerPreparationSort = "shuffled";
  private readonly conversationDeckIncludedSources = new Set<string>();
  private readonly conversationDeckExcludedSources = new Set<string>();
  private conversationAtmosphere: RoomAtmosphereID = loadRoomAtmosphere(
    window.localStorage,
  );
  private conversationLayout: ConversationLayout = "stack";
  private tabletopMode = false;
  private tabletopChoiceIndex: number | null = null;
  private stackMotionFrameID: number | null = null;
  private stackMotionGeneration = 0;
  private stackEntranceDirection: -1 | 0 | 1 = 0;
  private spreadMotionSignature: string | null = null;
  private spreadMotionGeneration = 0;
  private cardTurnFrameID: number | null = null;
  private cardTurnGeneration = 0;
  private pendingCardTurn: {
    key: string;
    direction: CardTurnDirection;
    source: CardTurnBounds;
  } | null = null;
  private activeCardTurnElement: HTMLElement | null = null;
  private conversationOutcomeUndo: ConversationOutcomeUndo | null = null;
  private conversationOutcomeUndoInFlight = false;
  private stackIndex = 0;
  private readonly revealedCardKeys = new Set<string>();
  private readonly viewAllConfirmed = new Set<string>();
  private readonly deckOrders = new Map<string, string[]>();
  private readonly bookmarkDrafts = new Map<string, BookmarkEdits>();
  private readonly stagedPrivateMedia = new Map<string, StagedPrivateMedia>();
  private readonly privateMediaObjectURLs = new Map<string, string>();
  private cardBackPhotos: CardBackPhoto[] = [];
  private readonly cardBackPhotoObjectURLs = new Map<string, string>();
  private cardBackPhotoOffset = 0;
  private cardBackPhotoAudienceID = "maya";
  private cardBackPhotoLoadGeneration = 0;
  private cardBackPhotosLoading = false;
  private readonly triageAudits = new Map<string, LibraryTriageDecision | null>();
  private readonly selectedThemes = new Set<string>();
  private readonly conversationEvents: ConversationEvent[] = [];
  private endedConversationSessions: EndedConversationSessionSnapshot[] = [];
  private conversationSessionID: string = crypto.randomUUID();
  private sessionStartedAt = new Date();
  private conversationSessionActive = true;
  private dateFilter: ConversationDateFilter = "all";
  private customDateStart = dateInputValue(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1_000),
  );
  private customDateEnd = dateInputValue(new Date());
  private showThemeSymbols = true;
  private sessionMetricsVisible = defaultSessionMetricsVisible();
  private filtersOpen = false;
  private historyOpen = false;
  private historyDetailEntryID: string | null = null;
  private statsOpen = false;
  private sessionLifecycleOpen = false;
  private sessionEndConfirming = false;
  private historyFilter: HistoryFilter = "all";
  private saveInProgress = false;
  private receiptSaveInProgress = false;
  private keyboardBound = false;
  private conversationViewport: MediaQueryList | null = null;
  private tabletopViewport: MediaQueryList | null = null;
  private libraryFilterViewport: MediaQueryList | null = null;
  private sessionClockID: number | null = null;
  private celebrationTimeoutID: number | null = null;
  private toastTimeoutID: number | null = null;
  private toastOnDismiss: (() => void) | null = null;
  private keyboardHelpReturnFocus: HTMLElement | null = null;
  private accountDialogReturnFocus: HTMLElement | null = null;
  private magicLinkToken: string | null = null;
  private readonly screenAwake: ScreenAwakeController;
  private screenAwakeActionFocusPending = false;
  private readonly versionSentinel: VersionSentinel;
  private versionSentinelState: VersionSentinelState = "unavailable";

  constructor(private readonly root: HTMLElement) {
    this.screenAwake = new ScreenAwakeController(
      browserScreenWakeLockProvider(window.navigator),
      () => this.updateScreenAwakeUI(),
      () => !document.hidden,
    );
    this.versionSentinel = new VersionSentinel({
      currentModuleURL: import.meta.url,
      appURL: new URL(WEB_ROUTES.app, window.location.origin).toString(),
      isSafeToNavigate: () => this.isSafeForVersionNavigation(),
      navigateFresh: () => {
        window.location.replace(this.freshAppRoute());
      },
      onStateChange: (state) => {
        this.versionSentinelState = state;
        this.syncVersionUpdateUI();
      },
    });
  }

  async start(): Promise<void> {
    if (
      window.location.pathname.replace(/\/+$/, "")
      === AUTH_COMPLETE_ROUTE
    ) {
      this.magicLinkToken = consumeMagicLinkFragment(
        window.location,
        window.history,
      );
      this.renderMagicLinkCompletion();
      return;
    }
    this.startSessionClock();
    this.restoreConversationDeckPreferences();
    const parameters = new URLSearchParams(window.location.search);
    this.mode = initialAppMode(parameters);
    this.demo = parameters.get("demo") === "1";
    if (this.demo) {
      this.viewerUserID = demoViewerUserID;
      this.audienceLabels.clear();
      demoAudiences.forEach(({ id, label }) => {
        this.audienceLabels.set(id, label);
      });
      const evidenceMode = parameters.get("evidence") === "1";
      if (evidenceMode) {
        const redactedBookmarks = structuredClone(demoBookmarks).map((bookmark) => ({
          ...bookmark,
          privateHumanSummary: "",
          privateSummaryInputMode: null,
          triageAudit: null,
          keywords: [],
          tags: [],
        }));
        const storage = new MemoryStringStorage();
        const repository = new LocalFixtureBookmarkRepository(
          storage,
          syntheticWorkspace(redactedBookmarks, demoLibraryReceipts),
          { receiptUserID: demoViewerUserID },
        );
        this.repository = repository;
        this.receiptRepository = repository;
        this.recipientLibraryRepository = repository;
        this.conversationOutcomeRepository = repository;
        this.resettableRepository = repository;
        this.conversationSessionRepository =
          new LocalFixtureConversationSessionRepository(storage);
        this.continuityRepository = new LocalContinuityManifestRepository(
          storage,
          `${PRIVATE_CONTINUITY_MANIFEST_KEY}.synthetic`,
        );
        this.cardBackPhotoRepository =
          new MemoryFixtureCardBackPhotoRepository();
        this.privateMediaRepository =
          new MemoryFixturePrivateMediaRepository();
        const loaded = await this.repository.load();
        const loadedReceipts = await loadFixtureReceipts(
          this.receiptRepository,
          loaded.workspace.bookmarks,
        );
        this.bookmarks = loaded.workspace.bookmarks;
        this.receipts = loadedReceipts;
        this.recipientEntries = await repository.loadLibrary();
        this.enrichmentJobs = loaded.workspace.enrichmentJobs;
      } else {
        const repository = new LocalFixtureBookmarkRepository(
          window.localStorage,
          syntheticWorkspace(demoBookmarks, demoLibraryReceipts),
          { receiptUserID: demoViewerUserID },
        );
        this.repository = repository;
        this.receiptRepository = repository;
        this.recipientLibraryRepository = repository;
        this.conversationOutcomeRepository = repository;
        this.resettableRepository = repository;
        this.conversationSessionRepository =
          new LocalFixtureConversationSessionRepository(window.sessionStorage);
        this.continuityRepository = new LocalContinuityManifestRepository(
          window.localStorage,
          `${PRIVATE_CONTINUITY_MANIFEST_KEY}.synthetic`,
        );
        this.cardBackPhotoRepository =
          new LocalFixtureCardBackPhotoRepository(window.indexedDB);
        this.privateMediaRepository =
          new LocalFixturePrivateMediaRepository(window.indexedDB);
        const loaded = await this.repository.load();
        const loadedReceipts = await loadFixtureReceipts(
          this.receiptRepository,
          loaded.workspace.bookmarks,
        );
        this.bookmarks = loaded.workspace.bookmarks;
        this.receipts = loadedReceipts;
        this.recipientEntries = await repository.loadLibrary();
        this.enrichmentJobs = loaded.workspace.enrichmentJobs;
        if (loaded.recoveredInvalidState) {
          window.setTimeout(() => {
            this.showToast(
              "Invalid synthetic workspace was safely reset to the demo seed.",
              true,
            );
          }, 0);
        }
      }
      await this.hydratePrivateMedia();
      const loadedSession = await this.conversationSessionRepository.load();
      this.endedConversationSessions = loadedSession.endedSessions;
      if (loadedSession.session) {
        this.restoreConversationSession(loadedSession.session);
      } else if (loadedSession.endedSessions.length > 0) {
        this.conversationSessionActive = false;
        this.conversationEvents.length = 0;
      }
      if (loadedSession.recoveredInvalidState) {
        window.setTimeout(() => {
          this.showToast(
            "Invalid synthetic Conversation session was safely cleared.",
            true,
          );
        }, 0);
      }
      await this.hydrateContinuityManifest(false);
      await this.hydrateCardBackPhotos(this.conversationAudienceID);
      await this.persistConversationSession();
      const first = filterLibrary(
        this.bookmarks,
        this.receipts,
        demoViewerUserID,
        this.filters,
      )[0] ?? null;
      this.selectedID = first?.bookmark?.id ?? null;
      this.selectedPublicationID = first?.entry.item.id ?? null;
      const recoveryNotice = evidenceMode
        ? null
        : await this.restoreNewCardDraft();
      this.bindGlobalKeyboard();
      this.renderApp();
      this.presentNewCardRecoveryNotice(recoveryNotice);
      return;
    }

    this.renderLoading();
    try {
      const session = await this.auth.session();
      if (session.authenticated) {
        await this.openAuthenticatedWorkspace();
      } else {
        this.renderSignIn();
      }
    } catch (error) {
      this.renderSignIn(
        error instanceof Error
          ? error.message
          : "We couldn’t reconnect this browser to your private space.",
      );
    }
  }

  private async openAuthenticatedWorkspace(): Promise<void> {
    this.renderLoading();
    try {
      const ownerRepository = new CloudflareBookmarkRepository();
      const loaded = await ownerRepository.load();
      if (!loaded.context) {
        throw new Error("The private workspace identity is unavailable.");
      }
      const recipientRepository = new CloudflareLibraryRepository(
        loaded.context.ownerUserID,
      );
      const recipientEntries = await recipientRepository.loadLibrary();
      const authoritativeConversationRepository =
        new CloudflareConversationSessionRepository();
      this.repository = ownerRepository;
      this.receiptRepository = recipientRepository;
      this.recipientLibraryRepository = recipientRepository;
      this.conversationOutcomeRepository = null;
      this.conversationSessionRepository = null;
      this.authoritativeConversationRepository =
        authoritativeConversationRepository;
      this.continuityRepository = new LocalContinuityManifestRepository(
        window.localStorage,
      );
      this.cardBackPhotoRepository = null;
      this.privateMediaRepository = null;
      this.resettableRepository = null;
      this.viewerUserID = loaded.context.ownerUserID;
      this.audienceLabels.clear();
      loaded.context.audiences.forEach(({ id, label }) => {
        this.audienceLabels.set(id, label);
      });
      if (!this.audienceLabels.has(this.conversationAudienceID)) {
        this.conversationAudienceID =
          loaded.context.audiences[0]?.id ?? this.conversationAudienceID;
      }
      this.bookmarks = loaded.workspace.bookmarks;
      this.enrichmentJobs = loaded.workspace.enrichmentJobs;
      this.recipientEntries = recipientEntries;
      this.receipts = recipientEntries.map(({ receipt }) => receipt);
      await this.loadAuthoritativeConversation();
      const first = this.allVisibleLibraryEntries()[0] ?? null;
      this.selectedID = first?.bookmark?.id ?? null;
      this.selectedPublicationID = first?.entry.item.id ?? null;
      const recoveryNotice = await this.restoreNewCardDraft();
      this.bindGlobalKeyboard();
      this.renderApp();
      this.presentNewCardRecoveryNotice(recoveryNotice);
    } catch (error) {
      const sourceMessage = error instanceof Error
        ? error.message
        : "The private workspace is unavailable.";
      const revoked = error instanceof CloudflareRepositoryError
        && error.status === 401;
      this.renderWorkspaceUnavailable(
        revoked ? revokedWorkspaceMessage : sourceMessage,
        revoked,
      );
    }
  }

  private renderWorkspaceUnavailable(
    message: string,
    revoked = false,
  ): void {
    this.destroyLibraryFilterMenus();
    this.root.innerHTML = `
      <main class="gate-shell" id="app-content" tabindex="-1">
        <section class="gate-card" aria-labelledby="workspace-error-title">
          <span class="brand-mark" aria-hidden="true">${PRODUCT.brandMark}</span>
          <p class="eyebrow">Private workspace</p>
          <h1 id="workspace-error-title">Your space stayed closed.</h1>
          <p class="form-error" role="alert" id="workspace-error"></p>
          <p class="gate-copy">
            No bookmark or receipt was written to browser storage.
          </p>
          <div class="gate-actions">
            <button class="primary-button" id="retry-workspace" type="button">
              Try again
            </button>
            <button class="text-button" id="leave-workspace" type="button">
              ${revoked ? "Return to sign in" : "Sign out this browser"}
            </button>
          </div>
        </section>
      </main>
    `;
    setText(this.root, "#workspace-error", message);
    this.root.querySelector<HTMLButtonElement>("#retry-workspace")
      ?.addEventListener("click", () => {
        void this.openAuthenticatedWorkspace();
      });
    this.root.querySelector<HTMLButtonElement>("#leave-workspace")
      ?.addEventListener("click", async () => {
        if (revoked) {
          this.clearAuthenticatedData();
          window.location.replace(WEB_ROUTES.app);
          return;
        }
        try {
          await this.auth.logout();
          this.clearAuthenticatedData();
          window.location.replace(WEB_ROUTES.app);
        } catch (error) {
          this.renderWorkspaceUnavailable(
            error instanceof Error
              ? error.message
              : "This browser could not be logged out.",
          );
        }
      });
  }

  private clearAuthenticatedData(): void {
    void this.screenAwake.disable();
    this.destroyBookmarkSelectMenus();
    this.newCardMediaGeneration += 1;
    this.resetConversationDeckPreferences(true);
    this.newCardDraftStore.clear();
    void this.newCardMediaStore?.clearAll();
    this.releaseNewCardPhotoObjectURL();
    this.newCardDraft = null;
    this.newCardPhotoStaging = false;
    this.newCardSaveInProgress = false;
    this.repository = null;
    this.receiptRepository = null;
    this.recipientLibraryRepository = null;
    this.conversationOutcomeRepository = null;
    this.conversationSessionRepository = null;
    this.authoritativeConversationRepository = null;
    this.authoritativeConversationState = null;
    this.authoritativeConversationCards.clear();
    this.authoritativeConversationRefreshRequired = false;
    this.authoritativeConversationRefreshInFlight = false;
    void this.continuityRepository?.clear();
    this.continuityRepository = null;
    this.resolvedContinuity = null;
    this.continuityLoadState = "loading";
    this.continuityOperationInFlight = false;
    this.cardBackPhotoRepository = null;
    this.privateMediaRepository = null;
    this.resettableRepository = null;
    this.bookmarks = [];
    this.receipts = [];
    this.recipientEntries = [];
    this.enrichmentJobs = [];
    this.audienceLabels.clear();
    this.selectedID = null;
    this.selectedPublicationID = null;
    this.ownerManagerQuery = "";
    this.ownerManagerAudienceID = "all";
    this.ownerManagerDestination = "all";
    this.ownerManagerCluster = "all";
    this.ownerManagerReview = "all";
    this.ownerManagerSelection.clear();
    this.ownerBulkPreviewSignature = null;
    this.ownerBulkSaveInProgress = false;
    this.ownerManagerWorkspaceChanged = false;
    this.ownerQuickEditBookmarkID = null;
    this.bookmarkDrafts.clear();
    this.triageAudits.clear();
    this.newBookmarkIDs.clear();
    this.conversationEvents.length = 0;
    this.endedConversationSessions = [];
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.releaseAllPrivateMediaObjectURLs();
  }

  private async restoreNewCardDraft(): Promise<NewCardRecoveryNotice> {
    const result = this.newCardDraftStore.load(this.viewerUserID);
    this.newCardDraft = result.draft;
    this.releaseNewCardPhotoObjectURL();
    if (result.draft?.sourceKind === "photo" && result.draft.photo) {
      try {
        const image = await this.newCardMediaStore?.load(
          this.viewerUserID,
          result.draft.id,
          result.draft.photo,
        );
        if (!image) {
          throw new Error("The private staged photo is missing.");
        }
        this.newCardPhotoObjectURL = URL.createObjectURL(image);
        await this.newCardMediaStore?.commit(
          this.viewerUserID,
          result.draft.id,
          result.draft.photo,
        ).catch(() => undefined);
      } catch {
        await this.newCardMediaStore?.clearDraft(
          this.viewerUserID,
          result.draft.id,
        ).catch(() => undefined);
        this.newCardDraftStore.clear();
        this.newCardDraft = null;
        return "invalid";
      }
    } else if (result.draft) {
      await this.newCardMediaStore?.clearAll().catch(() => undefined);
    }
    if (result.draft) return "recovered";
    await this.newCardMediaStore?.clearAll().catch(() => undefined);
    if (result.expired) return "expired";
    if (result.ownerMismatch) return "owner_mismatch";
    if (result.recoveredInvalidState) return "invalid";
    return null;
  }

  private presentNewCardRecoveryNotice(
    notice: NewCardRecoveryNotice,
  ): void {
    if (notice === "recovered") {
      this.showToast(
        "Your unfinished New card is still private in this browser.",
        false,
        {
          label: "Resume",
          keyboardShortcut: "N",
          onActivate: () => this.openNewCardComposer(),
        },
      );
      return;
    }
    if (notice === "expired") {
      this.showToast(
        "An unfinished New card expired after 24 hours and was cleared.",
      );
      return;
    }
    if (notice === "owner_mismatch") {
      this.showToast(
        "A New card draft from another private space was cleared.",
      );
      return;
    }
    if (notice === "invalid") {
      this.showToast(
        "An invalid New card draft was safely cleared.",
        true,
      );
    }
  }

  private availableNewCardAudiences(): NewCardAudience[] {
    return [...this.audienceLabels].map(([id, label]) => ({ id, label }));
  }

  private conversationDeckPreferences(): ConversationDeckPreferences {
    return {
      sort: this.conversationDeckSort,
      includedSources: this.conversationDeckIncludedSources,
      excludedSources: this.conversationDeckExcludedSources,
    };
  }

  private applyConversationDeckPreferences(
    preferences: ConversationDeckPreferences,
  ): void {
    this.conversationDeckSort = preferences.sort;
    this.conversationDeckIncludedSources.clear();
    preferences.includedSources.forEach((sourceID) => {
      this.conversationDeckIncludedSources.add(sourceID);
    });
    this.conversationDeckExcludedSources.clear();
    preferences.excludedSources.forEach((sourceID) => {
      this.conversationDeckExcludedSources.add(sourceID);
    });
  }

  private restoreConversationDeckPreferences(): void {
    try {
      const loaded = loadConversationDeckPreferences(
        window.localStorage.getItem(
          CONVERSATION_DECK_PREFERENCES_STORAGE_KEY,
        ),
      );
      this.applyConversationDeckPreferences(loaded.preferences);
      if (loaded.recoveredInvalidState) {
        window.localStorage.removeItem(
          CONVERSATION_DECK_PREFERENCES_STORAGE_KEY,
        );
      }
    } catch {
      this.applyConversationDeckPreferences(
        defaultConversationDeckPreferences(),
      );
    }
  }

  private persistConversationDeckPreferences(): boolean {
    try {
      window.localStorage.setItem(
        CONVERSATION_DECK_PREFERENCES_STORAGE_KEY,
        serializeConversationDeckPreferences(
          this.conversationDeckPreferences(),
        ),
      );
      return true;
    } catch {
      this.showToast(
        "These deck choices could not be kept for the next reload.",
        true,
      );
      return false;
    }
  }

  private resetConversationDeckPreferences(removeStored = false): void {
    this.applyConversationDeckPreferences(
      defaultConversationDeckPreferences(),
    );
    if (!removeStored) return;
    try {
      window.localStorage.removeItem(
        CONVERSATION_DECK_PREFERENCES_STORAGE_KEY,
      );
    } catch {
      // An unavailable browser store is already equivalent to no preference.
    }
  }

  private renderLoading(): void {
    this.destroyLibraryFilterMenus();
    this.root.innerHTML = `
      <main class="gate-shell" id="app-content" tabindex="-1" aria-busy="true">
        <div class="gate-card">
          <span class="brand-mark" aria-hidden="true">${PRODUCT.brandMark}</span>
          <p class="eyebrow">Private space</p>
          <h1>Opening your space…</h1>
          <p class="gate-copy">Reconnecting this browser to your private, synced Convoful space.</p>
        </div>
      </main>
    `;
  }

  private renderSignIn(
    errorMessage?: string,
    noticeMessage?: string,
    acceptedEmail?: string,
  ): void {
    this.destroyLibraryFilterMenus();
    const requestAccepted = acceptedEmail !== undefined;
    this.root.innerHTML = `
      <main class="gate-shell" id="app-content" tabindex="-1">
        <section class="gate-card" aria-labelledby="sign-in-title">
          <span class="brand-mark" aria-hidden="true">${PRODUCT.brandMark}</span>
          <p class="eyebrow">Your private space</p>
          <h1 id="sign-in-title">Pick up where you left off.</h1>
          <p class="gate-copy">
            Use one email link to sign in or create your Convoful account.
            Your cards, audiences, and private edits stay together.
          </p>
          ${errorMessage ? '<p class="form-error" role="alert" id="sign-in-error"></p>' : ""}
          ${noticeMessage ? '<p class="gate-notice" role="status" id="sign-in-notice"></p>' : ""}
          <form class="sign-in-form" id="sign-in-form">
            <label for="sign-in-email">Email</label>
            <input
              id="sign-in-email"
              name="email"
              type="email"
              inputmode="email"
              autocomplete="email"
              autocapitalize="none"
              autocorrect="off"
              spellcheck="false"
              required
              ${requestAccepted ? 'aria-describedby="sign-in-notice" disabled' : ""}
            />
            <button class="primary-button" type="submit" ${requestAccepted ? "disabled" : ""}>
              ${requestAccepted ? "Email sent" : "Email me a sign-in link"}
            </button>
            ${requestAccepted ? '<button class="text-button" id="clear-sign-in-email" type="button">Clear</button>' : ""}
          </form>
          <p class="privacy-note">
            The link expires after one use. Convoful never reveals whether an
            email has an account.
          </p>
          <a class="demo-link" href="${appRoute("?demo=1")}">Preview with sample bookmarks</a>
        </section>
      </main>
    `;
    const error = this.root.querySelector<HTMLElement>("#sign-in-error");
    if (error && errorMessage) error.textContent = errorMessage;
    const notice = this.root.querySelector<HTMLElement>("#sign-in-notice");
    if (notice && noticeMessage) notice.textContent = noticeMessage;
    const form = this.root.querySelector<HTMLFormElement>("#sign-in-form");
    const input = form?.elements.namedItem("email") as HTMLInputElement | null;
    if (input && acceptedEmail !== undefined) input.value = acceptedEmail;
    this.root.querySelector<HTMLButtonElement>("#clear-sign-in-email")
      ?.addEventListener("click", () => {
        this.renderSignIn();
        this.root.querySelector<HTMLInputElement>("#sign-in-email")?.focus();
      });
    if (requestAccepted) return;
    form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      const input = form.elements.namedItem("email") as HTMLInputElement;
      const button = form.querySelector<HTMLButtonElement>("button");
      if (!input.reportValidity() || !button) return;
      const inlineError = this.root.querySelector<HTMLElement>(
        "#sign-in-error",
      ) ?? document.createElement("p");
      if (!inlineError.isConnected) {
        inlineError.className = "form-error";
        inlineError.id = "sign-in-error";
        inlineError.setAttribute("role", "alert");
        form.before(inlineError);
      }
      inlineError.textContent = "";
      button.disabled = true;
      button.textContent = "Sending…";
      try {
        const email = input.value.trim();
        const state = await this.auth.requestMagicLink(email);
        if (state === "sent") {
          this.renderSignIn(
            undefined,
            WEB_AUTH_MESSAGES.magicLinkSent,
            email,
          );
        } else {
          this.renderSignIn(WEB_AUTH_MESSAGES.magicLinkRateLimited);
        }
      } catch (requestError) {
        inlineError.textContent = requestError instanceof Error
          ? requestError.message
          : WEB_AUTH_MESSAGES.magicLinkRequestUnavailable;
        button.disabled = false;
        button.textContent = "Email me a sign-in link";
        input.focus();
      }
    });
  }

  private renderMagicLinkCompletion(): void {
    this.destroyLibraryFilterMenus();
    const available = this.magicLinkToken !== null;
    document.title = `Sign in · ${PRODUCT.name}`;
    this.root.innerHTML = `
      <main class="gate-shell" id="app-content" tabindex="-1">
        <section class="gate-card" aria-labelledby="completion-title">
          <span class="brand-mark" aria-hidden="true">${PRODUCT.brandMark}</span>
          <p class="eyebrow">Private sign-in</p>
          <h1 id="completion-title">${
            available ? "Ready when you are." : "This link can’t be used."
          }</h1>
          <p class="${available ? "gate-copy" : "form-error"}"${
            available ? "" : ' role="alert"'
          } id="completion-copy"></p>
          ${available
            ? `<button class="primary-button gate-primary-action" id="finish-sign-in" type="button">
                Finish signing in
              </button>`
            : `<a class="primary-link gate-primary-action" href="${WEB_ROUTES.app}">
                Send a new link
              </a>`}
          <p class="privacy-note">
            Signing in opens your private Convoful space on this browser.
          </p>
        </section>
      </main>
    `;
    setText(
      this.root,
      "#completion-copy",
      available
        ? "Convoful will open only after you choose Finish signing in."
        : WEB_AUTH_MESSAGES.magicLinkUnavailable,
    );
    this.root.querySelector<HTMLButtonElement>("#finish-sign-in")
      ?.addEventListener("click", async (event) => {
        const button = event.currentTarget as HTMLButtonElement;
        const token = this.magicLinkToken;
        this.magicLinkToken = null;
        if (!token) {
          this.renderMagicLinkCompletion();
          return;
        }
        button.disabled = true;
        button.textContent = "Signing in…";
        try {
          await this.auth.exchangeMagicLink(token);
          window.location.replace(WEB_ROUTES.app);
        } catch {
          this.renderMagicLinkCompletion();
        }
      });
  }

  private renderDeletionPending(requestedAt: string): void {
    this.destroyLibraryFilterMenus();
    this.root.innerHTML = `
      <main class="gate-shell" id="app-content" tabindex="-1">
        <section class="gate-card" aria-labelledby="deletion-pending-title">
          <span class="brand-mark" aria-hidden="true">${PRODUCT.brandMark}</span>
          <p class="eyebrow">Request received</p>
          <h1 id="deletion-pending-title">Your account is signed out.</h1>
          <p class="gate-copy">
            Your full-account deletion is pending manual review and will finish
            within 30 calendar days of the request, or sooner where applicable
            law requires. Every session was revoked immediately and sign-in
            stays blocked.
          </p>
          <p class="privacy-note">
            Offline cards, History, saved Share extension items, and local
            photos remain on this device until you delete the app. We’ll send
            completion confirmation to your verified account email after
            deletion is complete.
          </p>
          <p class="privacy-note">
            Requested <time id="deletion-requested-at"></time>
          </p>
          <a class="primary-link gate-primary-action" href="${WEB_ROUTES.app}">
            Return to sign in
          </a>
        </section>
      </main>
    `;
    const time = this.root.querySelector<HTMLTimeElement>(
      "#deletion-requested-at",
    );
    if (time) {
      time.dateTime = requestedAt;
      time.textContent = this.formatAccountDate(requestedAt);
    }
  }

  private renderApp(): void {
    this.destroyLibraryFilterMenus();
    this.destroyBookmarkSelectMenus();
    this.syncScreenAwakeContext();
    document.title = `${PRODUCT.name} · ${
      this.mode === "library" ? PRODUCT.libraryTitle : "Conversation Cards"
    }`;
    this.root.innerHTML = `
      <div class="app-shell${this.demo ? " is-demo" : ""}" data-mode="${this.mode}">
        <header class="app-header">
          <div class="brand-lockup">
            <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
            <div>
              <strong data-product-title></strong>
              <span>Private workspace</span>
            </div>
          </div>
          <nav class="mode-nav" aria-label="Primary">
            <button
              type="button"
              data-mode-target="conversation"
              aria-label="Conversation Cards"
            >
              <span class="mode-label-full" aria-hidden="true">Conversation Cards</span>
              <span class="mode-label-compact" aria-hidden="true">Cards</span>
            </button>
            <button type="button" data-mode-target="library">Library</button>
          </nav>
          <div class="session-state">
            <span class="privacy-dot" aria-hidden="true"></span>
            <span>${this.demo ? "Sample data" : "Private & synced"}</span>
            <button
              class="keyboard-help-button"
              id="keyboard-help"
              type="button"
              aria-haspopup="dialog"
              aria-keyshortcuts="?"
              aria-label="Keyboard shortcuts"
            >
              <span aria-hidden="true">?</span>
              <span class="keyboard-help-label">Shortcuts</span>
            </button>
            ${this.demo
              ? `<a href="${WEB_ROUTES.app}">Exit demo</a>`
              : `<button
                  type="button"
                  id="account-button"
                  aria-haspopup="dialog"
                >Account</button>`}
          </div>
        </header>
        <aside
          class="version-update"
          id="version-update"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-labelledby="version-update-title"
          hidden
        >
          <div>
            <strong id="version-update-title">A newer Convoful is ready.</strong>
            <span id="version-update-copy">
              Your private work stays here until it is safe to update.
            </span>
          </div>
          <button type="button" id="apply-version-update">
            Update safely
          </button>
        </aside>
        <main id="app-content"></main>
        ${this.keyboardHelpMarkup()}
        ${this.demo ? "" : this.accountDialogMarkup()}
        <div class="celebration-layer" id="celebration-layer" aria-hidden="true"></div>
        <div class="toast" id="toast" role="status" aria-live="polite"></div>
      </div>
    `;
    setText(this.root, "[data-product-title]", PRODUCT.name);
    this.root.querySelectorAll<HTMLButtonElement>("[data-mode-target]")
      .forEach((button) => {
        const active = button.dataset.modeTarget === this.mode;
        if (active) button.setAttribute("aria-current", "page");
        button.addEventListener("click", () => {
          const targetMode = button.dataset.modeTarget as AppMode;
          this.clearConversationOutcomeUndo();
          this.mode = targetMode;
          this.syncScreenAwakeContext();
          if (this.mode === "library") {
            this.tabletopMode = false;
            this.tabletopChoiceIndex = null;
            this.historyOpen = false;
            this.historyDetailEntryID = null;
            this.sessionLifecycleOpen = false;
            this.sessionEndConfirming = false;
          }
          this.returnToConversation = false;
          this.renderApp();
          window.scrollTo({ top: 0, left: 0 });
          this.focusAfterRender(
            `[data-mode-target="${targetMode}"]`,
            { preventScroll: true },
          );
        });
      });
    const keyboardHelp = this.root.querySelector<HTMLButtonElement>(
      "#keyboard-help",
    );
    keyboardHelp?.addEventListener("click", () => {
      this.openKeyboardHelp(keyboardHelp);
    });
    const keyboardHelpDialog = this.root.querySelector<HTMLDialogElement>(
      "#keyboard-help-dialog",
    );
    keyboardHelpDialog?.querySelectorAll<HTMLButtonElement>(
      "[data-close-keyboard-help]",
    ).forEach((button) => {
      button.addEventListener("click", () => keyboardHelpDialog.close());
    });
    keyboardHelpDialog?.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        keyboardHelpDialog.querySelectorAll<HTMLButtonElement>(
          "button:not([disabled])",
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    keyboardHelpDialog?.addEventListener("close", () => {
      const returnFocus = this.keyboardHelpReturnFocus;
      this.keyboardHelpReturnFocus = null;
      if (returnFocus?.isConnected) {
        returnFocus.focus();
      } else {
        this.root.querySelector<HTMLButtonElement>("#keyboard-help")?.focus();
      }
    });
    if (!this.demo) {
      this.bindAccountDialog();
    }
    if (this.mode === "library") {
      this.renderLibraryMode();
    } else {
      this.renderConversationMode();
    }
    this.root.querySelector<HTMLButtonElement>("#apply-version-update")
      ?.addEventListener("click", () => {
        if (this.versionSentinel.requestFreshNavigation()) return;
        setText(
          this.root,
          "#version-update-copy",
          "Finish or revert private edits, saves, or the current session, then choose Update safely again.",
        );
        this.root.querySelector<HTMLButtonElement>(
          "#apply-version-update",
        )?.focus();
      });
    this.syncVersionUpdateUI();
  }

  private freshAppRoute(): string {
    const currentPath = window.location.pathname.replace(/\/+$/, "");
    return currentPath === WEB_ROUTES.app
      ? `${WEB_ROUTES.app}${window.location.search}`
      : WEB_ROUTES.app;
  }

  private isSafeForVersionNavigation(): boolean {
    return isVersionNavigationSafe({
      hasMagicLinkToken: this.magicLinkToken !== null,
      bookmarkDraftCount: this.bookmarkDrafts.size,
      hasUnsavedOwnerQuickEdit: this.hasUnsavedOwnerQuickEdit(),
      stagedPrivateMediaCount: this.stagedPrivateMedia.size,
      hasNewCardDraft: this.newCardDraft !== null,
      newCardPhotoStaging: this.newCardPhotoStaging,
      newCardSaveInProgress: this.newCardSaveInProgress,
      ownerBulkSaveInProgress: this.ownerBulkSaveInProgress,
      bookmarkSaveInProgress: this.saveInProgress,
      receiptSaveInProgress: this.receiptSaveInProgress,
      continuityOperationInFlight: this.continuityOperationInFlight,
      hasConversationOutcomeUndo: this.conversationOutcomeUndo !== null,
      conversationOutcomeUndoInFlight:
        this.conversationOutcomeUndoInFlight,
      hasPendingCardTurn: this.pendingCardTurn !== null,
      revealedCardCount: this.revealedCardKeys.size,
      viewAllCount: this.viewAllConfirmed.size,
      conversationEventCount: this.conversationEvents.length,
      hasHistoryDetail: this.historyDetailEntryID !== null,
      cardBackPhotoDialogOpen:
        this.root.querySelector<HTMLDialogElement>(
          "#card-back-photo-dialog",
        )?.open ?? false,
    });
  }

  private checkForCurrentWebVersion(): void {
    if (
      this.magicLinkToken !== null
      || !this.root.querySelector(".app-shell")
    ) return;
    void this.versionSentinel.check();
  }

  private syncVersionUpdateUI(): void {
    const update = this.root.querySelector<HTMLElement>("#version-update");
    if (!update) return;
    update.hidden = this.versionSentinelState !== "stale";
    if (update.hidden) return;
    setText(
      this.root,
      "#version-update-copy",
      "Your private work stays here until it is safe to update.",
    );
  }

  private accountDialogMarkup(): string {
    return `
      <dialog
        class="account-dialog"
        id="account-dialog"
        aria-labelledby="account-title"
      >
        <section class="account-sheet">
          <header class="account-heading">
            <div>
              <p class="eyebrow">Your account</p>
              <h2 id="account-title">Private wherever you sign in.</h2>
            </div>
            <button
              class="dialog-close"
              type="button"
              data-close-account
              aria-label="Close account"
            >×</button>
          </header>
          <p class="account-intro">
            See where your Convoful account is signed in, or close access when
            you are finished.
          </p>
          <section aria-labelledby="account-sessions-title">
            <h3 id="account-sessions-title">Signed-in devices</h3>
            <div id="account-session-content" aria-live="polite">
              <p class="account-loading">Loading your devices…</p>
            </div>
          </section>
          <div class="account-actions">
            <button class="text-button" id="sign-out-current" type="button">
              Sign out this browser
            </button>
            <button class="text-button" id="start-sign-out-all" type="button">
              Sign out everywhere
            </button>
          </div>
          <section
            class="account-confirmation"
            id="sign-out-all-confirmation"
            aria-labelledby="sign-out-all-title"
            hidden
          >
            <h3 id="sign-out-all-title">Sign out every device?</h3>
            <p>You will need a new email link everywhere, including here.</p>
            <div class="account-confirmation-actions">
              <button class="danger-button" id="confirm-sign-out-all" type="button">
                Yes, sign out everywhere
              </button>
              <button class="text-button" data-cancel-account-action type="button">
                Keep me signed in
              </button>
            </div>
          </section>
          <details class="account-danger-zone">
            <summary>Delete account</summary>
            <p>
              This requests deletion of your full Convoful account, not
              deactivation. Every session is revoked immediately and sign-in
              stays blocked. Manual deletion finishes within 30 calendar days,
              or sooner where applicable law requires. Offline cards, History,
              saved Share extension items, and local photos remain on this
              device until you delete the app. We’ll send completion
              confirmation to your verified account email after deletion is
              complete. Type <strong>delete my account</strong> to confirm.
            </p>
            <label for="delete-account-confirmation">
              Confirmation phrase
            </label>
            <input
              id="delete-account-confirmation"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
            />
            <button
              class="danger-button"
              id="request-account-deletion"
              type="button"
              disabled
            >
              Request account deletion
            </button>
          </details>
          <p class="form-error account-error" id="account-error" role="alert"></p>
        </section>
      </dialog>
    `;
  }

  private bindAccountDialog(): void {
    const button = this.root.querySelector<HTMLButtonElement>("#account-button");
    const dialog = this.root.querySelector<HTMLDialogElement>("#account-dialog");
    if (!button || !dialog) return;
    button.addEventListener("click", () => {
      this.accountDialogReturnFocus = button;
      dialog.showModal();
      void this.loadAccountSessions();
      dialog.querySelector<HTMLButtonElement>("[data-close-account]")?.focus();
    });
    dialog.querySelectorAll<HTMLButtonElement>("[data-close-account]")
      .forEach((close) => {
        close.addEventListener("click", () => dialog.close());
      });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", () => {
      const returnFocus = this.accountDialogReturnFocus;
      this.accountDialogReturnFocus = null;
      if (returnFocus?.isConnected) returnFocus.focus();
    });
    dialog.addEventListener("keydown", (event) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled]), summary, [href]",
        ),
      ).filter((element) => (
        !element.closest("[hidden]")
        && element.getClientRects().length > 0
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    dialog.querySelector<HTMLButtonElement>("#sign-out-current")
      ?.addEventListener("click", () => {
        void this.signOutCurrentBrowser(dialog);
      });
    const signOutConfirmation = dialog.querySelector<HTMLElement>(
      "#sign-out-all-confirmation",
    );
    dialog.querySelector<HTMLButtonElement>("#start-sign-out-all")
      ?.addEventListener("click", () => {
        if (!signOutConfirmation) return;
        signOutConfirmation.hidden = false;
        signOutConfirmation.querySelector<HTMLButtonElement>(
          "#confirm-sign-out-all",
        )?.focus();
      });
    dialog.querySelectorAll<HTMLButtonElement>("[data-cancel-account-action]")
      .forEach((cancel) => {
        cancel.addEventListener("click", () => {
          if (signOutConfirmation) signOutConfirmation.hidden = true;
          dialog.querySelector<HTMLButtonElement>(
            "#start-sign-out-all",
          )?.focus();
        });
      });
    dialog.querySelector<HTMLButtonElement>("#confirm-sign-out-all")
      ?.addEventListener("click", () => {
        void this.signOutEverywhere(dialog);
      });
    const deletionInput = dialog.querySelector<HTMLInputElement>(
      "#delete-account-confirmation",
    );
    const deletionButton = dialog.querySelector<HTMLButtonElement>(
      "#request-account-deletion",
    );
    deletionInput?.addEventListener("input", () => {
      if (deletionButton) {
        deletionButton.disabled =
          deletionInput.value !== "delete my account";
      }
    });
    deletionButton?.addEventListener("click", () => {
      if (deletionInput?.value !== "delete my account") return;
      void this.deleteAccount(dialog);
    });
  }

  private async loadAccountSessions(): Promise<void> {
    const content = this.root.querySelector<HTMLElement>(
      "#account-session-content",
    );
    if (!content) return;
    content.innerHTML = '<p class="account-loading">Loading your devices…</p>';
    try {
      const sessions = await this.auth.sessions();
      content.replaceChildren();
      const list = document.createElement("ul");
      list.className = "account-session-list";
      sessions.forEach((session) => {
        list.append(this.accountSessionItem(session));
      });
      content.append(list);
    } catch (error) {
      content.replaceChildren();
      const message = document.createElement("p");
      message.className = "form-error";
      message.setAttribute("role", "alert");
      message.textContent = error instanceof Error
        ? error.message
        : WEB_AUTH_MESSAGES.sessionsUnavailable;
      content.append(message);
    }
  }

  private accountSessionItem(session: AccountSession): HTMLLIElement {
    const item = document.createElement("li");
    item.className = "account-session";
    const copy = document.createElement("div");
    const name = document.createElement("strong");
    name.textContent = session.displayName;
    const detail = document.createElement("span");
    const timestamp = session.lastSeenAt ?? session.enrolledAt;
    detail.textContent = `${session.current ? "This browser · " : ""}Last used `;
    const time = document.createElement("time");
    time.dateTime = timestamp;
    time.textContent = this.formatAccountDate(timestamp);
    detail.append(time);
    copy.append(name, detail);
    item.append(copy);
    if (session.current) {
      const badge = document.createElement("span");
      badge.className = "account-current-badge";
      badge.textContent = "Current";
      item.append(badge);
    }
    return item;
  }

  private formatAccountDate(value: string): string {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  }

  private setAccountError(message: string): void {
    setText(this.root, "#account-error", message);
  }

  private async signOutCurrentBrowser(
    dialog: HTMLDialogElement,
  ): Promise<void> {
    const button = dialog.querySelector<HTMLButtonElement>(
      "#sign-out-current",
    );
    if (!button) return;
    button.disabled = true;
    this.setAccountError("");
    try {
      await this.auth.logout();
      dialog.close();
      this.clearAuthenticatedData();
      window.location.replace(WEB_ROUTES.app);
    } catch (error) {
      this.setAccountError(
        error instanceof Error
          ? error.message
          : WEB_AUTH_MESSAGES.logoutUnavailable,
      );
      button.disabled = false;
    }
  }

  private async signOutEverywhere(dialog: HTMLDialogElement): Promise<void> {
    const button = dialog.querySelector<HTMLButtonElement>(
      "#confirm-sign-out-all",
    );
    if (!button) return;
    button.disabled = true;
    this.setAccountError("");
    try {
      await this.auth.signOutEverywhere();
      dialog.close();
      this.clearAuthenticatedData();
      window.location.replace(WEB_ROUTES.app);
    } catch (error) {
      this.setAccountError(
        error instanceof Error
          ? error.message
          : WEB_AUTH_MESSAGES.signOutEverywhereUnavailable,
      );
      button.disabled = false;
    }
  }

  private async deleteAccount(dialog: HTMLDialogElement): Promise<void> {
    const button = dialog.querySelector<HTMLButtonElement>(
      "#request-account-deletion",
    );
    if (!button) return;
    button.disabled = true;
    this.setAccountError("");
    try {
      const deletion = await this.auth.requestDeletion();
      dialog.close();
      this.clearAuthenticatedData();
      this.renderDeletionPending(deletion.requestedAt);
    } catch (error) {
      this.setAccountError(
        error instanceof Error
          ? error.message
          : WEB_AUTH_MESSAGES.deletionUnavailable,
      );
      button.disabled = false;
    }
  }

  private keyboardHelpMarkup(): string {
    const libraryShortcuts = `
      <section class="keyboard-help-group" aria-labelledby="library-shortcuts-title">
        <div>
          <p class="eyebrow">Library</p>
          <h3 id="library-shortcuts-title">Shape your queue</h3>
        </div>
        <dl class="shortcut-list">
          <div>
            <dt><kbd>/</kbd></dt>
            <dd>Focus private Library search</dd>
          </div>
          <div>
            <dt><kbd>B</kbd></dt>
            <dd>Open every owner bookmark without changing recipient Library</dd>
          </div>
          <div>
            <dt><kbd>N</kbd></dt>
            <dd>Open a private New card draft</dd>
          </div>
          ${this.demo
            ? `<div>
                <dt><kbd>C</kbd></dt>
                <dd>Open Quick capture</dd>
              </div>`
            : ""}
          <div>
            <dt><kbd>↑</kbd><kbd>↓</kbd></dt>
            <dd>Move through visible Library rows</dd>
          </div>
          <div>
            <dt><kbd>R</kbd></dt>
            <dd>Open the selected audience-safe Reading view</dd>
          </div>
          <div>
            <dt><kbd>E</kbd></dt>
            <dd>Return to the owner editor or focus Private Notes</dd>
          </div>
          <div>
            <dt>
              <kbd>⌘</kbd>
              <span aria-hidden="true">/</span>
              <span class="sr-only">or</span>
              <kbd>Ctrl</kbd><kbd>S</kbd>
            </dt>
            <dd>Save the current owner draft</dd>
          </div>
        </dl>
      </section>
    `;
    const conversationShortcuts = `
      <section class="keyboard-help-group" aria-labelledby="conversation-shortcuts-title">
        <div>
          <p class="eyebrow">Conversation Cards</p>
          <h3 id="conversation-shortcuts-title">Keep the room moving</h3>
        </div>
        <dl class="shortcut-list">
          <div>
            <dt><kbd>←</kbd><kbd>→</kbd></dt>
            <dd>Move through the phone Stack</dd>
          </div>
          <div>
            <dt><kbd>Enter</kbd><kbd>Space</kbd></dt>
            <dd>Reveal or conceal the focused Stack card</dd>
          </div>
          <div>
            <dt><kbd>Arrow keys</kbd></dt>
            <dd>Move spatially across Deal 3 or Lay down 12</dd>
          </div>
          <div>
            <dt><kbd>Home</kbd><kbd>End</kbd></dt>
            <dd>Jump to the first or last table card</dd>
          </div>
          <div>
            <dt><kbd>R</kbd></dt>
            <dd>Choose one face-down card for the room</dd>
          </div>
          <div>
            <dt><kbd>D</kbd></dt>
            <dd>Deal three fresh face-down cards in Deal 3</dd>
          </div>
          <div>
            <dt><kbd>U</kbd></dt>
            <dd>Undo the immediately previous Discussed or Postponed action</dd>
          </div>
          <div>
            <dt><kbd>Page ↑</kbd><kbd>Page ↓</kbd></dt>
            <dd>Read an enlarged revealed card without clipping</dd>
          </div>
          <div>
            <dt><kbd>S</kbd></dt>
            <dd>Shuffle the current audience deck</dd>
          </div>
          <div>
            <dt><kbd>Esc</kbd></dt>
            <dd>Conceal private prompts or leave the current tool</dd>
          </div>
        </dl>
      </section>
    `;
    return `
      <dialog
        class="keyboard-help-dialog"
        id="keyboard-help-dialog"
        aria-labelledby="keyboard-help-title"
        aria-describedby="keyboard-help-privacy"
      >
        <div class="keyboard-help-sheet">
          <header class="keyboard-help-heading">
            <div>
              <p class="eyebrow">Keyboard guide</p>
              <h2 id="keyboard-help-title">
                ${this.mode === "library"
                  ? "Your Library, without leaving the keys."
                  : "Run the room without breaking the rhythm."}
              </h2>
            </div>
            <button
              class="dialog-close"
              type="button"
              data-close-keyboard-help
              aria-label="Close keyboard shortcuts"
            >×</button>
          </header>
          <p class="keyboard-help-intro">
            These are the shortcuts available in
            ${this.mode === "library" ? "Library" : "Conversation Cards"}.
            Shortcuts pause while you type in a field.
          </p>
          ${this.mode === "library" ? libraryShortcuts : conversationShortcuts}
          <section class="keyboard-help-global" aria-labelledby="global-shortcuts-title">
            <div>
              <p class="eyebrow">Anywhere</p>
              <h3 id="global-shortcuts-title">Open or leave this guide</h3>
            </div>
            <dl class="shortcut-list">
              <div>
                <dt><kbd>?</kbd></dt>
                <dd>Open keyboard shortcuts</dd>
              </div>
              <div>
                <dt><kbd>Esc</kbd></dt>
                <dd>Close this guide and return focus</dd>
              </div>
            </dl>
          </section>
          <p class="keyboard-help-privacy" id="keyboard-help-privacy">
            Commands only. This guide never reads or shows a bookmark,
            question, private field, or audience record.
          </p>
          <div class="keyboard-help-actions">
            <button
              class="primary-button"
              type="button"
              data-close-keyboard-help
            >Done</button>
          </div>
        </div>
      </dialog>
    `;
  }

  private newCardDialogMarkup(): string {
    const recoveredAuthenticatedPhoto = (
      !this.demo
      && this.newCardDraft?.sourceKind === "photo"
      && Boolean(this.newCardDraft.photo)
    );
    const showPhotoDraft = this.demo || recoveredAuthenticatedPhoto;
    const intro = this.demo
      ? `Start with a link, a thought, or a private photo. Nothing reaches
            an audience until you explicitly choose where it belongs and Save
            succeeds.`
      : recoveredAuthenticatedPhoto
        ? `This older private photo draft is still recoverable in this
            browser. It cannot be saved or synced. Switch it to Link or Text,
            or Discard it when you are ready.`
        : `Start with a link or a thought. Nothing reaches an audience until
            you explicitly choose where it belongs and Save succeeds.`;
    const photoOption = this.demo
      ? `<option value="photo">Photo</option>`
      : recoveredAuthenticatedPhoto
        ? `<option value="photo">Recovered local photo draft</option>`
        : "";
    const photoDraftMarkup = showPhotoDraft
      ? `
              <div
                class="new-card-photo-field new-card-wide"
                id="new-card-photo-field"
                hidden
              >
                ${this.demo
                  ? `
                <label for="new-card-photo">
                  Private photo
                  <input
                    id="new-card-photo"
                    type="file"
                    accept="${NEW_CARD_IMAGE_MIME_TYPES.join(",")}"
                    aria-describedby="new-card-photo-help new-card-error"
                  >
                </label>`
                  : `
                <p class="field-help" id="new-card-photo-recovery">
                  This photo remains only in this browser. Choose Link or Text
                  to remove it from the draft, or Discard the whole draft.
                </p>`}
                <figure class="new-card-photo-preview" id="new-card-photo-preview" hidden>
                  <img
                    id="new-card-photo-preview-image"
                    alt=""
                    aria-hidden="true"
                  >
                  <figcaption id="new-card-photo-state"></figcaption>
                </figure>
                ${this.demo
                  ? `
                <button
                  class="text-button"
                  id="remove-new-card-photo"
                  type="button"
                  hidden
                >
                  Remove photo
                </button>
                <p class="field-help" id="new-card-photo-help">
                  Private draft only. Choose one readable image up to ${
                    MAX_NEW_CARD_MEDIA_BYTES / (1024 * 1024)
                  } MB. Its bytes stay in this browser.
                </p>`
                  : ""}
              </div>`
      : "";
    const photoPreviewMarkup = showPhotoDraft
      ? `
            <img
              class="new-card-preview-photo"
              id="new-card-preview-photo"
              alt=""
              aria-hidden="true"
              hidden
            >`
      : "";
    return `
      <dialog
        class="new-card-dialog"
        id="new-card-dialog"
        aria-labelledby="new-card-title"
        aria-describedby="new-card-privacy"
      >
        <form class="new-card-form" id="new-card-form">
          <header class="new-card-heading">
            <div>
              <p class="eyebrow">Private first</p>
              <h2 id="new-card-title">Make a card without losing the thought.</h2>
            </div>
            <button
              class="dialog-close"
              id="close-new-card"
              type="button"
              aria-label="Close New card"
            >×</button>
          </header>
          <p class="new-card-intro">
            ${intro}
          </p>
          <section class="new-card-source" aria-labelledby="new-card-source-title">
            <div class="new-card-section-heading">
              <div>
                <p class="eyebrow">Source</p>
                <h3 id="new-card-source-title">What are you keeping?</h3>
              </div>
              <span>Owner view</span>
            </div>
            <div class="new-card-grid">
              <label>
                Card kind
                <select id="new-card-source-kind">
                  <option value="link">Link</option>
                  <option value="text">Text thought</option>
                  ${photoOption}
                </select>
              </label>
              <label class="new-card-wide" id="new-card-source-url-field">
                Original link
                <input
                  id="new-card-source-url"
                  type="url"
                  inputmode="url"
                  autocomplete="url"
                  placeholder="https://example.com/…"
                  aria-describedby="new-card-source-help new-card-error"
                >
              </label>
              ${photoDraftMarkup}
              <label class="new-card-wide">
                <span id="new-card-prompt-label">Private card title or thought</span>
                <textarea
                  id="new-card-prompt"
                  rows="3"
                  maxlength="${MAX_NEW_CARD_TITLE_LENGTH}"
                  placeholder="What should the card say when it is revealed?"
                  aria-describedby="new-card-source-help new-card-error"
                ></textarea>
              </label>
              <label class="new-card-wide">
                Original source label <span>Optional</span>
                <input
                  id="new-card-source-label"
                  maxlength="${MAX_NEW_CARD_SOURCE_LABEL_LENGTH}"
                  placeholder="Only use what you know"
                  aria-describedby="new-card-source-help new-card-error"
                >
              </label>
            </div>
            <p class="field-help" id="new-card-source-help">
              Pasting a link never fetches it. Text cards stay link-free.
            </p>
          </section>
          <section class="new-card-private" aria-labelledby="new-card-private-title">
            <div class="new-card-section-heading">
              <div>
                <p class="eyebrow">Only you</p>
                <h3 id="new-card-private-title">Private context</h3>
              </div>
              <span>Never recipient-facing</span>
            </div>
            <div class="new-card-grid">
              <label class="new-card-wide">
                Notes · only you <span>Optional</span>
                <textarea
                  id="new-card-private-notes"
                  rows="4"
                  maxlength="${MAX_NEW_CARD_PRIVATE_NOTES_LENGTH}"
                  placeholder="Why are you keeping this?"
                  aria-describedby="new-card-privacy new-card-error"
                ></textarea>
              </label>
              <div class="new-card-summary-starter new-card-wide">
                <button
                  class="quiet-button"
                  id="new-card-summary-from-notes"
                  type="button"
                  aria-describedby="new-card-summary-starter-help"
                >
                  Use Notes as starter Summary
                </button>
                <span id="new-card-summary-starter-help">
                  Copies your private Notes. Notes stay unchanged.
                </span>
              </div>
              <label class="new-card-wide">
                Human Summary · only you <span>Required</span>
                <textarea
                  id="new-card-private-summary"
                  rows="4"
                  maxlength="${MAX_NEW_CARD_PRIVATE_SUMMARY_LENGTH}"
                  placeholder="Your own framing, in your words"
                  aria-describedby="new-card-summary-help new-card-error"
                ></textarea>
              </label>
              <p class="field-help new-card-wide" id="new-card-summary-help">
                Required. A saved Summary starts private re-indexing. This
                draft does not claim that re-indexing has run.
              </p>
              <label>
                Keywords <span>Optional</span>
                <input
                  id="new-card-keywords"
                  placeholder="ritual, listening"
                  aria-describedby="new-card-privacy new-card-error"
                >
              </label>
              <label>
                Tags <span>Optional</span>
                <input
                  id="new-card-tags"
                  placeholder="Maya prep"
                  aria-describedby="new-card-privacy new-card-error"
                >
              </label>
              <label>
                New card theme
                <select id="new-card-theme"></select>
              </label>
              <label>
                Format
                <select id="new-card-format">
                  <option value="unknown">Unknown</option>
                  <option value="text">Text</option>
                  <option value="audio">Audio</option>
                  <option value="video">Video</option>
                </select>
              </label>
              <label>
                Library lane
                <select id="new-card-lane">
                  <option value="read">Read</option>
                  <option value="listen">Listen</option>
                  <option value="watch">Watch</option>
                </select>
              </label>
            </div>
          </section>
          <section class="new-card-placements" aria-labelledby="new-card-placements-title">
            <div class="new-card-section-heading">
              <div>
                <p class="eyebrow">People and places</p>
                <h3 id="new-card-placements-title">Choose every destination.</h3>
              </div>
              <span>No default is assumed</span>
            </div>
            <p class="new-card-placement-help">
              Each audience can get a Private card, a Shared Library item, or
              both. Choose exactly one enabled audience as the default.
            </p>
            <div id="new-card-placement-rows"></div>
          </section>
          <section class="new-card-preview" aria-labelledby="new-card-preview-title">
            ${photoPreviewMarkup}
            <div class="new-card-section-heading">
              <div>
                <p class="eyebrow">Preview</p>
                <h3 id="new-card-preview-title">Nothing placed yet.</h3>
              </div>
              <span id="new-card-preview-source"></span>
            </div>
            <p id="new-card-preview-placements">
              Choose a destination to preview where this card will live.
            </p>
            <p class="new-card-preview-private">
              Private Notes, Summary, keywords, and tags are deliberately not
              repeated in this placement preview.
            </p>
          </section>
          <p class="new-card-privacy" id="new-card-privacy">
            This unfinished card stays only in this browser for up to 24 hours.
            Closing keeps it; Discard clears it. Logging out clears it.
          </p>
          <p
            class="form-error"
            id="new-card-error"
            role="alert"
            tabindex="-1"
            hidden
          ></p>
          <p
            class="new-card-status"
            id="new-card-status"
            role="status"
            aria-live="polite"
          ></p>
          <footer class="new-card-actions">
            <button class="text-button new-card-discard" id="discard-new-card" type="button">
              Discard
            </button>
            <button class="text-button" id="cancel-new-card" type="button">
              Close and keep
            </button>
            <button class="primary-button" id="save-new-card" type="submit">
              Save New card
            </button>
          </footer>
        </form>
      </dialog>
    `;
  }

  private openKeyboardHelp(trigger: HTMLElement): void {
    if (this.mode === "conversation") {
      this.concealPrivateConversation();
    }
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#keyboard-help-dialog",
    );
    if (!dialog || dialog.open) return;
    const triggerCanReceiveFocus = trigger.matches(
      "a[href], button:not([disabled]), input:not([disabled]), "
        + "select:not([disabled]), textarea:not([disabled]), "
        + "[tabindex]:not([tabindex='-1'])",
    );
    this.keyboardHelpReturnFocus = trigger.isConnected && triggerCanReceiveFocus
      ? trigger
      : this.root.querySelector<HTMLButtonElement>("#keyboard-help");
    dialog.showModal();
    dialog.querySelector<HTMLButtonElement>(".dialog-close")?.focus();
  }

  private renderLibraryMode(): void {
    const main = this.root.querySelector<HTMLElement>("#app-content");
    if (!main) return;
    this.destroyLibraryFilterMenus();
    this.cancelCardTurnTransition();
    const compactFilters = window.matchMedia(compactConversationQuery).matches;
    const filtersExpanded = !compactFilters || this.libraryFiltersOpen;
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.sessionLifecycleOpen = false;
    this.sessionEndConfirming = false;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.root.querySelector(".app-shell")?.classList.remove("tabletop-active");
    main.className = "library-layout";
    delete main.dataset.atmosphere;
    main.innerHTML = `
      <aside class="library-sidebar" aria-label="Library filters">
        <div class="sidebar-heading">
          <p class="eyebrow">Solo mode</p>
          <h1>Library</h1>
          <p>Read, listen, watch—and privately shape the bookmarks you want to remember.</p>
        </div>

        <button
          class="library-filter-toggle"
          id="library-filter-toggle"
          type="button"
          aria-controls="library-filter-controls"
          aria-expanded="${String(filtersExpanded)}"
        >
          <span>
            <strong>Filters</strong>
            <small id="library-filter-summary">All items</small>
          </span>
          <span class="library-filter-toggle-icon" aria-hidden="true">⌄</span>
        </button>

        <div
          class="filter-stack"
          id="library-filter-controls"
          ${filtersExpanded ? "" : "hidden"}
        >
          <label id="audience-filter-label" for="audience-filter">
            Audience
          </label>
          <div id="audience-filter-slot"></div>

          <label id="sender-filter-label" for="sender-filter">
            Sent by
          </label>
          <div id="sender-filter-slot"></div>

          <label id="progress-filter-label" for="progress-filter">
            Progress
          </label>
          <div id="progress-filter-slot"></div>

          <fieldset class="format-filter">
            <legend>Lane</legend>
            <div>
              <button type="button" data-lane="all">All</button>
              <button type="button" data-lane="read">Read</button>
              <button type="button" data-lane="listen">Listen</button>
              <button type="button" data-lane="watch">Watch</button>
            </div>
          </fieldset>
          <div class="watch-scope" id="watch-scope" hidden>
            <label id="watch-mode-filter-label" for="watch-mode-filter">
              Watch includes
            </label>
            <div id="watch-mode-filter-slot"></div>
            <label for="long-video-threshold">Long video starts after</label>
            <div class="watch-threshold-input">
              <input
                id="long-video-threshold"
                type="number"
                min="1"
                step="1"
                inputmode="numeric"
                aria-describedby="watch-threshold-help watch-threshold-error"
              />
              <span>minutes</span>
            </div>
            <p id="watch-threshold-help"></p>
            <p
              class="watch-threshold-error"
              id="watch-threshold-error"
              role="alert"
              hidden
            >
              Use a whole number of minutes greater than zero.
            </p>
          </div>
          <div class="library-filter-actions">
            <button
              class="text-button"
              id="clear-library-filters"
              type="button"
              hidden
            >
              Clear filters
            </button>
          </div>
          <p class="library-filter-privacy">
            Private Notes, Summary, keywords, and tags never enter a recipient row.
          </p>
        </div>

        <div class="privacy-card">
          <span aria-hidden="true">✦</span>
          <div>
            <strong>Private fields stay private</strong>
            <p>Notes, Summary, keywords, and tags never enter audience-facing list projections.</p>
          </div>
        </div>
      </aside>

      <section class="library-list-panel" aria-labelledby="items-heading">
        <div class="list-heading">
          <div>
            <p class="eyebrow">Your queue</p>
            <h2 id="items-heading">Shared for later</h2>
          </div>
          <div class="list-heading-actions">
            <span class="item-count" id="item-count"></span>
            <div class="capture-actions">
              <button
                class="manage-bookmarks-button"
                id="manage-bookmarks"
                type="button"
                aria-haspopup="dialog"
                aria-keyshortcuts="B"
                aria-label="Your bookmarks"
              >
                <span aria-hidden="true">▤</span>
                <span class="action-label-full" aria-hidden="true">Your bookmarks</span>
                <span class="action-label-compact" aria-hidden="true">Bookmarks</span>
              </button>
              <button
                class="new-bookmark-button"
                id="new-card"
                type="button"
                aria-haspopup="dialog"
                aria-keyshortcuts="N"
                aria-label="New card"
              >
                <span aria-hidden="true">＋</span>
                <span class="action-label-full" aria-hidden="true">New card</span>
                <span class="action-label-compact" aria-hidden="true">New</span>
              </button>
              ${this.demo
                ? `
                  <button
                    class="quick-capture-button"
                    id="quick-capture"
                    type="button"
                    aria-keyshortcuts="C"
                    aria-label="Quick capture"
                  >
                    <span aria-hidden="true">⌁</span>
                    <span class="action-label-full" aria-hidden="true">Quick capture</span>
                    <span class="action-label-compact" aria-hidden="true">Capture</span>
                  </button>
                  <button
                    class="new-bookmark-button"
                    id="new-bookmark"
                    type="button"
                    aria-label="New bookmark"
                  >
                    <span aria-hidden="true">＋</span>
                    <span class="action-label-full" aria-hidden="true">New bookmark</span>
                    <span class="action-label-compact" aria-hidden="true">New</span>
                  </button>
                `
                : ""}
            </div>
          </div>
        </div>
        <search
          class="library-search"
          role="search"
          aria-label="Search your private Library"
        >
          <div class="search-label-row">
            <label for="library-search">Search your Library</label>
            <span><span aria-hidden="true">✦</span> Only you</span>
          </div>
          <div class="search-input-wrap">
            <span class="search-symbol" aria-hidden="true">⌕</span>
            <input
              id="library-search"
              type="search"
              inputmode="search"
              autocomplete="off"
              aria-describedby="library-search-help library-search-status"
              aria-keyshortcuts="/"
              placeholder="Try “listening” or “rituals”"
            >
            <button class="search-clear" id="clear-search" type="button">
              Clear
            </button>
          </div>
          <div class="search-context">
            <p id="library-search-help">
              Private Summary + keywords · Notes stay out
            </p>
            <p id="library-search-status" role="status" aria-live="polite"></p>
          </div>
        </search>
        <div class="demo-banner" ${this.demo ? "" : "hidden"}>
          <span>Synthetic workspace · saved only in this browser · never synced</span>
          <button class="text-button" id="reset-demo" type="button">
            Reset demo
          </button>
        </div>
        <ul class="library-list" id="library-list"></ul>
        <div class="search-result-window" id="search-result-window" hidden>
          <p id="search-result-window-status" tabindex="-1"></p>
          <button
            class="search-show-more"
            id="search-show-more"
            type="button"
          ></button>
        </div>
        <div class="empty-list" id="empty-list" hidden>
          <span aria-hidden="true">☾</span>
          <h3>Nothing in this view.</h3>
          <p>Try another filter. Private Conversation Cards never fall into Library by accident.</p>
        </div>
        ${this.demo ? `
          <dialog
            class="quick-capture-dialog"
            id="quick-capture-dialog"
            aria-labelledby="quick-capture-title"
            aria-describedby="quick-capture-privacy"
          >
            <form class="quick-capture-form" id="quick-capture-form">
              <div class="quick-capture-heading">
                <div>
                  <p class="eyebrow">Private first</p>
                  <h2 id="quick-capture-title">Catch it before it disappears.</h2>
                </div>
                <button
                  class="dialog-close"
                  id="close-quick-capture"
                  type="button"
                  aria-label="Close Quick capture"
                >×</button>
              </div>
              <label for="quick-capture-content">Link or thought</label>
              <textarea
                id="quick-capture-content"
                rows="3"
                maxlength="${MAX_QUICK_CAPTURE_CONTENT_LENGTH}"
                placeholder="Paste a full link, or type one short thought"
                aria-describedby="quick-capture-help quick-capture-error"
              ></textarea>
              <p class="field-help" id="quick-capture-help">
                Nothing is fetched. You will review the unsaved bookmark before Save.
              </p>
              <label for="quick-capture-note">
                Private note <span>Optional</span>
              </label>
              <textarea
                id="quick-capture-note"
                rows="3"
                maxlength="${MAX_QUICK_CAPTURE_NOTE_LENGTH}"
                placeholder="Why should you come back to this?"
                aria-describedby="quick-capture-privacy quick-capture-error"
              ></textarea>
              <label for="quick-capture-theme">Conversation theme</label>
              <select
                id="quick-capture-theme"
                aria-label="Quick capture theme"
                aria-describedby="quick-capture-privacy quick-capture-error"
              ></select>
              <p class="quick-capture-privacy" id="quick-capture-privacy">
                Kept in this tab until you review. Shared fields and destinations stay blank.
              </p>
              <p
                class="form-error"
                id="quick-capture-error"
                role="alert"
                tabindex="-1"
                hidden
              ></p>
              <div class="quick-capture-actions">
                <button class="text-button" id="cancel-quick-capture" type="button">
                  Cancel
                </button>
                <button class="primary-button" type="submit">
                  Review unsaved bookmark
                </button>
              </div>
            </form>
          </dialog>
        ` : ""}
        ${this.newCardDialogMarkup()}
        <dialog
          class="owner-bookmark-dialog"
          id="owner-bookmark-dialog"
          aria-labelledby="owner-bookmark-title"
          aria-describedby="owner-bookmark-privacy"
        >
          <section class="owner-bookmark-sheet">
            <header class="owner-bookmark-heading">
              <div>
                <p class="eyebrow">Only you</p>
                <h2 id="owner-bookmark-title">Every bookmark, one private desk.</h2>
              </div>
              <button
                class="dialog-close"
                id="close-owner-bookmarks"
                type="button"
                aria-label="Close Your bookmarks"
              >×</button>
            </header>
            <p class="owner-bookmark-intro">
              Find Conversation-only cards and Library shares without turning
              either one into the other.
            </p>
            <div class="owner-bookmark-filters">
              <label for="owner-bookmark-search">Find a bookmark</label>
              <div class="owner-bookmark-search-wrap">
                <span aria-hidden="true">⌕</span>
                <input
                  id="owner-bookmark-search"
                  type="search"
                  inputmode="search"
                  autocomplete="off"
                  placeholder="Title, source, cluster, Summary, keywords, tags"
                  aria-describedby="owner-bookmark-search-help owner-bookmark-status"
                >
              </div>
              <p id="owner-bookmark-search-help">
                Cluster, private Summary, keywords, and tags are searchable here.
                Notes stay out.
              </p>
              <div class="owner-bookmark-filter-row">
                <label for="owner-bookmark-audience">Audience</label>
                <select
                  id="owner-bookmark-audience"
                  aria-label="Owner bookmark audience"
                ></select>
                <fieldset class="owner-destination-filter">
                  <legend>Destination</legend>
                  <div>
                    <button type="button" data-owner-destination="all">All</button>
                    <button type="button" data-owner-destination="cards">Cards</button>
                    <button type="button" data-owner-destination="library">Library</button>
                    <button type="button" data-owner-destination="both">Both</button>
                  </div>
                </fieldset>
              </div>
              <div class="owner-bookmark-review-row">
                <label for="owner-bookmark-cluster">
                  Discussion cluster
                  <select
                    id="owner-bookmark-cluster"
                    aria-label="Owner bookmark cluster"
                  ></select>
                </label>
                <label for="owner-bookmark-review">
                  Review queue
                  <select
                    id="owner-bookmark-review"
                    aria-label="Owner bookmark review queue"
                  >
                    <option value="all">All bookmarks</option>
                    <option value="unclassified">Unclassified or unavailable</option>
                    <option value="summary">Summary or re-index attention</option>
                    <option value="search_metadata">Missing keywords or tags</option>
                    <option value="destination">No active destination</option>
                  </select>
                </label>
              </div>
              <div class="owner-preparation-controls">
                <label for="owner-bookmark-sort">
                  Order
                  <select
                    id="owner-bookmark-sort"
                    aria-label="Owner bookmark order"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="shuffled">Shuffle for this session</option>
                  </select>
                </label>
                <button
                  class="text-button"
                  id="reshuffle-owner-bookmarks"
                  type="button"
                  hidden
                >
                  Shuffle again
                </button>
              </div>
              <fieldset class="owner-source-filters">
                <legend>Where this came from</legend>
                <p>
                  Include narrows the desk. Exclude always wins. Unknown
                  providers stay grouped instead of being guessed from a link
                  or an editable label.
                </p>
                <div id="owner-source-filter-options"></div>
              </fieldset>
            </div>
            <dl
              class="owner-preparation-counts"
              id="owner-preparation-counts"
              aria-label="Preparation counts"
            >
              <div><dt>Shown</dt><dd data-owner-count="shown">0</dd></div>
              <div><dt>Selected</dt><dd data-owner-count="selected">0</dd></div>
              <div><dt>Unsaved</dt><dd data-owner-count="unsaved">0</dd></div>
              <div><dt>Re-indexing</dt><dd data-owner-count="pending">0</dd></div>
              <div><dt>Failed</dt><dd data-owner-count="failed">0</dd></div>
              <div><dt>Needs review</dt><dd data-owner-count="needs-review">0</dd></div>
            </dl>
            <div class="owner-bookmark-status-row">
              <p id="owner-bookmark-status" role="status" aria-live="polite"></p>
              <div>
                <button
                  class="text-button"
                  id="select-owner-bookmarks"
                  type="button"
                >
                  Select shown
                </button>
                <button
                  class="text-button"
                  id="clear-owner-bookmark-selection"
                  type="button"
                  hidden
                >
                  Clear selection
                </button>
                <button
                  class="text-button"
                  id="clear-owner-bookmark-filters"
                  type="button"
                  hidden
                >
                  Clear filters
                </button>
              </div>
            </div>
            <section
              class="owner-bulk-cleanup"
              id="owner-bulk-cleanup"
              aria-labelledby="owner-bulk-title"
              hidden
            >
              <div>
                <p class="eyebrow">Selected cleanup</p>
                <h3 id="owner-bulk-title">Preview a safe batch</h3>
                <p id="owner-bulk-selection-count"></p>
              </div>
              <div class="owner-bulk-fields">
                <label for="owner-bulk-theme">
                  Conversation theme
                  <select id="owner-bulk-theme">
                    <option value="">Keep each theme</option>
                  </select>
                </label>
                <label for="owner-bulk-tags">
                  Add private tags
                  <input
                    id="owner-bulk-tags"
                    autocomplete="off"
                    placeholder="maya prep, discuss soon"
                    aria-describedby="owner-bulk-privacy owner-bulk-status"
                  >
                </label>
                <label for="owner-bulk-remove-tags">
                  Remove private tags
                  <input
                    id="owner-bulk-remove-tags"
                    autocomplete="off"
                    placeholder="later, archived"
                    aria-describedby="owner-bulk-privacy owner-bulk-status"
                  >
                </label>
                <label for="owner-bulk-audience">
                  Audience
                  <select id="owner-bulk-audience">
                    <option value="">Keep audiences</option>
                  </select>
                </label>
                <label for="owner-bulk-destination">
                  Destination
                  <select id="owner-bulk-destination">
                    <option value="keep">Keep each destination</option>
                    <option value="cards">Private Cards only</option>
                    <option value="library">Shared Library only</option>
                    <option value="both">Cards + Library</option>
                    <option value="remove">Remove from this audience</option>
                  </select>
                </label>
                <label class="owner-bulk-primary" for="owner-bulk-primary">
                  <input id="owner-bulk-primary" type="checkbox">
                  Make this the default audience
                </label>
              </div>
              <p class="owner-bulk-privacy" id="owner-bulk-privacy">
                Private Notes, Summary, keywords, search evidence, and existing
                Shared Library copy stay untouched. A Library change is skipped
                until that bookmark already has explicit shared details.
              </p>
              <p
                class="owner-bulk-status"
                id="owner-bulk-status"
                role="status"
                aria-live="polite"
              >
                Choose a category or audience change, then preview before applying.
              </p>
              <div class="owner-bulk-actions">
                <button class="text-button" id="preview-owner-bulk" type="button">
                  Preview changes
                </button>
                <button
                  class="primary-button"
                  id="apply-owner-bulk"
                  type="button"
                  disabled
                >
                  Apply preview
                </button>
              </div>
            </section>
            <section
              class="owner-quick-edit"
              id="owner-quick-edit"
              aria-labelledby="owner-quick-title"
              hidden
            >
              <div class="owner-quick-heading">
                <div>
                  <p class="eyebrow">Only you</p>
                  <h3 id="owner-quick-title" aria-live="polite"></h3>
                </div>
                <button
                  class="icon-button"
                  id="close-owner-quick"
                  type="button"
                  aria-label="Close private quick edit"
                >×</button>
              </div>
              <div class="owner-quick-fields">
                <label for="owner-quick-notes">
                  Private Notes
                  <textarea
                    id="owner-quick-notes"
                    maxlength="${MAX_PRIVATE_NOTES_LENGTH}"
                    rows="5"
                    aria-describedby="owner-quick-title owner-quick-privacy owner-quick-status owner-quick-error"
                  ></textarea>
                </label>
                <label for="owner-quick-summary">
                  Private human Summary
                  <textarea
                    id="owner-quick-summary"
                    maxlength="${MAX_PRIVATE_SUMMARY_LENGTH}"
                    rows="5"
                    aria-describedby="owner-quick-title owner-quick-privacy owner-quick-vector owner-quick-status owner-quick-error"
                  ></textarea>
                </label>
                <label class="owner-quick-keywords" for="owner-quick-keywords">
                  Private keywords
                  <input
                    id="owner-quick-keywords"
                    autocomplete="off"
                    placeholder="maya, creative practice, follow up"
                    aria-describedby="owner-quick-title owner-quick-privacy owner-quick-status owner-quick-error"
                  >
                  <span>Comma-separated · up to ${MAX_PRIVATE_KEYWORDS}</span>
                </label>
              </div>
              <p class="owner-quick-privacy" id="owner-quick-privacy">
                These owner-only fields never enter a recipient Library row.
                Shared title, Shared Notes, audiences, and destinations stay untouched.
              </p>
              <p class="owner-quick-vector" id="owner-quick-vector"></p>
              <p
                class="owner-quick-status"
                id="owner-quick-status"
                role="status"
                aria-live="polite"
              ></p>
              <p
                class="owner-quick-error"
                id="owner-quick-error"
                role="alert"
                tabindex="-1"
                hidden
              ></p>
              <div class="owner-quick-actions">
                <button class="text-button" id="open-owner-full-editor" type="button">
                  Open full editor
                </button>
                <button class="text-button" id="cancel-owner-quick" type="button">
                  Cancel
                </button>
                <button class="primary-button" id="save-owner-quick" type="button">
                  Save private fields
                </button>
                <button
                  class="primary-button"
                  id="save-next-owner-quick"
                  type="button"
                  aria-keyshortcuts="Meta+Enter Control+Enter"
                >
                  Save &amp; next
                </button>
              </div>
            </section>
            <ul
              class="owner-bookmark-list"
              id="owner-bookmark-list"
              aria-label="Your owner bookmarks"
            ></ul>
            <div class="owner-bookmark-empty" id="owner-bookmark-empty" hidden>
              <span aria-hidden="true">◇</span>
              <h3>No bookmarks match.</h3>
              <p>Try another audience, destination, or private search phrase.</p>
            </div>
            <p class="owner-bookmark-privacy" id="owner-bookmark-privacy">
              This manager is built only from your authenticated owner workspace.
              It never adds a recipient row, receipt, or Library publication.
            </p>
            <footer class="owner-bookmark-footer">
              <button
                class="primary-button"
                id="done-owner-bookmarks"
                type="button"
              >
                Done
              </button>
            </footer>
          </section>
        </dialog>
      </section>

      <article class="detail-panel" id="detail-panel" aria-labelledby="detail-title">
        <div id="detail-content"></div>
      </article>
    `;

    this.populateLibraryFilters();
    this.populateQuickCaptureThemes();
    this.bindLibraryEvents();
    this.renderLibrary();
  }

  private populateLibraryFilters(): void {
    this.destroyLibraryFilterMenus();
    const audiences = uniqueOptions(
      this.recipientEntries.map(({ item }) => [
        item.audienceId,
        this.audienceLabels.get(item.audienceId) ?? item.audienceId,
      ]),
    );
    const senders = uniqueOptions(
      this.recipientEntries.map(({ item }) => [
        item.sharedByUserID,
        this.senderLabel(item.sharedByUserID),
      ]),
    );
    this.mountLibraryFilterMenu(
      "audience-filter",
      "audience-filter-label",
      [{ value: "all", label: "All audiences" }, ...audiences.map(
        ([value, label]) => ({ value, label }),
      )],
      this.filters.audienceId,
      (value) => {
        this.filters.audienceId = value;
        this.commitLibraryFilter();
      },
    );
    this.mountLibraryFilterMenu(
      "sender-filter",
      "sender-filter-label",
      [{ value: "all", label: "Anyone" }, ...senders.map(
        ([value, label]) => ({ value, label }),
      )],
      this.filters.senderID,
      (value) => {
        this.filters.senderID = value;
        this.commitLibraryFilter();
      },
    );
    this.mountLibraryFilterMenu(
      "progress-filter",
      "progress-filter-label",
      [
        { value: "all", label: "Any progress" },
        { value: "new", label: "New" },
        { value: "open", label: "Open" },
        { value: "in_progress", label: "In progress" },
        { value: "finished", label: "Finished" },
        { value: "dismissed", label: "Dismissed" },
      ],
      this.filters.status,
      (value) => {
        this.filters.status = value as LibraryStatus | "all";
        this.commitLibraryFilter();
      },
    );
    this.watchModeFilterMenu = this.mountLibraryFilterMenu(
      "watch-mode-filter",
      "watch-mode-filter-label",
      [
        {
          value: "longer_than_threshold",
          label: `Over ${this.longVideoThresholdLabel()}`,
        },
        { value: "all_video", label: "All videos" },
      ],
      this.filters.watchMode ?? "longer_than_threshold",
      (value) => {
        this.filters.watchMode = value as LibraryFilters["watchMode"];
        this.commitLibraryFilter();
      },
    );
    this.updateWatchScopeControls();
  }

  private mountLibraryFilterMenu(
    id: string,
    labelID: string,
    options: FilterMenuOption[],
    value: string,
    onChange: (value: string) => void,
  ): FilterMenuHandle | null {
    const root = this.root.querySelector<HTMLElement>(`#${id}-slot`);
    if (!root) return null;
    const handle = mountFilterMenu(root, {
      id,
      labelID,
      options,
      value,
      onChange,
    });
    this.libraryFilterMenus.push(handle);
    return handle;
  }

  private destroyLibraryFilterMenus(): void {
    for (const menu of this.libraryFilterMenus) menu.destroy();
    this.libraryFilterMenus.length = 0;
    this.watchModeFilterMenu = null;
  }

  private mountBookmarkSelectMenus(container: ParentNode): void {
    this.destroyBookmarkSelectMenus();
    for (const id of [
      "bookmark-theme",
      "bookmark-format",
      "bookmark-lane",
    ]) {
      const select = container.querySelector<HTMLSelectElement>(`#${id}`);
      if (select) this.bookmarkSelectMenus.set(id, mountSelectMenu(select));
    }
  }

  private syncBookmarkSelectMenu(id: string): void {
    this.bookmarkSelectMenus.get(id)?.sync();
  }

  private destroyBookmarkSelectMenus(): void {
    for (const menu of this.bookmarkSelectMenus.values()) menu.destroy();
    this.bookmarkSelectMenus.clear();
  }

  private commitLibraryFilter(): void {
    this.resetLibrarySearchWindow();
    this.ensureVisibleSelection();
    this.renderLibrary();
  }

  private activeLibraryFilterLabels(): string[] {
    const labels: string[] = [];
    if (this.filters.audienceId !== "all") {
      const audienceLabel = this.audienceLabels.get(this.filters.audienceId);
      labels.push(audienceLabel ?? this.filters.audienceId);
    }
    if (this.filters.senderID !== "all") {
      labels.push(this.senderLabel(this.filters.senderID));
    }
    if (this.filters.status !== "all") {
      labels.push(humanizeStatus(this.filters.status));
    }
    if (this.filters.lane !== "all") {
      if (this.filters.lane === "watch") {
        const watchLabel = this.filters.watchMode === "all_video"
          ? "Watch all videos"
          : `Watch over ${
            (this.filters.longVideoThresholdSeconds
              ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS) / 60
          } min`;
        labels.push(watchLabel);
      } else {
        labels.push(
          this.filters.lane === "read" ? "Read" : "Listen",
        );
      }
    }
    return labels;
  }

  private updateLibraryFilterDisclosure(): void {
    const compact = window.matchMedia(compactConversationQuery).matches;
    const expanded = !compact || this.libraryFiltersOpen;
    const toggle = this.root.querySelector<HTMLButtonElement>(
      "#library-filter-toggle",
    );
    const controls = this.root.querySelector<HTMLElement>(
      "#library-filter-controls",
    );
    const summary = this.root.querySelector<HTMLElement>(
      "#library-filter-summary",
    );
    const clear = this.root.querySelector<HTMLButtonElement>(
      "#clear-library-filters",
    );
    const activeLabels = this.activeLibraryFilterLabels();
    if (toggle) toggle.setAttribute("aria-expanded", String(expanded));
    if (controls) controls.hidden = !expanded;
    if (summary) {
      summary.textContent = activeLabels.length
        ? `${activeLabels.length} active · ${activeLabels.join(" · ")}`
        : "All items";
    }
    if (clear) clear.hidden = activeLabels.length === 0;
  }

  private clearLibraryFilters(): void {
    this.filters = {
      audienceId: "all",
      senderID: "all",
      lane: "all",
      status: "all",
      watchMode: "longer_than_threshold",
      longVideoThresholdSeconds: DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
    };
    this.libraryFiltersOpen = false;
    this.resetLibrarySearchWindow();
    this.populateLibraryFilters();
    this.ensureVisibleSelection();
    this.renderLibrary();
    this.focusAfterRender(
      window.matchMedia(compactConversationQuery).matches
        ? "#library-filter-toggle"
        : "#audience-filter",
    );
  }

  private populateQuickCaptureThemes(): void {
    const select = this.root.querySelector<HTMLSelectElement>(
      "#quick-capture-theme",
    );
    if (!select) return;
    select.replaceChildren(...NATIVE_CONVERSATION_THEMES.map((theme) => (
      option(theme, humanizeConversationTheme(theme))
    )));
    select.value = "interesting";
  }

  private openQuickCapture(): void {
    if (!this.demo || !this.repository) return;
    if (this.newBookmarkIDs.size > 0) {
      this.createLocalBookmark();
      return;
    }
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#quick-capture-dialog",
    );
    if (!dialog || dialog.open) return;
    this.resetQuickCaptureDialog(dialog);
    dialog.showModal();
    this.focusAfterRender("#quick-capture-content");
  }

  private bindQuickCapture(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#quick-capture-dialog",
    );
    const form = this.root.querySelector<HTMLFormElement>(
      "#quick-capture-form",
    );
    const content = this.root.querySelector<HTMLTextAreaElement>(
      "#quick-capture-content",
    );
    const privateNote = this.root.querySelector<HTMLTextAreaElement>(
      "#quick-capture-note",
    );
    const theme = this.root.querySelector<HTMLSelectElement>(
      "#quick-capture-theme",
    );
    const error = this.root.querySelector<HTMLElement>(
      "#quick-capture-error",
    );
    if (!dialog || !form || !content || !privateNote || !theme || !error) {
      return;
    }
    const close = () => {
      if (this.ownerBulkSaveInProgress) {
        this.showToast("Finish the current private cleanup before closing.");
        return;
      }
      dialog.close();
    };
    this.root.querySelector<HTMLButtonElement>("#close-quick-capture")
      ?.addEventListener("click", close);
    this.root.querySelector<HTMLButtonElement>("#cancel-quick-capture")
      ?.addEventListener("click", close);
    for (const field of [content, privateNote, theme]) {
      field.addEventListener("input", () => {
        field.removeAttribute("aria-invalid");
        error.hidden = true;
      });
    }
    dialog.addEventListener("close", () => {
      const handedOff = dialog.dataset.handedOff === "true";
      this.resetQuickCaptureDialog(dialog);
      delete dialog.dataset.handedOff;
      if (!handedOff) this.focusAfterRender("#quick-capture");
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const result = parseQuickCapture({
        content: content.value,
        privateNote: privateNote.value,
        theme: theme.value,
      });
      if (!result.ok) {
        const field = result.field === "content"
          ? content
          : result.field === "privateNote"
            ? privateNote
            : theme;
        field.setAttribute("aria-invalid", "true");
        error.hidden = false;
        error.textContent = result.message;
        error.focus();
        return;
      }
      dialog.dataset.handedOff = "true";
      dialog.close();
      this.createLocalBookmark(result.draft);
    });
  }

  private resetQuickCaptureDialog(dialog: HTMLDialogElement): void {
    dialog.querySelector<HTMLFormElement>("#quick-capture-form")?.reset();
    const theme = dialog.querySelector<HTMLSelectElement>(
      "#quick-capture-theme",
    );
    if (theme) theme.value = "interesting";
    dialog.querySelectorAll("[aria-invalid]")
      .forEach((field) => field.removeAttribute("aria-invalid"));
    const error = dialog.querySelector<HTMLElement>("#quick-capture-error");
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
  }

  private openNewCardComposer(): void {
    if (this.mode !== "library") {
      this.mode = "library";
      this.returnToConversation = false;
      this.renderApp();
    }
    const audiences = this.availableNewCardAudiences();
    if (!audiences.length) {
      this.showToast(
        "No current audience is available for a New card.",
        true,
      );
      return;
    }
    if (!this.newCardDraft) {
      this.newCardDraft = createNewCardDraft(
        `${NEW_CARD_DRAFT_ID_PREFIX}${crypto.randomUUID()}`,
        audiences,
      );
      this.newCardDraftStore.save(this.viewerUserID, this.newCardDraft);
    }
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#new-card-dialog",
    );
    if (!dialog || dialog.open) return;
    this.renderNewCardComposer();
    dialog.showModal();
    this.focusAfterRender(
      this.newCardDraft.sourceKind === "link"
        ? "#new-card-source-url"
        : this.newCardDraft.sourceKind === "photo"
          ? this.newCardDraft.photo
            ? "#new-card-prompt"
            : "#new-card-photo"
          : "#new-card-prompt",
    );
  }

  private bindNewCardComposer(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#new-card-dialog",
    );
    const form = this.root.querySelector<HTMLFormElement>("#new-card-form");
    const open = this.root.querySelector<HTMLButtonElement>("#new-card");
    if (!dialog || !form || !open) return;
    open.addEventListener("click", () => this.openNewCardComposer());
    const close = () => {
      if (this.newCardSaveInProgress || this.newCardPhotoStaging) return;
      this.checkpointNewCardFromForm();
      dialog.close();
    };
    this.root.querySelector<HTMLButtonElement>("#close-new-card")
      ?.addEventListener("click", close);
    this.root.querySelector<HTMLButtonElement>("#cancel-new-card")
      ?.addEventListener("click", close);
    this.root.querySelector<HTMLButtonElement>("#discard-new-card")
      ?.addEventListener("click", () => {
        if (this.newCardSaveInProgress || this.newCardPhotoStaging) return;
        void this.discardNewCard(dialog);
      });
    this.root.querySelector<HTMLSelectElement>("#new-card-source-kind")
      ?.addEventListener("change", (event) => {
        event.stopPropagation();
        const select = event.currentTarget as HTMLSelectElement;
        if (this.newCardSaveInProgress || this.newCardPhotoStaging) {
          select.value = this.newCardDraft?.sourceKind ?? "link";
          return;
        }
        void this.changeNewCardSourceKind(
          select.value as NewCardSourceKind,
        );
      });
    this.root.querySelector<HTMLInputElement>("#new-card-photo")
      ?.addEventListener("change", (event) => {
        const input = event.currentTarget as HTMLInputElement;
        const [image] = input.files ?? [];
        if (image) void this.stageNewCardPhoto(image);
      });
    this.root.querySelector<HTMLButtonElement>("#remove-new-card-photo")
      ?.addEventListener("click", () => {
        if (this.newCardSaveInProgress || this.newCardPhotoStaging) return;
        void this.removeNewCardPhoto();
      });
    this.root.querySelector<HTMLButtonElement>(
      "#new-card-summary-from-notes",
    )?.addEventListener("click", () => {
      const notes = this.root.querySelector<HTMLTextAreaElement>(
        "#new-card-private-notes",
      );
      const summary = this.root.querySelector<HTMLTextAreaElement>(
        "#new-card-private-summary",
      );
      if (!notes || !summary || !notes.value.trim()) return;
      summary.value = notes.value.trim();
      summary.focus();
      this.checkpointNewCardFromForm();
      this.updateNewCardPresentation();
    });
    dialog.addEventListener("cancel", (event) => {
      if (this.newCardSaveInProgress || this.newCardPhotoStaging) {
        event.preventDefault();
        return;
      }
      this.checkpointNewCardFromForm();
    });
    dialog.addEventListener("close", () => {
      const saved = dialog.dataset.saved === "true";
      const discarded = dialog.dataset.discarded === "true";
      delete dialog.dataset.saved;
      delete dialog.dataset.discarded;
      if (!saved && !discarded) this.checkpointNewCardFromForm();
      this.focusAfterRender("#new-card");
    });
    form.addEventListener("input", (event) => {
      if (
        event.target instanceof HTMLSelectElement
        && event.target.id === "new-card-source-kind"
      ) return;
      this.checkpointNewCardFromForm();
      this.updateNewCardPresentation();
    });
    form.addEventListener("change", () => {
      this.checkpointNewCardFromForm();
      this.updateNewCardPresentation();
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.saveNewCard();
    });
  }

  private async changeNewCardSourceKind(
    sourceKind: NewCardSourceKind,
  ): Promise<void> {
    const previous = this.newCardDraft;
    const select = this.root.querySelector<HTMLSelectElement>(
      "#new-card-source-kind",
    );
    if (!previous || !select) return;
    if (sourceKind === previous.sourceKind) {
      this.updateNewCardPresentation();
      return;
    }
    if (sourceKind === "photo" && !this.demo) {
      select.value = previous.sourceKind;
      this.showNewCardError(
        "Photo cards are not available for account saving. Choose Link or Text.",
        "#new-card-source-kind",
      );
      return;
    }
    if (sourceKind === "photo") {
      const title = this.root.querySelector<HTMLTextAreaElement>(
        "#new-card-prompt",
      );
      if (title && !title.value.trim()) title.value = "Photo";
    }
    const next = this.readNewCardDraftFromForm();
    if (!next) {
      select.value = previous.sourceKind;
      return;
    }

    const ownerUserID = this.viewerUserID;
    const generation = ++this.newCardMediaGeneration;
    const previousPhoto = previous.photo ?? null;
    const needsCleanup = sourceKind !== "photo" && previousPhoto !== null;
    let checkpointed = false;
    this.newCardPhotoStaging = needsCleanup;
    this.updateNewCardPresentation();
    try {
      this.newCardDraftStore.save(ownerUserID, next);
      checkpointed = true;
      if (generation !== this.newCardMediaGeneration) return;
      this.newCardDraft = next;
      if (needsCleanup && previousPhoto) {
        this.releaseNewCardPhotoObjectURL();
        await this.newCardMediaStore?.abort(
          ownerUserID,
          previous.id,
          previousPhoto,
        );
      }
      if (generation !== this.newCardMediaGeneration) return;
      this.renderNewCardComposer();
      if (!this.demo && needsCleanup) {
        this.root.querySelector<HTMLOptionElement>(
          '#new-card-source-kind option[value="photo"]',
        )?.remove();
        this.root.querySelector<HTMLElement>(
          "#new-card-photo-field",
        )?.remove();
        this.root.querySelector<HTMLElement>(
          "#new-card-preview-photo",
        )?.remove();
      }
      this.focusAfterRender(
        sourceKind === "photo"
          ? "#new-card-photo"
          : sourceKind === "link"
            ? "#new-card-source-url"
            : "#new-card-prompt",
      );
    } catch (error) {
      if (generation !== this.newCardMediaGeneration) return;
      if (!checkpointed) {
        this.newCardDraft = previous;
        select.value = previous.sourceKind;
        this.renderNewCardComposer();
      }
      this.showNewCardError(
        checkpointed
          ? "The card kind changed, but this browser could not finish clearing the prior private photo. Discard or log out to retry cleanup."
          : error instanceof Error
            ? error.message
            : "This card kind could not be kept in the private draft.",
        "#new-card-source-kind",
      );
    } finally {
      if (generation === this.newCardMediaGeneration) {
        this.newCardPhotoStaging = false;
        this.updateNewCardPresentation();
      }
    }
  }

  private async discardNewCard(dialog: HTMLDialogElement): Promise<void> {
    const draftID = this.newCardDraft?.id ?? null;
    const generation = ++this.newCardMediaGeneration;
    this.newCardPhotoStaging = true;
    this.updateNewCardPresentation();
    try {
      if (draftID) {
        await this.newCardMediaStore?.clearDraft(
          this.viewerUserID,
          draftID,
        );
      }
      if (generation !== this.newCardMediaGeneration) return;
      this.newCardDraftStore.clear();
      this.newCardDraft = null;
      this.releaseNewCardPhotoObjectURL();
      dialog.dataset.discarded = "true";
      dialog.close();
      this.showToast("Unfinished New card discarded.");
    } catch (error) {
      if (generation !== this.newCardMediaGeneration) return;
      this.showNewCardError(
        error instanceof Error
          ? error.message
          : "This private photo could not be cleared. The draft was kept.",
      );
    } finally {
      if (generation === this.newCardMediaGeneration) {
        this.newCardPhotoStaging = false;
        this.updateNewCardPresentation();
      }
    }
  }

  private async stageNewCardPhoto(image: Blob): Promise<void> {
    if (this.newCardPhotoStaging) return;
    const current = this.readNewCardDraftFromForm() ?? this.newCardDraft;
    if (!current || current.sourceKind !== "photo") return;
    if (!this.newCardMediaStore) {
      this.showNewCardError(
        "Private photo staging is unavailable in this browser.",
        "#new-card-photo",
      );
      return;
    }
    const ownerUserID = this.viewerUserID;
    const generation = ++this.newCardMediaGeneration;
    let candidate: NewCardPhotoMetadata | null = null;
    let checkpointed = false;
    this.newCardPhotoStaging = true;
    this.updateNewCardPresentation();
    try {
      candidate = await this.newCardMediaStore.stage(
        ownerUserID,
        current.id,
        image,
      );
      if (generation !== this.newCardMediaGeneration) {
        await this.newCardMediaStore.abort(
          ownerUserID,
          current.id,
          candidate,
        ).catch(() => undefined);
        return;
      }
      const staged = seedNewCardPhoto(
        current,
        candidate,
        new Date().toISOString(),
      );
      this.newCardDraftStore.save(ownerUserID, staged);
      checkpointed = true;
      if (generation !== this.newCardMediaGeneration) return;
      this.newCardDraft = staged;
      this.releaseNewCardPhotoObjectURL();
      this.newCardPhotoObjectURL = URL.createObjectURL(image);
      await this.newCardMediaStore.commit(
        ownerUserID,
        current.id,
        candidate,
      ).catch(() => undefined);
      if (generation !== this.newCardMediaGeneration) return;
      this.renderNewCardComposer();
      this.focusAfterRender("#new-card-prompt");
    } catch (error) {
      if (candidate && !checkpointed) {
        await this.newCardMediaStore.abort(
          ownerUserID,
          current.id,
          candidate,
        ).catch(() => undefined);
      }
      if (generation !== this.newCardMediaGeneration) return;
      const input = this.root.querySelector<HTMLInputElement>("#new-card-photo");
      if (input) input.value = "";
      this.showNewCardError(
        error instanceof Error
          ? error.message
          : "This private photo could not be staged.",
        "#new-card-photo",
      );
    } finally {
      if (generation === this.newCardMediaGeneration) {
        this.newCardPhotoStaging = false;
        this.updateNewCardPresentation();
      }
    }
  }

  private async removeNewCardPhoto(): Promise<void> {
    if (this.newCardPhotoStaging) return;
    const current = this.readNewCardDraftFromForm() ?? this.newCardDraft;
    if (!current) return;
    const ownerUserID = this.viewerUserID;
    const generation = ++this.newCardMediaGeneration;
    const previousPhoto = current.photo ?? null;
    let checkpointed = false;
    this.newCardPhotoStaging = true;
    this.updateNewCardPresentation();
    try {
      const next = checkpointNewCardDraft(
        current,
        {
          ...structuredClone(current),
          photo: null,
        },
        new Date().toISOString(),
      );
      this.newCardDraftStore.save(ownerUserID, next);
      checkpointed = true;
      if (generation !== this.newCardMediaGeneration) return;
      this.newCardDraft = next;
      this.releaseNewCardPhotoObjectURL();
      if (previousPhoto) {
        await this.newCardMediaStore?.abort(
          ownerUserID,
          current.id,
          previousPhoto,
        );
      }
      if (generation !== this.newCardMediaGeneration) return;
      this.renderNewCardComposer();
      this.focusAfterRender("#new-card-photo");
    } catch (error) {
      if (generation !== this.newCardMediaGeneration) return;
      if (!checkpointed) this.newCardDraft = current;
      this.showNewCardError(
        checkpointed
          ? "The photo was removed from this draft, but its old browser copy could not be cleared. Discard or log out to retry cleanup."
          : error instanceof Error
            ? error.message
            : "This private photo could not be removed.",
        "#new-card-photo",
      );
    } finally {
      if (generation === this.newCardMediaGeneration) {
        this.newCardPhotoStaging = false;
        this.updateNewCardPresentation();
      }
    }
  }

  private renderNewCardComposer(): void {
    const draft = this.newCardDraft;
    if (!draft) return;
    const sourceKind = this.root.querySelector<HTMLSelectElement>(
      "#new-card-source-kind",
    );
    const sourceURL = this.root.querySelector<HTMLInputElement>(
      "#new-card-source-url",
    );
    const ownerTitle = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-prompt",
    );
    const sourceLabel = this.root.querySelector<HTMLInputElement>(
      "#new-card-source-label",
    );
    const privateNotes = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-private-notes",
    );
    const privateSummary = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-private-summary",
    );
    const keywords = this.root.querySelector<HTMLInputElement>(
      "#new-card-keywords",
    );
    const tags = this.root.querySelector<HTMLInputElement>("#new-card-tags");
    const theme = this.root.querySelector<HTMLSelectElement>("#new-card-theme");
    const format = this.root.querySelector<HTMLSelectElement>("#new-card-format");
    const lane = this.root.querySelector<HTMLSelectElement>("#new-card-lane");
    if (
      !sourceKind
      || !sourceURL
      || !ownerTitle
      || !sourceLabel
      || !privateNotes
      || !privateSummary
      || !keywords
      || !tags
      || !theme
      || !format
      || !lane
    ) return;
    sourceKind.value = draft.sourceKind;
    sourceURL.value = draft.sourceURL;
    ownerTitle.value = draft.ownerTitle;
    sourceLabel.value = draft.sourceLabel;
    privateNotes.value = draft.privateNotes;
    privateSummary.value = draft.privateHumanSummary;
    keywords.value = draft.keywords.join(", ");
    tags.value = draft.tags.join(", ");
    theme.replaceChildren(...NATIVE_CONVERSATION_THEMES.map((value) => (
      option(value, humanizeConversationTheme(value))
    )));
    theme.value = draft.primaryTheme;
    format.value = draft.format;
    lane.value = draft.lane;
    this.renderNewCardPlacementRows();
    const error = this.root.querySelector<HTMLElement>("#new-card-error");
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
    this.root.querySelectorAll("[aria-invalid]")
      .forEach((field) => field.removeAttribute("aria-invalid"));
    this.updateNewCardPresentation();
  }

  private renderNewCardPlacementRows(): void {
    const draft = this.newCardDraft;
    const rows = this.root.querySelector<HTMLElement>(
      "#new-card-placement-rows",
    );
    if (!draft || !rows) return;
    const available = this.availableNewCardAudiences();
    const availableByID = new Map(
      available.map((audience) => [audience.id, audience]),
    );
    const placements = [
      ...draft.placements,
      ...available
        .filter(({ id }) => !draft.placements.some(
          ({ audienceId }) => audienceId === id,
        ))
        .map(({ id: audienceId, label: audienceLabel }) => ({
          audienceId,
          audienceLabel,
          conversationEnabled: false,
          libraryEnabled: false,
          isPrimary: false,
          sharedTitle: "",
          sharedSourceLabel: "",
          sharedURL: "",
          sharedNotes: "",
        } satisfies NewCardPlacement)),
    ];
    draft.placements = placements;
    rows.replaceChildren(...placements.map((placement) => {
      const availableAudience = availableByID.get(placement.audienceId);
      const key = opaqueDOMKey(placement.audienceId);
      const row = document.createElement("fieldset");
      row.className = "new-card-placement";
      row.dataset.audienceId = placement.audienceId;
      row.dataset.audienceLabel = placement.audienceLabel;
      row.dataset.available = String(Boolean(availableAudience));
      if (!availableAudience) row.classList.add("is-unavailable");

      const legend = document.createElement("legend");
      legend.textContent = availableAudience
        ? placement.audienceLabel
        : `${placement.audienceLabel} · unavailable`;
      const controls = document.createElement("div");
      controls.className = "new-card-placement-controls";
      const primary = labeledControl(
        "radio",
        `new-card-primary-${key}`,
        "Default audience",
        placement.isPrimary,
      );
      primary.input.name = "new-card-primary";
      primary.input.dataset.newCardPrimary = "";
      const conversation = labeledControl(
        "checkbox",
        `new-card-conversation-${key}`,
        "Private card",
        placement.conversationEnabled,
      );
      conversation.input.dataset.newCardDestination = "conversation";
      const library = labeledControl(
        "checkbox",
        `new-card-library-${key}`,
        "Shared Library",
        placement.libraryEnabled,
      );
      library.input.dataset.newCardDestination = "library";
      for (const control of [primary, conversation, library]) {
        control.input.disabled = !availableAudience;
        controls.append(control.label);
      }

      const shared = document.createElement("div");
      shared.className = "new-card-shared-fields";
      shared.hidden = !placement.libraryEnabled;
      const sharedTitle = labeledTextInput(
        `new-card-shared-title-${key}`,
        "Shared Library title",
        placement.sharedTitle,
      );
      sharedTitle.input.dataset.newCardSharedField = "title";
      const sharedSource = labeledTextInput(
        `new-card-shared-source-${key}`,
        "Shared source label",
        placement.sharedSourceLabel,
      );
      sharedSource.input.dataset.newCardSharedField = "source";
      const sharedURL = labeledTextInput(
        `new-card-shared-url-${key}`,
        "Shared source link",
        placement.sharedURL,
        "url",
      );
      sharedURL.input.dataset.newCardSharedField = "url";
      const sharedNotes = document.createElement("label");
      sharedNotes.htmlFor = `new-card-shared-notes-${key}`;
      sharedNotes.textContent = "Shared Notes";
      const sharedNotesInput = document.createElement("textarea");
      sharedNotesInput.id = `new-card-shared-notes-${key}`;
      sharedNotesInput.rows = 3;
      sharedNotesInput.maxLength = MAX_NEW_CARD_SHARED_NOTES_LENGTH;
      sharedNotesInput.value = placement.sharedNotes;
      sharedNotesInput.dataset.newCardSharedField = "notes";
      sharedNotes.append(sharedNotesInput);
      for (const field of [
        sharedTitle,
        sharedSource,
        sharedURL,
      ]) {
        field.input.disabled = !availableAudience;
        shared.append(field.label);
      }
      sharedNotesInput.disabled = !availableAudience;
      shared.append(sharedNotes);
      row.append(legend, controls, shared);
      return row;
    }));
  }

  private readNewCardDraftFromForm(): NewCardDraft | null {
    const previous = this.newCardDraft;
    if (!previous) return null;
    const sourceKind = this.root.querySelector<HTMLSelectElement>(
      "#new-card-source-kind",
    )?.value as NewCardSourceKind | undefined;
    const sourceURL = this.root.querySelector<HTMLInputElement>(
      "#new-card-source-url",
    )?.value ?? "";
    const ownerTitle = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-prompt",
    )?.value ?? "";
    const sourceLabel = this.root.querySelector<HTMLInputElement>(
      "#new-card-source-label",
    )?.value ?? "";
    const privateNotes = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-private-notes",
    )?.value ?? "";
    const privateHumanSummary = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-private-summary",
    )?.value ?? "";
    const keywords = parseTerms(
      this.root.querySelector<HTMLInputElement>("#new-card-keywords")?.value
        ?? "",
    );
    const tags = parseTerms(
      this.root.querySelector<HTMLInputElement>("#new-card-tags")?.value ?? "",
    );
    const primaryTheme = this.root.querySelector<HTMLSelectElement>(
      "#new-card-theme",
    )?.value ?? "";
    const format = this.root.querySelector<HTMLSelectElement>(
      "#new-card-format",
    )?.value as LibraryFormat | undefined;
    const lane = this.root.querySelector<HTMLSelectElement>(
      "#new-card-lane",
    )?.value as LibraryLane | undefined;
    if (!sourceKind || !format || !lane) return null;
    const placements = [...this.root.querySelectorAll<HTMLElement>(
      ".new-card-placement",
    )].map((row) => ({
      audienceId: row.dataset.audienceId ?? "",
      audienceLabel: row.dataset.audienceLabel ?? "",
      conversationEnabled: Boolean(
        row.querySelector<HTMLInputElement>(
          '[data-new-card-destination="conversation"]',
        )?.checked,
      ),
      libraryEnabled: Boolean(
        row.querySelector<HTMLInputElement>(
          '[data-new-card-destination="library"]',
        )?.checked,
      ),
      isPrimary: Boolean(
        row.querySelector<HTMLInputElement>("[data-new-card-primary]")?.checked,
      ),
      sharedTitle: row.querySelector<HTMLInputElement>(
        '[data-new-card-shared-field="title"]',
      )?.value ?? "",
      sharedSourceLabel: row.querySelector<HTMLInputElement>(
        '[data-new-card-shared-field="source"]',
      )?.value ?? "",
      sharedURL: row.querySelector<HTMLInputElement>(
        '[data-new-card-shared-field="url"]',
      )?.value ?? "",
      sharedNotes: row.querySelector<HTMLTextAreaElement>(
        '[data-new-card-shared-field="notes"]',
      )?.value ?? "",
    }));
    const next = {
      ...structuredClone(previous),
      sourceKind,
      photo: sourceKind === "photo" ? previous.photo ?? null : null,
      ownerTitle,
      sourceLabel,
      sourceURL: sourceKind === "link" ? sourceURL : "",
      primaryTheme,
      privateNotes,
      privateHumanSummary,
      keywords,
      tags,
      format,
      lane,
      placements,
    };
    return checkpointNewCardDraft(
      previous,
      next,
      new Date().toISOString(),
    );
  }

  private checkpointNewCardFromForm(): void {
    const draft = this.readNewCardDraftFromForm();
    if (!draft) return;
    this.newCardDraft = draft;
    try {
      this.newCardDraftStore.save(this.viewerUserID, draft);
      const error = this.root.querySelector<HTMLElement>("#new-card-error");
      if (error) {
        error.hidden = true;
        error.textContent = "";
      }
    } catch (error) {
      this.showNewCardError(
        error instanceof Error
          ? error.message
          : "This New card draft could not be kept in this browser.",
      );
    }
  }

  private updateNewCardPresentation(): void {
    const draft = this.newCardDraft;
    if (!draft) return;
    const sourceURLField = this.root.querySelector<HTMLElement>(
      "#new-card-source-url-field",
    );
    const sourceURL = this.root.querySelector<HTMLInputElement>(
      "#new-card-source-url",
    );
    const isLink = draft.sourceKind === "link";
    const isPhoto = draft.sourceKind === "photo";
    const sourceKind = this.root.querySelector<HTMLSelectElement>(
      "#new-card-source-kind",
    );
    if (sourceKind) sourceKind.disabled = this.newCardPhotoStaging;
    if (sourceURLField) sourceURLField.hidden = !isLink;
    if (sourceURL) {
      sourceURL.required = isLink;
      if (!isLink) sourceURL.value = "";
    }
    const photoField = this.root.querySelector<HTMLElement>(
      "#new-card-photo-field",
    );
    const photoInput = this.root.querySelector<HTMLInputElement>(
      "#new-card-photo",
    );
    const photoPreview = this.root.querySelector<HTMLElement>(
      "#new-card-photo-preview",
    );
    const photoPreviewImage = this.root.querySelector<HTMLImageElement>(
      "#new-card-photo-preview-image",
    );
    const removePhoto = this.root.querySelector<HTMLButtonElement>(
      "#remove-new-card-photo",
    );
    const photoState = this.root.querySelector<HTMLElement>(
      "#new-card-photo-state",
    );
    if (photoField) photoField.hidden = !isPhoto;
    if (photoInput) {
      photoInput.disabled = this.newCardPhotoStaging;
      if (!isPhoto) photoInput.value = "";
    }
    const photoReady = Boolean(
      isPhoto && draft.photo && this.newCardPhotoObjectURL,
    );
    if (photoPreview) photoPreview.hidden = !photoReady;
    if (photoPreviewImage) {
      if (photoReady && this.newCardPhotoObjectURL) {
        photoPreviewImage.src = this.newCardPhotoObjectURL;
      } else {
        photoPreviewImage.removeAttribute("src");
      }
    }
    if (removePhoto) {
      removePhoto.hidden = !photoReady;
      removePhoto.disabled = this.newCardPhotoStaging;
    }
    if (photoState) {
      photoState.textContent = this.newCardPhotoStaging
        ? "Checking this photo privately…"
        : draft.photo
          ? `${humanImageType(draft.photo.mimeType)} · ${
            humanFileSize(draft.photo.byteCount)
          } · private draft`
          : "No photo chosen.";
    }
    const promptLabel = this.root.querySelector<HTMLElement>(
      "#new-card-prompt-label",
    );
    if (promptLabel) {
      promptLabel.textContent = isPhoto
        ? "Private photo caption or card title"
        : "Private card title or thought";
    }
    const prompt = this.root.querySelector<HTMLTextAreaElement>(
      "#new-card-prompt",
    );
    if (prompt) {
      prompt.placeholder = isPhoto
        ? "Optional caption; “Photo” is used privately if left blank"
        : "What should the card say when it is revealed?";
    }
    setText(
      this.root,
      "#new-card-source-help",
      isPhoto
        ? this.demo
          ? "Photo bytes and caption stay private. Photo + link is not supported."
          : "This recovered photo remains only in this browser. Choose Link or Text to remove it, or Discard the draft."
        : "Pasting a link never fetches it. Text cards stay link-free.",
    );
    for (const row of this.root.querySelectorAll<HTMLElement>(
      ".new-card-placement",
    )) {
      const enabled = Boolean(
        row.querySelector<HTMLInputElement>(
          '[data-new-card-destination="library"]',
        )?.checked,
      );
      const shared = row.querySelector<HTMLElement>(
        ".new-card-shared-fields",
      );
      if (shared) shared.hidden = !enabled;
    }
    const retained = draft.placements.filter(
      ({ conversationEnabled, libraryEnabled }) => (
        conversationEnabled || libraryEnabled
      ),
    );
    setText(
      this.root,
      "#new-card-preview-title",
      draft.ownerTitle.trim() || "Nothing placed yet.",
    );
    setText(
      this.root,
      "#new-card-preview-source",
      draft.sourceKind === "link"
        ? safeExternalURL(draft.sourceURL)
          ? new URL(draft.sourceURL).hostname.replace(/^www\./i, "")
          : "Link"
        : draft.sourceKind === "photo"
          ? "Private photo"
          : "Text thought",
    );
    const placementPreviewPhoto = this.root.querySelector<HTMLImageElement>(
      "#new-card-preview-photo",
    );
    if (placementPreviewPhoto) {
      placementPreviewPhoto.hidden = !photoReady;
      if (photoReady && this.newCardPhotoObjectURL) {
        placementPreviewPhoto.src = this.newCardPhotoObjectURL;
      } else {
        placementPreviewPhoto.removeAttribute("src");
      }
    }
    setText(
      this.root,
      "#new-card-preview-placements",
      retained.length
        ? retained.map((placement) => {
          const destinations = [
            placement.conversationEnabled ? "Private card" : "",
            placement.libraryEnabled ? "Shared Library" : "",
          ].filter(Boolean).join(" + ");
          return `${placement.audienceLabel}: ${destinations}${
            placement.isPrimary ? " · default" : ""
          }`;
        }).join(" · ")
        : "Choose a destination to preview where this card will live.",
    );
    const status = this.root.querySelector<HTMLElement>("#new-card-status");
    if (status) {
      if (this.newCardSaveInProgress) {
        status.textContent = "Saving this exact draft. Keep this page open.";
      } else if (this.newCardPhotoStaging) {
        status.textContent =
          "Checking image type, readability, size, and integrity privately.";
      } else if (isPhoto) {
        status.textContent = this.demo
          ? photoReady
            ? "Photo staged privately. This sample does not upload it."
            : "Choose a photo to keep this private draft recoverable."
          : "Recovered local photo draft. It cannot be saved or synced; switch to Link or Text, or Discard it.";
      } else if (this.repository && supportsBookmarkCreate(this.repository)) {
        status.textContent = this.demo
          ? "This sample save stays only in this browser."
          : "Ready to save to your private account after validation.";
      } else {
        status.textContent =
          "This draft stays private here; account saving is not available yet.";
      }
    }
    const save = this.root.querySelector<HTMLButtonElement>("#save-new-card");
    if (save) {
      save.disabled = (
        this.newCardSaveInProgress
        || this.newCardPhotoStaging
        || (isPhoto && !this.demo)
      );
      save.textContent = this.newCardSaveInProgress
        ? "Saving…"
        : "Save New card";
    }
    const summaryFromNotes = this.root.querySelector<HTMLButtonElement>(
      "#new-card-summary-from-notes",
    );
    if (summaryFromNotes) {
      summaryFromNotes.disabled = (
        this.newCardSaveInProgress
        || this.newCardPhotoStaging
        || !draft.privateNotes.trim()
      );
    }
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#new-card-dialog",
    );
    dialog?.setAttribute(
      "aria-busy",
      String(this.newCardSaveInProgress || this.newCardPhotoStaging),
    );
  }

  private showNewCardError(message: string, selector?: string): void {
    const error = this.root.querySelector<HTMLElement>("#new-card-error");
    if (!error) return;
    this.root.querySelectorAll("[aria-invalid]")
      .forEach((field) => field.removeAttribute("aria-invalid"));
    if (selector) {
      this.root.querySelector<HTMLElement>(selector)
        ?.setAttribute("aria-invalid", "true");
    }
    error.hidden = false;
    error.textContent = message;
    error.focus();
  }

  private async saveNewCard(): Promise<void> {
    if (this.newCardSaveInProgress || this.newCardPhotoStaging) return;
    const draft = this.readNewCardDraftFromForm();
    if (!draft) return;
    this.newCardDraft = draft;
    try {
      this.newCardDraftStore.save(this.viewerUserID, draft);
    } catch (error) {
      this.showNewCardError(
        error instanceof Error
          ? error.message
          : "This exact New card draft could not be kept before Save.",
      );
      return;
    }
    const validationError = validateNewCardDraft(
      draft,
      this.availableNewCardAudiences(),
    );
    if (validationError) {
      const selector = validationError.field === "title"
        ? "#new-card-prompt"
        : validationError.field === "source"
          ? draft.sourceKind === "link"
            ? "#new-card-source-url"
            : "#new-card-prompt"
          : validationError.field === "photo"
            ? "#new-card-photo"
          : validationError.field === "theme"
            ? "#new-card-theme"
            : validationError.field === "privateNotes"
              ? "#new-card-private-notes"
              : validationError.field === "privateHumanSummary"
                ? "#new-card-private-summary"
                : validationError.field === "keywords"
                  ? "#new-card-keywords"
                  : validationError.field === "tags"
                    ? "#new-card-tags"
                    : validationError.field.startsWith("placement:")
                      ? `.new-card-placement[data-audience-id="${
                        CSS.escape(validationError.field.slice(10))
                      }"]`
                      : "#new-card-placement-rows";
      this.showNewCardError(validationError.message, selector);
      return;
    }
    if (draft.sourceKind === "photo") {
      this.showNewCardError(
        "Your photo is staged privately, but secure account photo saving is not available yet. Close and keep this draft; nothing was uploaded.",
        "#new-card-photo",
      );
      return;
    }
    if (!this.repository || !supportsBookmarkCreate(this.repository)) {
      this.showNewCardError(
        "Saving a New card to this account is not available yet. Your exact draft remains private in this browser.",
      );
      return;
    }
    this.newCardSaveInProgress = true;
    this.updateNewCardPresentation();
    try {
      const bookmark = newCardBookmark(
        draft,
        this.viewerUserID,
        this.availableNewCardAudiences(),
      );
      const result = await this.repository.createBookmark(bookmark);
      this.bookmarks = result.workspace.bookmarks;
      this.enrichmentJobs = result.workspace.enrichmentJobs;
      if (this.demo) {
        for (const publication of result.bookmark.libraryPublications.filter(
          ({ revokedAt }) => revokedAt === null,
        )) {
          const receipt = await this.receiptRepository?.provisionReceipt(
            publication.id,
          );
          if (receipt) {
            this.receipts = replaceLibraryReceipt(this.receipts, receipt);
          }
        }
      }
      if (this.recipientLibraryRepository) {
        this.recipientEntries =
          await this.recipientLibraryRepository.loadLibrary();
        this.receipts = this.recipientEntries.map(({ receipt }) => receipt);
      }
      await this.newCardMediaStore?.clearDraft(
        this.viewerUserID,
        draft.id,
      ).catch(() => undefined);
      this.releaseNewCardPhotoObjectURL();
      this.newCardDraftStore.clear();
      this.newCardDraft = null;
      const created = result.bookmark;
      const firstPublication = created.libraryPublications.find(
        ({ revokedAt }) => revokedAt === null,
      ) ?? null;
      this.selectedID = created.id;
      this.selectedPublicationID = firstPublication?.id ?? null;
      this.libraryDetailMode = "edit";
      const dialog = this.root.querySelector<HTMLDialogElement>(
        "#new-card-dialog",
      );
      if (dialog) {
        dialog.dataset.saved = "true";
        dialog.close();
      }
      this.populateLibraryFilters();
      this.renderLibrary();
      const summaryDeliveryRetrying = (
        !this.demo
        && result.summaryEnrichmentJob?.state === "failed"
      );
      this.showToast(
        result.summaryEnrichmentJob
          ? this.demo
            ? "New card saved here. Private search has not updated online."
            : summaryDeliveryRetrying
              ? "New card saved. Private Summary re-index delivery will retry."
              : "New card saved. Private Summary re-indexing is queued."
          : this.demo
            ? "New card saved in this sample browser space."
            : "New card saved to your private workspace.",
        summaryDeliveryRetrying,
        {
          label: "View card",
          onActivate: () => {
            this.selectedID = created.id;
            this.selectedPublicationID = firstPublication?.id ?? null;
            this.libraryDetailMode = "edit";
            this.renderLibrary();
            this.root.querySelector(".app-shell")?.classList.add("detail-open");
            this.focusAfterRender("#detail-title");
          },
        },
      );
    } catch (error) {
      this.showNewCardError(
        error instanceof Error
          ? error.message
          : "This New card could not be saved. The exact draft remains here.",
      );
    } finally {
      this.newCardSaveInProgress = false;
      this.updateNewCardPresentation();
    }
  }

  private bindOwnerBookmarkManager(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#owner-bookmark-dialog",
    );
    const open = this.root.querySelector<HTMLButtonElement>(
      "#manage-bookmarks",
    );
    const search = this.root.querySelector<HTMLInputElement>(
      "#owner-bookmark-search",
    );
    const audience = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-audience",
    );
    const cluster = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-cluster",
    );
    const review = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-review",
    );
    const sort = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-sort",
    );
    if (
      !dialog
      || !open
      || !search
      || !audience
      || !cluster
      || !review
      || !sort
    ) return;

    open.addEventListener("click", () => this.openOwnerBookmarkManager());
    const close = () => {
      if (this.ownerBulkSaveInProgress) {
        this.showToast("Finish saving these private fields before closing.");
        return;
      }
      dialog.close();
    };
    this.root.querySelector<HTMLButtonElement>("#close-owner-bookmarks")
      ?.addEventListener("click", close);
    this.root.querySelector<HTMLButtonElement>("#done-owner-bookmarks")
      ?.addEventListener("click", close);
    dialog.addEventListener("cancel", (event) => {
      if (this.ownerBulkSaveInProgress) event.preventDefault();
    });
    dialog.addEventListener("close", () => {
      if (this.ownerBulkSaveInProgress) return;
      const handedOff = dialog.dataset.handedOff === "true";
      delete dialog.dataset.handedOff;
      this.resetOwnerQuickEdit();
      this.resetOwnerBookmarkManager();
      this.resetOwnerBulkCleanup();
      if (this.ownerManagerWorkspaceChanged) {
        this.ownerManagerWorkspaceChanged = false;
        this.populateLibraryFilters();
        this.renderLibrary();
      }
      if (!handedOff) this.focusAfterRender("#manage-bookmarks");
    });
    search.addEventListener("input", () => {
      this.ownerManagerQuery = search.value;
      this.renderOwnerBookmarkManager();
    });
    search.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowDown") return;
      const first = dialog.querySelector<HTMLButtonElement>(
        ".owner-bookmark-row",
      );
      if (!first) return;
      event.preventDefault();
      first.focus();
    });
    audience.addEventListener("change", () => {
      this.ownerManagerAudienceID = audience.value;
      this.renderOwnerBookmarkManager();
    });
    cluster.addEventListener("change", () => {
      this.ownerManagerCluster = cluster.value;
      this.renderOwnerBookmarkManager();
    });
    review.addEventListener("change", () => {
      this.ownerManagerReview = review.value as OwnerReviewFilter;
      this.renderOwnerBookmarkManager();
    });
    sort.addEventListener("change", () => {
      this.ownerManagerSort = sort.value as OwnerPreparationSort;
      this.renderOwnerBookmarkManager();
    });
    this.root.querySelector<HTMLButtonElement>("#reshuffle-owner-bookmarks")
      ?.addEventListener("click", () => {
        this.ownerManagerShuffleSeed = crypto.randomUUID();
        this.renderOwnerBookmarkManager();
        this.focusAfterRender("#reshuffle-owner-bookmarks");
      });
    dialog.querySelectorAll<HTMLButtonElement>(
      "[data-owner-destination]",
    ).forEach((button) => {
      button.addEventListener("click", () => {
        this.ownerManagerDestination = (
          button.dataset.ownerDestination ?? "all"
        ) as OwnerDestinationFilter;
        this.renderOwnerBookmarkManager();
      });
    });
    this.root.querySelector<HTMLButtonElement>(
      "#clear-owner-bookmark-filters",
    )?.addEventListener("click", () => {
      this.resetOwnerBookmarkManager();
      this.populateOwnerBookmarkAudiences();
      this.populateOwnerBookmarkClusters();
      this.populateOwnerBookmarkSources();
      this.renderOwnerBookmarkManager();
      this.focusAfterRender("#owner-bookmark-search");
    });
    this.root.querySelector<HTMLButtonElement>(
      "#select-owner-bookmarks",
    )?.addEventListener("click", () => {
      const shown = this.ownerBookmarkCandidates();
      const allSelected = shown.length > 0 && shown.every(
        ({ id }) => this.ownerManagerSelection.has(id),
      );
      for (const { id } of shown) {
        if (allSelected) {
          this.ownerManagerSelection.delete(id);
        } else {
          this.ownerManagerSelection.add(id);
        }
      }
      this.invalidateOwnerBulkPreview();
      this.renderOwnerBookmarkManager();
      this.focusAfterRender("#select-owner-bookmarks");
    });
    this.root.querySelector<HTMLButtonElement>(
      "#clear-owner-bookmark-selection",
    )?.addEventListener("click", () => {
      this.ownerManagerSelection.clear();
      this.invalidateOwnerBulkPreview();
      this.renderOwnerBookmarkManager();
      this.focusAfterRender("#select-owner-bookmarks");
    });
    const invalidateBulkPreview = () => {
      this.invalidateOwnerBulkPreview();
      this.renderOwnerBulkCleanup();
    };
    this.root.querySelector<HTMLSelectElement>("#owner-bulk-theme")
      ?.addEventListener("change", invalidateBulkPreview);
    this.root.querySelector<HTMLInputElement>("#owner-bulk-tags")
      ?.addEventListener("input", invalidateBulkPreview);
    this.root.querySelector<HTMLInputElement>("#owner-bulk-remove-tags")
      ?.addEventListener("input", invalidateBulkPreview);
    this.root.querySelector<HTMLSelectElement>("#owner-bulk-audience")
      ?.addEventListener("change", invalidateBulkPreview);
    this.root.querySelector<HTMLSelectElement>("#owner-bulk-destination")
      ?.addEventListener("change", invalidateBulkPreview);
    this.root.querySelector<HTMLInputElement>("#owner-bulk-primary")
      ?.addEventListener("change", invalidateBulkPreview);
    this.root.querySelector<HTMLButtonElement>("#preview-owner-bulk")
      ?.addEventListener("click", () => this.previewOwnerBulkCleanup());
    this.root.querySelector<HTMLButtonElement>("#apply-owner-bulk")
      ?.addEventListener("click", () => void this.applyOwnerBulkCleanup());
    const updateOwnerQuickEdit = () => {
      this.renderOwnerQuickEdit();
      this.renderOwnerPreparationCounts();
    };
    this.root.querySelector<HTMLTextAreaElement>("#owner-quick-notes")
      ?.addEventListener("input", updateOwnerQuickEdit);
    this.root.querySelector<HTMLTextAreaElement>("#owner-quick-summary")
      ?.addEventListener("input", updateOwnerQuickEdit);
    this.root.querySelector<HTMLInputElement>("#owner-quick-keywords")
      ?.addEventListener("input", updateOwnerQuickEdit);
    this.root.querySelector<HTMLButtonElement>("#close-owner-quick")
      ?.addEventListener("click", () => this.closeOwnerQuickEdit(true));
    this.root.querySelector<HTMLButtonElement>("#cancel-owner-quick")
      ?.addEventListener("click", () => this.closeOwnerQuickEdit(true));
    this.root.querySelector<HTMLButtonElement>("#save-owner-quick")
      ?.addEventListener("click", () => void this.saveOwnerQuickEdit());
    this.root.querySelector<HTMLButtonElement>("#save-next-owner-quick")
      ?.addEventListener("click", () => void this.saveOwnerQuickEdit(true));
    this.root.querySelector<HTMLButtonElement>("#open-owner-full-editor")
      ?.addEventListener("click", () => {
        if (this.ownerBulkSaveInProgress) return;
        const bookmark = this.ownerQuickEditBookmark();
        if (!bookmark) return;
        if (this.retainUnsavedOwnerQuickEdit(
          "Save or cancel this private draft before opening the full editor.",
        )) return;
        dialog.dataset.handedOff = "true";
        this.resetOwnerQuickEdit();
        dialog.close();
        this.openOwnerBookmarkFromManager(bookmark);
      });
    dialog.addEventListener("keydown", (event) => {
      if (
        (event.metaKey || event.ctrlKey)
        && event.key === "Enter"
        && (event.target as HTMLElement | null)?.closest("#owner-quick-edit")
      ) {
        event.preventDefault();
        void this.saveOwnerQuickEdit(true);
        return;
      }
      const row = (event.target as HTMLElement | null)
        ?.closest<HTMLButtonElement>(".owner-bookmark-row");
      if (
        !row
        || !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
      ) return;
      const rows = [...dialog.querySelectorAll<HTMLButtonElement>(
        ".owner-bookmark-row",
      )];
      const current = rows.indexOf(row);
      if (current < 0) return;
      event.preventDefault();
      const next = event.key === "Home"
        ? 0
        : event.key === "End"
          ? rows.length - 1
          : Math.min(
            Math.max(current + (event.key === "ArrowDown" ? 1 : -1), 0),
            rows.length - 1,
          );
      rows[next]?.focus();
    });
  }

  private openOwnerBookmarkManager(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#owner-bookmark-dialog",
    );
    if (!dialog || dialog.open || this.ownerBulkSaveInProgress) return;
    this.resetOwnerBookmarkManager();
    this.resetOwnerBulkCleanup();
    this.resetOwnerQuickEdit();
    this.populateOwnerBookmarkAudiences();
    this.populateOwnerBookmarkClusters();
    this.populateOwnerBookmarkSources();
    this.populateOwnerBulkThemes();
    this.populateOwnerBulkAudiences();
    this.renderOwnerBookmarkManager();
    dialog.showModal();
    dialog.querySelector<HTMLInputElement>("#owner-bookmark-search")?.focus();
  }

  private resetOwnerBookmarkManager(): void {
    this.ownerManagerQuery = "";
    this.ownerManagerAudienceID = "all";
    this.ownerManagerDestination = "all";
    this.ownerManagerCluster = "all";
    this.ownerManagerReview = "all";
    this.ownerManagerIncludedSources.clear();
    this.ownerManagerExcludedSources.clear();
    this.ownerManagerSort = "newest";
    const search = this.root.querySelector<HTMLInputElement>(
      "#owner-bookmark-search",
    );
    if (search) search.value = "";
    const audience = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-audience",
    );
    if (audience) audience.value = "all";
    const cluster = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-cluster",
    );
    if (cluster) cluster.value = "all";
    const review = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-review",
    );
    if (review) review.value = "all";
    const sort = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-sort",
    );
    if (sort) sort.value = "newest";
  }

  private resetOwnerBulkCleanup(): void {
    this.ownerManagerSelection.clear();
    this.ownerBulkPreviewSignature = null;
    this.ownerBulkSaveInProgress = false;
    this.resetOwnerBulkPatchFields();
  }

  private resetOwnerBulkPatchFields(): void {
    const theme = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-theme",
    );
    if (theme) theme.value = "";
    const tags = this.root.querySelector<HTMLInputElement>("#owner-bulk-tags");
    if (tags) tags.value = "";
    const removeTags = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-remove-tags",
    );
    if (removeTags) removeTags.value = "";
    const audience = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-audience",
    );
    if (audience) audience.value = "";
    const destination = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-destination",
    );
    if (destination) destination.value = "keep";
    const primary = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-primary",
    );
    if (primary) primary.checked = false;
  }

  private ownerQuickEditBookmark(): Bookmark | null {
    if (!this.ownerQuickEditBookmarkID) return null;
    return this.bookmarks.find(({ id }) => (
      id === this.ownerQuickEditBookmarkID
    )) ?? null;
  }

  private ownerQuickEditInput(): PrivateQuickEditInput | null {
    const notes = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-notes",
    );
    const summary = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-summary",
    );
    const keywords = this.root.querySelector<HTMLInputElement>(
      "#owner-quick-keywords",
    );
    if (!notes || !summary || !keywords) return null;
    return {
      privateNotes: notes.value,
      privateHumanSummary: summary.value,
      keywords: parseTerms(keywords.value),
    };
  }

  private hasUnsavedOwnerQuickEdit(): boolean {
    if (!this.ownerQuickEditBookmarkID) return false;
    const bookmark = this.ownerQuickEditBookmark();
    const input = this.ownerQuickEditInput();
    if (!bookmark || !input) return true;
    try {
      return privateQuickEditChangesBookmark(bookmark, input);
    } catch {
      return true;
    }
  }

  private retainUnsavedOwnerQuickEdit(guidance?: string): boolean {
    if (!this.hasUnsavedOwnerQuickEdit()) return false;
    if (guidance) {
      this.showToast(guidance);
      const status = this.root.querySelector<HTMLElement>(
        "#owner-quick-status",
      );
      if (status) status.textContent = guidance;
    }
    this.root.querySelector<HTMLTextAreaElement>("#owner-quick-notes")
      ?.focus();
    return true;
  }

  private populateOwnerQuickEditFields(bookmark: Bookmark): void {
    const notes = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-notes",
    );
    const summary = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-summary",
    );
    const keywords = this.root.querySelector<HTMLInputElement>(
      "#owner-quick-keywords",
    );
    if (notes) notes.value = bookmark.privateNotes;
    if (summary) summary.value = bookmark.privateHumanSummary;
    if (keywords) keywords.value = bookmark.keywords.join(", ");
  }

  private resetOwnerQuickEdit(): void {
    this.ownerQuickEditBookmarkID = null;
    const section = this.root.querySelector<HTMLElement>("#owner-quick-edit");
    const title = this.root.querySelector<HTMLElement>("#owner-quick-title");
    const notes = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-notes",
    );
    const summary = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-summary",
    );
    const keywords = this.root.querySelector<HTMLInputElement>(
      "#owner-quick-keywords",
    );
    const vector = this.root.querySelector<HTMLElement>("#owner-quick-vector");
    const status = this.root.querySelector<HTMLElement>("#owner-quick-status");
    const error = this.root.querySelector<HTMLElement>("#owner-quick-error");
    if (section) section.hidden = true;
    if (title) title.textContent = "";
    if (notes) notes.value = "";
    if (summary) summary.value = "";
    if (keywords) keywords.value = "";
    if (vector) {
      vector.className = "owner-quick-vector";
      vector.textContent = "";
    }
    if (status) status.textContent = "";
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
    for (const field of [notes, summary, keywords]) {
      field?.removeAttribute("aria-invalid");
      if (field) field.disabled = false;
    }
  }

  private openOwnerQuickEdit(bookmark: Bookmark): void {
    if (this.ownerBulkSaveInProgress) return;
    const current = this.ownerQuickEditBookmark();
    const guidance = current && current.id !== bookmark.id
      ? "Save or cancel this private draft before opening another bookmark."
      : undefined;
    if (this.retainUnsavedOwnerQuickEdit(guidance)) return;
    this.resetOwnerBulkCleanup();
    this.ownerQuickEditBookmarkID = bookmark.id;
    this.populateOwnerQuickEditFields(bookmark);
    this.renderOwnerBookmarkManager();
    this.root.querySelector<HTMLElement>("#owner-quick-edit")
      ?.scrollIntoView({ block: "nearest" });
    this.root.querySelector<HTMLTextAreaElement>("#owner-quick-notes")?.focus();
  }

  private closeOwnerQuickEdit(restoreFocus: boolean): void {
    if (this.ownerBulkSaveInProgress) return;
    const candidates = this.ownerBookmarkCandidates();
    const quickButtonIndex = candidates.findIndex(({ id }) => (
      id === this.ownerQuickEditBookmarkID
    ));
    this.resetOwnerQuickEdit();
    this.renderOwnerBookmarkManager();
    if (restoreFocus && quickButtonIndex >= 0) {
      this.root.querySelectorAll<HTMLButtonElement>(
        ".owner-bookmark-quick-button",
      )[quickButtonIndex]?.focus();
    }
  }

  private renderOwnerQuickEdit(): void {
    const section = this.root.querySelector<HTMLElement>("#owner-quick-edit");
    const title = this.root.querySelector<HTMLElement>("#owner-quick-title");
    const vector = this.root.querySelector<HTMLElement>("#owner-quick-vector");
    const status = this.root.querySelector<HTMLElement>("#owner-quick-status");
    const error = this.root.querySelector<HTMLElement>("#owner-quick-error");
    const save = this.root.querySelector<HTMLButtonElement>("#save-owner-quick");
    const saveNext = this.root.querySelector<HTMLButtonElement>(
      "#save-next-owner-quick",
    );
    const cancel = this.root.querySelector<HTMLButtonElement>(
      "#cancel-owner-quick",
    );
    const close = this.root.querySelector<HTMLButtonElement>(
      "#close-owner-quick",
    );
    const full = this.root.querySelector<HTMLButtonElement>(
      "#open-owner-full-editor",
    );
    const notes = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-notes",
    );
    const summary = this.root.querySelector<HTMLTextAreaElement>(
      "#owner-quick-summary",
    );
    const keywords = this.root.querySelector<HTMLInputElement>(
      "#owner-quick-keywords",
    );
    const bookmark = this.ownerQuickEditBookmark();
    if (
      !section
      || !title
      || !vector
      || !status
      || !error
      || !save
      || !saveNext
      || !cancel
      || !close
      || !full
      || !notes
      || !summary
      || !keywords
    ) return;
    if (!bookmark) {
      this.resetOwnerQuickEdit();
      return;
    }

    section.hidden = false;
    title.textContent = `Quick edit · ${bookmark.title}`;
    const busy = this.ownerBulkSaveInProgress;
    for (const field of [notes, summary, keywords]) {
      field.disabled = busy;
      field.removeAttribute("aria-invalid");
    }
    cancel.disabled = busy;
    close.disabled = busy;
    full.disabled = busy;
    error.hidden = true;
    error.textContent = "";

    const input = this.ownerQuickEditInput();
    if (!input) return;
    const validationError = validatePrivateQuickEdit(bookmark, input);
    if (validationError) {
      const field = validationError.field === "notes"
        ? notes
        : validationError.field === "summary"
          ? summary
          : keywords;
      field.setAttribute("aria-invalid", "true");
      error.hidden = false;
      error.textContent = validationError.message;
      status.textContent = "Fix the private field above before saving.";
      save.disabled = true;
      saveNext.disabled = true;
    } else {
      const changed = privateQuickEditChangesBookmark(bookmark, input);
      status.textContent = busy
        ? "Saving these private fields. Keep this page open."
        : changed
          ? "Private changes are ready to save."
          : "No private changes yet.";
      save.disabled = busy || !changed;
      saveNext.disabled = busy || !changed;
    }

    const summaryDraftChanged = (
      input.privateHumanSummary.trim() !== bookmark.privateHumanSummary
    );
    const vectorState = summaryDraftChanged
      ? {
        state: "stale" as const,
        badge: "stale draft",
        detail: this.demo
          ? "Private search ignores the older Summary while this draft is unsaved. Save to queue an update here."
          : "Private search ignores the older Summary while this edit is unsaved. Save to request re-indexing.",
      }
      : this.vectorPresentation(bookmark);
    vector.className = `owner-quick-vector is-${vectorState.state}`;
    vector.textContent = `${vectorState.badge} · ${vectorState.detail}`;
  }

  private async saveOwnerQuickEdit(advanceToNext = false): Promise<void> {
    if (this.ownerBulkSaveInProgress || !this.repository) return;
    const bookmark = this.ownerQuickEditBookmark();
    const input = this.ownerQuickEditInput();
    const error = this.root.querySelector<HTMLElement>("#owner-quick-error");
    if (!bookmark || !input || !error) return;
    const validationError = validatePrivateQuickEdit(bookmark, input);
    if (validationError) {
      this.renderOwnerQuickEdit();
      error.focus();
      return;
    }
    if (!privateQuickEditChangesBookmark(bookmark, input)) return;

    const candidates = this.ownerBookmarkCandidates();
    const currentIndex = candidates.findIndex(({ id }) => id === bookmark.id);
    const nextBookmarkID = !advanceToNext
      ? null
      : currentIndex >= 0
        ? candidates[currentIndex + 1]?.id ?? null
        : candidates[0]?.id ?? null;

    this.ownerBulkSaveInProgress = true;
    this.renderOwnerBookmarkManager();
    let failureMessage: string | null = null;
    let didAdvance = false;
    try {
      const result = await this.repository.saveBookmark(
        applyPrivateQuickEdit(bookmark, input),
      );
      this.bookmarks = result.workspace.bookmarks;
      this.enrichmentJobs = result.workspace.enrichmentJobs;
      const saved = this.bookmarks.find(({ id }) => id === bookmark.id) ?? null;
      if (!saved) {
        throw new Error("The saved bookmark was missing from the workspace.");
      }
      this.populateOwnerQuickEditFields(saved);
      this.ownerManagerWorkspaceChanged = true;
      this.populateOwnerBookmarkClusters();
      const nextBookmark = nextBookmarkID
        ? this.bookmarks.find(({ id }) => id === nextBookmarkID) ?? null
        : null;
      if (advanceToNext && nextBookmark) {
        this.ownerQuickEditBookmarkID = nextBookmark.id;
        this.populateOwnerQuickEditFields(nextBookmark);
        didAdvance = true;
      }
      const persistenceMessage = !this.demo
        ? result.summaryEnrichmentJob?.state === "queued"
          ? "Bookmark saved. Private Summary re-indexing is queued."
          : result.summaryEnrichmentJob?.state === "failed"
            ? "Bookmark saved. Private Summary re-index delivery will retry."
            : "Bookmark saved to your private workspace."
        : result.summaryEnrichmentJob
          ? "Saved in this sample browser space. Private search has not updated online."
          : "Bookmark saved in this sample browser space.";
      const navigationMessage = !advanceToNext
        ? ""
        : nextBookmark
          ? `Next private edit opened: ${nextBookmark.title}.`
          : currentIndex >= 0
            ? "This is the last bookmark in this view."
            : "No other bookmark matches this view.";
      this.showToast(
        [persistenceMessage, navigationMessage].filter(Boolean).join(" "),
        !this.demo && result.summaryEnrichmentJob?.state === "failed",
      );
    } catch (saveError) {
      failureMessage = saveError instanceof Error
        ? saveError.message
        : this.demo
          ? "The synthetic bookmark could not be saved."
          : "The private bookmark could not be saved.";
    } finally {
      this.ownerBulkSaveInProgress = false;
      this.renderOwnerBookmarkManager();
    }
    if (!failureMessage && didAdvance) {
      this.root.querySelector<HTMLTextAreaElement>("#owner-quick-notes")
        ?.focus();
    }
    if (failureMessage) {
      error.hidden = false;
      error.textContent = failureMessage;
      error.focus();
    }
  }

  private populateOwnerBulkThemes(): void {
    const select = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-theme",
    );
    if (!select) return;
    select.replaceChildren(option("", "Keep each theme"));
    for (const theme of NATIVE_CONVERSATION_THEMES) {
      select.append(option(theme, humanizeConversationTheme(theme)));
    }
  }

  private populateOwnerBulkAudiences(): void {
    const select = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-audience",
    );
    if (!select) return;
    const current = select.value;
    const labels = new Map<string, string>();
    for (const bookmark of this.bookmarks) {
      for (const assignment of bookmark.audienceAssignments) {
        labels.set(assignment.audienceId, assignment.audienceLabel);
      }
    }
    select.replaceChildren(option("", "Keep audiences"));
    for (const [audienceID, label] of [...labels.entries()].sort(
      (left, right) => left[1].localeCompare(right[1]),
    )) {
      select.append(option(audienceID, label));
    }
    select.value = labels.has(current) ? current : "";
  }

  private ownerBulkPatch(): BulkCleanupPatch {
    const theme = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-theme",
    )?.value.trim() ?? "";
    const tags = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-tags",
    )?.value ?? "";
    const removeTags = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-remove-tags",
    )?.value ?? "";
    const audienceID = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-audience",
    )?.value.trim() ?? "";
    const destination = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-destination",
    )?.value ?? "keep";
    const makePrimary = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-primary",
    )?.checked ?? false;
    return {
      primaryTheme: theme || null,
      addTags: parseTerms(tags),
      removeTags: parseTerms(removeTags),
      audienceID: audienceID || null,
      destination: destination as BulkCleanupPatch["destination"],
      makePrimary,
    };
  }

  private ownerBulkSignature(patch = this.ownerBulkPatch()): string {
    return JSON.stringify({
      bookmarkIDs: [...this.ownerManagerSelection].sort(),
      primaryTheme: patch.primaryTheme,
      addTags: patch.addTags,
      removeTags: patch.removeTags,
      audienceID: patch.audienceID,
      destination: patch.destination,
      makePrimary: patch.makePrimary,
    });
  }

  private invalidateOwnerBulkPreview(): void {
    this.ownerBulkPreviewSignature = null;
  }

  private previewOwnerBulkCleanup(): void {
    const status = this.root.querySelector<HTMLElement>("#owner-bulk-status");
    if (!status || !this.ownerManagerSelection.size) return;
    const patch = this.ownerBulkPatch();
    const audienceAction = Boolean(
      patch.audienceID
      && (patch.destination !== "keep" || patch.makePrimary)
    );
    if (
      !patch.primaryTheme
      && patch.addTags.length === 0
      && patch.removeTags.length === 0
      && !audienceAction
    ) {
      this.ownerBulkPreviewSignature = null;
      status.textContent = (
        "Choose a theme, tag change, or audience destination."
      );
      this.root.querySelector<HTMLButtonElement>("#apply-owner-bulk")
        ?.setAttribute("disabled", "");
      return;
    }
    if (
      !patch.audienceID
      && (patch.destination !== "keep" || patch.makePrimary)
    ) {
      this.ownerBulkPreviewSignature = null;
      status.textContent = "Choose an audience for that destination change.";
      this.root.querySelector<HTMLButtonElement>("#apply-owner-bulk")
        ?.setAttribute("disabled", "");
      return;
    }
    this.ownerBulkPreviewSignature = this.ownerBulkSignature(patch);
    this.renderOwnerBulkCleanup();
    this.root.querySelector<HTMLButtonElement>("#apply-owner-bulk")?.focus();
  }

  private renderOwnerBulkCleanup(): void {
    const section = this.root.querySelector<HTMLElement>("#owner-bulk-cleanup");
    const count = this.root.querySelector<HTMLElement>(
      "#owner-bulk-selection-count",
    );
    const status = this.root.querySelector<HTMLElement>("#owner-bulk-status");
    const preview = this.root.querySelector<HTMLButtonElement>(
      "#preview-owner-bulk",
    );
    const apply = this.root.querySelector<HTMLButtonElement>(
      "#apply-owner-bulk",
    );
    const theme = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-theme",
    );
    const tags = this.root.querySelector<HTMLInputElement>("#owner-bulk-tags");
    const removeTags = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-remove-tags",
    );
    const audience = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-audience",
    );
    const destination = this.root.querySelector<HTMLSelectElement>(
      "#owner-bulk-destination",
    );
    const primary = this.root.querySelector<HTMLInputElement>(
      "#owner-bulk-primary",
    );
    const clearSelection = this.root.querySelector<HTMLButtonElement>(
      "#clear-owner-bookmark-selection",
    );
    const selectShown = this.root.querySelector<HTMLButtonElement>(
      "#select-owner-bookmarks",
    );
    if (
      !section
      || !count
      || !status
      || !preview
      || !apply
      || !theme
      || !tags
      || !removeTags
      || !audience
      || !destination
      || !primary
      || !clearSelection
      || !selectShown
    ) return;

    for (const bookmarkID of this.ownerManagerSelection) {
      if (!this.bookmarks.some(({ id }) => id === bookmarkID)) {
        this.ownerManagerSelection.delete(bookmarkID);
      }
    }
    const selected = this.bookmarks.filter(({ id }) => (
      this.ownerManagerSelection.has(id)
    ));
    section.hidden = selected.length === 0;
    clearSelection.hidden = selected.length === 0;
    count.textContent = `${selected.length} ${
      selected.length === 1 ? "bookmark" : "bookmarks"
    } selected.`;
    const shown = this.ownerBookmarkCandidates();
    const allShownSelected = shown.length > 0 && shown.every(
      ({ id }) => this.ownerManagerSelection.has(id),
    );
    selectShown.textContent = allShownSelected ? "Clear shown" : "Select shown";
    selectShown.disabled = shown.length === 0 || this.ownerBulkSaveInProgress;

    theme.disabled = this.ownerBulkSaveInProgress;
    tags.disabled = this.ownerBulkSaveInProgress;
    removeTags.disabled = this.ownerBulkSaveInProgress;
    audience.disabled = this.ownerBulkSaveInProgress;
    destination.disabled = this.ownerBulkSaveInProgress;
    primary.disabled = this.ownerBulkSaveInProgress;
    preview.disabled = this.ownerBulkSaveInProgress || selected.length === 0;
    apply.disabled = true;
    if (selected.length === 0) return;

    const patch = this.ownerBulkPatch();
    const hasAction = Boolean(
      patch.primaryTheme
      || patch.addTags.length
      || patch.removeTags.length
      || (
        patch.audienceID
        && (patch.destination !== "keep" || patch.makePrimary)
      )
    );
    const signatureMatches = (
      hasAction
      && this.ownerBulkPreviewSignature === this.ownerBulkSignature(patch)
    );
    if (!signatureMatches) {
      status.textContent = this.ownerBulkSaveInProgress
        ? "Applying the preview in order. Keep this page open."
        : "Choose a category or audience change, then preview before applying.";
      return;
    }
    const evaluations = selected.map((bookmark) => (
      evaluateBulkCleanupPatch(bookmark, patch)
    ));
    const changed = evaluations.filter(({ outcome }) => outcome === "changed")
      .length;
    const unchanged = evaluations.filter(
      ({ outcome }) => outcome === "unchanged",
    ).length;
    const needsReview = evaluations.filter(
      ({ outcome }) => outcome === "needs_review",
    ).length;
    status.textContent = `${changed} will change · ${unchanged} already ${
      unchanged === 1 ? "matches" : "match"
    } · ${needsReview} need individual review. Private fields and existing Shared Library copy stay untouched.`;
    apply.disabled = this.ownerBulkSaveInProgress || changed === 0;
  }

  private async applyOwnerBulkCleanup(): Promise<void> {
    if (
      this.ownerBulkSaveInProgress
      || !this.repository
      || this.ownerManagerSelection.size === 0
    ) return;
    const patch = this.ownerBulkPatch();
    if (this.ownerBulkPreviewSignature !== this.ownerBulkSignature(patch)) {
      this.previewOwnerBulkCleanup();
      return;
    }
    const selectedIDs = this.bookmarks
      .filter(({ id }) => this.ownerManagerSelection.has(id))
      .map(({ id }) => id);
    this.ownerBulkSaveInProgress = true;
    this.renderOwnerBookmarkManager();
    try {
      const result = await saveBulkCleanup(
        this.repository,
        {
          bookmarks: this.bookmarks,
          enrichmentJobs: this.enrichmentJobs,
        },
        selectedIDs,
        patch,
      );
      this.bookmarks = result.workspace.bookmarks;
      this.enrichmentJobs = result.workspace.enrichmentJobs;
      this.ownerManagerWorkspaceChanged ||= (
        result.completedBookmarkIDs.length > 0
      );
      for (const bookmarkID of [
        ...result.completedBookmarkIDs,
        ...result.unchangedBookmarkIDs,
      ]) {
        this.ownerManagerSelection.delete(bookmarkID);
      }
      this.invalidateOwnerBulkPreview();
      if (!result.failure) {
        if (result.needsReviewBookmarks.length === 0) {
          this.ownerManagerSelection.clear();
        }
        this.resetOwnerBulkPatchFields();
        this.showToast(
          `${result.completedBookmarkIDs.length} updated · ${
            result.unchangedBookmarkIDs.length
          } already matched · ${
            result.needsReviewBookmarks.length
          } need individual review.`,
        );
      } else {
        this.showToast(
          `${result.completedBookmarkIDs.length} updated · 1 failed · ${
            result.notAttemptedBookmarkIDs.length
          } not attempted · ${
            result.needsReviewBookmarks.length
          } need individual review. Refresh before retrying.`,
          true,
        );
      }
      this.populateOwnerBookmarkClusters();
      this.populateOwnerBookmarkSources();
      this.populateOwnerBulkAudiences();
    } catch (error) {
      this.showToast(
        error instanceof Error
          ? error.message
          : "The selected bookmarks could not be updated.",
        true,
      );
    } finally {
      this.ownerBulkSaveInProgress = false;
      this.renderOwnerBookmarkManager();
    }
  }

  private populateOwnerBookmarkAudiences(): void {
    const select = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-audience",
    );
    if (!select) return;
    const labels = new Map<string, string>();
    for (const bookmark of this.bookmarks) {
      for (const assignment of bookmark.audienceAssignments) {
        labels.set(assignment.audienceId, assignment.audienceLabel);
      }
    }
    const options = [["all", "All audiences"], ...[...labels.entries()]
      .sort((left, right) => left[1].localeCompare(right[1]))];
    select.replaceChildren(...options.map(([value, label]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      return option;
    }));
    select.value = labels.has(this.ownerManagerAudienceID)
      ? this.ownerManagerAudienceID
      : "all";
    this.ownerManagerAudienceID = select.value;
  }

  private populateOwnerBookmarkClusters(): void {
    const select = this.root.querySelector<HTMLSelectElement>(
      "#owner-bookmark-cluster",
    );
    if (!select) return;
    const clusters = [...new Set(this.bookmarks.flatMap(({ cluster }) => (
      cluster ? [cluster] : []
    )))].sort((left, right) => (
      humanizeCluster(left).localeCompare(humanizeCluster(right))
      || left.localeCompare(right)
    ));
    select.replaceChildren(option("all", "All clusters"));
    for (const cluster of clusters) {
      select.append(option(cluster, humanizeCluster(cluster)));
    }
    if (clusters.length < this.bookmarks.length) {
      select.append(option("unavailable", "Cluster unavailable"));
    }
    select.value = (
      this.ownerManagerCluster === "all"
      || clusters.includes(this.ownerManagerCluster)
      || (
        this.ownerManagerCluster === "unavailable"
        && clusters.length < this.bookmarks.length
      )
    )
      ? this.ownerManagerCluster
      : "all";
    this.ownerManagerCluster = select.value;
  }

  private populateOwnerBookmarkSources(): void {
    const container = this.root.querySelector<HTMLElement>(
      "#owner-source-filter-options",
    );
    if (!container) return;
    const options = ownerSourceOptions(this.bookmarks);
    const available = new Set(options.map(({ id }) => id));
    for (const sourceID of this.ownerManagerIncludedSources) {
      if (!available.has(sourceID)) this.ownerManagerIncludedSources.delete(
        sourceID,
      );
    }
    for (const sourceID of this.ownerManagerExcludedSources) {
      if (!available.has(sourceID)) this.ownerManagerExcludedSources.delete(
        sourceID,
      );
    }
    container.replaceChildren(...options.map((source) => {
      const label = document.createElement("label");
      label.className = "owner-source-filter";
      const copy = document.createElement("span");
      copy.textContent = `${source.label} · ${source.count}`;
      const select = document.createElement("select");
      select.dataset.ownerSource = source.id;
      select.setAttribute("aria-label", `${source.label} source filter`);
      select.append(
        option("any", "Any"),
        option("include", "Include"),
        option("exclude", "Exclude"),
      );
      const mode: OwnerSourceFilterMode = this.ownerManagerExcludedSources.has(
        source.id,
      )
        ? "exclude"
        : this.ownerManagerIncludedSources.has(source.id)
          ? "include"
          : "any";
      select.value = mode;
      select.addEventListener("change", () => {
        const next = select.value as OwnerSourceFilterMode;
        this.ownerManagerIncludedSources.delete(source.id);
        this.ownerManagerExcludedSources.delete(source.id);
        if (next === "include") {
          this.ownerManagerIncludedSources.add(source.id);
        } else if (next === "exclude") {
          this.ownerManagerExcludedSources.add(source.id);
        }
        this.renderOwnerBookmarkManager();
      });
      label.append(copy, select);
      return label;
    }));
  }

  private ownerUnsavedBookmarkIDs(): Set<string> {
    const unsaved = new Set<string>();
    for (const bookmark of this.bookmarks) {
      if ([...this.bookmarkDrafts.keys()].some((draftKey) => (
        draftKey.startsWith(`${bookmark.id}:`)
      ))) {
        unsaved.add(bookmark.id);
      }
    }
    const quickBookmark = this.ownerQuickEditBookmark();
    const quickInput = this.ownerQuickEditInput();
    if (quickBookmark && quickInput) {
      try {
        if (privateQuickEditChangesBookmark(quickBookmark, quickInput)) {
          unsaved.add(quickBookmark.id);
        }
      } catch {
        unsaved.add(quickBookmark.id);
      }
    }
    return unsaved;
  }

  private ownerBookmarkCandidates(): Bookmark[] {
    return ownerPreparationBookmarks(this.bookmarks, {
      query: this.ownerManagerQuery,
      audienceID: this.ownerManagerAudienceID,
      destination: this.ownerManagerDestination,
      cluster: this.ownerManagerCluster,
      review: this.ownerManagerReview,
      includedSources: this.ownerManagerIncludedSources,
      excludedSources: this.ownerManagerExcludedSources,
      sort: this.ownerManagerSort,
      shuffleSeed: this.ownerManagerShuffleSeed,
    });
  }

  private renderOwnerPreparationCounts(
    shownBookmarks = this.ownerBookmarkCandidates(),
  ): void {
    const container = this.root.querySelector<HTMLElement>(
      "#owner-preparation-counts",
    );
    if (!container) return;
    const counts = ownerPreparationCounts({
      bookmarks: this.bookmarks,
      shownBookmarks,
      selectedBookmarkIDs: this.ownerManagerSelection,
      unsavedBookmarkIDs: this.ownerUnsavedBookmarkIDs(),
      enrichmentJobs: this.enrichmentJobs,
    });
    const values: Record<string, number> = {
      shown: counts.shown,
      selected: counts.selected,
      unsaved: counts.unsaved,
      pending: counts.pending,
      failed: counts.failed,
      "needs-review": counts.needsReview,
    };
    for (const [name, value] of Object.entries(values)) {
      const output = container.querySelector<HTMLElement>(
        `[data-owner-count="${name}"]`,
      );
      if (output) output.textContent = String(value);
    }
    container.setAttribute(
      "aria-label",
      `Preparation counts. ${counts.shown} shown of ${counts.total}. ${
        counts.selected
      } selected. ${counts.unsaved} unsaved. ${
        counts.pending
      } re-indexing. ${counts.failed} failed. ${
        counts.needsReview
      } need review.`,
    );
  }

  private renderOwnerBookmarkManager(): void {
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#owner-bookmark-dialog",
    );
    const list = dialog?.querySelector<HTMLUListElement>(
      "#owner-bookmark-list",
    );
    const empty = dialog?.querySelector<HTMLElement>("#owner-bookmark-empty");
    const status = dialog?.querySelector<HTMLElement>("#owner-bookmark-status");
    const clear = dialog?.querySelector<HTMLButtonElement>(
      "#clear-owner-bookmark-filters",
    );
    const close = dialog?.querySelector<HTMLButtonElement>(
      "#close-owner-bookmarks",
    );
    const done = dialog?.querySelector<HTMLButtonElement>(
      "#done-owner-bookmarks",
    );
    const search = dialog?.querySelector<HTMLInputElement>(
      "#owner-bookmark-search",
    );
    const audience = dialog?.querySelector<HTMLSelectElement>(
      "#owner-bookmark-audience",
    );
    const cluster = dialog?.querySelector<HTMLSelectElement>(
      "#owner-bookmark-cluster",
    );
    const review = dialog?.querySelector<HTMLSelectElement>(
      "#owner-bookmark-review",
    );
    const sort = dialog?.querySelector<HTMLSelectElement>(
      "#owner-bookmark-sort",
    );
    if (!dialog || !list || !empty || !status || !clear) return;
    if (close) close.disabled = this.ownerBulkSaveInProgress;
    if (done) done.disabled = this.ownerBulkSaveInProgress;
    if (search && search.value !== this.ownerManagerQuery) {
      search.value = this.ownerManagerQuery;
    }
    if (search) search.disabled = this.ownerBulkSaveInProgress;
    if (audience && audience.value !== this.ownerManagerAudienceID) {
      audience.value = this.ownerManagerAudienceID;
    }
    if (audience) audience.disabled = this.ownerBulkSaveInProgress;
    if (cluster && cluster.value !== this.ownerManagerCluster) {
      cluster.value = this.ownerManagerCluster;
    }
    if (cluster) cluster.disabled = this.ownerBulkSaveInProgress;
    if (review && review.value !== this.ownerManagerReview) {
      review.value = this.ownerManagerReview;
    }
    if (review) review.disabled = this.ownerBulkSaveInProgress;
    if (sort && sort.value !== this.ownerManagerSort) {
      sort.value = this.ownerManagerSort;
    }
    if (sort) sort.disabled = this.ownerBulkSaveInProgress;
    const reshuffle = dialog.querySelector<HTMLButtonElement>(
      "#reshuffle-owner-bookmarks",
    );
    if (reshuffle) {
      reshuffle.hidden = this.ownerManagerSort !== "shuffled";
      reshuffle.disabled = this.ownerBulkSaveInProgress;
    }
    dialog.querySelectorAll<HTMLSelectElement>(
      "[data-owner-source]",
    ).forEach((select) => {
      select.disabled = this.ownerBulkSaveInProgress;
    });
    dialog.querySelectorAll<HTMLButtonElement>(
      "[data-owner-destination]",
    ).forEach((button) => {
      const active = (
        button.dataset.ownerDestination === this.ownerManagerDestination
      );
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.disabled = this.ownerBulkSaveInProgress;
    });

    const bookmarks = this.ownerBookmarkCandidates();
    const clusterCount = new Set(bookmarks.flatMap(({ cluster }) => (
      cluster ? [cluster] : []
    ))).size;
    status.textContent = `${bookmarks.length} of ${this.bookmarks.length} owner ${
      this.bookmarks.length === 1 ? "bookmark" : "bookmarks"
    } · ${clusterCount} ${
      clusterCount === 1 ? "cluster" : "clusters"
    } · ${this.ownerManagerSelection.size} selected`;
    clear.hidden = !(
      this.ownerManagerQuery
      || this.ownerManagerAudienceID !== "all"
      || this.ownerManagerDestination !== "all"
      || this.ownerManagerCluster !== "all"
      || this.ownerManagerReview !== "all"
      || this.ownerManagerIncludedSources.size > 0
      || this.ownerManagerExcludedSources.size > 0
      || this.ownerManagerSort !== "newest"
    );
    clear.disabled = this.ownerBulkSaveInProgress;
    empty.hidden = bookmarks.length > 0;
    list.hidden = bookmarks.length === 0;
    list.replaceChildren(...bookmarks.map((bookmark, index) => (
      this.ownerBookmarkRow(bookmark, index, dialog)
    )));
    this.renderOwnerPreparationCounts(bookmarks);
    this.renderOwnerBulkCleanup();
    this.renderOwnerQuickEdit();
  }

  private ownerBookmarkRow(
    bookmark: Bookmark,
    index: number,
    dialog: HTMLDialogElement,
  ): HTMLLIElement {
    const item = document.createElement("li");
    item.className = "owner-bookmark-item";
    const selection = document.createElement("input");
    selection.type = "checkbox";
    selection.className = "owner-bookmark-selection";
    selection.checked = this.ownerManagerSelection.has(bookmark.id);
    selection.disabled = this.ownerBulkSaveInProgress;
    selection.setAttribute(
      "aria-label",
      `Select ${bookmark.title} for bulk cleanup`,
    );
    selection.addEventListener("change", () => {
      if (selection.checked) {
        this.ownerManagerSelection.add(bookmark.id);
      } else {
        this.ownerManagerSelection.delete(bookmark.id);
      }
      this.invalidateOwnerBulkPreview();
      this.renderOwnerBookmarkManager();
      this.root.querySelectorAll<HTMLInputElement>(
        ".owner-bookmark-selection",
      )[index]?.focus();
    });
    const button = document.createElement("button");
    button.type = "button";
    button.className = "owner-bookmark-row";
    button.disabled = this.ownerBulkSaveInProgress;
    button.dataset.ownerIndex = String(index);

    const symbol = document.createElement("span");
    symbol.className = "owner-bookmark-symbol";
    symbol.textContent = cardBackSymbols[index % cardBackSymbols.length];
    symbol.setAttribute("aria-hidden", "true");

    const copy = document.createElement("span");
    copy.className = "owner-bookmark-copy";
    const title = document.createElement("strong");
    title.textContent = bookmark.title;
    const sourcePresentation = ownerSourceLabel(ownerSourceID(bookmark));
    const source = document.createElement("span");
    source.className = "owner-bookmark-source";
    source.textContent = `${sourcePresentation} · ${
      humanizeConversationTheme(bookmark.primaryTheme)
    }`;
    const cluster = document.createElement("span");
    cluster.className = `owner-cluster-chip${
      bookmark.cluster ? "" : " is-unavailable"
    }`;
    cluster.textContent = bookmark.cluster
      ? `Cluster · ${humanizeCluster(bookmark.cluster)}`
      : "Cluster metadata unavailable";
    const destinations = document.createElement("span");
    destinations.className = "owner-bookmark-destinations";
    const destinationLabels = bookmark.audienceAssignments
      .filter(({ conversationEnabled, libraryEnabled }) => (
        conversationEnabled || libraryEnabled
      ))
      .map((assignment) => {
        const destination = (
          assignment.conversationEnabled && assignment.libraryEnabled
        )
          ? "Cards + Library"
          : assignment.conversationEnabled ? "Cards" : "Library";
        const chip = document.createElement("span");
        chip.className = `owner-destination-chip ${
          assignment.conversationEnabled ? "has-cards" : ""
        } ${assignment.libraryEnabled ? "has-library" : ""}`;
        chip.textContent = `${assignment.audienceLabel} · ${destination}`;
        destinations.append(chip);
        return `${assignment.audienceLabel}, ${destination}`;
      });
    copy.append(title, source, cluster, destinations);

    const vector = document.createElement("span");
    const vectorState = this.vectorPresentation(bookmark);
    vector.className = `owner-vector-state vector-${vectorState.state}`;
    vector.textContent = vectorState.badge;
    button.setAttribute(
      "aria-label",
      `${bookmark.title}. ${sourcePresentation}. ${
        cluster.textContent
      }. ${
        destinationLabels.join(". ")
      }. ${vector.textContent}. ${vectorState.detail}`,
    );
    button.addEventListener("click", () => {
      if (this.retainUnsavedOwnerQuickEdit(
        "Save or cancel this private draft before opening the full editor.",
      )) return;
      dialog.dataset.handedOff = "true";
      dialog.close();
      this.openOwnerBookmarkFromManager(bookmark);
    });
    button.append(symbol, copy, vector);
    const quickEdit = document.createElement("button");
    quickEdit.type = "button";
    quickEdit.className = "owner-bookmark-quick-button";
    quickEdit.disabled = this.ownerBulkSaveInProgress;
    quickEdit.dataset.ownerIndex = String(index);
    quickEdit.setAttribute(
      "aria-label",
      `Quick private edit ${bookmark.title}`,
    );
    quickEdit.setAttribute(
      "aria-expanded",
      String(this.ownerQuickEditBookmarkID === bookmark.id),
    );
    quickEdit.textContent = "Quick private edit";
    quickEdit.addEventListener("click", () => {
      this.openOwnerQuickEdit(bookmark);
    });
    item.append(selection, button, quickEdit);
    return item;
  }

  private openOwnerBookmarkFromManager(bookmark: Bookmark): void {
    const preferredAssignment = bookmark.audienceAssignments.find(
      ({ isPrimary }) => isPrimary,
    ) ?? bookmark.audienceAssignments[0];
    const preferredPublication = bookmark.libraryPublications.find(
      ({ assignmentId, revokedAt }) => (
        assignmentId === preferredAssignment?.id && revokedAt === null
      ),
    ) ?? bookmark.libraryPublications.find(({ revokedAt }) => revokedAt === null);
    this.selectedID = bookmark.id;
    this.selectedPublicationID = preferredPublication?.id ?? null;
    this.libraryDetailMode = "edit";
    this.returnToConversation = false;
    this.renderLibrary();
    this.root.querySelector(".app-shell")?.classList.add("detail-open");
    this.focusAfterRender("#private-notes");
  }

  private bindCardBackPhotoLibrary(): void {
    const repository = this.cardBackPhotoRepository;
    const dialog = this.root.querySelector<HTMLDialogElement>(
      "#card-back-photo-dialog",
    );
    const open = this.root.querySelector<HTMLButtonElement>(
      "#manage-card-backs",
    );
    const input = this.root.querySelector<HTMLInputElement>(
      "#card-back-photo-input",
    );
    const add = this.root.querySelector<HTMLButtonElement>(
      "#add-card-back-photos",
    );
    if (!repository || !dialog || !open || !input || !add) return;

    open.addEventListener("click", () => {
      this.renderCardBackPhotoLibrary();
      dialog.showModal();
      this.focusAfterRender("#close-card-back-photos");
    });
    const close = () => dialog.close();
    this.root.querySelector<HTMLButtonElement>("#close-card-back-photos")
      ?.addEventListener("click", close);
    this.root.querySelector<HTMLButtonElement>("#done-card-back-photos")
      ?.addEventListener("click", close);
    dialog.addEventListener("close", () => {
      input.value = "";
      this.root.querySelector("#card-back-photo-library")?.replaceChildren();
      const result = this.root.querySelector<HTMLElement>(
        "#card-back-photo-result",
      );
      if (result) {
        result.textContent = "";
        result.classList.remove("is-error");
      }
      this.focusAfterRender("#manage-card-backs");
    });
    input.addEventListener("change", () => {
      void this.importCardBackPhotos(input);
    });
    add.addEventListener("click", () => input.click());
    this.root.querySelector<HTMLButtonElement>("#clear-card-back-photos")
      ?.addEventListener("click", () => {
        void this.clearCardBackPhotos();
      });
  }

  private renderCardBackPhotoContext(): void {
    const audienceLabel = this.conversationAudienceLabel(
      this.conversationAudienceID,
    );
    const manager = this.root.querySelector<HTMLButtonElement>(
      "#manage-card-backs",
    );
    if (manager) {
      const unavailable = (
        this.cardBackPhotosLoading
        || this.cardBackPhotoAudienceID !== this.conversationAudienceID
      );
      manager.textContent = `Card backs · ${this.cardBackPhotos.length}`;
      manager.disabled = unavailable;
      manager.setAttribute(
        "aria-label",
        `Manage ${audienceLabel} card-back photos, ${
          this.cardBackPhotos.length
        } ${this.cardBackPhotos.length === 1 ? "photo" : "photos"}`,
      );
      manager.setAttribute("aria-busy", String(unavailable));
    }
    setText(
      this.root,
      "#card-back-photo-title",
      `${audienceLabel} card backs`,
    );
    setText(this.root, "#card-back-audience-name", audienceLabel);
    setText(
      this.root,
      ".card-back-audience-monogram",
      audienceLabel.slice(0, 1),
    );
    setText(
      this.root,
      "#card-back-photo-intro",
      `A private pool for ${audienceLabel}. Photos rotate independently and `
        + "never imply a connection to the question on the front.",
    );
  }

  private renderCardBackPhotoLibrary(): void {
    this.renderCardBackPhotoContext();
    const audienceLabel = this.conversationAudienceLabel(
      this.conversationAudienceID,
    );
    const library = this.root.querySelector<HTMLElement>(
      "#card-back-photo-library",
    );
    const count = this.root.querySelector<HTMLElement>(
      "#card-back-photo-count",
    );
    const input = this.root.querySelector<HTMLInputElement>(
      "#card-back-photo-input",
    );
    const add = this.root.querySelector<HTMLButtonElement>(
      "#add-card-back-photos",
    );
    const clear = this.root.querySelector<HTMLButtonElement>(
      "#clear-card-back-photos",
    );
    if (!library || !count || !input || !add || !clear) return;

    const full = this.cardBackPhotos.length >= MAX_SYNTHETIC_CARD_BACK_PHOTOS;
    count.textContent = (
      `${this.cardBackPhotos.length} of ${MAX_SYNTHETIC_CARD_BACK_PHOTOS}`
    );
    input.disabled = full;
    add.disabled = full;
    clear.hidden = this.cardBackPhotos.length === 0;
    library.replaceChildren();

    if (!this.cardBackPhotos.length) {
      const empty = document.createElement("div");
      empty.className = "card-back-photo-empty";
      const symbol = document.createElement("span");
      symbol.setAttribute("aria-hidden", "true");
      symbol.textContent = "▧";
      const title = document.createElement("h3");
      title.textContent = `No photos for ${audienceLabel} yet`;
      const copy = document.createElement("p");
      copy.textContent = (
        "Choose places, objects, or people that belong in this room. "
        + "The original files stay untouched."
      );
      empty.append(symbol, title, copy);
      library.append(empty);
      return;
    }

    const heading = document.createElement("div");
    heading.className = "card-back-photo-library-heading";
    const title = document.createElement("h3");
    title.textContent = `${audienceLabel}’s private pool`;
    const note = document.createElement("p");
    note.textContent = (
      `Only ${audienceLabel}’s selected deck uses these photos. `
      + "No photo is attached to a question."
    );
    heading.append(title, note);

    const grid = document.createElement("div");
    grid.className = "card-back-photo-grid";
    this.cardBackPhotos.forEach((photo, index) => {
      const item = document.createElement("article");
      item.className = "card-back-photo-item";
      const image = document.createElement("img");
      image.src = this.cardBackPhotoObjectURLs.get(photo.reference) ?? "";
      image.alt = "";
      image.setAttribute("aria-hidden", "true");
      image.draggable = false;
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "card-back-photo-remove";
      remove.setAttribute(
        "aria-label",
        `Remove card-back photo ${index + 1}`,
      );
      remove.textContent = "×";
      remove.addEventListener("click", () => {
        void this.removeCardBackPhoto(photo.reference, index);
      });
      item.append(image, remove);
      grid.append(item);
    });
    library.append(heading, grid);
  }

  private async importCardBackPhotos(input: HTMLInputElement): Promise<void> {
    const result = this.root.querySelector<HTMLElement>(
      "#card-back-photo-result",
    );
    const files = [...(input.files ?? [])];
    input.value = "";
    if (
      !result
      || !this.cardBackPhotoRepository
      || !files.length
      || this.cardBackPhotosLoading
      || this.cardBackPhotoAudienceID !== this.conversationAudienceID
    ) return;

    const remaining = (
      MAX_SYNTHETIC_CARD_BACK_PHOTOS - this.cardBackPhotos.length
    );
    const accepted = files.filter((file) => (
      isAllowedSyntheticImageType(file.type)
      && file.size > 0
      && file.size <= MAX_SYNTHETIC_IMAGE_BYTES
    )).slice(0, remaining);
    const skipped = files.length - accepted.length;
    if (!accepted.length) {
      result.classList.add("is-error");
      result.textContent = remaining <= 0
        ? "The private card-back library already has 40 photos."
        : "No selected file was a supported nonempty image up to 10 MB.";
      return;
    }

    try {
      const photos = await this.cardBackPhotoRepository.add(
        this.conversationAudienceID,
        accepted,
      );
      this.replaceCardBackPhotos(photos);
      this.cardBackPhotoAudienceID = this.conversationAudienceID;
      this.cardBackPhotoOffset = 0;
      this.renderCardBackPhotoLibrary();
      this.renderConversationStage(this.orderedConversationDeck());
      result.classList.remove("is-error");
      result.textContent = (
        `Added ${accepted.length} private ${
          accepted.length === 1 ? "photo" : "photos"
        } for ${this.conversationAudienceLabel(
          this.conversationAudienceID,
        )}.${skipped ? ` Skipped ${skipped}.` : ""}`
      );
    } catch (error) {
      result.classList.add("is-error");
      result.textContent = error instanceof Error
        ? error.message
        : "The selected photos could not be added.";
    }
  }

  private async removeCardBackPhoto(
    reference: string,
    index: number,
  ): Promise<void> {
    if (
      !this.cardBackPhotoRepository
      || !isSyntheticCardBackReference(reference)
      || this.cardBackPhotosLoading
      || this.cardBackPhotoAudienceID !== this.conversationAudienceID
    ) return;
    const result = this.root.querySelector<HTMLElement>(
      "#card-back-photo-result",
    );
    try {
      const photos = await this.cardBackPhotoRepository.remove(
        this.conversationAudienceID,
        reference,
      );
      this.replaceCardBackPhotos(photos);
      this.cardBackPhotoOffset = this.cardBackPhotos.length
        ? this.cardBackPhotoOffset % this.cardBackPhotos.length
        : 0;
      this.renderCardBackPhotoLibrary();
      this.renderConversationStage(this.orderedConversationDeck());
      if (result) {
        result.classList.remove("is-error");
        result.textContent = (
          `Removed one private card-back photo for ${this.conversationAudienceLabel(
            this.conversationAudienceID,
          )}.`
        );
      }
      const nextIndex = Math.min(index, this.cardBackPhotos.length - 1) + 1;
      this.focusAfterRender(
        this.cardBackPhotos.length
          ? `[aria-label="Remove card-back photo ${nextIndex}"]`
          : "#add-card-back-photos",
      );
    } catch (error) {
      if (result) {
        result.classList.add("is-error");
        result.textContent = error instanceof Error
          ? error.message
          : "That private card-back photo could not be removed.";
      }
    }
  }

  private async clearCardBackPhotos(): Promise<void> {
    if (
      !this.cardBackPhotoRepository
      || !this.cardBackPhotos.length
      || this.cardBackPhotosLoading
      || this.cardBackPhotoAudienceID !== this.conversationAudienceID
    ) return;
    const audienceLabel = this.conversationAudienceLabel(
      this.conversationAudienceID,
    );
    const confirmed = window.confirm(
      `Clear every private card-back photo for ${audienceLabel} from this `
        + "synthetic browser?",
    );
    if (!confirmed) return;
    const result = this.root.querySelector<HTMLElement>(
      "#card-back-photo-result",
    );
    try {
      await this.cardBackPhotoRepository.clear(this.conversationAudienceID);
      this.replaceCardBackPhotos([]);
      this.cardBackPhotoOffset = 0;
      this.renderCardBackPhotoLibrary();
      this.renderConversationStage(this.orderedConversationDeck());
      if (result) {
        result.classList.remove("is-error");
        result.textContent = (
          `Cleared every private card-back photo for ${audienceLabel}.`
        );
      }
      this.focusAfterRender("#add-card-back-photos");
    } catch (error) {
      if (result) {
        result.classList.add("is-error");
        result.textContent = error instanceof Error
          ? error.message
          : "The private card-back library could not be cleared.";
      }
    }
  }

  private bindLibraryEvents(): void {
    const filterToggle = this.root.querySelector<HTMLButtonElement>(
      "#library-filter-toggle",
    );
    const clearFilters = this.root.querySelector<HTMLButtonElement>(
      "#clear-library-filters",
    );
    const longVideoThreshold = this.root.querySelector<HTMLInputElement>(
      "#long-video-threshold",
    );
    const search = this.root.querySelector<HTMLInputElement>("#library-search");
    const clearSearch = this.root.querySelector<HTMLButtonElement>("#clear-search");
    const showMoreSearch = this.root.querySelector<HTMLButtonElement>(
      "#search-show-more",
    );
    filterToggle?.addEventListener("click", () => {
      this.libraryFiltersOpen = !this.libraryFiltersOpen;
      this.updateLibraryFilterDisclosure();
    });
    filterToggle?.addEventListener("focus", () => {
      this.libraryFilterToggleFocused = true;
    });
    filterToggle?.addEventListener("blur", () => {
      window.requestAnimationFrame(() => {
        if (window.matchMedia(compactConversationQuery).matches) {
          this.libraryFilterToggleFocused = false;
        }
      });
    });
    clearFilters?.addEventListener(
      "click",
      () => this.clearLibraryFilters(),
    );
    this.root.querySelector<HTMLButtonElement>("#quick-capture")
      ?.addEventListener("click", () => this.openQuickCapture());
    this.root.querySelector<HTMLButtonElement>("#new-bookmark")
      ?.addEventListener("click", () => this.createLocalBookmark());
    this.root.querySelector<HTMLButtonElement>("#reset-demo")
      ?.addEventListener("click", () => {
        void this.resetSyntheticWorkspace();
      });
    const commitLongVideoThreshold = () => {
      if (!longVideoThreshold) return;
      const seconds = parseLongVideoThresholdSeconds(longVideoThreshold.value);
      const error = this.root.querySelector<HTMLElement>(
        "#watch-threshold-error",
      );
      if (seconds === null) {
        longVideoThreshold.setAttribute("aria-invalid", "true");
        if (error) error.hidden = false;
        return;
      }
      longVideoThreshold.removeAttribute("aria-invalid");
      if (error) error.hidden = true;
      const selectedBookmark = this.bookmarks.find(
        ({ id }) => id === this.selectedID,
      );
      const selectedDraftKey = selectedBookmark
        ? this.bookmarkDraftKey(selectedBookmark.id)
        : null;
      const hadReviewedDraft = Boolean(
        selectedDraftKey && this.triageAudits.has(selectedDraftKey),
      );
      this.filters.longVideoThresholdSeconds = seconds;
      this.resetLibrarySearchWindow();
      this.ensureVisibleSelection();
      this.renderLibrary();
      if (hadReviewedDraft && selectedBookmark) {
        this.refreshTriageAudit(this.root, selectedBookmark);
        this.rememberBookmarkDraft(selectedBookmark);
      }
    };
    longVideoThreshold?.addEventListener("input", () => {
      if (parseLongVideoThresholdSeconds(longVideoThreshold.value) === null) {
        return;
      }
      longVideoThreshold.removeAttribute("aria-invalid");
      const error = this.root.querySelector<HTMLElement>(
        "#watch-threshold-error",
      );
      if (error) error.hidden = true;
    });
    longVideoThreshold?.addEventListener("change", commitLongVideoThreshold);
    longVideoThreshold?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      commitLongVideoThreshold();
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-lane]").forEach((button) => {
      button.addEventListener("click", () => {
        this.filters.lane = button.dataset.lane as LibraryLane | "all";
        this.resetLibrarySearchWindow();
        this.ensureVisibleSelection();
        this.renderLibrary();
      });
    });
    search?.addEventListener("input", () => {
      const priorQuery = normalizedLibraryQuery(this.libraryQuery);
      this.libraryQuery = search.value;
      if (normalizedLibraryQuery(this.libraryQuery) !== priorQuery) {
        this.resetLibrarySearchWindow();
      }
      this.ensureVisibleSelection();
      this.renderLibrary();
    });
    search?.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.libraryQuery) return;
      event.preventDefault();
      this.clearLibrarySearch();
    });
    clearSearch?.addEventListener("click", () => this.clearLibrarySearch());
    showMoreSearch?.addEventListener("click", () => {
      const total = this.allVisibleLibraryEntries().length;
      this.librarySearchVisibleCount = Math.min(
        total,
        this.librarySearchVisibleCount + librarySearchWindowSize,
      );
      this.renderLibrary();
      this.focusAfterRender(
        this.librarySearchVisibleCount < total
          ? "#search-show-more"
          : "#search-result-window-status",
      );
    });
    this.bindNewCardComposer();
    this.bindQuickCapture();
    this.bindOwnerBookmarkManager();
  }

  private renderLibrary(): void {
    const allVisible = this.allVisibleLibraryEntries();
    const visible = this.windowedLibraryEntries(allVisible);
    const list = this.root.querySelector<HTMLUListElement>("#library-list");
    const count = this.root.querySelector<HTMLElement>("#item-count");
    const empty = this.root.querySelector<HTMLElement>("#empty-list");
    const emptyTitle = empty?.querySelector<HTMLElement>("h3");
    const emptyCopy = empty?.querySelector<HTMLElement>("p");
    if (!list || !count || !empty) return;

    const searching = Boolean(this.libraryQuery.trim());
    const itemCount = searching ? allVisible.length : visible.length;
    count.textContent = `${itemCount} ${
      itemCount === 1
        ? searching ? "result" : "item"
        : searching ? "results" : "items"
    }`;
    if (emptyTitle) {
      emptyTitle.textContent = searching
        ? "No private matches."
        : "Nothing in this view.";
    }
    if (emptyCopy) {
      emptyCopy.textContent = searching
        ? "Try another phrase. Notes are deliberately not searched."
        : "Try another filter. Private Conversation Cards never fall into Library by accident.";
    }
    empty.hidden = visible.length > 0;
    list.hidden = visible.length === 0;
    list.replaceChildren(...visible.map((candidate) => (
      this.libraryRow(candidate)
    )));
    this.updateLibrarySearchControls(allVisible.length, visible.length);
    this.root.querySelectorAll<HTMLButtonElement>("[data-lane]").forEach((button) => {
      const active = button.dataset.lane === this.filters.lane;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
    });
    const watchScope = this.root.querySelector<HTMLElement>("#watch-scope");
    if (watchScope) watchScope.hidden = this.filters.lane !== "watch";
    this.updateWatchScopeControls();
    this.updateLibraryFilterDisclosure();
    this.renderDetail();
  }

  private updateWatchScopeControls(): void {
    const thresholdSeconds = this.filters.longVideoThresholdSeconds
      ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS;
    const thresholdMinutes = thresholdSeconds / 60;
    const thresholdLabel = `${thresholdMinutes} ${
      thresholdMinutes === 1 ? "minute" : "minutes"
    }`;
    const input = this.root.querySelector<HTMLInputElement>(
      "#long-video-threshold",
    );
    if (input && document.activeElement !== input) {
      input.value = String(thresholdMinutes);
      input.removeAttribute("aria-invalid");
      const error = this.root.querySelector<HTMLElement>(
        "#watch-threshold-error",
      );
      if (error) error.hidden = true;
    }
    const help = this.root.querySelector<HTMLElement>("#watch-threshold-help");
    this.watchModeFilterMenu?.setOptionLabel(
      "longer_than_threshold",
      `Over ${thresholdLabel}`,
    );
    if (help) {
      help.textContent = this.filters.watchMode === "all_video"
        ? `All videos ignores the ${thresholdLabel} threshold. Missing duration is still never inferred.`
        : `Uses explicit shared duration. Videos at or below ${thresholdLabel}, or with no duration, stay out.`;
    }
  }

  private longVideoThresholdLabel(): string {
    const thresholdSeconds = this.filters.longVideoThresholdSeconds
      ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS;
    const thresholdMinutes = thresholdSeconds / 60;
    return `${thresholdMinutes} ${
      thresholdMinutes === 1 ? "minute" : "minutes"
    }`;
  }

  private senderLabel(userID: string): string {
    return demoUserLabels[userID]
      ?? (userID === this.viewerUserID ? "You" : userID);
  }

  private candidateForRecipientEntry(
    entry: LibraryRecipientEntry,
  ): LibraryViewCandidate {
    for (const bookmark of this.bookmarks) {
      const publication = bookmark.libraryPublications.find(
        ({ id, revokedAt }) => id === entry.item.id && revokedAt === null,
      );
      if (!publication) continue;
      const assignment = bookmark.audienceAssignments.find(
        ({ id }) => id === publication.assignmentId,
      );
      if (assignment) return { bookmark, assignment, entry };
    }
    return {
      bookmark: null,
      assignment: {
        id: `recipient-assignment:${entry.item.audienceId}`,
        audienceId: entry.item.audienceId,
        audienceLabel: this.audienceLabels.get(entry.item.audienceId)
          ?? entry.item.audienceId,
        conversationEnabled: false,
        libraryEnabled: true,
        isPrimary: false,
        conversationState: "available",
      },
      entry,
    };
  }

  private allVisibleLibraryEntries(): LibraryViewCandidate[] {
    if (!this.libraryQuery.trim()) {
      return filterRecipientLibrary(this.recipientEntries, this.filters)
        .map((entry) => this.candidateForRecipientEntry(entry));
    }
    const recipientByPublicationID = new Map(
      this.recipientEntries.map((entry) => [entry.item.id, entry]),
    );
    const ranked = rankLibraryBookmarks(
      this.bookmarks,
      this.receipts,
      this.viewerUserID,
      this.filters,
      this.libraryQuery,
      this.semanticScoresForQuery(),
    );
    return ranked
      .filter(({ score }) => score > 0)
      .flatMap(({ candidate }) => {
        const entry = recipientByPublicationID.get(candidate.entry.item.id);
        return entry
          ? [{
            bookmark: candidate.bookmark,
            assignment: candidate.assignment,
            entry,
          }]
          : [];
      });
  }

  private windowedLibraryEntries(
    candidates: LibraryViewCandidate[],
  ): LibraryViewCandidate[] {
    if (!this.libraryQuery.trim()) return candidates;
    return candidates.slice(0, this.librarySearchVisibleCount);
  }

  private visibleLibraryEntries(): LibraryViewCandidate[] {
    return this.windowedLibraryEntries(this.allVisibleLibraryEntries());
  }

  private semanticScoresForQuery(): SemanticScoreMap | null {
    if (!this.demo) return null;
    const normalizedQuery = this.libraryQuery
      .trim()
      .toLocaleLowerCase("en");
    const fixture = demoSemanticScoreFixtures[normalizedQuery];
    return fixture ? normalizeSemanticScoreMap(fixture) : null;
  }

  private updateLibrarySearchControls(
    resultCount: number,
    visibleCount: number,
  ): void {
    const input = this.root.querySelector<HTMLInputElement>("#library-search");
    const clear = this.root.querySelector<HTMLButtonElement>("#clear-search");
    const status = this.root.querySelector<HTMLElement>("#library-search-status");
    const window = this.root.querySelector<HTMLElement>("#search-result-window");
    const windowStatus = this.root.querySelector<HTMLElement>(
      "#search-result-window-status",
    );
    const showMore = this.root.querySelector<HTMLButtonElement>(
      "#search-show-more",
    );
    if (input && input.value !== this.libraryQuery) input.value = this.libraryQuery;
    if (clear) clear.hidden = !this.libraryQuery;
    if (!this.libraryQuery.trim()) {
      if (status) status.textContent = "Press / to search";
      if (window) window.hidden = true;
      return;
    }
    if (window) window.hidden = false;
    if (windowStatus) {
      windowStatus.textContent = `Showing ${visibleCount} of ${resultCount} private ${
        resultCount === 1 ? "result" : "results"
      }`;
    }
    const remaining = Math.max(0, resultCount - visibleCount);
    if (showMore) {
      showMore.hidden = remaining === 0;
      showMore.textContent = remaining > 0
        ? `Show ${Math.min(librarySearchWindowSize, remaining)} more`
        : "";
    }
    if (!status) return;
    const semanticAvailable = this.semanticScoresForQuery() !== null;
    status.textContent = [
      `${resultCount} private ${resultCount === 1 ? "result" : "results"}`,
      semanticAvailable
        ? "synthetic semantic score + private metadata"
        : "private metadata fallback",
    ].join(" · ");
  }

  private clearLibrarySearch(): void {
    this.libraryQuery = "";
    this.resetLibrarySearchWindow();
    this.ensureVisibleSelection();
    this.renderLibrary();
    this.focusAfterRender("#library-search");
  }

  private resetLibrarySearchWindow(): void {
    this.librarySearchVisibleCount = librarySearchWindowSize;
  }

  private libraryRow(candidate: LibraryViewCandidate): HTMLLIElement {
    const { bookmark, assignment, entry } = candidate;
    const { item, receipt } = entry;
    const row = document.createElement("li");
    const button = document.createElement("button");
    const lane = document.createElement("span");
    const copy = document.createElement("span");
    const metadata = document.createElement("span");
    const status = document.createElement("span");

    button.type = "button";
    button.dataset.publicationId = item.id;
    button.className = "library-row";
    button.classList.toggle(
      "is-selected",
      item.id === this.selectedPublicationID,
    );
    button.setAttribute(
      "aria-current",
      item.id === this.selectedPublicationID ? "true" : "false",
    );
    button.addEventListener("click", () => {
      this.selectedID = bookmark?.id ?? null;
      this.selectedPublicationID = item.id;
      if (!bookmark) this.libraryDetailMode = "read";
      this.returnToConversation = false;
      this.renderLibrary();
      this.root.querySelector(".app-shell")?.classList.add("detail-open");
      window.setTimeout(() => {
        this.root.querySelector<HTMLButtonElement>("#mobile-back")?.focus();
      }, 0);
    });

    lane.className = `format-icon format-${item.lane}`;
    lane.textContent = laneSymbols[item.lane];
    lane.setAttribute("aria-label", laneLabels[item.lane]);

    copy.className = "row-copy";
    const title = document.createElement("strong");
    title.textContent = item.title;
    metadata.className = "row-metadata";
    metadata.textContent = [
      item.sourceLabel,
      this.senderLabel(item.sharedByUserID),
      assignment.audienceLabel,
      formatLabels[item.format],
      videoDurationLabel(item.videoDurationSeconds),
    ].filter(Boolean).join(" · ");
    copy.append(title, metadata);

    status.className = `status-dot status-${receipt.state}`;
    status.textContent = humanizeStatus(receipt.state);
    button.append(lane, copy, status);
    row.append(button);
    return row;
  }

  private renderDetail(): void {
    const container = this.root.querySelector<HTMLElement>("#detail-content");
    if (!container) return;
    this.destroyBookmarkSelectMenus();
    const bookmark = this.bookmarks.find((candidate) => candidate.id === this.selectedID);
    const recipientEntry = this.recipientEntries.find(
      ({ item }) => item.id === this.selectedPublicationID,
    ) ?? null;
    if (!bookmark) {
      if (recipientEntry) {
        const candidate = this.candidateForRecipientEntry(recipientEntry);
        this.libraryDetailMode = "read";
        this.renderReadingDetail(
          container,
          null,
          candidate.assignment,
          recipientEntry,
          false,
        );
        return;
      }
      container.innerHTML = `
        <div class="detail-empty">
          <span aria-hidden="true">✦</span>
          <h2 id="detail-title">A quiet place for later.</h2>
          <p>Select an explicit Library item to edit its owner-private context.</p>
        </div>
      `;
      return;
    }
    const selectedPublication = bookmark.libraryPublications.find(
      (publication) => (
        publication.id === this.selectedPublicationID
        && publication.revokedAt === null
      ),
    ) ?? null;
    const selectedAssignment = selectedPublication
      ? bookmark.audienceAssignments.find(
        ({ id }) => id === selectedPublication.assignmentId,
      ) ?? null
      : null;
    const selectedEntry = selectedAssignment && recipientEntry
      ? recipientEntry
      : selectedAssignment
        ? toLibraryRecipientEntry(
          bookmark,
          selectedAssignment.audienceId,
          this.viewerUserID,
          this.receipts,
        )
        : null;
    const draftKey = this.bookmarkDraftKey(bookmark.id);
    const draft = this.bookmarkDrafts.get(draftKey);
    if (this.libraryDetailMode === "read" && selectedEntry && selectedAssignment) {
      this.renderReadingDetail(
        container,
        bookmark,
        selectedAssignment,
        selectedEntry,
        Boolean(draft),
      );
      return;
    }
    if (this.libraryDetailMode === "read") {
      this.libraryDetailMode = "edit";
    }

    const backLabel = this.returnToConversation ? "← Cards" : "← Library";
    container.innerHTML = `
      <div class="detail-toolbar">
        <button class="back-button" id="mobile-back" type="button">${backLabel}</button>
        <div class="audience-pill-group" id="detail-audiences" aria-label="Library audiences"></div>
        ${selectedEntry ? `
          <button class="reader-toggle" id="show-reading-view" type="button">
            Reading view
          </button>
        ` : ""}
        <a class="source-link" id="source-link" target="_blank" rel="noopener noreferrer">
          Open original <span aria-hidden="true">↗</span>
        </a>
      </div>
      <div class="reading-card">
        <div class="reading-meta">
          <span class="format-label" id="detail-format"></span>
          <span id="detail-date"></span>
        </div>
        <h2 id="detail-title"></h2>
        <p class="source-byline" id="detail-source"></p>
        <form
          id="bookmark-form"
          class="${this.newBookmarkIDs.has(bookmark.id)
            ? "new-bookmark-form"
            : "existing-bookmark-form"}"
        >
          <nav class="editor-jump-nav" aria-label="Bookmark editor sections">
            <span class="editor-jump-label">Edit</span>
            <div class="editor-jump-actions">
              <button
                class="editor-jump-button is-private"
                type="button"
                data-editor-target="private-notes"
                aria-controls="private-notes"
                aria-label="Jump to private Notes"
              ><span aria-hidden="true">✎</span><span class="editor-jump-copy">Notes</span></button>
              <button
                class="editor-jump-button is-private"
                type="button"
                data-editor-target="private-summary"
                aria-controls="private-summary"
                aria-label="Jump to private human Summary"
              ><span aria-hidden="true">✦</span><span class="editor-jump-copy">Summary</span></button>
              <button
                class="editor-jump-button is-shared"
                type="button"
                data-editor-target="shared-title"
                aria-controls="shared-title"
                aria-label="Jump to Shared Library fields"
              ><span aria-hidden="true">↗</span><span class="editor-jump-copy">Shared</span></button>
              <button
                class="editor-jump-button"
                type="button"
                data-editor-target="audience-rows"
                aria-controls="audience-rows"
                aria-label="Jump to audiences and destinations"
              ><span aria-hidden="true">◎</span><span class="editor-jump-copy">People</span></button>
              <button
                class="editor-jump-button"
                type="button"
                data-editor-target="bookmark-title"
                aria-controls="bookmark-title"
                aria-label="Jump to original bookmark details"
              ><span aria-hidden="true">⌁</span><span class="editor-jump-copy">Details</span></button>
            </div>
          </nav>
          <section class="edit-section owner-source-section" aria-labelledby="owner-source-heading">
            <div class="field-heading">
              <div>
                <h3 id="owner-source-heading">Your saved bookmark</h3>
                <span>Owner-only source details; never copied into a recipient row</span>
              </div>
              <span class="private-badge">Only you</span>
            </div>
            <label for="bookmark-title">Private bookmark title</label>
            <input id="bookmark-title" autocomplete="off" />
            <div class="term-grid owner-source-fields">
              <div>
                <label for="bookmark-source-label">Original source label</label>
                <input id="bookmark-source-label" autocomplete="off" />
              </div>
              <div>
                <label for="bookmark-source-url">Original source URL</label>
                <input id="bookmark-source-url" inputmode="url" autocomplete="url" />
              </div>
            </div>
            ${this.demo ? "" : `
              <p class="field-help">
                Original title and source are read-only here; this adapter
                sends only the approved owner-private and publication fields.
              </p>
            `}
            <div class="owner-theme-field">
              <label for="bookmark-theme">Conversation theme</label>
              <select
                id="bookmark-theme"
                aria-describedby="bookmark-theme-help bookmark-theme-error"
              ></select>
              <p class="field-help" id="bookmark-theme-help">
                Private Conversation deck grouping. It is never copied into Shared Library rows.
              </p>
              <p
                class="form-error inline-error"
                id="bookmark-theme-error"
                role="alert"
                tabindex="-1"
                hidden
              ></p>
            </div>
            <div
              class="owner-media-field"
              id="owner-media-field"
              data-media-reference=""
            >
              <div class="field-heading">
                <div>
                  <label for="private-card-image">Private card image</label>
                  <span>Visible only after a private card is revealed</span>
                </div>
                <span class="private-badge">Only you</span>
              </div>
              <input
                id="private-card-image"
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.gif,.avif,.heic,.heif"
                aria-describedby="private-card-image-help private-card-image-error"
              />
              <label for="private-card-image-alt">Image description</label>
              <input
                id="private-card-image-alt"
                autocomplete="off"
                aria-describedby="private-card-image-help private-card-image-error"
              />
              <figure class="private-media-preview" id="private-media-preview" hidden>
                <img id="private-media-preview-image" alt="" />
              </figure>
              <button
                class="text-button"
                id="remove-private-card-image"
                type="button"
                hidden
              >
                Remove image
              </button>
              <p class="field-help" id="private-card-image-help">
                One PNG, JPEG, WebP, GIF, AVIF, HEIC, or HEIF image, up to 10 MB. Synthetic preview bytes stay in this browser and are never synced.
              </p>
              <p
                class="form-error inline-error"
                id="private-card-image-error"
                role="alert"
                tabindex="-1"
                hidden
              ></p>
            </div>
          </section>

          <section class="edit-section shared-section" aria-labelledby="shared-title-label">
            <div class="field-heading">
              <div>
                <label id="shared-title-label" for="shared-title">Shared Library title</label>
                <span>The audience-safe title shown only in explicitly shared Library rows</span>
              </div>
              <span class="shared-badge shared-publication-badge">Shared</span>
            </div>
            <input id="shared-title" />
            <div class="term-grid shared-source-fields">
              <div>
                <label for="shared-source-label">Shared source label</label>
                <input id="shared-source-label" />
              </div>
              <div>
                <label for="shared-source-url">Shared source URL</label>
                <input id="shared-source-url" inputmode="url" />
              </div>
            </div>
            <div class="field-heading shared-notes-heading">
              <div>
                <label for="shared-notes">Shared Notes</label>
                <span>Audience-visible context from this explicit publication</span>
              </div>
              <span class="shared-badge shared-publication-badge">Shared</span>
            </div>
            <textarea id="shared-notes" rows="3"></textarea>
          </section>

          <section class="edit-section private-notes-section" aria-labelledby="notes-label">
            <div class="field-heading">
              <div>
                <label id="notes-label" for="private-notes"></label>
                <span>The prompt shown only after a private card is turned over</span>
              </div>
              <span class="private-badge">Only you</span>
            </div>
            <textarea id="private-notes" rows="4"></textarea>
          </section>

          <section class="edit-section private-section" aria-labelledby="summary-label">
            <div class="field-heading">
              <div>
                <label id="summary-label" for="private-summary"></label>
                <span>Editing it pauses this Summary in private search until re-indexing finishes</span>
              </div>
              <span class="vector-badge" id="vector-badge"></span>
            </div>
            <textarea id="private-summary" rows="4" aria-describedby="summary-error vector-state private-terms-help"></textarea>
            <p
              class="form-error inline-error"
              id="summary-error"
              role="alert"
              tabindex="-1"
              hidden
            ></p>
            <p class="vector-state" id="vector-state" aria-live="polite"></p>
            <div class="summary-starter-row">
              <button
                class="text-button"
                id="notes-to-summary"
                type="button"
              >
                Use Notes as starter Summary
              </button>
              <span id="summary-provenance"></span>
            </div>

            <div class="term-grid">
              <div>
                <label id="keywords-label" for="keywords">Keywords · only you</label>
                <input id="keywords" aria-describedby="private-terms-help" />
              </div>
              <div>
                <label for="tags">Tags · only you</label>
                <input id="tags" aria-describedby="private-terms-help" />
              </div>
            </div>
            <p class="field-help" id="private-terms-help"></p>
          </section>

          <section class="edit-section compact-fields" aria-labelledby="reading-state-heading">
            <div class="field-heading">
              <div>
                <h3 id="reading-state-heading">Reading state</h3>
                <span>Format and lane describe the shared publication</span>
              </div>
            </div>
            <div class="term-grid">
              <div>
                <label for="bookmark-format">Content format</label>
                <select id="bookmark-format">
                  <option value="text">Text</option>
                  <option value="audio">Audio</option>
                  <option value="video">Video</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div>
                <label for="bookmark-lane">Library lane</label>
                <select id="bookmark-lane">
                  <option value="read">Read</option>
                  <option value="listen">Listen</option>
                  <option value="watch">Watch</option>
                </select>
              </div>
              <div id="video-duration-field" hidden>
                <label for="video-duration-minutes">Video length (minutes)</label>
                <input
                  id="video-duration-minutes"
                  type="number"
                  min="0"
                  step="any"
                  inputmode="decimal"
                  aria-describedby="video-duration-help"
                />
                <p class="field-help" id="video-duration-help">
                  Optional. Watch shows videos over 4 minutes by default.
                </p>
              </div>
            </div>
            <div
              class="triage-review"
              id="triage-review"
              aria-labelledby="triage-review-label"
            >
              <div>
                <span class="triage-kicker" id="triage-review-label">Source suggestion</span>
                <strong id="triage-status" aria-live="polite"></strong>
                <p id="triage-detail"></p>
              </div>
              <button
                class="quiet-button"
                id="apply-triage"
                type="button"
              >
                Use suggestion
              </button>
            </div>
          </section>

          <fieldset class="edit-section audience-editor">
            <legend>Audiences & destinations</legend>
            <div class="audience-editor-heading">
              <div>
                <strong>One bookmark, any number of decks.</strong>
                <p>Private card and Shared Library are separate choices for every audience.</p>
              </div>
              <span class="private-badge">Explicit only</span>
            </div>
            <div class="destination-legend" aria-hidden="true">
              <span>Default</span>
              <span>Private card</span>
              <span>Shared Library</span>
            </div>
            <div class="audience-rows" id="audience-rows"></div>
            <p
              class="form-error inline-error"
              id="audience-error"
              role="alert"
              tabindex="-1"
              hidden
            ></p>
          </fieldset>

          <div class="save-bar" role="region" aria-label="Bookmark save actions">
            <div class="save-state">
              <strong id="draft-state" aria-live="polite"></strong>
              <span><kbd>⌘</kbd><kbd>S</kbd> saves · <kbd>E</kbd> edits Notes</span>
            </div>
            <div class="save-actions">
              <button class="text-button" id="revert-bookmark" type="button">
                ${this.newBookmarkIDs.has(bookmark.id) ? "Discard" : "Revert"}
              </button>
              <button class="primary-button save-button" type="submit">
                ${this.newBookmarkIDs.has(bookmark.id)
                  ? "Create bookmark"
                  : "Save bookmark"}
              </button>
            </div>
          </div>
        </form>
      </div>
    `;
    this.arrangeBookmarkEditorSections(container, bookmark);

    const visibleFormat = draft?.format
      ?? selectedPublication?.format
      ?? bookmark.format;
    const visibleLane = draft?.lane
      ?? selectedPublication?.lane
      ?? bookmark.lane;
    setText(
      container,
      "#detail-format",
      `${laneLabels[visibleLane]} · ${formatLabels[visibleFormat]}`,
    );
    setText(
      container,
      "#detail-date",
      new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" })
        .format(new Date(bookmark.createdAt)),
    );
    setText(
      container,
      "#detail-title",
      draft?.title.trim() || bookmark.title || "New bookmark",
    );
    const visibleDurationLabel = selectedEntry
      ? videoDurationLabel(selectedEntry.item.videoDurationSeconds)
        ?? bookmark.durationLabel
        ?? null
      : bookmark.durationLabel ?? null;
    setText(
      container,
      "#detail-source",
      `From ${
        selectedEntry
          ? this.senderLabel(selectedEntry.item.sharedByUserID)
          : bookmark.senderLabel
      } · ${selectedEntry?.item.sourceLabel ?? bookmark.sourceLabel}${
        visibleDurationLabel ? ` · ${visibleDurationLabel}` : ""
      }`,
    );
    setText(container, "#notes-label", PRIVACY_COPY.privateNotes);
    setText(container, "#summary-label", PRIVACY_COPY.privateSummary);
    setText(container, "#private-terms-help", PRIVACY_COPY.privateTerms);
    const vector = this.vectorPresentation(bookmark, draft);
    setText(container, "#vector-badge", vector.badge);
    setText(
      container,
      "#vector-state",
      vector.detail,
    );

    const vectorBadge = container.querySelector<HTMLElement>("#vector-badge");
    vectorBadge?.classList.add(`vector-${vector.state}`);
    const source = container.querySelector<HTMLAnchorElement>("#source-link");
    const safeSourceURL = safeExternalURL(
      selectedEntry?.item.sourceURL ?? bookmark.sourceURL,
    );
    if (source && safeSourceURL) {
      source.href = safeSourceURL;
    } else {
      source?.remove();
    }
    setValue(
      container,
      "#bookmark-title",
      draft?.title ?? bookmark.title,
    );
    setValue(
      container,
      "#bookmark-source-label",
      draft?.sourceLabel ?? bookmark.sourceLabel,
    );
    setValue(
      container,
      "#bookmark-source-url",
      draft?.sourceURL ?? bookmark.sourceURL,
    );
    if (!this.demo) {
      [
        "#bookmark-title",
        "#bookmark-source-label",
        "#bookmark-source-url",
      ].forEach((selector) => {
        const input = container.querySelector<HTMLInputElement>(selector);
        if (input) input.readOnly = true;
      });
    }
    this.populateConversationThemeSelect(
      container,
      bookmark.primaryTheme,
      draft?.primaryTheme ?? bookmark.primaryTheme,
    );
    this.populatePrivateMediaEditor(container, bookmark, draft);
    setValue(
      container,
      "#shared-title",
      draft?.sharedTitle ?? selectedPublication?.sharedTitle ?? "",
    );
    setValue(
      container,
      "#shared-notes",
      draft?.sharedNotes ?? selectedPublication?.sharedNotes ?? "",
    );
    setValue(
      container,
      "#shared-source-label",
      draft?.sharedSourceLabel
        ?? selectedPublication?.sharedSourceLabel
        ?? "",
    );
    setValue(
      container,
      "#shared-source-url",
      draft?.sharedURL ?? selectedPublication?.sharedURL ?? "",
    );
    setValue(container, "#private-notes", draft?.privateNotes ?? bookmark.privateNotes);
    setValue(
      container,
      "#private-summary",
      draft?.privateHumanSummary ?? bookmark.privateHumanSummary,
    );
    setValue(
      container,
      "#keywords",
      (draft?.keywords ?? bookmark.keywords).join(", "),
    );
    setValue(container, "#tags", (draft?.tags ?? bookmark.tags).join(", "));
    setSelectValue(container, "#bookmark-format", visibleFormat);
    setSelectValue(container, "#bookmark-lane", visibleLane);
    setValue(
      container,
      "#video-duration-minutes",
      videoDurationMinutesInput(
        draft
          ? draft.videoDurationSeconds
          : selectedPublication
            ? selectedPublication.videoDurationSeconds
            : bookmark.videoDurationSeconds,
      ),
    );
    this.configureVideoDurationControl(container, visibleFormat);
    this.renderTriageReview(container);
    for (const badge of container.querySelectorAll<HTMLElement>(
      ".shared-publication-badge",
    )) {
      badge.textContent = selectedAssignment
        ? `Shared · ${selectedAssignment.audienceLabel}`
        : "Shared if chosen";
    }
    this.renderDetailAudiencePills(container, bookmark);
    this.renderAudienceEditor(
      container,
      bookmark,
      draft?.audienceAssignments,
    );
    this.updateSummaryStarter(container, bookmark);
    this.bindBookmarkEditorNavigation(container);

    this.bindDetailBack(container);
    container.querySelector<HTMLButtonElement>("#show-reading-view")
      ?.addEventListener("click", () => this.showReadingDetail());
    const form = container.querySelector<HTMLFormElement>("#bookmark-form");
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.saveSelected(bookmark);
    });
    form?.addEventListener("input", () => this.rememberBookmarkDraft(bookmark));
    form?.addEventListener("change", () => this.rememberBookmarkDraft(bookmark));
    container.querySelector<HTMLInputElement>("#bookmark-source-url")
      ?.addEventListener("input", () => {
        this.refreshTriageAudit(container, bookmark);
      });
    container.querySelector<HTMLSelectElement>("#bookmark-format")
      ?.addEventListener("change", (event) => {
        this.configureVideoDurationControl(
          container,
          (event.currentTarget as HTMLSelectElement).value as LibraryFormat,
        );
        this.refreshTriageAudit(container, bookmark);
      });
    container.querySelector<HTMLSelectElement>("#bookmark-lane")
      ?.addEventListener("change", () => {
        this.refreshTriageAudit(container, bookmark);
      });
    container.querySelector<HTMLInputElement>("#video-duration-minutes")
      ?.addEventListener("input", () => {
        this.refreshTriageAudit(container, bookmark);
      });
    container.querySelector<HTMLTextAreaElement>("#private-notes")
      ?.addEventListener("input", () => {
        this.updateSummaryStarter(container, bookmark);
      });
    container.querySelector<HTMLTextAreaElement>("#private-summary")
      ?.addEventListener("input", () => {
        const summaryError = container.querySelector<HTMLElement>(
          "#summary-error",
        );
        if (summaryError) summaryError.hidden = true;
        this.updateSummaryStarter(container, bookmark);
      });
    container.querySelector<HTMLButtonElement>("#notes-to-summary")
      ?.addEventListener("click", () => {
        const notes = container.querySelector<HTMLTextAreaElement>(
          "#private-notes",
        );
        const summary = container.querySelector<HTMLTextAreaElement>(
          "#private-summary",
        );
        if (!notes || !summary || !notes.value.trim() || summary.value.trim()) {
          return;
        }
        summary.value = notes.value;
        summary.dispatchEvent(new Event("input", { bubbles: true }));
        summary.focus();
        this.showToast(
          "Notes copied into an unsaved private Summary. Shared Notes did not change.",
        );
      });
    container.querySelector<HTMLButtonElement>("#apply-triage")
      ?.addEventListener("click", () => {
        const automatic = this.automaticTriageFromForm(container);
        if (!automatic) {
          return;
        }
        if (automatic.lane === null || automatic.requiresReview) {
          const reviewTarget = automatic.detectedFormat === "video"
            ? container.querySelector<HTMLInputElement>(
              "#video-duration-minutes",
            )
            : container.querySelector<HTMLSelectElement>("#bookmark-format");
          reviewTarget?.focus();
          this.showToast(
            automatic.detectedFormat === "video"
              ? "Add a known video length, or deliberately keep your manual lane."
              : "Choose the Format and Lane you reviewed for this source.",
          );
          return;
        }
        setSelectValue(container, "#bookmark-format", automatic.format);
        setSelectValue(container, "#bookmark-lane", automatic.lane);
        this.syncBookmarkSelectMenu("bookmark-format");
        this.syncBookmarkSelectMenu("bookmark-lane");
        this.configureVideoDurationControl(container, automatic.format);
        this.triageAudits.set(this.bookmarkDraftKey(bookmark.id), automatic);
        this.renderTriageReview(container);
        this.rememberBookmarkDraft(bookmark);
        container.querySelector<HTMLSelectElement>("#bookmark-lane")?.focus();
        this.showToast(
          `${formatLabels[automatic.format]} · ${
            laneLabels[automatic.lane]
          } suggestion applied to this unsaved draft.`,
        );
      });
    container.querySelector<HTMLButtonElement>("#revert-bookmark")
      ?.addEventListener("click", () => {
        this.releaseStagedPrivateMedia(draftKey);
        this.bookmarkDrafts.delete(draftKey);
        this.triageAudits.delete(draftKey);
        if (this.newBookmarkIDs.delete(bookmark.id)) {
          this.bookmarks = this.bookmarks.filter(({ id }) => id !== bookmark.id);
          const first = this.visibleLibraryEntries()[0] ?? null;
          this.selectedID = first?.bookmark?.id ?? null;
          this.selectedPublicationID = first?.entry.item.id ?? null;
          this.renderLibrary();
          this.focusAfterRender("#new-bookmark");
          this.showToast("New synthetic bookmark discarded.");
          return;
        }
        this.renderDetail();
        this.focusAfterRender("#shared-title");
        this.showToast("Unsaved draft reverted.");
    });
    this.mountBookmarkSelectMenus(container);
    this.updateDraftControls(bookmark);
  }

  private arrangeBookmarkEditorSections(
    container: HTMLElement,
    bookmark: Bookmark,
  ): void {
    const navigation = container.querySelector<HTMLElement>(".editor-jump-nav");
    if (!navigation) return;
    const selectors = this.newBookmarkIDs.has(bookmark.id)
      ? [
          ".owner-source-section",
          ".private-notes-section",
          ".private-section",
          ".shared-section",
          ".compact-fields",
          ".audience-editor",
        ]
      : [
          ".private-notes-section",
          ".private-section",
          ".shared-section",
          ".compact-fields",
          ".audience-editor",
          ".owner-source-section",
        ];
    const sections = selectors.flatMap((selector) => {
      const section = container.querySelector<HTMLElement>(selector);
      return section ? [section] : [];
    });
    navigation.after(...sections);
  }

  private bindBookmarkEditorNavigation(container: HTMLElement): void {
    container.querySelectorAll<HTMLButtonElement>("[data-editor-target]")
      .forEach((button) => {
        button.addEventListener("click", () => {
          const targetID = button.dataset.editorTarget;
          if (!targetID) return;
          const target = targetID === "audience-rows"
            ? container.querySelector<HTMLElement>(
              "#audience-rows input:not([disabled])",
            )
            : container.querySelector<HTMLElement>(`#${targetID}`);
          if (!target) return;
          target.focus({ preventScroll: true });
          target.scrollIntoView({
            behavior: "auto",
            block: "center",
            inline: "nearest",
          });
        });
      });
  }

  private renderReadingDetail(
    container: HTMLElement,
    bookmark: Bookmark | null,
    assignment: AudienceAssignment,
    entry: LibraryRecipientEntry,
    hasDraft: boolean,
  ): void {
    const { item, receipt } = entry;
    const backLabel = this.returnToConversation ? "← Cards" : "← Library";
    container.innerHTML = `
      <div class="detail-toolbar">
        <button class="back-button" id="mobile-back" type="button">${backLabel}</button>
        <div class="audience-pill-group" id="detail-audiences" aria-label="Library audiences"></div>
        ${bookmark ? `
          <button class="reader-toggle is-active" id="edit-bookmark" type="button">
            Edit bookmark
          </button>
        ` : ""}
        <a class="source-link" id="source-link" target="_blank" rel="noopener noreferrer">
          Open original <span aria-hidden="true">↗</span>
        </a>
      </div>
      <article class="reading-card reading-preview" id="reading-preview">
        <div class="reading-meta">
          <span class="format-label" id="detail-format"></span>
          <span id="detail-date"></span>
        </div>
        <p class="reader-kicker" id="reader-audience"></p>
        <h2 id="detail-title" tabindex="-1"></h2>
        <p class="source-byline" id="detail-source"></p>
        <section class="reader-note" aria-labelledby="reader-note-title">
          <div class="reader-note-heading">
            <div>
              <span>Shared context</span>
              <h3 id="reader-note-title">Why this was shared</h3>
            </div>
            <span class="shared-badge" id="reader-shared-badge"></span>
          </div>
          <p id="reader-shared-notes"></p>
        </section>
        <dl class="reader-facts" aria-label="Publication details">
          <div>
            <dt>Format</dt>
            <dd id="reader-format"></dd>
          </div>
          <div>
            <dt>Progress</dt>
            <dd id="reader-progress"></dd>
          </div>
          <div>
            <dt>Shared by</dt>
            <dd id="reader-sender"></dd>
          </div>
        </dl>
        <section
          class="reader-progress"
          id="reader-progress-region"
          aria-labelledby="reader-progress-heading"
          aria-busy="false"
        >
          <div class="reader-progress-copy">
            <span>Your reading progress</span>
            <h3 id="reader-progress-heading">What happens next?</h3>
            <strong class="reader-progress-state" id="reader-progress-state" tabindex="-1"></strong>
            <p id="reader-progress-help"></p>
          </div>
          <div
            class="reader-progress-actions"
            id="reader-progress-actions"
            aria-label="Update your reading progress"
          ></div>
        </section>
        <p class="reader-draft-note" id="reader-draft-note" hidden>
          Unsaved edits stay out of this shared reading view until you Save.
        </p>
        <div class="reader-foot">
          <span>Reading view uses only this audience’s explicit publication.</span>
          ${bookmark ? "<span><kbd>E</kbd> edit · <kbd>R</kbd> read</span>" : ""}
        </div>
      </article>
    `;

    setText(
      container,
      "#detail-format",
      `${laneLabels[item.lane]} · ${formatLabels[item.format]}`,
    );
    setText(
      container,
      "#detail-date",
      new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" })
        .format(new Date(item.publishedAt)),
    );
    setText(container, "#reader-audience", `Shared with ${assignment.audienceLabel}`);
    setText(container, "#detail-title", item.title);
    const duration = videoDurationLabel(item.videoDurationSeconds);
    const sender = this.senderLabel(item.sharedByUserID);
    setText(
      container,
      "#detail-source",
      `From ${sender} · ${item.sourceLabel}${duration ? ` · ${duration}` : ""}`,
    );
    setText(container, "#reader-shared-badge", assignment.audienceLabel);
    const sharedNotes = item.sharedNotes.trim();
    setText(
      container,
      "#reader-shared-notes",
      sharedNotes || "No Shared Notes were added to this publication.",
    );
    container.querySelector("#reader-shared-notes")
      ?.classList.toggle("is-empty", !sharedNotes);
    setText(
      container,
      "#reader-format",
      `${formatLabels[item.format]} · ${laneLabels[item.lane]}`,
    );
    setText(container, "#reader-progress", humanizeStatus(receipt.state));
    setText(container, "#reader-progress-state", humanizeStatus(receipt.state));
    setText(container, "#reader-progress-help", progressHelp(receipt.state));
    setText(container, "#reader-sender", sender);
    const draftNote = container.querySelector<HTMLElement>("#reader-draft-note");
    if (draftNote) draftNote.hidden = !hasDraft;

    const source = container.querySelector<HTMLAnchorElement>("#source-link");
    const safeSourceURL = item.sourceURL
      ? safeExternalURL(item.sourceURL)
      : null;
    if (source && safeSourceURL) {
      source.href = safeSourceURL;
    } else {
      source?.remove();
    }

    this.renderReceiptActions(container, entry);
    if (bookmark) {
      this.renderDetailAudiencePills(container, bookmark);
    } else {
      const pills = container.querySelector<HTMLElement>("#detail-audiences");
      if (pills) {
        const pill = document.createElement("span");
        pill.className = "audience-pill";
        pill.textContent = assignment.audienceLabel;
        pills.replaceChildren(pill);
      }
    }
    this.bindDetailBack(container);
    container.querySelector<HTMLButtonElement>("#edit-bookmark")
      ?.addEventListener("click", () => this.showEditorDetail());
  }

  private renderReceiptActions(
    container: HTMLElement,
    entry: LibraryRecipientEntry,
  ): void {
    const actions = container.querySelector<HTMLElement>(
      "#reader-progress-actions",
    );
    if (!actions) return;
    const states = allowedLibraryStatusTransitions(entry.receipt.state);
    if (states.length === 0) {
      const complete = document.createElement("p");
      complete.className = "reader-progress-complete";
      complete.textContent = "No further changes are available.";
      actions.replaceChildren(complete);
      return;
    }
    actions.replaceChildren(...states.map((state) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = state === "dismissed"
        ? "quiet-button reader-progress-action"
        : "reader-progress-action";
      button.dataset.receiptState = state;
      button.textContent = receiptActionLabel(entry.receipt.state, state);
      button.disabled = this.receiptSaveInProgress;
      button.addEventListener("click", () => {
        void this.transitionReadingProgress(entry.item.id, state);
      });
      return button;
    }));
  }

  private async transitionReadingProgress(
    publicationID: string,
    state: LibraryStatus,
  ): Promise<void> {
    if (this.receiptSaveInProgress) return;
    if (!this.receiptRepository) {
      this.showToast(
        "Reading progress couldn’t be saved right now.",
        true,
      );
      return;
    }
    if (
      state === "dismissed"
      && !window.confirm(
        "Dismiss this Library item? Dismissed is terminal for this recipient.",
      )
    ) return;

    this.receiptSaveInProgress = true;
    const region = this.root.querySelector<HTMLElement>(
      "#reader-progress-region",
    );
    region?.setAttribute("aria-busy", "true");
    this.root.querySelectorAll<HTMLButtonElement>(
      "#reader-progress-actions button",
    ).forEach((button) => {
      button.disabled = true;
    });
    try {
      const receipt = await this.receiptRepository.transitionReceipt(
        publicationID,
        state,
      );
      this.receipts = replaceLibraryReceipt(this.receipts, receipt);
      this.recipientEntries = this.recipientEntries.map((entry) => (
        entry.item.id === publicationID
          ? { item: entry.item, receipt }
          : entry
      ));
      this.receiptSaveInProgress = false;
      this.populateLibraryFilters();
      this.renderLibrary();
      this.focusAfterRender("#reader-progress-state");
      this.showToast(
        this.demo
          ? `${humanizeStatus(receipt.state)} saved for this recipient in this browser.`
          : `${humanizeStatus(receipt.state)} saved for your private receipt.`,
      );
    } catch (error) {
      this.receiptSaveInProgress = false;
      this.renderDetail();
      this.focusAfterRender(
        `[data-receipt-state="${state}"]`,
      );
      this.showToast(
        error instanceof Error
          ? error.message
          : "Reading progress could not be saved.",
        true,
      );
    }
  }

  private bindDetailBack(container: HTMLElement): void {
    container.querySelector<HTMLButtonElement>("#mobile-back")
      ?.addEventListener("click", () => {
        if (this.returnToConversation) {
          this.mode = "conversation";
          this.returnToConversation = false;
          this.libraryDetailMode = "edit";
          this.renderApp();
          this.focusConversationStage();
          return;
        }
        this.root.querySelector(".app-shell")?.classList.remove("detail-open");
        this.root.querySelector<HTMLButtonElement>(
          `[data-publication-id="${this.selectedPublicationID ?? ""}"]`,
        )
          ?.focus();
      });
  }

  private showReadingDetail(): void {
    this.libraryDetailMode = "read";
    this.renderDetail();
    this.focusAfterRender("#detail-title");
  }

  private showEditorDetail(): void {
    if (!this.selectedID) return;
    this.libraryDetailMode = "edit";
    this.renderDetail();
    this.focusAfterRender("#private-notes");
  }

  private renderDetailAudiencePills(
    container: HTMLElement,
    bookmark: Bookmark,
  ): void {
    const pills = container.querySelector<HTMLElement>("#detail-audiences");
    if (!pills) return;
    const detailPanel = container.querySelector<HTMLElement>(
      "#bookmark-form, #reading-preview",
    );
    const libraryAssignments = bookmark.audienceAssignments
      .filter((assignment) => assignment.libraryEnabled);
    const livePublicationTabs = libraryAssignments.flatMap((assignment) => {
      const publication = bookmark.libraryPublications.find((candidate) => (
        candidate.assignmentId === assignment.id
        && candidate.revokedAt === null
      ));
      return publication ? [{ assignment, publication }] : [];
    });
    if (!livePublicationTabs.length) {
      pills.setAttribute("role", "group");
      detailPanel?.setAttribute("role", "region");
      detailPanel?.setAttribute("aria-label", "Bookmark editor");
      const pendingAssignments = libraryAssignments.length
        ? libraryAssignments
        : [{
          audienceLabel: "Conversation-only",
        }];
      for (const assignment of pendingAssignments) {
        const pill = document.createElement("span");
        pill.className = "audience-pill audience-pill-private";
        pill.textContent = libraryAssignments.length
          ? `${assignment.audienceLabel} · not saved`
          : assignment.audienceLabel;
        pills.append(pill);
      }
      return;
    }
    const selectedLiveTab = livePublicationTabs.find(
      ({ publication }) => publication.id === this.selectedPublicationID,
    );
    if (!selectedLiveTab) {
      const conversationAssignment = this.returnToConversation
        ? bookmark.audienceAssignments.find(({ audienceId }) => (
          audienceId === this.conversationAudienceID
        ))
        : null;
      const contextLabel = conversationAssignment
        ? `${conversationAssignment.audienceLabel} · Conversation-only`
        : "No Library publication selected";
      pills.setAttribute("role", "group");
      detailPanel?.setAttribute("role", "region");
      detailPanel?.setAttribute(
        "aria-label",
        conversationAssignment
          ? `${conversationAssignment.audienceLabel} Conversation-only bookmark editor`
          : "Bookmark editor",
      );
      const pill = document.createElement("span");
      pill.className = "audience-pill audience-pill-private";
      pill.textContent = contextLabel;
      pills.append(pill);
      return;
    }
    pills.setAttribute("role", "tablist");
    for (const { assignment, publication } of livePublicationTabs) {
      const pill = document.createElement("button");
      pill.type = "button";
      pill.className = "audience-pill";
      pill.dataset.audienceId = assignment.audienceId;
      pill.id = `detail-audience-${opaqueDOMKey(assignment.audienceId)}`;
      pill.setAttribute("role", "tab");
      pill.setAttribute(
        "aria-controls",
        detailPanel?.id ?? "bookmark-form",
      );
      const isSelected = publication.id === this.selectedPublicationID;
      pill.setAttribute("aria-selected", String(isSelected));
      pill.tabIndex = isSelected ? 0 : -1;
      if (isSelected) {
        pill.classList.add("is-selected");
        detailPanel?.setAttribute("role", "tabpanel");
        detailPanel?.setAttribute("aria-labelledby", pill.id);
      }
      pill.textContent = assignment.audienceLabel;
      pill.addEventListener("click", () => {
        this.switchDetailPublication(
          bookmark,
          publication.id,
          assignment.audienceId,
        );
      });
      pill.addEventListener("keydown", (event) => {
        const tabs = [...pills.querySelectorAll<HTMLButtonElement>(
          '[role="tab"]',
        )];
        const currentIndex = tabs.indexOf(pill);
        let nextIndex: number | null = null;
        if (event.key === "ArrowRight") {
          nextIndex = (currentIndex + 1) % tabs.length;
        } else if (event.key === "ArrowLeft") {
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (event.key === "Home") {
          nextIndex = 0;
        } else if (event.key === "End") {
          nextIndex = tabs.length - 1;
        }
        if (nextIndex === null) return;
        event.preventDefault();
        tabs[nextIndex]?.click();
      });
      pills.append(pill);
    }
    this.updateDetailAudienceDraftIndicators(bookmark);
  }

  private switchDetailPublication(
    bookmark: Bookmark,
    publicationID: string,
    audienceID: string,
  ): void {
    if (publicationID === this.selectedPublicationID) return;
    this.rememberBookmarkDraft(bookmark);
    this.selectedPublicationID = publicationID;
    this.renderDetail();
    window.setTimeout(() => {
      const target = [...this.root.querySelectorAll<HTMLButtonElement>(
        '#detail-audiences [role="tab"]',
      )].find(({ dataset }) => dataset.audienceId === audienceID);
      target?.focus();
    }, 0);
  }

  private updateDetailAudienceDraftIndicators(bookmark: Bookmark): void {
    const tabs = this.root.querySelectorAll<HTMLButtonElement>(
      '#detail-audiences [role="tab"]',
    );
    for (const tab of tabs) {
      const assignment = bookmark.audienceAssignments.find(
        ({ audienceId }) => audienceId === tab.dataset.audienceId,
      );
      const publication = assignment
        ? bookmark.libraryPublications.find((candidate) => (
          candidate.assignmentId === assignment.id
          && candidate.revokedAt === null
        ))
        : null;
      const hasDraft = Boolean(
        publication
        && this.bookmarkDrafts.has(
          this.bookmarkDraftKey(bookmark.id, publication.id),
        ),
      );
      let badge = tab.querySelector<HTMLElement>(".audience-pill-draft");
      if (hasDraft && !badge) {
        badge = document.createElement("span");
        badge.className = "audience-pill-draft";
        badge.textContent = "Draft";
        tab.append(badge);
      } else if (!hasDraft) {
        badge?.remove();
      }
      const audienceLabel = assignment?.audienceLabel
        ?? tab.dataset.audienceId
        ?? "Audience";
      tab.setAttribute(
        "aria-label",
        hasDraft ? `${audienceLabel}, unsaved draft` : audienceLabel,
      );
    }
  }

  private renderAudienceEditor(
    container: HTMLElement,
    bookmark: Bookmark,
    draftAssignments?: AudienceAssignment[],
  ): void {
    const rows = container.querySelector<HTMLElement>("#audience-rows");
    if (!rows) return;
    const assignments = draftAssignments ?? bookmark.audienceAssignments;
    const availableAudiences = this.demo
      ? demoAudiences
      : [...this.audienceLabels].map(([id, label]) => ({
        id,
        label,
        hue: "mint" as const,
      }));
    for (const audience of availableAudiences) {
      const existing = assignments.find(
        ({ audienceId }) => audienceId === audience.id,
      );
      const row = document.createElement("div");
      row.className = "audience-row";
      row.dataset.assignmentId = existing?.id
        ?? `assignment-${audience.id}-${opaqueDOMKey(bookmark.id)}`;
      row.dataset.audienceId = audience.id;
      row.dataset.audienceLabel = audience.label;
      row.dataset.conversationState = existing?.conversationState ?? "available";

      const identity = document.createElement("span");
      identity.className = "audience-identity";
      const avatar = document.createElement("span");
      avatar.className = `audience-avatar audience-${audience.hue}`;
      avatar.setAttribute("aria-hidden", "true");
      avatar.textContent = audience.label.slice(0, 1);
      const label = document.createElement("strong");
      label.textContent = audience.label;
      identity.append(avatar, label);

      const primary = primaryAudienceRadio(
        audience.id,
        existing?.isPrimary ?? false,
      );
      const conversation = destinationCheckbox(
        audience.id,
        "conversation",
        "Private card",
        existing?.conversationEnabled ?? false,
      );
      const library = destinationCheckbox(
        audience.id,
        "library",
        "Shared Library",
        existing?.libraryEnabled ?? false,
      );
      row.append(identity, primary, conversation, library);
      rows.append(row);
    }
  }

  private async saveSelected(original: Bookmark): Promise<void> {
    const edits = this.readBookmarkEdits();
    if (!edits) return;
    const privateSummaryError = validatePrivateSummaryTransition(
      original.privateHumanSummary,
      edits.privateHumanSummary,
    );
    const themeError = validateConversationTheme(edits.primaryTheme);
    const mediaError = validatePrivateCardMedia(edits.privateCardMedia);
    const assignmentError = validateOwnerTitle(edits.title)
      ?? validateOptionalExternalURL(
        edits.sourceURL,
        "the original source",
      )
      ?? validateOptionalExternalURL(
        edits.sharedURL,
        "the Shared source",
      )
      ?? validateVideoDurationSeconds(edits.videoDurationSeconds)
      ?? validateAudienceAssignments(edits.audienceAssignments)
      ?? validateSharedTitle(edits.sharedTitle, edits.audienceAssignments)
      ?? validateSharedSourceLabel(
        edits.sharedSourceLabel,
        edits.audienceAssignments,
      );
    const summaryErrorElement = this.root.querySelector<HTMLElement>(
      "#summary-error",
    );
    const themeErrorElement = this.root.querySelector<HTMLElement>(
      "#bookmark-theme-error",
    );
    const mediaErrorElement = this.root.querySelector<HTMLElement>(
      "#private-card-image-error",
    );
    const error = this.root.querySelector<HTMLElement>("#audience-error");
    if (summaryErrorElement) summaryErrorElement.hidden = true;
    if (themeErrorElement) themeErrorElement.hidden = true;
    if (mediaErrorElement) mediaErrorElement.hidden = true;
    if (error) error.hidden = true;
    if (privateSummaryError) {
      if (summaryErrorElement) {
        summaryErrorElement.hidden = false;
        summaryErrorElement.textContent = privateSummaryError;
        summaryErrorElement.focus();
      }
      return;
    }
    if (themeError) {
      if (themeErrorElement) {
        themeErrorElement.hidden = false;
        themeErrorElement.textContent = themeError;
        themeErrorElement.focus();
      }
      return;
    }
    if (mediaError) {
      if (mediaErrorElement) {
        mediaErrorElement.hidden = false;
        mediaErrorElement.textContent = mediaError;
        mediaErrorElement.focus();
      }
      return;
    }
    if (assignmentError) {
      if (error) {
        error.hidden = false;
        error.textContent = assignmentError;
        error.focus();
      }
      return;
    }
    if (!this.repository) {
      this.showToast(
        "Bookmark changes couldn’t be saved right now.",
        true,
      );
      return;
    }
    this.saveInProgress = true;
    this.setSaveBusy(true);
    const activeDraftKey = this.bookmarkDraftKey(original.id);
    const stagedMedia = this.stagedPrivateMedia.get(activeDraftKey);
    let stagedMediaStored = false;
    let bookmarkCommitted = false;

    try {
      const editedAt = new Date().toISOString();
      const priorLivePublicationIDs = new Set(
        original.libraryPublications
          .filter(({ revokedAt }) => revokedAt === null)
          .map(({ id }) => id),
      );
      const updated = applyBookmarkEdits(original, edits, editedAt);
      const publicationError = validatePublicationAlignment(updated);
      if (publicationError) {
        if (error) {
          error.hidden = false;
          error.textContent = publicationError;
          error.focus();
        }
        return;
      }
      if (
        stagedMedia
        && updated.privateCardMedia?.[0]?.url === stagedMedia.reference
      ) {
        if (!this.privateMediaRepository) {
          throw new Error(
            "Private image storage is unavailable in this browser.",
          );
        }
        await this.privateMediaRepository.save(
          stagedMedia.reference,
          stagedMedia.image,
        );
        stagedMediaStored = true;
      }
      const livePublications = updated.libraryPublications.filter(
        ({ revokedAt }) => revokedAt === null,
      );
      const newPublicationIDs = livePublications
        .filter(({ id }) => !priorLivePublicationIDs.has(id))
        .map(({ id }) => id);
      const saveResult = await this.repository.saveBookmark(updated);
      bookmarkCommitted = true;
      this.bookmarks = saveResult.workspace.bookmarks;
      this.enrichmentJobs = saveResult.workspace.enrichmentJobs;
      const receiptProvisionFailures: string[] = [];
      if (this.demo) {
        for (const publicationID of newPublicationIDs) {
          if (!this.receiptRepository) {
            receiptProvisionFailures.push(publicationID);
            continue;
          }
          try {
            const receipt = await this.receiptRepository.provisionReceipt(
              publicationID,
            );
            this.receipts = replaceLibraryReceipt(this.receipts, receipt);
          } catch {
            receiptProvisionFailures.push(publicationID);
          }
        }
        this.recipientEntries = this.recipientLibraryRepository
          ? await this.recipientLibraryRepository.loadLibrary()
          : this.recipientEntries;
      } else if (this.recipientLibraryRepository) {
        this.recipientEntries =
          await this.recipientLibraryRepository.loadLibrary();
        this.receipts = this.recipientEntries.map(({ receipt }) => receipt);
      }
      this.pruneConversationSessionPointers();
      await this.persistConversationSession();
      this.newBookmarkIDs.delete(original.id);
      const savedDraftKey = this.bookmarkDraftKey(original.id);
      this.bookmarkDrafts.delete(savedDraftKey);
      this.triageAudits.delete(savedDraftKey);
      if (stagedMediaStored && stagedMedia) {
        this.privateMediaObjectURLs.set(
          stagedMedia.reference,
          stagedMedia.objectURL,
        );
        this.stagedPrivateMedia.delete(savedDraftKey);
      }
      await this.removeReplacedPrivateMedia(original, updated);
      const savedBookmark = this.bookmarks.find(({ id }) => id === original.id);
      const savedLivePublications = savedBookmark?.libraryPublications.filter(
        ({ revokedAt }) => revokedAt === null,
      ) ?? [];
      if (
        !this.selectedPublicationID
        || !savedLivePublications.some(
          ({ id }) => id === this.selectedPublicationID,
        )
      ) {
        this.selectedPublicationID = savedLivePublications[0]?.id ?? null;
      }
      this.populateLibraryFilters();
      this.renderLibrary();
      this.showToast(
        !this.demo
          ? saveResult.summaryEnrichmentJob?.state === "queued"
            ? "Bookmark saved. Private Summary re-indexing is queued."
            : saveResult.summaryEnrichmentJob?.state === "failed"
              ? "Bookmark saved. Private Summary re-index delivery will retry."
              : "Bookmark saved to your private workspace."
          : receiptProvisionFailures.length > 0
          ? "Bookmark saved, but recipient progress could not be initialized locally."
          : saveResult.summaryEnrichmentJob
          ? "Saved in this sample browser space. Private search has not updated online."
          : "Bookmark saved in this sample browser space.",
        receiptProvisionFailures.length > 0
          || (!this.demo && saveResult.summaryEnrichmentJob?.state === "failed"),
      );
    } catch (saveError) {
      if (stagedMediaStored && stagedMedia && !bookmarkCommitted) {
        await this.privateMediaRepository?.delete(stagedMedia.reference)
          .catch(() => undefined);
        this.releaseStagedPrivateMedia(activeDraftKey);
      }
      this.showToast(
        saveError instanceof Error
          ? saveError.message
          : this.demo
            ? "The synthetic bookmark could not be saved."
            : "The private bookmark could not be saved.",
        true,
      );
    } finally {
      this.saveInProgress = false;
      this.setSaveBusy(false);
    }
  }

  private readBookmarkEdits(): BookmarkEdits | null {
    const title = this.root.querySelector<HTMLInputElement>("#bookmark-title");
    const sourceLabel = this.root.querySelector<HTMLInputElement>(
      "#bookmark-source-label",
    );
    const sourceURL = this.root.querySelector<HTMLInputElement>(
      "#bookmark-source-url",
    );
    const primaryTheme = this.root.querySelector<HTMLSelectElement>(
      "#bookmark-theme",
    );
    const mediaField = this.root.querySelector<HTMLElement>(
      "#owner-media-field",
    );
    const mediaAlt = this.root.querySelector<HTMLInputElement>(
      "#private-card-image-alt",
    );
    const sharedTitle = this.root.querySelector<HTMLInputElement>("#shared-title");
    const sharedSourceLabel = this.root.querySelector<HTMLInputElement>(
      "#shared-source-label",
    );
    const sharedURL = this.root.querySelector<HTMLInputElement>(
      "#shared-source-url",
    );
    const sharedNotes = this.root.querySelector<HTMLTextAreaElement>("#shared-notes");
    const notes = this.root.querySelector<HTMLTextAreaElement>("#private-notes");
    const summary = this.root.querySelector<HTMLTextAreaElement>("#private-summary");
    const keywords = this.root.querySelector<HTMLInputElement>("#keywords");
    const tags = this.root.querySelector<HTMLInputElement>("#tags");
    const format = this.root.querySelector<HTMLSelectElement>("#bookmark-format");
    const lane = this.root.querySelector<HTMLSelectElement>("#bookmark-lane");
    const videoDuration = this.root.querySelector<HTMLInputElement>(
      "#video-duration-minutes",
    );
    if (
      !title
      || !sourceLabel
      || !sourceURL
      || !primaryTheme
      || !mediaField
      || !mediaAlt
      || !sharedTitle
      || !sharedSourceLabel
      || !sharedURL
      || !sharedNotes
      || !notes
      || !summary
      || !keywords
      || !tags
      || !format
      || !lane
      || !videoDuration
    ) return null;
    const videoDurationSeconds = format.value === "video"
      ? parseVideoDurationSeconds(videoDuration.value)
      : undefined;
    const selectedBookmark = this.bookmarks.find(
      ({ id }) => id === this.selectedID,
    );
    const draftKey = selectedBookmark
      ? this.bookmarkDraftKey(selectedBookmark.id)
      : null;
    const triageAudit = draftKey && this.triageAudits.has(draftKey)
      ? this.triageAudits.get(draftKey) ?? null
      : selectedBookmark?.triageAudit ?? null;
    const mediaReference = mediaField.dataset.mediaReference?.trim() ?? "";
    return {
      publicationID: this.selectedPublicationID,
      sharedByUserID: (
        this.bookmarks
          .find(({ id }) => id === this.selectedID)
          ?.libraryPublications
          .find(({ id }) => id === this.selectedPublicationID)
          ?.sharedByUserID
        ?? "mark"
      ),
      title: title.value,
      sourceLabel: sourceLabel.value,
      sourceURL: sourceURL.value,
      primaryTheme: primaryTheme.value,
      privateCardMedia: mediaReference
        ? [{
          kind: "image",
          url: mediaReference,
          alt: mediaAlt.value,
        }]
        : [],
      sharedTitle: sharedTitle.value,
      sharedSourceLabel: sharedSourceLabel.value,
      sharedURL: sharedURL.value,
      sharedNotes: sharedNotes.value,
      privateNotes: notes.value,
      privateHumanSummary: summary.value,
      triageAudit,
      keywords: parseTerms(keywords.value),
      tags: parseTerms(tags.value),
      format: format.value as LibraryFormat,
      lane: lane.value as LibraryLane,
      videoDurationSeconds,
      audienceAssignments: this.readAudienceAssignments(),
    };
  }

  private rememberBookmarkDraft(original: Bookmark): void {
    if (this.saveInProgress) return;
    const edits = this.readBookmarkEdits();
    if (!edits) return;
    if (
      !this.newBookmarkIDs.has(original.id)
      && bookmarkEditsMatch(original, edits)
    ) {
      this.bookmarkDrafts.delete(this.bookmarkDraftKey(original.id));
    } else {
      this.bookmarkDrafts.set(this.bookmarkDraftKey(original.id), edits);
    }
    this.updateDraftControls(original);
  }

  private updateDraftControls(original: Bookmark): void {
    const draft = this.bookmarkDrafts.get(this.bookmarkDraftKey(original.id));
    const dirty = Boolean(draft);
    const state = this.root.querySelector<HTMLElement>("#draft-state");
    const save = this.root.querySelector<HTMLButtonElement>(".save-button");
    const revert = this.root.querySelector<HTMLButtonElement>("#revert-bookmark");
    const form = this.root.querySelector<HTMLFormElement>("#bookmark-form");
    if (state) {
      state.textContent = this.newBookmarkIDs.has(original.id)
        ? "New synthetic bookmark not saved."
        : dirty
          ? "Unsaved draft kept in this tab."
          : this.demo
            ? "All changes saved in this sample browser space."
            : "All changes saved to your private workspace.";
    }
    if (save) save.disabled = !dirty;
    if (revert) revert.hidden = !dirty;
    form?.classList.toggle("has-draft", dirty);
    if (dirty && form?.classList.contains("existing-bookmark-form")) {
      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        const saveBar = form.querySelector<HTMLElement>(".save-bar");
        if (
          !(active instanceof HTMLElement)
          || !form.contains(active)
          || !saveBar
          || getComputedStyle(saveBar).position !== "fixed"
        ) {
          return;
        }
        const activeBox = active.getBoundingClientRect();
        const saveBox = saveBar.getBoundingClientRect();
        if (
          activeBox.top < 0
          || activeBox.bottom > saveBox.top - 12
        ) {
          active.scrollIntoView({ block: "center", inline: "nearest" });
        }
      });
    }
    this.updateDetailAudienceDraftIndicators(original);

    const vector = this.vectorPresentation(original, draft);
    const vectorBadge = this.root.querySelector<HTMLElement>("#vector-badge");
    if (vectorBadge) {
      vectorBadge.textContent = vector.badge;
      vectorBadge.classList.remove(
        "vector-ready",
        "vector-stale",
        "vector-pending",
        "vector-queued",
        "vector-failed",
      );
      vectorBadge.classList.add(`vector-${vector.state}`);
    }
    setText(
      this.root,
      "#vector-state",
      vector.detail,
    );
  }

  private setSaveBusy(busy: boolean): void {
    const save = this.root.querySelector<HTMLButtonElement>(".save-button");
    const form = this.root.querySelector<HTMLFormElement>("#bookmark-form");
    if (save) {
      save.textContent = busy
        ? "Saving locally…"
        : this.selectedID && this.newBookmarkIDs.has(this.selectedID)
          ? "Create bookmark"
          : "Save bookmark";
      const selectedDraft = this.selectedID
        ? this.bookmarkDrafts.has(this.bookmarkDraftKey(this.selectedID))
        : false;
      save.disabled = busy || !selectedDraft;
    }
    form?.setAttribute("aria-busy", String(busy));
  }

  private populateConversationThemeSelect(
    container: ParentNode,
    originalTheme: string,
    selectedTheme: string,
  ): void {
    const select = container.querySelector<HTMLSelectElement>("#bookmark-theme");
    if (!select) return;
    const nativeThemes = new Set<string>(NATIVE_CONVERSATION_THEMES);
    const preservedThemes = [originalTheme, selectedTheme]
      .map((theme) => theme.trim())
      .filter((theme, index, themes) => (
        Boolean(theme)
        && !nativeThemes.has(theme)
        && themes.indexOf(theme) === index
      ));
    if (preservedThemes.length) {
      const existing = document.createElement("optgroup");
      existing.label = "Existing theme";
      for (const theme of preservedThemes) {
        existing.append(option(theme, humanizeConversationTheme(theme)));
      }
      select.append(existing);
    }
    const native = document.createElement("optgroup");
    native.label = "Agenda themes";
    for (const theme of NATIVE_CONVERSATION_THEMES) {
      native.append(option(theme, humanizeConversationTheme(theme)));
    }
    select.append(native);
    select.value = selectedTheme;
  }

  private populatePrivateMediaEditor(
    container: ParentNode,
    bookmark: Bookmark,
    draft?: BookmarkEdits,
  ): void {
    const draftKey = this.bookmarkDraftKey(bookmark.id);
    const field = container.querySelector<HTMLElement>("#owner-media-field");
    const fileInput = container.querySelector<HTMLInputElement>(
      "#private-card-image",
    );
    const altInput = container.querySelector<HTMLInputElement>(
      "#private-card-image-alt",
    );
    const preview = container.querySelector<HTMLElement>(
      "#private-media-preview",
    );
    const previewImage = container.querySelector<HTMLImageElement>(
      "#private-media-preview-image",
    );
    const remove = container.querySelector<HTMLButtonElement>(
      "#remove-private-card-image",
    );
    const error = container.querySelector<HTMLElement>(
      "#private-card-image-error",
    );
    if (
      !field
      || !fileInput
      || !altInput
      || !preview
      || !previewImage
      || !remove
      || !error
    ) return;
    if (!this.demo) {
      field.hidden = true;
      return;
    }

    const media = draft?.privateCardMedia ?? bookmark.privateCardMedia ?? [];
    const [image] = media;
    field.dataset.mediaReference = image?.url ?? "";
    altInput.value = image?.alt ?? "";
    const staged = this.stagedPrivateMedia.get(draftKey);
    const objectURL = image
      ? staged?.reference === image.url
        ? staged.objectURL
        : this.privateMediaObjectURLs.get(image.url)
          ?? safeSameOriginMediaURL(image.url, window.location.origin)
      : null;
    this.updatePrivateMediaPreview(
      preview,
      previewImage,
      remove,
      objectURL,
      image?.alt ?? "",
    );
    if (image && !objectURL) {
      error.hidden = false;
      error.textContent = "This image is unavailable in this browser.";
    }

    fileInput.addEventListener("change", () => {
      const [file] = fileInput.files ?? [];
      if (!file) return;
      if (
        !isAllowedSyntheticImageType(file.type)
        || file.size <= 0
        || file.size > MAX_SYNTHETIC_IMAGE_BYTES
      ) {
        fileInput.value = "";
        error.hidden = false;
        error.textContent =
          "Choose a nonempty PNG, JPEG, WebP, GIF, AVIF, HEIC, or HEIF image no larger than 10 MB.";
        error.focus();
        return;
      }
      this.releaseStagedPrivateMedia(draftKey);
      const reference = `${SYNTHETIC_MEDIA_REFERENCE_PREFIX}${
        crypto.randomUUID()
      }`;
      const objectURL = URL.createObjectURL(file);
      this.stagedPrivateMedia.set(draftKey, {
        reference,
        image: file,
        objectURL,
      });
      field.dataset.mediaReference = reference;
      altInput.value = "";
      error.hidden = true;
      this.updatePrivateMediaPreview(
        preview,
        previewImage,
        remove,
        objectURL,
        "",
      );
      this.rememberBookmarkDraft(bookmark);
      this.focusAfterRender("#private-card-image-alt");
    });
    altInput.addEventListener("input", () => {
      previewImage.alt = altInput.value.trim()
        || "Private card image preview";
      error.hidden = true;
    });
    remove.addEventListener("click", () => {
      this.releaseStagedPrivateMedia(draftKey);
      field.dataset.mediaReference = "";
      altInput.value = "";
      fileInput.value = "";
      error.hidden = true;
      this.updatePrivateMediaPreview(
        preview,
        previewImage,
        remove,
        null,
        "",
      );
      this.rememberBookmarkDraft(bookmark);
      fileInput.focus();
    });
  }

  private updatePrivateMediaPreview(
    preview: HTMLElement,
    image: HTMLImageElement,
    remove: HTMLButtonElement,
    objectURL: string | null,
    alt: string,
  ): void {
    preview.hidden = objectURL === null;
    remove.hidden = objectURL === null;
    if (objectURL === null) {
      image.removeAttribute("src");
      image.alt = "";
      return;
    }
    image.src = objectURL;
    image.alt = alt.trim() || "Private card image preview";
  }

  private configureVideoDurationControl(
    container: HTMLElement,
    format: LibraryFormat,
  ): void {
    const field = container.querySelector<HTMLElement>("#video-duration-field");
    const input = container.querySelector<HTMLInputElement>(
      "#video-duration-minutes",
    );
    if (!field || !input) return;
    const isVideo = format === "video";
    field.hidden = !isVideo;
    input.disabled = !isVideo;
    if (!isVideo) input.value = "";
  }

  private automaticTriageFromForm(
    container: ParentNode,
  ): LibraryTriageDecision | null {
    const source = container.querySelector<HTMLInputElement>(
      "#bookmark-source-url",
    );
    const format = container.querySelector<HTMLSelectElement>(
      "#bookmark-format",
    );
    const duration = container.querySelector<HTMLInputElement>(
      "#video-duration-minutes",
    );
    if (!source || !format || !duration) return null;
    const sourceURL = source.value.trim();
    if (!sourceURL || !safeExternalURL(sourceURL)) return null;
    return classifyLibraryTriage({
      sourceURL,
      videoDurationSeconds: format.value === "video"
        ? parseVideoDurationSeconds(duration.value)
        : undefined,
      longVideoThresholdSeconds: this.filters.longVideoThresholdSeconds
        ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
    });
  }

  private refreshTriageAudit(
    container: HTMLElement,
    bookmark: Bookmark,
  ): void {
    const automatic = this.automaticTriageFromForm(container);
    const format = container.querySelector<HTMLSelectElement>(
      "#bookmark-format",
    );
    const lane = container.querySelector<HTMLSelectElement>("#bookmark-lane");
    if (!format || !lane) return;
    this.triageAudits.set(
      this.bookmarkDraftKey(bookmark.id),
      automatic
        ? reviewedLibraryTriage(
          automatic,
          format.value as LibraryFormat,
          lane.value as LibraryLane,
        )
        : null,
    );
    this.renderTriageReview(container);
  }

  private renderTriageReview(container: ParentNode): void {
    const status = container.querySelector<HTMLElement>("#triage-status");
    const detail = container.querySelector<HTMLElement>("#triage-detail");
    const button = container.querySelector<HTMLButtonElement>("#apply-triage");
    const source = container.querySelector<HTMLInputElement>(
      "#bookmark-source-url",
    );
    const format = container.querySelector<HTMLSelectElement>(
      "#bookmark-format",
    );
    const lane = container.querySelector<HTMLSelectElement>("#bookmark-lane");
    if (!status || !detail || !button || !source || !format || !lane) return;

    const sourceValue = source.value.trim();
    const automatic = this.automaticTriageFromForm(container);
    button.disabled = true;
    if (!sourceValue) {
      status.textContent = "No source to check";
      detail.textContent = "Format and lane stay manual; no triage audit is stored.";
      button.textContent = "No suggestion";
      return;
    }
    if (!safeExternalURL(sourceValue)) {
      status.textContent = "Fix the source URL first";
      detail.textContent = "Suggestions use only a valid http:// or https:// source.";
      button.textContent = "Unavailable";
      return;
    }
    if (!automatic) return;
    if (automatic.detectedFormat === "video" && automatic.detectedLane === null) {
      status.textContent = "Video detected · lane needs review";
      detail.textContent = [
        "Choose Video to add a known length for the four-minute Read/Watch rule,",
        "or keep a deliberate manual lane.",
      ].join(" ");
      button.disabled = false;
      button.textContent = "Add video length";
      return;
    }
    if (automatic.requiresReview || automatic.lane === null) {
      status.textContent = "Needs your review";
      detail.textContent = [
        "No trusted format was inferred from this source.",
        "Saving records the current Format and Lane as a human correction.",
      ].join(" ");
      button.disabled = false;
      button.textContent = "Review Format & Lane";
      return;
    }

    const suggestion = `${
      formatLabels[automatic.format]
    } · ${laneLabels[automatic.lane]}`;
    status.textContent = `${suggestion} suggested`;
    detail.textContent = `${
      humanizeTriageConfidence(automatic.confidence)
    } · ${automatic.ruleVersion} · videos over ${
      automatic.longVideoThresholdSeconds / 60
    } minutes go to Watch.`;
    if (
      format.value === automatic.format
      && lane.value === automatic.lane
    ) {
      button.textContent = "Suggestion applied";
      return;
    }
    button.disabled = false;
    button.textContent = `Use ${suggestion}`;
  }

  private updateSummaryStarter(
    container: ParentNode,
    bookmark: Bookmark,
  ): void {
    const notes = container.querySelector<HTMLTextAreaElement>("#private-notes");
    const summary = container.querySelector<HTMLTextAreaElement>(
      "#private-summary",
    );
    const button = container.querySelector<HTMLButtonElement>(
      "#notes-to-summary",
    );
    const provenance = container.querySelector<HTMLElement>(
      "#summary-provenance",
    );
    if (!notes || !summary || !button || !provenance) return;
    button.hidden = !notes.value.trim() || Boolean(summary.value.trim());
    if (!summary.value.trim()) {
      provenance.textContent = "No private Summary yet";
      return;
    }
    const changed = summary.value.trim() !== bookmark.privateHumanSummary;
    const mode = changed ? "typed" : bookmark.privateSummaryInputMode ?? null;
    provenance.textContent = mode === "spoken"
      ? "Spoken and reviewed · only you"
      : mode === "typed" || changed
        ? "Typed · only you"
        : "Private Summary · only you";
  }

  private createLocalBookmark(capture?: QuickCaptureDraft): void {
    if (!this.demo || !this.repository) return;
    const existingNewID = this.newBookmarkIDs.values().next().value as
      | string
      | undefined;
    if (existingNewID) {
      this.selectedID = existingNewID;
      this.selectedPublicationID = null;
      this.renderLibrary();
      this.root.querySelector(".app-shell")?.classList.add("detail-open");
      this.focusAfterRender("#bookmark-title");
      this.showToast("Finish or discard the current new bookmark first.");
      return;
    }
    const createdAt = new Date().toISOString();
    const id = `local-${crypto.randomUUID()}`;
    const justMe = demoAudiences.find(({ id: audienceID }) => audienceID === "me");
    if (!justMe) return;
    const audienceAssignments: AudienceAssignment[] = [{
      id: `assignment-me-${opaqueDOMKey(id)}`,
      audienceId: justMe.id,
      audienceLabel: justMe.label,
      conversationEnabled: false,
      libraryEnabled: true,
      isPrimary: true,
      conversationState: "available",
    }];
    const bookmark: Bookmark = {
      id,
      title: capture?.title ?? "",
      sourceLabel: capture?.sourceLabel ?? "",
      sourceURL: capture?.sourceURL ?? "",
      senderID: "mark",
      senderLabel: "Mark",
      audienceAssignments,
      libraryPublications: [],
      format: "text",
      lane: "read",
      createdAt,
      primaryTheme: capture?.theme ?? "interesting",
      privateNotes: capture?.privateNotes ?? "",
      privateHumanSummary: "",
      privateSummaryInputMode: null,
      triageAudit: null,
      summaryFingerprint: fingerprintPrivateSummary(""),
      embeddedSummaryFingerprint: null,
      vectorStatus: "pending",
      keywords: [],
      tags: [],
    };
    this.bookmarks = [...this.bookmarks, bookmark];
    this.newBookmarkIDs.add(id);
    this.filters = {
      audienceId: "all",
      senderID: "all",
      lane: "all",
      status: "all",
      watchMode: "longer_than_threshold",
      longVideoThresholdSeconds: DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
    };
    this.libraryQuery = "";
    this.libraryDetailMode = "edit";
    this.selectedID = id;
    this.selectedPublicationID = null;
    this.bookmarkDrafts.set(this.bookmarkDraftKey(id), {
      publicationID: null,
      sharedByUserID: "mark",
      title: capture?.title ?? "",
      sourceLabel: capture?.sourceLabel ?? "",
      sourceURL: capture?.sourceURL ?? "",
      primaryTheme: capture?.theme ?? "interesting",
      privateCardMedia: [],
      sharedTitle: "",
      sharedSourceLabel: "",
      sharedURL: "",
      sharedNotes: "",
      privateNotes: capture?.privateNotes ?? "",
      privateHumanSummary: "",
      triageAudit: null,
      keywords: [],
      tags: [],
      format: "text",
      lane: "read",
      videoDurationSeconds: undefined,
      audienceAssignments: structuredClone(audienceAssignments),
    });
    this.renderLibrary();
    this.root.querySelector(".app-shell")?.classList.add("detail-open");
    this.focusAfterRender("#bookmark-title");
    this.showToast(
      capture
        ? `${
          capture.kind === "link" ? "Link" : "Thought"
        } captured as an unsaved bookmark. Review every field before Save.`
        : "New synthetic bookmark. Nothing is saved until you choose Save.",
    );
  }

  private async resetSyntheticWorkspace(): Promise<void> {
    if (!this.resettableRepository) return;
    const confirmed = window.confirm(
      "Reset all sample edits, private images, card-back photos, outcomes, "
      + "pending search updates, and this tab’s Conversation session?",
    );
    if (!confirmed) return;
    try {
      const workspace = await this.resettableRepository.reset();
      let privateMediaCleanupFailed = false;
      this.bookmarks = workspace.bookmarks;
      this.receipts = workspace.receipts.filter(
        ({ userId }) => userId === demoViewerUserID,
      );
      this.recipientEntries = this.recipientLibraryRepository
        ? await this.recipientLibraryRepository.loadLibrary()
        : [];
      this.enrichmentJobs = workspace.enrichmentJobs;
      this.newCardDraftStore.clear();
      await this.newCardMediaStore?.clearAll().catch(() => {
        privateMediaCleanupFailed = true;
      });
      this.releaseNewCardPhotoObjectURL();
      this.newCardDraft = null;
      this.newCardPhotoStaging = false;
      this.newCardSaveInProgress = false;
      this.bookmarkDrafts.clear();
      this.triageAudits.clear();
      this.newBookmarkIDs.clear();
      await this.privateMediaRepository?.clear().catch(() => {
        privateMediaCleanupFailed = true;
      });
      await this.cardBackPhotoRepository?.clearAll().catch(() => {
        privateMediaCleanupFailed = true;
      });
      await this.continuityRepository?.clear().catch(() => {
        privateMediaCleanupFailed = true;
      });
      this.cardBackPhotos = [];
      this.cardBackPhotoOffset = 0;
      this.cardBackPhotoAudienceID = "maya";
      this.cardBackPhotoLoadGeneration += 1;
      this.cardBackPhotosLoading = false;
      this.releaseAllPrivateMediaObjectURLs();
      await this.conversationSessionRepository?.clear();
      this.conversationSessionID = crypto.randomUUID();
      this.sessionStartedAt = new Date();
      this.conversationSessionActive = true;
      this.endedConversationSessions = [];
      this.conversationAudienceID = "maya";
      this.conversationLayout = "stack";
      this.tabletopMode = false;
      this.tabletopChoiceIndex = null;
      this.stackIndex = 0;
      this.deckOrders.clear();
      this.resolvedContinuity = null;
      this.continuityLoadState = "missing";
      this.continuityOperationInFlight = false;
      this.conversationEvents.length = 0;
      this.selectedThemes.clear();
      this.dateFilter = "all";
      this.resetConversationDeckPreferences(true);
      this.customDateStart = dateInputValue(
        new Date(Date.now() - 30 * 24 * 60 * 60 * 1_000),
      );
      this.customDateEnd = dateInputValue(new Date());
      this.showThemeSymbols = true;
      this.sessionMetricsVisible = defaultSessionMetricsVisible();
      this.revealedCardKeys.clear();
      this.viewAllConfirmed.clear();
      this.filtersOpen = false;
      this.historyOpen = false;
      this.historyDetailEntryID = null;
      this.statsOpen = false;
      this.sessionLifecycleOpen = false;
      this.sessionEndConfirming = false;
      this.filters = {
        audienceId: "all",
        senderID: "all",
        lane: "all",
        status: "all",
        watchMode: "longer_than_threshold",
        longVideoThresholdSeconds: DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
      };
      this.libraryQuery = "";
      this.resetLibrarySearchWindow();
      const first = filterLibrary(
        this.bookmarks,
        this.receipts,
        demoViewerUserID,
        this.filters,
      )[0] ?? null;
      this.selectedID = first?.bookmark?.id ?? null;
      this.selectedPublicationID = first?.entry.item.id ?? null;
      await this.persistConversationSession();
      this.renderApp();
      this.focusAfterRender("#reset-demo");
      this.showToast(
        privateMediaCleanupFailed
          ? "Workspace reset. Some private image bytes could not be cleared; "
            + "reload to retry cleanup."
          : "Synthetic workspace reset.",
        privateMediaCleanupFailed,
      );
    } catch (resetError) {
      this.showToast(
        resetError instanceof Error
          ? resetError.message
          : "The synthetic workspace could not be reset.",
        true,
      );
    }
  }

  private async hydrateCardBackPhotos(
    audienceID = this.conversationAudienceID,
  ): Promise<void> {
    if (!this.cardBackPhotoRepository) return;
    const loadGeneration = this.cardBackPhotoLoadGeneration + 1;
    this.cardBackPhotoLoadGeneration = loadGeneration;
    this.cardBackPhotosLoading = true;
    try {
      const photos = await this.cardBackPhotoRepository.load(audienceID);
      if (
        loadGeneration !== this.cardBackPhotoLoadGeneration
        || audienceID !== this.conversationAudienceID
      ) return;
      this.cardBackPhotoAudienceID = audienceID;
      this.replaceCardBackPhotos(photos);
    } catch {
      if (
        loadGeneration !== this.cardBackPhotoLoadGeneration
        || audienceID !== this.conversationAudienceID
      ) return;
      this.cardBackPhotoAudienceID = audienceID;
      this.replaceCardBackPhotos([]);
      window.setTimeout(() => {
        this.showToast(
          `The private card-back library for ${this.conversationAudienceLabel(
            audienceID,
          )} is unavailable in this browser.`,
          true,
        );
      }, 0);
    } finally {
      if (loadGeneration === this.cardBackPhotoLoadGeneration) {
        this.cardBackPhotosLoading = false;
      }
    }
  }

  private replaceCardBackPhotos(photos: CardBackPhoto[]): void {
    for (const objectURL of this.cardBackPhotoObjectURLs.values()) {
      URL.revokeObjectURL(objectURL);
    }
    this.cardBackPhotoObjectURLs.clear();
    this.cardBackPhotos = photos;
    for (const photo of photos) {
      this.cardBackPhotoObjectURLs.set(
        photo.reference,
        URL.createObjectURL(photo.image),
      );
    }
  }

  private cardBackPhotoURL(position: number): string | null {
    if (
      !this.cardBackPhotos.length
      || this.cardBackPhotoAudienceID !== this.conversationAudienceID
    ) return null;
    const photo = this.cardBackPhotos[
      (position + this.cardBackPhotoOffset) % this.cardBackPhotos.length
    ];
    return this.cardBackPhotoObjectURLs.get(photo.reference) ?? null;
  }

  private advanceCardBackPhotos(): void {
    if (this.cardBackPhotos.length < 2) return;
    this.cardBackPhotoOffset = (
      this.cardBackPhotoOffset + 1
    ) % this.cardBackPhotos.length;
  }

  private async hydratePrivateMedia(): Promise<void> {
    if (!this.privateMediaRepository) return;
    const retainedReferences = new Set(
      this.bookmarks.flatMap((bookmark) => (
        (bookmark.privateCardMedia ?? [])
          .map(({ url }) => url)
          .filter(isSyntheticMediaReference)
      )),
    );
    let unavailable = false;
    try {
      await this.privateMediaRepository.prune(retainedReferences);
      for (const reference of retainedReferences) {
        const image = await this.privateMediaRepository.load(reference);
        if (!image) {
          unavailable = true;
          continue;
        }
        this.privateMediaObjectURLs.set(
          reference,
          URL.createObjectURL(image),
        );
      }
    } catch {
      unavailable = true;
    }
    if (unavailable) {
      window.setTimeout(() => {
        this.showToast(
          "One private image is unavailable in this browser.",
          true,
        );
      }, 0);
    }
  }

  private async removeReplacedPrivateMedia(
    original: Bookmark,
    updated: Bookmark,
  ): Promise<void> {
    if (!this.privateMediaRepository) return;
    const retainedReferences = new Set(
      this.bookmarks.flatMap((bookmark) => (
        (bookmark.privateCardMedia ?? [])
          .map(({ url }) => url)
          .filter(isSyntheticMediaReference)
      )),
    );
    const removedReferences = (original.privateCardMedia ?? [])
      .map(({ url }) => url)
      .filter((reference) => (
        isSyntheticMediaReference(reference)
        && !(updated.privateCardMedia ?? []).some(
          ({ url }) => url === reference,
        )
        && !retainedReferences.has(reference)
      ));
    for (const reference of removedReferences) {
      await this.privateMediaRepository.delete(reference)
        .catch(() => undefined);
      const objectURL = this.privateMediaObjectURLs.get(reference);
      if (objectURL) URL.revokeObjectURL(objectURL);
      this.privateMediaObjectURLs.delete(reference);
    }
  }

  private releaseStagedPrivateMedia(draftKey: string): void {
    const staged = this.stagedPrivateMedia.get(draftKey);
    if (!staged) return;
    URL.revokeObjectURL(staged.objectURL);
    this.stagedPrivateMedia.delete(draftKey);
  }

  private releaseNewCardPhotoObjectURL(): void {
    if (!this.newCardPhotoObjectURL) return;
    URL.revokeObjectURL(this.newCardPhotoObjectURL);
    this.newCardPhotoObjectURL = null;
  }

  private readonly releaseAllPrivateMediaObjectURLs = (): void => {
    this.releaseNewCardPhotoObjectURL();
    for (const { objectURL } of this.stagedPrivateMedia.values()) {
      URL.revokeObjectURL(objectURL);
    }
    this.stagedPrivateMedia.clear();
    for (const objectURL of this.privateMediaObjectURLs.values()) {
      URL.revokeObjectURL(objectURL);
    }
    this.privateMediaObjectURLs.clear();
    for (const objectURL of this.cardBackPhotoObjectURLs.values()) {
      URL.revokeObjectURL(objectURL);
    }
    this.cardBackPhotoObjectURLs.clear();
  };

  private readonly handlePageHide = (event: PageTransitionEvent): void => {
    void this.screenAwake.disable();
    this.markAuthoritativeConversationReturnRefresh();
    if (!event.persisted) this.releaseAllPrivateMediaObjectURLs();
  };

  private vectorPresentation(
    bookmark: Bookmark,
    draft?: BookmarkEdits,
  ): {
    state: VisibleVectorState;
    badge: string;
    detail: string;
  } {
    if (
      draft
      && draft.privateHumanSummary.trim() !== bookmark.privateHumanSummary
    ) {
      if (this.newBookmarkIDs.has(bookmark.id)) {
        return {
          state: "pending",
          badge: this.demo ? "ready to queue" : "ready to request",
          detail: this.demo
            ? "Private search is not ready yet. Save to queue an update in this browser."
            : "Private search is not ready for this Summary. Save to request re-indexing.",
        };
      }
      return {
        state: "stale",
        badge: "stale draft",
        detail: this.demo
          ? "Private search ignores the older Summary while this draft is unsaved. Save to queue an update here."
          : "Private search ignores the older Summary while this edit is unsaved. Save to request re-indexing.",
      };
    }
    const queued = this.enrichmentJobs.find(
      ({ bookmarkId }) => bookmarkId === bookmark.id,
    );
    if (bookmark.vectorStatus === "pending" && queued) {
      if (queued.state === "failed") {
        return {
          state: "failed",
          badge: this.demo ? "local queue failed" : "delivery retrying",
          detail: this.demo
            ? "Private search could not refresh here. Save a Summary edit to try again."
            : "The private search update needs a retry. The outdated Summary stays out of search.",
        };
      }
      return {
        state: "queued",
        badge: this.demo ? "queued locally" : "re-indexing queued",
        detail: this.demo
          ? "Saved in this sample browser space. No online search update runs in preview."
          : "Your edit is saved. Private search has not finished updating.",
      };
    }
    if (bookmark.vectorStatus === "pending" && this.newBookmarkIDs.has(bookmark.id)) {
      return {
        state: "pending",
        badge: "not indexed",
        detail: this.demo
          ? "Add a private Summary, then Save to queue a private search update here."
          : "Add a private Summary, then Save to request private search indexing.",
      };
    }
    return {
      state: bookmark.vectorStatus,
      badge: bookmark.vectorStatus,
      detail: vectorLabels[bookmark.vectorStatus],
    };
  }

  private bookmarkDraftKey(
    bookmarkID: string,
    publicationID: string | null = this.selectedPublicationID,
  ): string {
    return `${bookmarkID}:${publicationID ?? "owner"}`;
  }

  private readAudienceAssignments(): AudienceAssignment[] {
    return [...this.root.querySelectorAll<HTMLElement>(".audience-row")]
      .map((row) => ({
        id: row.dataset.assignmentId ?? "",
        audienceId: row.dataset.audienceId ?? "",
        audienceLabel: row.dataset.audienceLabel ?? "",
        conversationEnabled: Boolean(
          row.querySelector<HTMLInputElement>('[data-destination="conversation"]')
            ?.checked,
        ),
        libraryEnabled: Boolean(
          row.querySelector<HTMLInputElement>('[data-destination="library"]')
            ?.checked,
        ),
        isPrimary: Boolean(
          row.querySelector<HTMLInputElement>("[data-primary-audience]")
            ?.checked,
        ),
        conversationState: (
          row.dataset.conversationState ?? "available"
        ) as ConversationState,
      }));
  }

  private async hydrateContinuityManifest(
    adoptLatestSession: boolean,
  ): Promise<void> {
    const repository = this.continuityRepository;
    const principalUserID = this.viewerUserID;
    const audienceID = this.conversationAudienceID;
    this.resolvedContinuity = null;
    this.continuityLoadState = "loading";
    if (
      !repository
      || !principalUserID
      || !audienceID
      || !this.audienceLabels.has(audienceID)
    ) {
      this.continuityLoadState = "failed";
      return;
    }
    try {
      const loaded = adoptLatestSession
        ? await repository.loadLatest(principalUserID, audienceID)
        : await repository.load({
          principalUserID,
          audienceID,
          sessionID: this.conversationSessionID,
        });
      if (
        principalUserID !== this.viewerUserID
        || audienceID !== this.conversationAudienceID
      ) return;
      if (loaded.recoveredInvalidState) {
        this.continuityLoadState = "invalid";
        return;
      }
      if (!loaded.manifest) {
        this.continuityLoadState = "missing";
        return;
      }
      if (adoptLatestSession) {
        this.conversationSessionID = loaded.manifest.sessionID;
        this.sessionStartedAt = new Date(loaded.manifest.createdAt);
      }
      const resolved = await resolveContinuityManifest(
        loaded.manifest,
        {
          principalUserID,
          audienceID,
          sessionID: this.conversationSessionID,
        },
      );
      if (
        principalUserID !== this.viewerUserID
        || audienceID !== this.conversationAudienceID
      ) return;
      this.resolvedContinuity = resolved;
      this.continuityLoadState = "ready";
      const committedIDs = new Set(resolved.manifest.orderedCardIDs);
      const restoredOrder = this.deckOrders.get(audienceID);
      if (
        adoptLatestSession
        || !restoredOrder?.length
        || restoredOrder.some((cardID) => !committedIDs.has(cardID))
      ) {
        this.deckOrders.set(
          audienceID,
          [...resolved.manifest.orderedCardIDs],
        );
      }
    } catch {
      await repository.remove(principalUserID, audienceID).catch(() => {});
      if (
        principalUserID !== this.viewerUserID
        || audienceID !== this.conversationAudienceID
      ) return;
      this.resolvedContinuity = null;
      this.continuityLoadState = "invalid";
    }
  }

  private async createAndPersistContinuityManifest(
    sessionID: string,
    shuffle = true,
  ): Promise<ResolvedContinuityManifest> {
    const repository = this.continuityRepository;
    if (
      !repository
      || !this.viewerUserID
      || !this.conversationAudienceID
      || !this.audienceLabels.has(this.conversationAudienceID)
    ) {
      throw new Error("This private deck cannot be prepared in this browser.");
    }
    const cardIDs = this.authoritativePlayableConversationCards()
      .map(({ bookmarkID }) => bookmarkID);
    if (cardIDs.length === 0) {
      throw new Error("There are no eligible cards to prepare yet.");
    }
    if (shuffle) {
      for (let index = cardIDs.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [cardIDs[index], cardIDs[randomIndex]] = [
          cardIDs[randomIndex],
          cardIDs[index],
        ];
      }
    }
    const resolved = await generateContinuityManifest({
      principalUserID: this.viewerUserID,
      audienceID: this.conversationAudienceID,
      sessionID,
      orderedCardIDs: cardIDs,
    });
    await repository.save(resolved.manifest);
    return resolved;
  }

  private async startContinuityDeck(): Promise<void> {
    if (
      this.continuityOperationInFlight
      || !this.conversationSessionActive
    ) return;
    this.continuityOperationInFlight = true;
    this.renderConversationMode();
    try {
      const resolved = await this.createAndPersistContinuityManifest(
        this.conversationSessionID,
        false,
      );
      this.resolvedContinuity = resolved;
      this.continuityLoadState = "ready";
      this.deckOrders.set(
        this.conversationAudienceID,
        [...resolved.manifest.orderedCardIDs],
      );
      this.stackIndex = 0;
      this.tabletopChoiceIndex = null;
      this.revealedCardKeys.clear();
      this.viewAllConfirmed.delete(this.conversationAudienceID);
      await this.persistConversationSession();
    } catch (error) {
      this.resolvedContinuity = null;
      this.continuityLoadState = "failed";
      this.showToast(
        error instanceof Error
          ? error.message
          : "This private deck could not be prepared.",
        true,
      );
    } finally {
      this.continuityOperationInFlight = false;
      this.renderConversationMode();
      this.focusConversationStage();
    }
  }

  private async changeConversationAudience(audienceID: string): Promise<void> {
    if (!audienceID || audienceID === this.conversationAudienceID) return;
    if (
      this.authoritativeConversationRepository
      && this.conversationSessionActive
    ) {
      this.showToast(
        "End this synced session before choosing another audience.",
        true,
      );
      this.renderConversationMode();
      this.focusAfterRender("#conversation-audience");
      return;
    }
    this.clearConversationOutcomeUndo();
    this.conversationAudienceID = audienceID;
    this.selectedThemes.clear();
    this.stackIndex = 0;
    this.tabletopChoiceIndex = null;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.statsOpen = false;
    this.sessionLifecycleOpen = false;
    this.sessionEndConfirming = false;
    this.cardBackPhotoLoadGeneration += 1;
    this.cardBackPhotosLoading = true;
    this.cardBackPhotoAudienceID = "";
    this.cardBackPhotoOffset = 0;
    this.replaceCardBackPhotos([]);
    this.resolvedContinuity = null;
    this.continuityLoadState = "loading";
    if (this.authoritativeConversationRepository) {
      this.authoritativeConversationRefreshRequired = true;
      this.authoritativeConversationRefreshInFlight = true;
    }
    void this.persistConversationSession();
    this.renderConversationMode();
    this.focusAfterRender("#conversation-audience");

    if (this.authoritativeConversationRepository) {
      let acceptedAuthority = false;
      try {
        const state = await this.authoritativeConversationRepository.load();
        if (audienceID !== this.conversationAudienceID) return;
        await this.adoptAuthoritativeConversation(state);
        acceptedAuthority = true;
      } catch (error) {
        if (audienceID !== this.conversationAudienceID) return;
        this.revealedCardKeys.clear();
        this.resolvedContinuity = null;
        this.continuityLoadState = "failed";
        this.showToast(
          error instanceof Error
            ? error.message
            : "Conversation Cards could not load for this audience.",
          true,
        );
      } finally {
        this.authoritativeConversationRefreshInFlight = false;
        if (acceptedAuthority) {
          this.authoritativeConversationRefreshRequired = false;
        }
        this.renderConversationMode();
        this.focusAfterRender("#conversation-audience");
      }
      if (audienceID !== this.conversationAudienceID) return;
    } else {
      await this.hydrateContinuityManifest(false);
    }
    if (audienceID !== this.conversationAudienceID) return;
    await this.hydrateCardBackPhotos(audienceID);
    if (audienceID !== this.conversationAudienceID) return;
    this.renderCardBackPhotoContext();
    this.renderConversationStage(this.orderedConversationDeck());
  }

  private renderConversationMode(): void {
    const main = this.root.querySelector<HTMLElement>("#app-content");
    if (!main) return;
    this.destroyLibraryFilterMenus();
    main.dataset.atmosphere = this.conversationAtmosphere;
    const effectiveLayout = this.effectiveConversationLayout();
    const tabletopActive = (
      this.tabletopMode
      && (effectiveLayout === "hand" || effectiveLayout === "table")
    );
    this.root.querySelector(".app-shell")?.classList.toggle(
      "tabletop-active",
      tabletopActive,
    );
    main.className = `conversation-layout${tabletopActive ? " tabletop-layout" : ""}`;
    const audienceOptions = uniqueOptions(
      this.bookmarks.flatMap((bookmark) => (
        bookmark.audienceAssignments
          .filter((assignment) => assignment.conversationEnabled)
          .map((assignment) => [assignment.audienceId, assignment.audienceLabel])
      )),
    );
    if (!audienceOptions.some(([id]) => id === this.conversationAudienceID)) {
      this.conversationAudienceID = audienceOptions[0]?.[0] ?? "";
    }
    if (
      this.demo
      && this.cardBackPhotoRepository
      && this.cardBackPhotoAudienceID !== this.conversationAudienceID
      && !this.cardBackPhotosLoading
    ) {
      const audienceID = this.conversationAudienceID;
      this.cardBackPhotoOffset = 0;
      this.replaceCardBackPhotos([]);
      void this.hydrateCardBackPhotos(audienceID).then(() => {
        if (
          this.mode !== "conversation"
          || this.conversationAudienceID !== audienceID
        ) return;
        this.renderCardBackPhotoContext();
        this.renderConversationStage(this.orderedConversationDeck());
      });
    }
    const cards = this.orderedConversationDeck();
    const eligibleCards = this.authoritativePlayableConversationCards();
    const eligiblePresentation = presentConversationDeck(
      filterConversationCards(
        eligibleCards,
        this.conversationFilters(),
      ),
      this.conversationDeckPreferences(),
    );
    const stats = this.currentConversationSessionStats(
      this.continuityLoadState === "ready" ? cards : eligiblePresentation,
    );
    const activeFilterCount = (
      this.selectedThemes.size + (this.dateFilter === "all" ? 0 : 1)
      + this.conversationDeckIncludedSources.size
      + this.conversationDeckExcludedSources.size
      + (this.conversationDeckSort === "shuffled" ? 0 : 1)
    );
    const deckPreferenceSummary = conversationDeckPreferenceSummary(
      this.conversationDeckPreferences(),
      this.continuityLoadState === "ready"
        ? cards.length
        : eligiblePresentation.length,
    );
    const audienceHistoryCount = this.conversationHistoryGroups()
      .reduce((count, group) => count + group.entries.length, 0);
    const elapsed = this.conversationSessionElapsedLabel();
    const phoneComposition = window.matchMedia(
      mobileConversationQuery,
    ).matches;
    const secondarySessionActionsMarkup = `
      <button
        type="button"
        id="toggle-session-metrics"
        aria-expanded="${this.sessionMetricsVisible}"
        aria-controls="stat-left stat-discussed stat-postponed stat-session"
      >
        ${this.sessionMetricsVisible ? "Hide stats" : "Show stats"}
      </button>
      <button
        type="button"
        id="show-stats"
        aria-expanded="${this.statsOpen}"
        ${
          this.conversationSessionActive
          && !this.authoritativeConversationRefreshRequired
            ? ""
            : "disabled"
        }
      >
        Details
      </button>
      <button
        type="button"
        id="show-history"
        aria-expanded="${this.historyOpen}"
        ${this.authoritativeConversationRefreshRequired ? "disabled" : ""}
      >
        History${audienceHistoryCount ? ` · ${audienceHistoryCount}` : ""}
      </button>
    `;
    const sessionStripMarkup = `
      <section class="session-strip${
        this.sessionMetricsVisible ? "" : " is-collapsed"
      }" aria-label="Session statistics">
        <div class="session-metric" ${
          this.sessionMetricsVisible ? "" : "hidden"
        }><strong id="stat-left">${stats.available}</strong><span>Left</span></div>
        <div class="session-metric" ${
          this.sessionMetricsVisible ? "" : "hidden"
        }><strong id="stat-discussed">${stats.discussed}</strong><span>Discussed</span></div>
        <div class="session-metric" ${
          this.sessionMetricsVisible ? "" : "hidden"
        }><strong id="stat-postponed">${stats.postponed}</strong><span>Postponed</span></div>
        <div class="session-metric" ${
          this.sessionMetricsVisible ? "" : "hidden"
        }><strong id="stat-session">${elapsed}</strong><span>Together</span></div>
        <div class="session-actions">
          <button
            type="button"
            id="manage-conversation-session"
            aria-expanded="${this.sessionLifecycleOpen}"
            ${this.authoritativeConversationRefreshRequired ? "disabled" : ""}
          >
            Session · ${this.conversationSessionActive ? "Live" : "Ended"}${
              this.screenAwake.snapshot().active ? " · Awake" : ""
            }
          </button>
          <button
            type="button"
            id="shuffle-deck"
            ${
              this.conversationSessionActive
              && this.continuityLoadState === "ready"
              && !this.continuityOperationInFlight
              && !this.authoritativeConversationRefreshRequired
              && !this.conversationOutcomeUndo
              && !this.conversationOutcomeUndoInFlight
                ? ""
                : "disabled"
            }
            ${
              this.conversationOutcomeUndo || this.conversationOutcomeUndoInFlight
                ? 'title="Use or dismiss Undo before shuffling this private deck."'
                : ""
            }
          >↝ Shuffle</button>
          ${phoneComposition
            ? `
              <details class="session-more" id="session-more">
                <summary>More</summary>
                <div
                  class="session-more-actions"
                  role="group"
                  aria-label="More session controls"
                >
                  ${secondarySessionActionsMarkup}
                </div>
              </details>
            `
            : secondarySessionActionsMarkup}
        </div>
      </section>
    `;
    const conversationStageMarkup = `
      <div id="conversation-tools"></div>
      <section class="conversation-stage" id="conversation-stage" aria-live="polite"></section>
    `;
    const conversationShortcutsMarkup = `
      <p class="conversation-shortcuts">
        <kbd>Space</kbd> reveal · <kbd>←</kbd><kbd>→</kbd> move ·
        <kbd>R</kbd> choose · <kbd>D</kbd> deal · <kbd>S</kbd> shuffle ·
        <kbd>Esc</kbd> turn face down
      </p>
    `;

    main.innerHTML = `
      <section class="conversation-header">
        <div>
          <p class="eyebrow">IRL conversation mode</p>
          <h1>Make room for<br />a good question.</h1>
          <p class="conversation-intro">
            Pick a shared deck, put the screen between you, and reveal only what
            you are ready to talk about.
          </p>
        </div>
        <div class="conversation-controls">
          <label for="conversation-audience">Deck for</label>
          <select id="conversation-audience"></select>
          <div class="session-config-row">
            <button
              type="button"
              id="choose-session"
              aria-expanded="${this.filtersOpen}"
            >
              Choose session${activeFilterCount ? ` · ${activeFilterCount}` : ""}
            </button>
            <button
              type="button"
              id="toggle-theme-hints"
              aria-pressed="${this.showThemeSymbols}"
            >
              ${this.showThemeSymbols ? "Theme hints on" : "Theme hints off"}
            </button>
            ${this.demo ? `
              <button
                type="button"
                id="manage-card-backs"
                aria-haspopup="dialog"
                aria-controls="card-back-photo-dialog"
                aria-label="Manage card-back photos"
                ${this.cardBackPhotosLoading ? "disabled" : ""}
              >
                Card backs · ${this.cardBackPhotos.length}
              </button>
            ` : ""}
            <details class="room-atmosphere-menu" id="room-atmosphere-menu">
              <summary>
                Room · <strong data-room-atmosphere-label>${
                  ROOM_ATMOSPHERE_OPTIONS.find(
                    ({ id }) => id === this.conversationAtmosphere,
                  )?.label ?? "Velvet"
                }</strong>
              </summary>
              <fieldset
                class="room-atmosphere-picker"
                aria-describedby="room-atmosphere-note"
              >
                <legend>Room atmosphere</legend>
                <div class="room-atmosphere-options">
                  ${ROOM_ATMOSPHERE_OPTIONS.map((option) => `
                    <label
                      class="room-atmosphere-option${
                        option.id === this.conversationAtmosphere
                          ? " is-active"
                          : ""
                      }"
                      data-room-atmosphere-option="${option.id}"
                      for="room-atmosphere-${option.id}"
                    >
                      <input
                        id="room-atmosphere-${option.id}"
                        name="room-atmosphere"
                        type="radio"
                        value="${option.id}"
                        data-room-atmosphere="${option.id}"
                        ${
                          option.id === this.conversationAtmosphere
                            ? "checked"
                            : ""
                        }
                      >
                      <span
                        class="room-atmosphere-swatch"
                        aria-hidden="true"
                      ></span>
                      <span class="room-atmosphere-copy">
                        <strong>${option.label}</strong>
                        <small>${option.description}</small>
                      </span>
                    </label>
                  `).join("")}
                  <button
                    type="button"
                    id="reset-room-atmosphere"
                    class="room-atmosphere-reset"
                    ${
                      this.conversationAtmosphere === DEFAULT_ROOM_ATMOSPHERE
                        ? "disabled"
                        : ""
                    }
                  >Reset</button>
                </div>
                <span class="sr-only" id="room-atmosphere-note">
                  Changes only how this room looks in this browser.
                </span>
              </fieldset>
            </details>
          </div>
          <p
            class="deck-findability-summary"
            id="deck-findability-summary"
            role="status"
            aria-live="polite"
          >${deckPreferenceSummary}</p>
          <div class="layout-picker" role="group" aria-label="Card layout">
            <button type="button" data-layout="stack">Stack</button>
            <button type="button" data-layout="hand">Deal 3</button>
            <button type="button" data-layout="table">Lay down 12</button>
            <button type="button" data-layout="all">View all</button>
          </div>
          <p class="desktop-layout-note">Larger layouts keep every card face down until you choose otherwise.</p>
        </div>
      </section>

      ${phoneComposition
        ? `${conversationStageMarkup}${sessionStripMarkup}${conversationShortcutsMarkup}`
        : `${sessionStripMarkup}${conversationStageMarkup}${conversationShortcutsMarkup}`}
      ${this.demo ? `
        <dialog
          class="card-back-photo-dialog"
          id="card-back-photo-dialog"
          aria-labelledby="card-back-photo-title"
          aria-describedby="card-back-photo-intro card-back-photo-privacy"
        >
          <div class="card-back-photo-sheet">
            <div class="card-back-photo-heading">
              <div>
                <p class="eyebrow">Private ritual</p>
                <h2 id="card-back-photo-title">Card backs</h2>
              </div>
              <button
                class="dialog-close"
                id="close-card-back-photos"
                type="button"
                aria-label="Close card-back photo library"
              >×</button>
            </div>
            <div
              class="card-back-audience-context"
              id="card-back-audience-context"
            >
              <span class="card-back-audience-monogram" aria-hidden="true"></span>
              <span>
                <strong id="card-back-audience-name"></strong>
                <small>Visible only while this private deck is selected</small>
              </span>
            </div>
            <p class="card-back-photo-intro" id="card-back-photo-intro">
              A private pool for the selected deck. Photos rotate independently
              and never imply a connection to the question on the front.
            </p>
            <div class="card-back-photo-add-row">
              <button
                class="primary-button"
                id="add-card-back-photos"
                type="button"
                aria-label="Add private card-back photos"
              >
                <span aria-hidden="true">＋</span> Add photos
              </button>
              <input
                class="sr-only"
                id="card-back-photo-input"
                type="file"
                tabindex="-1"
                aria-label="Choose private card-back photos"
                accept=".png,.jpg,.jpeg,.webp,.gif,.avif,.heic,.heif"
                multiple
              >
              <span id="card-back-photo-count"></span>
            </div>
            <p
              class="card-back-photo-result"
              id="card-back-photo-result"
              role="status"
              aria-live="polite"
            ></p>
            <div id="card-back-photo-library"></div>
            <div class="card-back-photo-privacy" id="card-back-photo-privacy">
              <strong><span aria-hidden="true">⌾</span> Private by default</strong>
              <p>
                This audience pool stays only in this sample browser space. It
                is not attached to bookmarks, synced, or shared.
              </p>
            </div>
            <div class="card-back-photo-footer">
              <button
                class="text-button danger-button"
                id="clear-card-back-photos"
                type="button"
              >
                Clear all
              </button>
              <button
                class="primary-button"
                id="done-card-back-photos"
                type="button"
              >
                Done
              </button>
            </div>
          </div>
        </dialog>
      ` : ""}
    `;

    const select = main.querySelector<HTMLSelectElement>("#conversation-audience");
    if (select) {
      for (const [value, label] of audienceOptions) {
        select.append(option(value, label));
      }
      select.value = this.conversationAudienceID;
      select.disabled = Boolean(
        this.authoritativeConversationRepository
        && (
          this.conversationSessionActive
          || this.authoritativeConversationRefreshRequired
        ),
      );
      if (select.disabled) {
        select.title = this.authoritativeConversationRefreshRequired
          ? "Wait while the latest synced session is checked."
          : "End this synced session before changing audience.";
      }
      select.addEventListener("change", () => {
        void this.changeConversationAudience(select.value);
      });
    }
    main.querySelector<HTMLButtonElement>("#choose-session")
      ?.addEventListener("click", () => {
        const opening = !this.filtersOpen;
        this.filtersOpen = opening;
        this.historyOpen = false;
        this.historyDetailEntryID = null;
        this.statsOpen = false;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        this.renderConversationMode();
        this.focusAfterRender(
          opening ? "#close-session-builder" : "#choose-session",
        );
      });
    main.querySelector<HTMLButtonElement>("#toggle-theme-hints")
      ?.addEventListener("click", () => {
        this.clearConversationOutcomeUndo();
        this.showThemeSymbols = !this.showThemeSymbols;
        this.revealedCardKeys.clear();
        this.historyDetailEntryID = null;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        void this.persistConversationSession();
        this.renderConversationMode();
      });
    for (const option of ROOM_ATMOSPHERE_OPTIONS) {
      main.querySelector<HTMLInputElement>(
        `[data-room-atmosphere="${option.id}"]`,
      )?.addEventListener("change", (event) => {
        if (!(event.currentTarget as HTMLInputElement).checked) return;
        this.setConversationAtmosphere(option.id);
      });
    }
    main.querySelector<HTMLButtonElement>("#reset-room-atmosphere")
      ?.addEventListener("click", () => {
        this.resetConversationAtmosphere();
      });
    main.querySelector<HTMLElement>(".room-atmosphere-picker")
      ?.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        const menu = main.querySelector<HTMLDetailsElement>(
          "#room-atmosphere-menu",
        );
        if (!menu?.open) return;
        event.preventDefault();
        menu.open = false;
        menu.querySelector<HTMLElement>("summary")?.focus({
          preventScroll: true,
        });
      });
    main.querySelectorAll<HTMLButtonElement>("[data-layout]").forEach((button) => {
      const active = button.dataset.layout === this.effectiveConversationLayout();
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.addEventListener("click", () => {
        this.clearConversationOutcomeUndo();
        const layout = button.dataset.layout as ConversationLayout;
        this.tabletopMode = false;
        this.tabletopChoiceIndex = null;
        this.conversationLayout = layout;
        this.revealedCardKeys.clear();
        this.historyDetailEntryID = null;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        void this.persistConversationSession();
        this.renderConversationMode();
        this.focusConversationStage(layout);
      });
    });
    main.querySelector<HTMLButtonElement>("#shuffle-deck")
      ?.addEventListener("click", () => {
        void this.shuffleDeck();
      });
    main.querySelector<HTMLDetailsElement>("#session-more")
      ?.addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        const menu = event.currentTarget as HTMLDetailsElement;
        if (!menu.open) return;
        event.preventDefault();
        menu.open = false;
        menu.querySelector<HTMLElement>("summary")?.focus({
          preventScroll: true,
        });
      });
    main.querySelector<HTMLButtonElement>("#manage-conversation-session")
      ?.addEventListener("click", () => {
        const opening = !this.sessionLifecycleOpen;
        this.sessionLifecycleOpen = opening;
        this.sessionEndConfirming = false;
        this.filtersOpen = false;
        this.historyOpen = false;
        this.historyDetailEntryID = null;
        this.statsOpen = false;
        this.renderConversationMode();
        this.focusAfterRender(
          opening
            ? "#close-session-lifecycle"
            : "#manage-conversation-session",
        );
      });
    main.querySelector<HTMLButtonElement>("#toggle-session-metrics")
      ?.addEventListener("click", () => {
        this.sessionMetricsVisible = !this.sessionMetricsVisible;
        this.historyDetailEntryID = null;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        void this.persistConversationSession();
        this.renderConversationMode();
        this.focusAfterRender(
          window.matchMedia(mobileConversationQuery).matches
            ? "#session-more > summary"
            : "#toggle-session-metrics",
        );
      });
    main.querySelector<HTMLButtonElement>("#show-history")
      ?.addEventListener("click", () => {
        const opening = !this.historyOpen;
        this.historyOpen = opening;
        this.historyDetailEntryID = null;
        this.filtersOpen = false;
        this.statsOpen = false;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        this.renderConversationMode();
        this.focusAfterRender(
          opening
            ? "#close-history"
            : window.matchMedia(mobileConversationQuery).matches
              ? "#session-more > summary"
              : "#show-history",
        );
      });
    main.querySelector<HTMLButtonElement>("#show-stats")
      ?.addEventListener("click", () => {
        const opening = !this.statsOpen;
        this.statsOpen = opening;
        this.filtersOpen = false;
        this.historyOpen = false;
        this.historyDetailEntryID = null;
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        this.renderConversationMode();
        this.focusAfterRender(
          opening
            ? "#close-stats"
            : window.matchMedia(mobileConversationQuery).matches
              ? "#session-more > summary"
              : "#show-stats",
        );
      });
    this.bindCardBackPhotoLibrary();
    this.renderCardBackPhotoContext();
    this.renderConversationTools();
    this.renderConversationStage(cards);
  }

  private setConversationAtmosphere(
    atmosphere: RoomAtmosphereID,
    persist = true,
  ): void {
    this.conversationAtmosphere = atmosphere;
    if (persist) {
      try {
        saveRoomAtmosphere(window.localStorage, atmosphere);
      } catch {
        // The visual change can still apply if storage is unavailable.
      }
    }
    const main = this.root.querySelector<HTMLElement>("#app-content");
    if (!main || !main.classList.contains("conversation-layout")) return;
    main.dataset.atmosphere = atmosphere;
    for (const option of ROOM_ATMOSPHERE_OPTIONS) {
      const input = main.querySelector<HTMLInputElement>(
        `[data-room-atmosphere="${option.id}"]`,
      );
      const active = option.id === atmosphere;
      if (input) input.checked = active;
      input?.closest(".room-atmosphere-option")
        ?.classList.toggle("is-active", active);
    }
    const reset = main.querySelector<HTMLButtonElement>(
      "#reset-room-atmosphere",
    );
    if (reset) reset.disabled = atmosphere === DEFAULT_ROOM_ATMOSPHERE;
    const label = main.querySelector<HTMLElement>(
      "[data-room-atmosphere-label]",
    );
    if (label) {
      label.textContent = ROOM_ATMOSPHERE_OPTIONS.find(
        ({ id }) => id === atmosphere,
      )?.label ?? "Velvet";
    }
  }

  private resetConversationAtmosphere(): void {
    let atmosphere = DEFAULT_ROOM_ATMOSPHERE;
    try {
      atmosphere = resetRoomAtmosphere(window.localStorage);
    } catch {
      // The default can still apply to this tab if storage is unavailable.
    }
    this.setConversationAtmosphere(atmosphere, false);
  }

  private renderConversationTools(): void {
    const tools = this.root.querySelector<HTMLElement>("#conversation-tools");
    if (!tools) return;
    if (this.sessionLifecycleOpen) {
      this.renderSessionLifecycle(tools);
      return;
    }
    if (this.filtersOpen) {
      this.renderSessionBuilder(tools);
      return;
    }
    if (this.historyOpen) {
      this.renderHistory(tools);
      return;
    }
    if (this.statsOpen) {
      this.renderStatsDetails(tools);
      return;
    }
    tools.replaceChildren();
  }

  private syncScreenAwakeContext(): void {
    this.screenAwake.setContext({
      eligible: (
        this.mode === "conversation" && this.conversationSessionActive
      ),
      visible: !document.hidden,
    });
  }

  private screenAwakeControl(): HTMLElement {
    const presentation = presentScreenAwake(this.screenAwake.snapshot());
    const control = document.createElement("section");
    control.id = "screen-awake-control";
    control.className = "screen-awake-control";
    control.dataset.state = this.screenAwake.snapshot().state;
    control.setAttribute("aria-labelledby", "screen-awake-status");

    const copy = document.createElement("div");
    copy.className = "screen-awake-copy";
    const status = document.createElement("strong");
    status.id = "screen-awake-status";
    status.dataset.screenAwakeStatus = "";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.textContent = presentation.status;
    const detail = document.createElement("p");
    detail.dataset.screenAwakeDetail = "";
    detail.textContent = presentation.detail;
    copy.append(status, detail);

    const button = document.createElement("button");
    button.id = "toggle-screen-awake";
    button.className = "quiet-button screen-awake-action";
    button.type = "button";
    button.dataset.screenAwakeAction = "";
    button.hidden = presentation.action === null;
    button.disabled = presentation.disabled;
    button.setAttribute("aria-pressed", String(presentation.pressed));
    button.setAttribute("aria-describedby", "screen-awake-safety");
    button.textContent = presentation.action ?? "";
    button.addEventListener("click", () => {
      const snapshot = this.screenAwake.snapshot();
      if (snapshot.active || snapshot.intended) {
        void this.screenAwake.disable();
      } else {
        void this.screenAwake.enable();
      }
    });

    const safety = document.createElement("p");
    safety.id = "screen-awake-safety";
    safety.className = "screen-awake-safety";
    safety.textContent = (
      "Uses more battery. It does not secure this browser, hide notifications, "
      + "or replace your device lock."
    );
    control.append(copy, button, safety);
    return control;
  }

  private updateScreenAwakeUI(): void {
    const snapshot = this.screenAwake.snapshot();
    const presentation = presentScreenAwake(snapshot);
    const manage = this.root.querySelector<HTMLButtonElement>(
      "#manage-conversation-session",
    );
    if (manage && this.conversationSessionActive) {
      manage.textContent = `Session · Live${snapshot.active ? " · Awake" : ""}`;
    }
    const control = this.root.querySelector<HTMLElement>(
      "#screen-awake-control",
    );
    if (!control) return;
    control.dataset.state = snapshot.state;
    setText(control, "[data-screen-awake-status]", presentation.status);
    setText(control, "[data-screen-awake-detail]", presentation.detail);
    const action = control.querySelector<HTMLButtonElement>(
      "[data-screen-awake-action]",
    );
    if (!action) return;
    const preserveActionFocus = document.activeElement === action;
    if (preserveActionFocus && snapshot.state === "pending") {
      this.screenAwakeActionFocusPending = true;
    }
    action.hidden = presentation.action === null;
    action.disabled = presentation.disabled;
    action.setAttribute("aria-pressed", String(presentation.pressed));
    action.textContent = presentation.action ?? "";
    if (
      (preserveActionFocus || this.screenAwakeActionFocusPending)
      && !action.disabled
      && !action.hidden
    ) {
      action.focus({ preventScroll: true });
      this.screenAwakeActionFocusPending = false;
    } else if (snapshot.state !== "pending") {
      this.screenAwakeActionFocusPending = false;
    }
  }

  private renderSessionLifecycle(container: HTMLElement): void {
    const latestEndedSession = this.endedConversationSessions[0] ?? null;
    const lifecycleEvents = (
      this.conversationSessionActive
        ? this.conversationEvents
        : latestEndedSession?.events ?? []
    ).filter(({ audienceId }) => audienceId === this.conversationAudienceID);
    const discussed = lifecycleEvents.filter(
      ({ action }) => action === "discussed",
    ).length;
    const postponed = lifecycleEvents.filter(
      ({ action }) => action === "postponed",
    ).length;
    container.innerHTML = `
      <section class="session-lifecycle-panel" aria-labelledby="session-lifecycle-title">
        <div class="tool-panel-heading">
          <div>
            <p class="eyebrow">This browser tab</p>
            <h2 id="session-lifecycle-title">${
              this.conversationSessionActive
                ? "Stay in this moment."
                : "That session is tucked away."
            }</h2>
            <p class="session-lifecycle-intro">
              Session history keeps only action pointers. Private questions
              remain concealed until you explicitly review one in History.
            </p>
          </div>
          <button
            class="quiet-button"
            type="button"
            id="close-session-lifecycle"
          >Done</button>
        </div>
        <div class="session-lifecycle-current ${
          this.conversationSessionActive ? "is-live" : "is-ended"
        }">
          <div class="session-lifecycle-status">
            <span class="session-status-badge">${
              this.conversationSessionActive ? "Live now" : "Session ended"
            }</span>
            <strong id="session-lifecycle-audience"></strong>
            <p id="session-lifecycle-time"></p>
          </div>
          <div class="session-lifecycle-totals" aria-label="Selected audience session actions">
            <div><strong>${discussed}</strong><span>Discussed</span></div>
            <div><strong>${postponed}</strong><span>Postponed</span></div>
          </div>
          <div class="session-lifecycle-actions"></div>
        </div>
        <div class="ended-session-history">
          <div class="ended-session-heading">
            <div>
              <p class="eyebrow">Earlier in this tab</p>
              <h3>Sessions worth remembering.</h3>
            </div>
            <span>${this.endedConversationSessions.length} ${
              this.endedConversationSessions.length === 1
                ? "session"
                : "sessions"
            }</span>
          </div>
          <ol id="ended-session-list"></ol>
          <p class="ended-session-empty" ${
            this.endedConversationSessions.length ? "hidden" : ""
          }>End a session when the room is ready. Its action-only story will stay here.</p>
        </div>
      </section>
    `;
    const audience = container.querySelector<HTMLElement>(
      "#session-lifecycle-audience",
    );
    if (audience) {
      audience.textContent = this.conversationSessionActive
        ? `${this.currentConversationAudienceLabel()} deck`
        : latestEndedSession
        ? `${this.currentConversationAudienceLabel()} · last ended session`
        : "Ready for another conversation";
    }
    const time = container.querySelector<HTMLElement>(
      "#session-lifecycle-time",
    );
    if (time) {
      if (this.conversationSessionActive) {
        time.innerHTML = `Together · <span data-session-elapsed>${
          compactElapsedTime(this.sessionStartedAt, new Date())
        }</span> · started ${formatSessionTime(this.sessionStartedAt)}`;
      } else {
        time.textContent = latestEndedSession
          ? `Last session ended ${
            formatSessionTime(new Date(latestEndedSession.endedAt))
          }.`
          : "No active session.";
      }
    }
    const actions = container.querySelector<HTMLElement>(
      ".session-lifecycle-actions",
    );
    if (actions) {
      if (this.conversationSessionActive) {
        actions.append(this.screenAwakeControl());
      }
      if (this.conversationSessionActive && !this.sessionEndConfirming) {
        const end = actionButton("End this session", "end-session", false);
        end.id = "end-session";
        end.classList.add("danger-button");
        end.addEventListener("click", () => {
          this.sessionEndConfirming = true;
          this.historyDetailEntryID = null;
          this.renderConversationMode();
          this.focusAfterRender("#confirm-end-session");
        });
        actions.append(end);
      } else if (this.conversationSessionActive) {
        const confirmation = document.createElement("div");
        confirmation.className = "session-end-confirmation";
        confirmation.setAttribute("role", "alert");
        const copy = document.createElement("div");
        const heading = document.createElement("strong");
        heading.textContent = "End this session?";
        const detail = document.createElement("p");
        detail.textContent = (
          "Elapsed time will freeze and the private stage will close. "
          + "Action-only History stays available in this tab."
        );
        copy.append(heading, detail);
        const buttons = document.createElement("div");
        const keepGoing = actionButton(
          "Keep going",
          "cancel-end-session",
          false,
        );
        const confirm = actionButton(
          "End session",
          "confirm-end-session",
          false,
        );
        keepGoing.id = "cancel-end-session";
        confirm.id = "confirm-end-session";
        confirm.classList.add("danger-button");
        keepGoing.addEventListener("click", () => {
          this.sessionEndConfirming = false;
          this.renderConversationMode();
          this.focusAfterRender("#end-session");
        });
        confirm.addEventListener("click", () => {
          void this.endConversationSession();
        });
        buttons.append(keepGoing, confirm);
        confirmation.append(copy, buttons);
        actions.append(confirmation);
      } else {
        const start = actionButton(
          "Start a new session",
          "start-new-session",
          false,
        );
        start.id = "start-new-session";
        start.classList.add("primary-button");
        start.addEventListener("click", () => {
          void this.startNewConversationSession();
        });
        actions.append(start);
      }
    }
    const list = container.querySelector<HTMLOListElement>(
      "#ended-session-list",
    );
    if (list) {
      this.endedConversationSessions.forEach((session) => {
        const item = document.createElement("li");
        const summary = document.createElement("div");
        summary.className = "ended-session-summary";
        const heading = document.createElement("div");
        const date = document.createElement("strong");
        date.textContent = formatSessionDate(new Date(session.startedAt));
        const duration = document.createElement("span");
        duration.textContent = compactElapsedTime(
          new Date(session.startedAt),
          new Date(session.endedAt),
        );
        heading.append(date, duration);
        const metadata = document.createElement("p");
        const audienceLabels = [...new Set(
          session.events.map(({ audienceId }) => (
            this.conversationAudienceLabel(audienceId)
          )),
        )];
        const sessionDiscussed = session.events.filter(
          ({ action }) => action === "discussed",
        ).length;
        const sessionPostponed = session.events.filter(
          ({ action }) => action === "postponed",
        ).length;
        metadata.textContent = `${
          audienceLabels.length ? audienceLabels.join(" + ") : "No deck touched"
        } · ${sessionDiscussed} discussed · ${sessionPostponed} postponed`;
        summary.append(heading, metadata);
        item.append(summary);
        list.append(item);
      });
    }
    container.querySelector<HTMLButtonElement>("#close-session-lifecycle")
      ?.addEventListener("click", () => {
        this.sessionLifecycleOpen = false;
        this.sessionEndConfirming = false;
        this.renderConversationMode();
        this.focusAfterRender("#manage-conversation-session");
      });
  }

  private renderSessionBuilder(container: HTMLElement): void {
    const sourceOptions = conversationDeckSourceOptions(
      this.authoritativePlayableConversationCards(),
    );
    container.innerHTML = `
      <section class="session-builder" aria-labelledby="session-builder-title">
        <div class="tool-panel-heading">
          <div>
            <p class="eyebrow">Choose this session</p>
            <h2 id="session-builder-title">Follow the room’s energy.</h2>
          </div>
          <button class="quiet-button" type="button" id="close-session-builder">Done</button>
        </div>
        <div class="session-builder-grid">
          <div class="session-builder-basics">
            <div class="date-builder">
              <label for="conversation-date-filter">Date added</label>
              <select id="conversation-date-filter"></select>
              <div class="custom-date-range" id="custom-date-range" ${
                this.dateFilter === "custom" ? "" : "hidden"
              }>
                <label for="custom-date-start">From</label>
                <input type="date" id="custom-date-start" />
                <label for="custom-date-end">Through</label>
                <input type="date" id="custom-date-end" />
              </div>
            </div>
            <div class="deck-order-builder">
              <label for="conversation-deck-sort">Order</label>
              <select id="conversation-deck-sort">
                <option value="shuffled">Shuffled session order</option>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <p>
                Changes presentation only. Your saved session order stays intact.
              </p>
            </div>
          </div>
          <fieldset class="theme-builder">
            <legend>Themes</legend>
            <div id="theme-filter-buttons"></div>
          </fieldset>
          <fieldset class="conversation-source-filters">
            <legend>Where this came from</legend>
            <p>
              Include narrows this deck. Exclude always wins. Unknown sources
              stay grouped instead of being guessed from a link or private text.
            </p>
            <div id="conversation-source-filter-options"></div>
          </fieldset>
        </div>
        <div class="tool-panel-footer">
          <p>
            No themes or included sources keeps every matching playable card.
          </p>
          <button class="text-button" type="button" id="clear-session-filters">
            Clear all filters
          </button>
        </div>
      </section>
    `;
    const dateSelect = container.querySelector<HTMLSelectElement>(
      "#conversation-date-filter",
    );
    if (dateSelect) {
      for (const [value, label] of Object.entries(dateFilterLabels)) {
        dateSelect.append(option(value, label));
      }
      dateSelect.value = this.dateFilter;
      dateSelect.addEventListener("change", () => {
        this.dateFilter = dateSelect.value as ConversationDateFilter;
        this.resetConversationAfterFilter();
      });
    }
    const deckSort = container.querySelector<HTMLSelectElement>(
      "#conversation-deck-sort",
    );
    if (deckSort) {
      deckSort.value = this.conversationDeckSort;
      deckSort.addEventListener("change", () => {
        this.conversationDeckSort = deckSort.value as OwnerPreparationSort;
        this.persistConversationDeckPreferences();
        this.resetConversationAfterFilter("#conversation-deck-sort");
      });
    }
    const customStart = container.querySelector<HTMLInputElement>(
      "#custom-date-start",
    );
    const customEnd = container.querySelector<HTMLInputElement>(
      "#custom-date-end",
    );
    if (customStart) {
      customStart.value = this.customDateStart;
      customStart.addEventListener("change", () => {
        this.customDateStart = customStart.value;
        this.resetConversationAfterFilter();
      });
    }
    if (customEnd) {
      customEnd.value = this.customDateEnd;
      customEnd.addEventListener("change", () => {
        this.customDateEnd = customEnd.value;
        this.resetConversationAfterFilter();
      });
    }

    const themeButtons = container.querySelector<HTMLElement>(
      "#theme-filter-buttons",
    );
    if (themeButtons) {
      for (const theme of this.themesForCurrentAudience()) {
        const button = document.createElement("button");
        const active = this.selectedThemes.has(theme);
        button.type = "button";
        button.className = "theme-filter-chip";
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
        button.textContent = humanizeConversationTheme(theme);
        button.addEventListener("click", () => {
          if (this.selectedThemes.has(theme)) {
            this.selectedThemes.delete(theme);
          } else {
            this.selectedThemes.add(theme);
          }
          this.resetConversationAfterFilter();
        });
        themeButtons.append(button);
      }
    }
    const sourceFilters = container.querySelector<HTMLElement>(
      "#conversation-source-filter-options",
    );
    if (sourceFilters) {
      sourceFilters.replaceChildren(...sourceOptions.map((source) => {
        const label = document.createElement("label");
        label.className = "conversation-source-filter";
        const copy = document.createElement("span");
        copy.textContent = `${source.label} · ${source.count}`;
        const select = document.createElement("select");
        select.id = `conversation-source-filter-${source.id}`;
        select.dataset.conversationSource = source.id;
        select.setAttribute(
          "aria-label",
          `${source.label} Conversation source filter`,
        );
        select.append(
          option("any", "Any"),
          option("include", "Include"),
          option("exclude", "Exclude"),
        );
        const mode: OwnerSourceFilterMode = (
          this.conversationDeckExcludedSources.has(source.id)
            ? "exclude"
            : this.conversationDeckIncludedSources.has(source.id)
              ? "include"
              : "any"
        );
        select.value = mode;
        select.addEventListener("change", () => {
          const next = select.value as OwnerSourceFilterMode;
          this.conversationDeckIncludedSources.delete(source.id);
          this.conversationDeckExcludedSources.delete(source.id);
          if (next === "include") {
            this.conversationDeckIncludedSources.add(source.id);
          } else if (next === "exclude") {
            this.conversationDeckExcludedSources.add(source.id);
          }
          this.persistConversationDeckPreferences();
          this.resetConversationAfterFilter(`#${select.id}`);
        });
        label.append(copy, select);
        return label;
      }));
    }
    container.querySelector<HTMLButtonElement>("#close-session-builder")
      ?.addEventListener("click", () => {
        this.filtersOpen = false;
        this.renderConversationMode();
        this.focusAfterRender("#choose-session");
      });
    container.querySelector<HTMLButtonElement>("#clear-session-filters")
      ?.addEventListener("click", () => this.clearConversationFilters());
  }

  private renderHistory(container: HTMLElement): void {
    container.innerHTML = `
      <section class="history-panel" aria-labelledby="history-title">
        <div class="tool-panel-heading">
          <div>
            <p class="eyebrow">This browser tab</p>
            <h2 id="history-title">The moments you touched.</h2>
            <p class="history-privacy-copy">
              Questions stay concealed until you review one.
            </p>
          </div>
          <button class="quiet-button" type="button" id="close-history">Done</button>
        </div>
        <div class="history-filter" role="group" aria-label="History filter">
          <button type="button" data-history-filter="all">All</button>
          <button type="button" data-history-filter="discussed">Discussed</button>
          <button type="button" data-history-filter="postponed">Postponed</button>
        </div>
        <div class="history-groups" id="history-list"></div>
        <div class="history-empty" id="history-empty" hidden>
          Reveal a card in a live session to begin its action-only story.
        </div>
        <div id="history-detail-slot"></div>
      </section>
    `;
    container.querySelectorAll<HTMLButtonElement>("[data-history-filter]")
      .forEach((button) => {
        const filter = button.dataset.historyFilter as HistoryFilter;
        const active = filter === this.historyFilter;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
        button.addEventListener("click", () => {
          this.historyFilter = filter;
          this.historyDetailEntryID = null;
          this.renderConversationMode();
          this.focusAfterRender(`[data-history-filter="${filter}"]`);
        });
      });
    container.querySelector<HTMLButtonElement>("#close-history")
      ?.addEventListener("click", () => {
        this.historyOpen = false;
        this.historyDetailEntryID = null;
        this.renderConversationMode();
        this.focusAfterRender(
          window.matchMedia(mobileConversationQuery).matches
            ? "#session-more > summary"
            : "#show-history",
        );
      });

    const groups = this.conversationHistoryGroups()
      .map((group) => ({
        ...group,
        entries: group.entries.filter(({ event }) => (
          this.historyFilter === "all"
          || event.action === this.historyFilter
        )),
      }))
      .filter(({ entries }) => entries.length > 0);
    const entries = groups.flatMap(({ entries: groupEntries }) => groupEntries);
    const list = container.querySelector<HTMLElement>("#history-list");
    const empty = container.querySelector<HTMLElement>("#history-empty");
    const detailSlot = container.querySelector<HTMLElement>(
      "#history-detail-slot",
    );
    if (!list || !empty || !detailSlot) return;
    list.hidden = entries.length === 0;
    empty.hidden = entries.length > 0;
    let historyIndex = 0;
    groups.forEach((group, groupIndex) => {
      const section = document.createElement("section");
      section.className = "history-session-group";
      const headingID = `history-session-heading-${groupIndex}`;
      section.setAttribute("aria-labelledby", headingID);
      const groupHeading = document.createElement("div");
      groupHeading.className = "history-session-heading";
      const headingCopy = document.createElement("div");
      const heading = document.createElement("h3");
      heading.id = headingID;
      heading.textContent = group.active
        ? "Current session"
        : formatSessionDate(group.startedAt);
      const metadata = document.createElement("p");
      metadata.textContent = `${
        group.active
          ? this.conversationSessionElapsedLabel()
          : compactElapsedTime(group.startedAt, group.endedAt ?? new Date())
      } · ${group.entries.length} ${
        group.entries.length === 1 ? "moment" : "moments"
      }`;
      headingCopy.append(heading, metadata);
      const status = document.createElement("span");
      status.textContent = group.active ? "Live" : "Ended";
      groupHeading.append(headingCopy, status);
      const groupList = document.createElement("ol");
      groupList.className = "history-list";
      group.entries.forEach(({ id, event }) => {
        const index = historyIndex;
        historyIndex += 1;
        const item = document.createElement("li");
        const reveal = document.createElement("button");
        reveal.type = "button";
        reveal.className = "history-entry";
        reveal.dataset.historyIndex = String(index);
        const isSelected = id === this.historyDetailEntryID;
        reveal.setAttribute("aria-expanded", String(isSelected));
        if (isSelected) reveal.setAttribute("aria-controls", "history-detail");
        const symbol = document.createElement("span");
        symbol.className = `history-symbol history-${event.action}`;
        symbol.setAttribute("aria-hidden", "true");
        symbol.textContent = event.action === "discussed"
          ? "✓"
          : event.action === "postponed" ? "↝" : "✦";
        const copy = document.createElement("div");
        const entryHeading = document.createElement("strong");
        entryHeading.textContent = `${humanizeHistoryAction(event.action)} card`;
        const entryMetadata = document.createElement("span");
        entryMetadata.textContent = new Intl.DateTimeFormat(undefined, {
          hour: "numeric",
          minute: "2-digit",
        }).format(event.occurredAt);
        const cue = document.createElement("span");
        cue.className = "history-review-cue";
        cue.textContent = isSelected
          ? "Conceal private question"
          : "Review private question";
        copy.append(entryHeading, entryMetadata, cue);
        reveal.append(symbol, copy);
        reveal.addEventListener("click", () => {
          this.historyDetailEntryID = isSelected ? null : id;
          this.renderConversationMode();
          this.focusAfterRender(
            isSelected
              ? `[data-history-index="${index}"]`
              : "#history-detail-title",
          );
        });
        item.append(reveal);
        groupList.append(item);
      });
      section.append(groupHeading, groupList);
      list.append(section);
    });

    const selectedIndex = entries.findIndex(
      ({ id }) => id === this.historyDetailEntryID,
    );
    if (selectedIndex < 0) {
      this.historyDetailEntryID = null;
      return;
    }
    const selected = entries[selectedIndex];
    const detail = document.createElement("section");
    detail.className = "history-detail";
    detail.id = "history-detail";
    detail.setAttribute("aria-labelledby", "history-detail-title");
    const detailHeading = document.createElement("div");
    detailHeading.className = "history-detail-heading";
    const detailLabel = document.createElement("p");
    detailLabel.className = "eyebrow";
    detailLabel.textContent = "One private question";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "quiet-button";
    close.id = "close-history-detail";
    close.textContent = "Conceal";
    const prompt = document.createElement("h3");
    prompt.id = "history-detail-title";
    prompt.tabIndex = -1;
    prompt.textContent = selected.card.title;
    const detailMetadata = document.createElement("p");
    detailMetadata.className = "history-detail-metadata";
    detailMetadata.textContent = `${
      humanizeHistoryAction(selected.event.action)
    } · ${humanizeConversationTheme(selected.card.theme)}`;
    detailHeading.append(detailLabel, close);
    detail.append(detailHeading, prompt, detailMetadata);
    close.addEventListener("click", () => {
      this.historyDetailEntryID = null;
      this.renderConversationMode();
      this.focusAfterRender(`[data-history-index="${selectedIndex}"]`);
    });
    detailSlot.append(detail);
  }

  private renderStatsDetails(container: HTMLElement): void {
    const playable = this.orderedConversationDeck();
    const stats = this.currentConversationSessionStats(playable);
    const themeCounts = [...playable.reduce((counts, card) => {
      counts.set(card.theme, (counts.get(card.theme) ?? 0) + 1);
      return counts;
    }, new Map<string, number>()).entries()]
      .sort((left, right) => (
        right[1] - left[1] || left[0].localeCompare(right[0])
      ));

    container.innerHTML = `
      <section class="stats-panel" aria-labelledby="stats-title">
        <div class="tool-panel-heading">
          <div>
            <p class="eyebrow">Session details</p>
            <h2 id="stats-title">See the shape of the deck.</h2>
          </div>
          <button class="quiet-button" type="button" id="close-stats">Done</button>
        </div>
        <div class="stats-summary-grid">
          <div><strong>${stats.available}</strong><span>Cards left</span></div>
          <div><strong>${stats.discussed}</strong><span>Discussed</span></div>
          <div><strong>${stats.postponed}</strong><span>Postponed</span></div>
          <div>
            <strong data-session-elapsed>${compactElapsedTime(
              this.sessionStartedAt,
              new Date(),
            )}</strong>
            <span>Together</span>
          </div>
        </div>
        <div class="theme-distribution">
          <h3>Remaining by theme</h3>
          <div id="theme-distribution-rows"></div>
        </div>
      </section>
    `;
    container.querySelector<HTMLButtonElement>("#close-stats")
      ?.addEventListener("click", () => {
        this.statsOpen = false;
        this.renderConversationMode();
        this.focusAfterRender(
          window.matchMedia(mobileConversationQuery).matches
            ? "#session-more > summary"
            : "#show-stats",
        );
      });
    const rows = container.querySelector<HTMLElement>(
      "#theme-distribution-rows",
    );
    if (!rows) return;
    if (!themeCounts.length) {
      rows.innerHTML = `
        <p class="stats-empty">
          No matching cards. Change the active filters to refill this view.
        </p>
      `;
      return;
    }
    for (const [theme, count] of themeCounts) {
      const row = document.createElement("div");
      row.className = "theme-distribution-row";
      row.setAttribute(
        "aria-label",
        `${humanizeConversationTheme(theme)}, ${count} of ${playable.length} remaining`,
      );
      const heading = document.createElement("div");
      const label = document.createElement("strong");
      label.textContent = humanizeConversationTheme(theme);
      const value = document.createElement("span");
      value.textContent = String(count);
      heading.append(label, value);
      const track = document.createElement("span");
      track.className = "theme-distribution-track";
      track.setAttribute("aria-hidden", "true");
      const fill = document.createElement("span");
      fill.style.width = `${(count / Math.max(playable.length, 1)) * 100}%`;
      track.append(fill);
      row.append(heading, track);
      rows.append(row);
    }
  }

  private conversationFilters() {
    return {
      themes: [...this.selectedThemes],
      dateFilter: this.dateFilter,
      customStart: this.customDateStart,
      customEnd: this.customDateEnd,
    };
  }

  private authoritativePlayableConversationCards(): ConversationCard[] {
    const cards = conversationDeck(
      this.bookmarks,
      this.conversationAudienceID,
    ).filter(({ state }) => state !== "discussed");
    if (
      !this.authoritativeConversationRepository
      || !this.conversationSessionActive
    ) return cards;
    return cards.filter((card) => {
      const assignmentID = this.assignmentIDForConversationCard(card);
      return assignmentID !== null
        && this.authoritativeConversationCards.has(assignmentID);
    });
  }

  private themesForCurrentAudience(): string[] {
    return [...new Set(
      this.authoritativePlayableConversationCards()
        .map(({ theme }) => theme),
    )].sort((left, right) => left.localeCompare(right));
  }

  private resetConversationAfterFilter(focusSelector?: string): void {
    this.clearConversationOutcomeUndo();
    this.stackIndex = 0;
    this.tabletopChoiceIndex = null;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.delete(this.conversationAudienceID);
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    void this.persistConversationSession();
    this.renderConversationMode();
    if (focusSelector) this.focusAfterRender(focusSelector);
  }

  private clearConversationFilters(): void {
    this.selectedThemes.clear();
    this.dateFilter = "all";
    this.resetConversationDeckPreferences();
    this.persistConversationDeckPreferences();
    this.resetConversationAfterFilter("#clear-session-filters");
  }

  private renderConversationStage(
    cards: ConversationCard[],
    preserveCardTurn = false,
  ): void {
    if (!preserveCardTurn) this.cancelCardTurnTransition();
    const stage = this.root.querySelector<HTMLElement>("#conversation-stage");
    if (!stage) return;
    if (
      this.authoritativeConversationRepository
      && this.authoritativeConversationRefreshRequired
    ) {
      stage.className = "conversation-stage continuity-recovery-stage";
      stage.innerHTML = `
        <div class="continuity-recovery">
          <span class="continuity-recovery-mark" aria-hidden="true">↻</span>
          <p class="eyebrow">Synced room privacy</p>
          <h2>${
            this.authoritativeConversationRefreshInFlight
              ? "Checking the latest private deck…"
              : "This deck stayed face down."
          }</h2>
          <p>
            ${
              this.authoritativeConversationRefreshInFlight
                ? "Convoful is checking for accepted actions from another browser or device."
                : "The latest accepted state could not be verified, so no private prompt or stale History is available."
            }
          </p>
          <button
            class="primary-button"
            type="button"
            id="refresh-authoritative-conversation"
            ${this.authoritativeConversationRefreshInFlight ? "disabled" : ""}
          >
            ${
              this.authoritativeConversationRefreshInFlight
                ? "Checking…"
                : "Try again"
            }
          </button>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>(
        "#refresh-authoritative-conversation",
      )?.addEventListener("click", () => {
        this.refreshAuthoritativeConversationOnReturn();
      });
      return;
    }
    if (!this.conversationSessionActive) {
      stage.className = "conversation-stage ended-session-stage";
      stage.innerHTML = `
        <div class="ended-session-card">
          <span class="ended-session-mark" aria-hidden="true">✓</span>
          <p class="eyebrow">Session ended</p>
          <h2>Leave the questions face down for now.</h2>
          <p>
            The clock is stopped and every private prompt is concealed.
            Start fresh whenever this room is ready.
          </p>
          <div class="ended-session-card-actions">
            <button class="primary-button" type="button" id="stage-start-session">
              Start a new session
            </button>
            <button class="quiet-button" type="button" id="stage-open-history">
              Review action-only History
            </button>
          </div>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>("#stage-start-session")
        ?.addEventListener("click", () => {
          void this.startNewConversationSession();
        });
      stage.querySelector<HTMLButtonElement>("#stage-open-history")
        ?.addEventListener("click", () => {
          this.sessionLifecycleOpen = false;
          this.historyOpen = true;
          this.historyDetailEntryID = null;
          this.filtersOpen = false;
          this.statsOpen = false;
          this.renderConversationMode();
          this.focusAfterRender("#close-history");
        });
      return;
    }
    if (
      this.continuityLoadState !== "ready"
      && presentConversationDeck(
        filterConversationCards(
          this.authoritativePlayableConversationCards(),
          this.conversationFilters(),
        ),
        this.conversationDeckPreferences(),
      ).length > 0
    ) {
      const loading = (
        this.continuityLoadState === "loading"
        || this.continuityOperationInFlight
      );
      stage.className = "conversation-stage continuity-recovery-stage";
      stage.innerHTML = `
        <div class="continuity-recovery">
          <span class="continuity-recovery-mark" aria-hidden="true">↻</span>
          <p class="eyebrow">Private deck continuity</p>
          <h2>${
            loading
              ? "Preparing this private deck…"
              : "Start this deck deliberately."
          }</h2>
          <p>
            ${
              this.continuityLoadState === "invalid"
                ? "Unrecognized private deck state was cleared without showing a card."
                : this.continuityLoadState === "failed"
                ? "No card is shown because this browser could not safely keep the deck state."
                : "A new deck creates one continuity code for each eligible card before anything is shown."
            }
          </p>
          <p class="continuity-explainer">
            A continuity code only helps the room recognize the same selected
            card before and after reveal. It is not proof of shuffle fairness.
          </p>
          <button
            class="primary-button"
            type="button"
            id="start-continuity-deck"
            ${loading ? "disabled" : ""}
          >
            ${loading ? "Preparing…" : "Start a new deck"}
          </button>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>("#start-continuity-deck")
        ?.addEventListener("click", () => {
          void this.startContinuityDeck();
        });
      return;
    }
    if (!cards.length) {
      const activeAuthoritativeSession =
        this.authoritativeConversationState?.sessions.find((session) => (
          session.id === this.conversationSessionID
          && session.endedAt === null
        )) ?? null;
      const acceptedSessionNeedsEnd = (
        this.authoritativeConversationRepository !== null
        && this.conversationSessionActive
        && activeAuthoritativeSession?.cards.length === 0
      );
      const hasFilters = (
        this.selectedThemes.size > 0
        || this.dateFilter !== "all"
        || this.conversationDeckIncludedSources.size > 0
        || this.conversationDeckExcludedSources.size > 0
      );
      const matchingCards = filterConversationCards(
        conversationDeck(this.bookmarks, this.conversationAudienceID),
        this.conversationFilters(),
      );
      const completedSession = (
        matchingCards.length > 0
        && matchingCards.every(({ state }) => state === "discussed")
      );
      stage.innerHTML = `
        <div class="empty-deck">
          <span aria-hidden="true">✦</span>
          <h2>${
            acceptedSessionNeedsEnd
              ? "This synced session stayed face down."
              : completedSession
              ? "Every card has had its turn."
              : "No cards are ready in this deck."
          }</h2>
          <p>${
            acceptedSessionNeedsEnd
              ? "No accepted cards are still available. End this session before starting another private deck."
              : completedSession
              ? "That sounds like a very good conversation."
              : hasFilters
              ? "This session’s filters are hiding the rest of the deck."
              : "Assign a bookmark to this audience’s Private cards."
          }</p>
          ${
            acceptedSessionNeedsEnd
              ? '<button class="primary-button" type="button" id="empty-session-recovery-button">Review session controls</button>'
              : completedSession
              ? '<button class="primary-button" type="button" id="empty-history-button">Review this session</button>'
              : hasFilters
              ? '<button class="primary-button" type="button" id="empty-clear-button">Clear session filters</button>'
              : '<button class="primary-button" type="button" id="empty-edit-button">Open Library</button>'
          }
        </div>
      `;
      stage.querySelector<HTMLButtonElement>("#empty-session-recovery-button")
        ?.addEventListener("click", () => {
          this.sessionLifecycleOpen = true;
          this.historyOpen = false;
          this.historyDetailEntryID = null;
          this.filtersOpen = false;
          this.statsOpen = false;
          this.renderConversationMode();
          this.focusAfterRender("#end-session");
        });
      stage.querySelector<HTMLButtonElement>("#empty-history-button")
        ?.addEventListener("click", () => {
          this.historyOpen = true;
          this.historyDetailEntryID = null;
          this.filtersOpen = false;
          this.statsOpen = false;
          this.renderConversationMode();
          this.focusAfterRender("#close-history");
        });
      stage.querySelector<HTMLButtonElement>("#empty-clear-button")
        ?.addEventListener("click", () => this.clearConversationFilters());
      stage.querySelector<HTMLButtonElement>("#empty-edit-button")
        ?.addEventListener("click", () => {
          this.mode = "library";
          this.renderApp();
        });
      return;
    }

    switch (this.effectiveConversationLayout()) {
      case "hand":
        this.renderHandLayout(stage, cards);
        break;
      case "table":
        this.renderTableLayout(stage, cards);
        break;
      case "all":
        this.renderViewAllLayout(stage, cards);
        break;
      default:
        this.renderStackLayout(stage, cards);
    }
  }

  private renderStackLayout(stage: HTMLElement, cards: ConversationCard[]): void {
    this.cancelStackMotion();
    this.spreadMotionSignature = null;
    this.stackIndex = clampIndex(this.stackIndex, cards.length);
    const card = cards[this.stackIndex];
    const key = cardKey(card);
    const revealed = this.revealedCardKeys.has(key);
    stage.className = "conversation-stage stack-stage";
    stage.innerHTML = `
      <div class="ritual-stack-wrap">
        <div class="ritual-stack" aria-hidden="true">
          <span></span><span></span>
        </div>
        <button class="ritual-card${revealed ? " is-revealed" : ""}" type="button" id="stack-card"></button>
        <div class="stack-actions" id="stack-actions"></div>
        <div class="stack-pager">
          <button type="button" id="previous-card" aria-label="Previous card">←</button>
          <span>${this.stackIndex + 1} of ${cards.length}</span>
          <button type="button" id="next-card" aria-label="Next card">→</button>
        </div>
      </div>
    `;
    const button = stage.querySelector<HTMLButtonElement>("#stack-card");
    if (button) {
      if (revealed) {
        button.setAttribute("aria-keyshortcuts", "PageUp PageDown");
        renderRevealedCardFace(
          button,
          card,
          this.privateMediaObjectURLs,
          true,
          this.continuityCode(card),
        );
        button.setAttribute(
          "aria-label",
          revealedCardAriaLabel(
            card,
            `${humanizeConversationTheme(card.theme)} card.`,
            "Turn this card face down.",
            this.privateMediaObjectURLs,
            this.continuityCode(card),
          ),
        );
      } else {
        renderCardBack(
          button,
          this.stackIndex,
          card.audienceLabel,
          card.theme,
          this.showThemeSymbols,
          this.cardBackPhotoURL(this.stackIndex),
          this.continuityCode(card),
        );
        button.setAttribute(
          "aria-label",
          cardBackAriaLabel(
            `Reveal card ${this.stackIndex + 1} of ${cards.length} for ${card.audienceLabel}`,
            card.theme,
            this.showThemeSymbols,
            this.cardBackPhotoURL(this.stackIndex) !== null,
            this.continuityCode(card),
          ),
        );
      }
      let pointerState: {
        pointerID: number;
        startX: number;
        startY: number;
        lastX: number;
        lastTime: number;
        displacement: number;
        renderedDisplacement: number;
        velocity: number;
        horizontal: boolean;
      } | null = null;
      let suppressNextClick = false;
      button.addEventListener("pointerdown", (event) => {
        if (
          revealed
          || !event.isPrimary
          || (event.pointerType === "mouse" && event.button !== 0)
        ) return;
        this.cancelStackMotion();
        pointerState = {
          pointerID: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastTime: event.timeStamp,
          displacement: 0,
          renderedDisplacement: 0,
          velocity: 0,
          horizontal: false,
        };
        button.dataset.motionState = "tracking";
      });
      button.addEventListener("pointermove", (event) => {
        if (
          !pointerState
          || pointerState.pointerID !== event.pointerId
          || revealed
        ) return;
        const displacement = event.clientX - pointerState.startX;
        const vertical = event.clientY - pointerState.startY;
        if (!pointerState.horizontal) {
          if (Math.abs(displacement) < 7) return;
          if (Math.abs(displacement) <= Math.abs(vertical) * 1.15) return;
          pointerState.horizontal = true;
          button.setPointerCapture(event.pointerId);
          button.classList.add("is-dragging");
        }
        event.preventDefault();
        const elapsed = Math.max(1, event.timeStamp - pointerState.lastTime);
        const measuredVelocity = (
          (event.clientX - pointerState.lastX) / elapsed
        ) * 1_000;
        pointerState.velocity = (
          pointerState.velocity * 0.35 + measuredVelocity * 0.65
        );
        pointerState.displacement = displacement;
        pointerState.renderedDisplacement = resistedDrag(
          displacement,
          Math.max(button.clientWidth, 1),
        );
        pointerState.lastX = event.clientX;
        pointerState.lastTime = event.timeStamp;
        this.setStackCardMotion(
          button,
          pointerState.renderedDisplacement,
        );
        button.dataset.motionState = "dragging";
      });
      button.addEventListener("pointercancel", (event) => {
        if (
          !pointerState
          || pointerState.pointerID !== event.pointerId
        ) return;
        const state = pointerState;
        pointerState = null;
        suppressNextClick = state.horizontal;
        const releaseVelocity = decayReleaseVelocity(
          state.velocity,
          Math.max(0, event.timeStamp - state.lastTime) / 1_000,
        );
        this.animateStackCard(
          button,
          {
            position: state.renderedDisplacement,
            velocity: releaseVelocity,
          },
          0,
        );
      });
      button.addEventListener("pointerup", (event) => {
        if (
          !pointerState
          || pointerState.pointerID !== event.pointerId
          || revealed
        ) return;
        const state = pointerState;
        pointerState = null;
        const displacement = event.clientX - state.startX;
        const verticalDisplacement = event.clientY - state.startY;
        const releaseVelocity = decayReleaseVelocity(
          state.velocity,
          Math.max(0, event.timeStamp - state.lastTime) / 1_000,
        );
        suppressNextClick = (
          state.horizontal
          || Math.hypot(displacement, verticalDisplacement) > 8
        );
        if (!state.horizontal) {
          button.dataset.motionState = "idle";
          return;
        }
        event.preventDefault();
        const direction = releaseDirection({
          displacement,
          velocity: releaseVelocity,
          extent: Math.max(button.clientWidth, 1),
        });
        if (direction === 0) {
          this.animateStackCard(
            button,
            {
              position: state.renderedDisplacement,
              velocity: releaseVelocity,
            },
            0,
          );
          return;
        }
        const exitDistance = (
          Math.max(window.innerWidth, button.clientWidth) + button.clientWidth
        ) * direction;
        this.animateStackCard(
          button,
          {
            position: state.renderedDisplacement,
            velocity: releaseVelocity,
          },
          exitDistance,
          () => this.moveStack(
            direction < 0 ? 1 : -1,
            cards,
            direction < 0 ? 1 : -1,
          ),
        );
      });
      button.addEventListener("click", () => {
        if (
          !button.isConnected
          || button.classList.contains("is-card-turning")
        ) return;
        if (suppressNextClick) {
          suppressNextClick = false;
          return;
        }
        if (!this.toggleRevealed(key, card, button)) return;
        this.renderConversationStage(cards, true);
        this.focusStackCard();
      });
      const entranceDirection = this.stackEntranceDirection;
      this.stackEntranceDirection = 0;
      if (entranceDirection !== 0 && !this.reducedMotionPreferred()) {
        const entranceDistance = Math.min(
          Math.max(button.clientWidth * 0.32, 84),
          142,
        ) * entranceDirection;
        this.setStackCardMotion(button, entranceDistance);
        button.classList.add("is-entering");
        button.dataset.motionState = "entering";
        window.requestAnimationFrame(() => {
          if (!button.isConnected) return;
          this.animateStackCard(
            button,
            {
              position: entranceDistance,
              velocity: -entranceDirection * 240,
            },
            0,
          );
        });
      }
    }
    const actions = stage.querySelector<HTMLElement>("#stack-actions");
    if (actions && revealed) this.renderCardActions(actions, card);
    if (
      button
      && this.pendingCardTurn
      && !this.startPendingCardTurn(key, button)
    ) {
      this.cancelCardTurnTransition();
    }
    stage.querySelector<HTMLButtonElement>("#previous-card")
      ?.addEventListener("click", () => this.moveStack(-1, cards));
    stage.querySelector<HTMLButtonElement>("#next-card")
      ?.addEventListener("click", () => this.moveStack(1, cards));
  }

  private renderHandLayout(stage: HTMLElement, cards: ConversationCard[]): void {
    this.renderSpreadLayout(stage, cards, "hand");
  }

  private renderTableLayout(stage: HTMLElement, cards: ConversationCard[]): void {
    this.renderSpreadLayout(stage, cards, "table");
  }

  private renderSpreadLayout(
    stage: HTMLElement,
    cards: ConversationCard[],
    spread: "hand" | "table",
  ): void {
    const spreadCardLimit = spread === "hand" ? 3 : 12;
    const spreadCards = cards.slice(0, spreadCardLimit);
    const spreadMotionSignature = `${this.conversationAudienceID}:${spread}`;
    const shouldChoreograph = (
      this.spreadMotionSignature !== spreadMotionSignature
    );
    this.spreadMotionSignature = spreadMotionSignature;
    const hasRevealedCard = spreadCards.some((card) => (
      this.revealedCardKeys.has(cardKey(card))
    ));
    const spreadCardCount = spreadCards.length;
    if (
      hasRevealedCard
      || (
        this.tabletopChoiceIndex !== null
        && this.tabletopChoiceIndex >= spreadCardCount
      )
    ) {
      this.tabletopChoiceIndex = null;
    }
    const chosenCardNumber = this.tabletopChoiceIndex === null
      ? null
      : this.tabletopChoiceIndex + 1;
    const hand = spread === "hand";
    const revealedSpreadIndex = spreadCards.findIndex((card) => (
      this.revealedCardKeys.has(cardKey(card))
    ));
    const tabletopSpotlightOrder = new Map<number, number>();
    if (
      this.tabletopMode
      && revealedSpreadIndex >= 0
    ) {
      const concealedIndices = spreadCards.flatMap((_, index) => (
        index === revealedSpreadIndex ? [] : [index]
      ));
      const leadingConcealedCards = hand ? 1 : 2;
      [
        ...concealedIndices.slice(0, leadingConcealedCards),
        revealedSpreadIndex,
        ...concealedIndices.slice(leadingConcealedCards),
      ].forEach((cardIndex, visualOrder) => {
        tabletopSpotlightOrder.set(cardIndex, visualOrder);
      });
    }
    stage.className = `conversation-stage table-stage${
      hand ? " hand-stage" : ""
    }`;
    stage.innerHTML = `
      <div class="table-heading">
        <div>
          <p class="eyebrow">${hand ? "A small hand" : "The table"}</p>
          <h2>${
            hand
              ? hasRevealedCard
                ? tableCardCountHeading(spreadCardCount, true)
                : spreadCardCount === 1
                  ? "One card. Maybe the one."
                  : `${spreadCardCount} cards. Enough choice.`
              : tableCardCountHeading(spreadCardCount, hasRevealedCard)
          }</h2>
        </div>
        <div class="table-heading-controls">
          <div class="table-heading-copy">
            <p>${
              hasRevealedCard
                ? "One at a time. Another card turns this question down."
                : hand
                ? "Pick one without covering the table. Deal again whenever none feel right."
                : "Turn over any card. Leave the rest face down until the room is ready."
            }</p>
            ${
              chosenCardNumber === null
                ? ""
                : `<p
                    class="tabletop-choice-status"
                    id="tabletop-choice-status"
                    role="status"
                    aria-live="polite"
                  >
                    Card ${chosenCardNumber} is ready. Press Enter to reveal.
                  </p>`
            }
          </div>
          <div class="table-heading-actions">
            <button
              class="quiet-button choose-table-card"
              type="button"
              id="choose-table-card"
              aria-keyshortcuts="R"
              ${hasRevealedCard ? "disabled" : ""}
            >
              ${chosenCardNumber === null ? "Choose for us" : "Choose again"}
            </button>
            ${
              hand
                ? `
                  <button
                    class="quiet-button deal-hand-button"
                    type="button"
                    id="deal-another-hand"
                    aria-keyshortcuts="D"
                    ${
                      hasRevealedCard || cards.length <= spreadCardCount
                        ? "disabled"
                        : ""
                    }
                  >
                    Deal another ${spreadCardCount}
                  </button>
                  <button
                    class="quiet-button tabletop-button"
                    type="button"
                    id="toggle-room-view"
                    aria-pressed="${this.tabletopMode}"
                    ${this.tabletopMode ? 'aria-keyshortcuts="Escape"' : ""}
                  >
                    ${this.tabletopMode ? "Leave room view" : "Enter room view"}
                  </button>
                `
                : `
                  <button
                    class="quiet-button tabletop-button"
                    type="button"
                    id="toggle-tabletop"
                    aria-pressed="${this.tabletopMode}"
                    ${this.tabletopMode ? 'aria-keyshortcuts="Escape"' : ""}
                  >
                    ${this.tabletopMode ? "Leave tabletop" : "Enter tabletop"}
                  </button>
                `
            }
          </div>
        </div>
      </div>
      <div
        class="card-table${hand ? " hand-card-table" : ""}"
        id="card-table"
        data-card-count="${spreadCardCount}"
        role="group"
        aria-label="${
          hand
            ? `${spreadCardCount}-card hand, ${
                hasRevealedCard ? "one card revealed" : "all cards face down"
              }`
            : `${spreadCardCount} cards on the table, ${
                hasRevealedCard ? "one card revealed" : "all cards face down"
              }`
        }"
      ></div>
    `;
    stage.querySelector<HTMLButtonElement>("#choose-table-card")
      ?.addEventListener(
        "click",
        () => this.chooseSpreadCard(cards, spreadCardCount),
      );
    if (hand) {
      stage.querySelector<HTMLButtonElement>("#deal-another-hand")
        ?.addEventListener("click", () => this.dealAnotherHand(cards));
    }
    const roomViewToggle = stage.querySelector<HTMLButtonElement>(
      hand ? "#toggle-room-view" : "#toggle-tabletop",
    );
    roomViewToggle?.addEventListener("click", () => {
      this.tabletopMode = !this.tabletopMode;
      this.tabletopChoiceIndex = null;
      this.renderConversationMode();
      if (this.tabletopMode) window.scrollTo({ top: 0 });
      this.focusAfterRender(
        hand ? "#toggle-room-view" : "#toggle-tabletop",
      );
    });
    const table = stage.querySelector<HTMLElement>("#card-table");
    if (!table) return;
    const lastRowCardCount = hand
      ? spreadCardCount
      : spreadCardCount > 6
        ? spreadCardCount - 6
        : spreadCardCount;
    const firstPartialRowIndex = spreadCardCount > 6 ? 6 : 0;
    if (!hand && lastRowCardCount > 0 && lastRowCardCount < 6) {
      table.style.setProperty(
        "--table-partial-row-start",
        String(7 - lastRowCardCount),
      );
    }
    spreadCards.forEach((card, index) => {
      const key = cardKey(card);
      const revealed = this.revealedCardKeys.has(key);
      const chosen = !revealed && index === this.tabletopChoiceIndex;
      const article = document.createElement("article");
      article.className = `table-card${revealed ? " is-revealed" : ""}${
        chosen ? " is-chosen" : ""
      }${
        !hand
        && lastRowCardCount < 6
        && index === firstPartialRowIndex
          ? " starts-partial-row"
          : ""
      }${shouldChoreograph ? " is-dealing" : ""}`;
      article.style.setProperty("--deal-index", String(index));
      article.style.setProperty(
        "--deal-delay",
        `${Math.min(index * 42, 330)}ms`,
      );
      article.style.setProperty(
        "--deal-x",
        `${Math.round(((spreadCardCount - 1) / 2 - index) * 12)}px`,
      );
      const spotlightOrder = tabletopSpotlightOrder.get(index);
      if (spotlightOrder !== undefined) {
        article.style.setProperty(
          "--tabletop-spotlight-order",
          String(spotlightOrder),
        );
      }
      const flip = document.createElement("button");
      flip.type = "button";
      flip.className = "table-card-face";
      flip.dataset.cardIndex = String(index);
      flip.setAttribute(
        "aria-keyshortcuts",
        "ArrowLeft ArrowRight ArrowUp ArrowDown Home End"
        + (revealed ? " PageUp PageDown" : ""),
      );
      if (revealed) {
        renderRevealedCardFace(
          flip,
          card,
          this.privateMediaObjectURLs,
          false,
          this.continuityCode(card),
        );
        flip.setAttribute(
          "aria-label",
          revealedCardAriaLabel(
            card,
            `${humanizeConversationTheme(card.theme)} card ${index + 1}.`,
            "Turn face down.",
            this.privateMediaObjectURLs,
            this.continuityCode(card),
          ),
        );
      } else {
        renderCardBack(
          flip,
          index,
          card.audienceLabel,
          card.theme,
          this.showThemeSymbols,
          this.cardBackPhotoURL(index),
          this.continuityCode(card),
        );
        const concealedLabel = cardBackAriaLabel(
          `Reveal ${hand ? "hand" : "table"} card ${index + 1} for ${
            card.audienceLabel
          }`,
          card.theme,
          this.showThemeSymbols,
          this.cardBackPhotoURL(index) !== null,
          this.continuityCode(card),
        );
        flip.setAttribute(
          "aria-label",
          chosen
            ? `Chosen for the room. ${concealedLabel} Press Enter to reveal.`
            : concealedLabel,
        );
        if (chosen) {
          flip.setAttribute("aria-describedby", "tabletop-choice-status");
          const cue = document.createElement("span");
          cue.className = "tabletop-choice-cue";
          cue.textContent = "Chosen";
          cue.setAttribute("aria-hidden", "true");
          flip.append(cue);
        }
      }
      flip.addEventListener("click", () => {
        if (
          !flip.isConnected
          || flip.classList.contains("is-card-turning")
        ) return;
        this.tabletopChoiceIndex = null;
        if (!this.toggleRevealed(key, card, flip)) return;
        this.renderConversationStage(cards, true);
        this.focusTableCard(index);
      });
      if (!revealed) this.bindTableCardMotion(article, flip);
      article.append(flip);
      if (revealed) {
        const actions = document.createElement("div");
        actions.className = "table-card-actions";
        this.renderCardActions(actions, card, true);
        article.append(actions);
      }
      table.append(article);
    });
    if (this.pendingCardTurn) {
      const pendingIndex = spreadCards.findIndex((card) => (
        cardKey(card) === this.pendingCardTurn?.key
      ));
      const pendingTarget = pendingIndex < 0
        ? null
        : table.querySelector<HTMLElement>(
          `[data-card-index="${pendingIndex}"]`,
        );
      if (
        !pendingTarget
        || !this.startPendingCardTurn(
          this.pendingCardTurn.key,
          pendingTarget,
        )
      ) {
        this.cancelCardTurnTransition();
      }
    }
    this.startSpreadChoreography(table, shouldChoreograph);
  }

  private renderViewAllLayout(stage: HTMLElement, cards: ConversationCard[]): void {
    stage.className = "conversation-stage all-stage";
    if (this.authoritativeConversationRepository) {
      stage.innerHTML = `
        <div class="privacy-curtain">
          <span class="curtain-symbol" aria-hidden="true">◉</span>
          <p class="eyebrow">Synced room privacy</p>
          <h2>Every card stays face down.</h2>
          <p>
            Synced sessions reveal one card at a time, so a second browser
            cannot expose the whole private deck at once.
          </p>
          <button class="primary-button" type="button" id="choose-table">
            Return to the face-down table
          </button>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>("#choose-table")
        ?.addEventListener("click", () => {
          this.conversationLayout = "table";
          this.renderConversationMode();
          this.focusConversationStage("table");
        });
      return;
    }
    if (!this.viewAllConfirmed.has(this.conversationAudienceID)) {
      stage.innerHTML = `
        <div class="privacy-curtain">
          <span class="curtain-symbol" aria-hidden="true">◉</span>
          <p class="eyebrow">Room privacy check</p>
          <h2>Turn every card face up?</h2>
          <p>
            View all puts every private prompt on screen at once. Check who can
            see the display before opening the board.
          </p>
          <button class="primary-button" type="button" id="confirm-view-all">
            I’m ready · show all prompts
          </button>
          <button class="quiet-button" type="button" id="choose-table">
            Keep them face down
          </button>
        </div>
      `;
      stage.querySelector<HTMLButtonElement>("#confirm-view-all")
        ?.addEventListener("click", () => {
          this.viewAllConfirmed.add(this.conversationAudienceID);
          this.renderConversationStage(cards);
          this.focusConversationStage("all");
        });
      stage.querySelector<HTMLButtonElement>("#choose-table")
        ?.addEventListener("click", () => {
          this.conversationLayout = "table";
          this.renderConversationMode();
          this.focusConversationStage("table");
        });
      return;
    }

    stage.innerHTML = `
      <div class="all-heading">
        <div>
          <p class="eyebrow">Open board</p>
          <h2>Follow the question that glows.</h2>
        </div>
        <button class="quiet-button" type="button" id="hide-all">Hide every title</button>
      </div>
      <div class="open-board" id="open-board"></div>
    `;
    const board = stage.querySelector<HTMLElement>("#open-board");
    if (!board) return;
    cards.forEach((card, index) => {
      const article = document.createElement("article");
      article.className = `open-card open-card-${(index % 4) + 1}`;
      const theme = document.createElement("span");
      theme.className = "open-card-theme";
      theme.textContent = humanizeConversationTheme(card.theme);
      const image = privateCardImage(card, this.privateMediaObjectURLs);
      const title = document.createElement("h3");
      title.textContent = card.title;
      const source = document.createElement("p");
      source.textContent = card.supportingText || card.sourceLabel;
      const continuityCode = this.continuityCode(card);
      const continuity = continuityCue(continuityCode);
      const accessibleContinuity = document.createElement("span");
      accessibleContinuity.className = "sr-only";
      accessibleContinuity.textContent = continuityAccessibleCopy(continuityCode);
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "text-button";
      edit.textContent = "Edit bookmark";
      edit.addEventListener("click", () => this.openBookmarkEditor(card.bookmarkID));
      const actions = document.createElement("div");
      actions.className = "open-card-actions";
      actions.append(edit);
      const original = sourceLink(card);
      if (original) actions.append(original);
      article.append(theme, continuity, accessibleContinuity);
      if (image) {
        const media = document.createElement("img");
        media.className = "open-card-media";
        media.src = image.url;
        media.alt = image.alt;
        media.loading = "lazy";
        media.decoding = "async";
        article.append(media);
      }
      article.append(title, source, actions);
      board.append(article);
    });
    stage.querySelector<HTMLButtonElement>("#hide-all")
      ?.addEventListener("click", () => {
        this.viewAllConfirmed.delete(this.conversationAudienceID);
        this.revealedCardKeys.clear();
        this.renderConversationStage(cards);
        this.focusConversationStage("all");
      });
  }

  private renderCardActions(
    container: HTMLElement,
    card: ConversationCard,
    compact = false,
  ): void {
    const edit = actionButton("Edit", "edit", compact);
    const postpone = actionButton("Postpone", "postpone", compact);
    const discussed = actionButton("Discussed", "discussed", compact);
    edit.addEventListener("click", () => this.openBookmarkEditor(card.bookmarkID));
    postpone.addEventListener("click", () => (
      this.setConversationOutcome(card, "postponed")
    ));
    discussed.addEventListener("click", () => (
      this.setConversationOutcome(card, "discussed")
    ));
    if (
      !this.conversationOutcomeRepository
      && !this.authoritativeConversationRepository
    ) {
      for (const button of [postpone, discussed]) {
        button.disabled = true;
        button.title =
          "Conversation outcomes stay read-only until audience-scoped event sync is available.";
      }
    }
    container.append(edit, postpone, discussed);
    if (
      !this.conversationOutcomeRepository
      && !this.authoritativeConversationRepository
    ) {
      const gate = document.createElement("span");
      gate.className = "conversation-outcome-gate";
      gate.textContent = "Outcomes are read-only in this browser for now.";
      container.append(gate);
    }
    const original = sourceLink(card);
    if (original) container.append(original);
  }

  private setConversationOutcome(
    card: ConversationCard,
    outcome: Exclude<ConversationState, "available">,
  ): void {
    if (this.authoritativeConversationRepository) {
      void this.setAuthoritativeConversationOutcome(card, outcome);
      return;
    }
    if (
      !this.conversationSessionActive
      || this.conversationOutcomeUndoInFlight
    ) return;
    this.clearConversationOutcomeUndo();
    this.tabletopChoiceIndex = null;
    const currentOrder = this.orderedConversationDeck()
      .map(({ bookmarkID }) => bookmarkID);
    const bookmarkBefore = this.bookmarks.find(
      ({ id }) => id === card.bookmarkID,
    );
    if (!bookmarkBefore) return;
    const currentIndex = currentOrder.indexOf(card.bookmarkID);
    const orderAfterCurrent = currentIndex < 0
      ? currentOrder.filter((id) => id !== card.bookmarkID)
      : [
          ...currentOrder.slice(currentIndex + 1),
          ...currentOrder.slice(0, currentIndex),
        ];
    if (outcome === "postponed") {
      orderAfterCurrent.push(card.bookmarkID);
    }
    const undoID = crypto.randomUUID();
    const cardBackPhotoOffsetBefore = this.cardBackPhotoOffset;
    this.deckOrders.set(card.audienceId, orderAfterCurrent);
    const outcomeEvent = this.recordConversationEvent(card, outcome);
    if (!outcomeEvent) return;
    this.bookmarks = this.bookmarks.map((bookmark) => (
      bookmark.id === card.bookmarkID
        ? updateConversationState(bookmark, card.audienceId, outcome)
        : bookmark
    ));
    const updatedBookmark = this.bookmarks.find(
      ({ id }) => id === card.bookmarkID,
    );
    let outcomePersistence: Promise<void>;
    if (updatedBookmark && this.conversationOutcomeRepository) {
      outcomePersistence = this.conversationOutcomeRepository
        .saveConversationOutcome(updatedBookmark)
        .then(async (workspace) => {
          this.enrichmentJobs = workspace.enrichmentJobs;
          await this.persistConversationSession();
        })
        .catch((persistenceError: unknown) => {
          this.showToast(
            persistenceError instanceof Error
              ? persistenceError.message
              : "The synthetic Conversation outcome could not be saved.",
            true,
          );
        });
    } else {
      outcomePersistence = this.persistConversationSession().then(() => {});
    }
    const undo: ConversationOutcomeUndo = {
      id: undoID,
      audienceId: card.audienceId,
      bookmarkBefore: structuredClone(bookmarkBefore),
      deckOrderBefore: [...currentOrder],
      outcome,
      outcomeEventID: outcomeEvent.id,
      cardBackPhotoOffsetBefore,
      outcomePersistence,
    };
    this.conversationOutcomeUndo = undo;
    this.revealedCardKeys.delete(cardKey(card));
    this.stackIndex = 0;
    this.advanceCardBackPhotos();
    this.renderConversationMode();
    this.focusConversationStage();
    if (outcome === "discussed") this.celebrateDiscussed();
    this.showToast(
      outcome === "discussed"
        ? "Discussed. A good question did its job."
        : "Postponed. This card moved to the back.",
      false,
      {
        label: `Undo ${humanizeHistoryAction(outcome)}`,
        keyboardShortcut: "U",
        onActivate: () => {
          void this.undoConversationOutcome(undo);
        },
        onDismiss: () => {
          if (this.conversationOutcomeUndo?.id === undo.id) {
            this.conversationOutcomeUndo = null;
          }
        },
      },
    );
  }

  private async setAuthoritativeConversationOutcome(
    card: ConversationCard,
    outcome: Exclude<ConversationState, "available">,
  ): Promise<void> {
    const repository = this.authoritativeConversationRepository;
    const state = this.authoritativeConversationState;
    const assignmentID = this.assignmentIDForConversationCard(card);
    if (
      !repository
      || !state
      || !assignmentID
      || !this.conversationSessionActive
      || this.authoritativeConversationRefreshRequired
      || this.conversationOutcomeUndoInFlight
    ) return;
    const bookmarkBefore = this.bookmarks.find(
      ({ id }) => id === card.bookmarkID,
    );
    if (!bookmarkBefore) return;
    this.clearConversationOutcomeUndo();
    this.conversationOutcomeUndoInFlight = true;
    const deckOrderBefore = [
      ...(this.deckOrders.get(card.audienceId) ?? []),
    ];
    const cardBackPhotoOffsetBefore = this.cardBackPhotoOffset;
    try {
      const mutation = await repository.command({
        sessionID: this.conversationSessionID,
        action: outcome,
        assignmentID,
        targetEventID: null,
        endReason: null,
        etag: state.etag,
        idempotencyKey: `web-${outcome}-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      const eventID = mutation.event?.id;
      if (!eventID) {
        throw new Error(
          "Convoful did not return the accepted Conversation event.",
        );
      }
      const undo: ConversationOutcomeUndo = {
        id: crypto.randomUUID(),
        audienceId: card.audienceId,
        bookmarkBefore: structuredClone(bookmarkBefore),
        deckOrderBefore,
        outcome,
        outcomeEventID: eventID,
        authoritativeAssignmentID: assignmentID,
        cardBackPhotoOffsetBefore,
        outcomePersistence: Promise.resolve(),
      };
      this.conversationOutcomeUndo = undo;
      this.stackIndex = 0;
      this.advanceCardBackPhotos();
      this.renderConversationMode();
      this.focusConversationStage();
      if (outcome === "discussed") this.celebrateDiscussed();
      this.showToast(
        outcome === "discussed"
          ? "Discussed. A good question did its job."
          : "Postponed. This card moved to the back.",
        false,
        {
          label: `Undo ${humanizeHistoryAction(outcome)}`,
          keyboardShortcut: "U",
          onActivate: () => {
            void this.undoConversationOutcome(undo);
          },
          onDismiss: () => {
            if (this.conversationOutcomeUndo?.id === undo.id) {
              this.conversationOutcomeUndo = null;
            }
          },
        },
      );
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.showToast(
        error instanceof Error
          ? error.message
          : "The Conversation outcome was not accepted.",
        true,
      );
      this.renderConversationMode();
      this.focusConversationStage();
    } finally {
      this.conversationOutcomeUndoInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private async undoConversationOutcome(
    undo: ConversationOutcomeUndo,
  ): Promise<void> {
    if (
      this.conversationOutcomeUndo?.id !== undo.id
      || this.conversationOutcomeUndoInFlight
    ) return;
    if (
      this.authoritativeConversationRepository
      && undo.authoritativeAssignmentID
    ) {
      await this.undoAuthoritativeConversationOutcome(undo);
      return;
    }
    this.conversationOutcomeUndoInFlight = true;
    this.conversationOutcomeUndo = null;
    this.dismissToast(false);
    try {
      await undo.outcomePersistence;
      if (this.conversationOutcomeRepository) {
        const workspace = await this.conversationOutcomeRepository
          .saveConversationOutcome(undo.bookmarkBefore);
        this.enrichmentJobs = workspace.enrichmentJobs;
      }
      this.bookmarks = this.bookmarks.map((bookmark) => (
        bookmark.id === undo.bookmarkBefore.id
          ? structuredClone(undo.bookmarkBefore)
          : bookmark
      ));
      this.deckOrders.set(undo.audienceId, [...undo.deckOrderBefore]);
      const outcomeEventIndex = this.conversationEvents.findIndex(
        ({ id }) => id === undo.outcomeEventID,
      );
      if (outcomeEventIndex >= 0) {
        this.conversationEvents.splice(outcomeEventIndex, 1);
      }
      this.cardBackPhotoOffset = undo.cardBackPhotoOffsetBefore;
      this.revealedCardKeys.clear();
      this.tabletopChoiceIndex = null;
      this.stackIndex = 0;
      await this.persistConversationSession();
      this.renderConversationMode();
      this.focusConversationStage();
      this.showToast(
        `${humanizeHistoryAction(undo.outcome)} undone. `
        + "The card is face down again.",
      );
    } catch (undoError) {
      this.showToast(
        undoError instanceof Error
          ? undoError.message
          : "The synthetic Conversation outcome could not be undone.",
        true,
      );
    } finally {
      this.conversationOutcomeUndoInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private async undoAuthoritativeConversationOutcome(
    undo: ConversationOutcomeUndo,
  ): Promise<void> {
    const repository = this.authoritativeConversationRepository;
    const state = this.authoritativeConversationState;
    const assignmentID = undo.authoritativeAssignmentID;
    if (
      !repository
      || !state
      || !assignmentID
      || this.authoritativeConversationRefreshRequired
    ) return;
    this.conversationOutcomeUndoInFlight = true;
    this.conversationOutcomeUndo = null;
    this.dismissToast(false);
    try {
      const mutation = await repository.command({
        sessionID: this.conversationSessionID,
        action: "correct",
        assignmentID,
        targetEventID: undo.outcomeEventID,
        endReason: null,
        etag: state.etag,
        idempotencyKey: `web-correct-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      this.cardBackPhotoOffset = undo.cardBackPhotoOffsetBefore;
      this.stackIndex = 0;
      this.renderConversationMode();
      this.focusConversationStage();
      this.showToast(
        `${humanizeHistoryAction(undo.outcome)} undone. `
        + "The card is face down again.",
      );
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.showToast(
        error instanceof Error
          ? error.message
          : "The accepted Conversation outcome could not be corrected.",
        true,
      );
      this.renderConversationMode();
      this.focusConversationStage();
    } finally {
      this.conversationOutcomeUndoInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private orderedConversationDeck(): ConversationCard[] {
    if (
      this.continuityLoadState !== "ready"
      || !this.resolvedContinuity
    ) return [];
    const playable = this.authoritativePlayableConversationCards();
    const manifestCardIDs = new Set(
      this.resolvedContinuity.manifest.orderedCardIDs,
    );
    const knownOrder = this.deckOrders.get(this.conversationAudienceID) ?? [];
    const playableByID = new Map(playable.flatMap((card) => (
      manifestCardIDs.has(card.bookmarkID)
      && continuityCodeForCard(this.resolvedContinuity, card.bookmarkID)
        ? [[card.bookmarkID, card] as const]
        : []
    )));
    const sessionOrdered = knownOrder.flatMap((id) => {
      const card = playableByID.get(id);
      if (!card) return [];
      playableByID.delete(id);
      return [card];
    });
    for (const id of this.resolvedContinuity.manifest.orderedCardIDs) {
      const card = playableByID.get(id);
      if (!card) continue;
      sessionOrdered.push(card);
      playableByID.delete(id);
    }
    this.deckOrders.set(
      this.conversationAudienceID,
      sessionOrdered.map(({ bookmarkID }) => bookmarkID),
    );
    const dateAndThemeFiltered = filterConversationCards(
      sessionOrdered,
      this.conversationFilters(),
    );
    return presentConversationDeck(
      dateAndThemeFiltered,
      this.conversationDeckPreferences(),
    );
  }

  private continuityCode(card: ConversationCard): string {
    return continuityCodeForCard(
      this.resolvedContinuity,
      card.bookmarkID,
    ) ?? "";
  }

  private chooseSpreadCard(
    cards: ConversationCard[],
    visibleCardCount = 12,
  ): void {
    const spreadCardCount = Math.min(cards.length, visibleCardCount);
    if (
      !this.conversationSessionActive
      || spreadCardCount < 1
      || this.revealedCardKeys.size > 0
    ) return;
    this.clearConversationOutcomeUndo();
    const candidates = Array.from(
      { length: spreadCardCount },
      (_, index) => index,
    ).filter((index) => (
      spreadCardCount === 1 || index !== this.tabletopChoiceIndex
    ));
    const nextIndex = candidates[
      Math.floor(Math.random() * candidates.length)
    ] ?? 0;
    this.tabletopChoiceIndex = nextIndex;
    this.renderConversationStage(cards);
    this.focusTableCard(nextIndex);
    this.showToast(
      `Card ${nextIndex + 1} is ready. Turn it over when the room is ready.`,
    );
  }

  private dealAnotherHand(cards: ConversationCard[]): void {
    const handSize = Math.min(cards.length, 3);
    if (
      !this.conversationSessionActive
      || handSize < 1
      || cards.length <= handSize
      || this.revealedCardKeys.size > 0
    ) return;
    this.clearConversationOutcomeUndo();
    const currentOrder = cards.map(({ bookmarkID }) => bookmarkID);
    this.deckOrders.set(
      this.conversationAudienceID,
      [
        ...currentOrder.slice(handSize),
        ...currentOrder.slice(0, handSize),
      ],
    );
    this.stackIndex = 0;
    this.tabletopChoiceIndex = null;
    this.spreadMotionSignature = null;
    this.revealedCardKeys.clear();
    this.historyDetailEntryID = null;
    this.advanceCardBackPhotos();
    void this.persistConversationSession();
    this.renderConversationMode();
    this.focusConversationStage("hand");
    this.showToast(
      `Dealt ${handSize} fresh card backs. Nothing was revealed or dismissed.`,
    );
  }

  private async shuffleDeck(): Promise<void> {
    if (this.authoritativeConversationRepository) {
      await this.restartAuthoritativeConversation();
      return;
    }
    if (
      !this.conversationSessionActive
      || this.continuityOperationInFlight
    ) return;
    if (
      this.conversationOutcomeUndo
      || this.conversationOutcomeUndoInFlight
    ) {
      this.root.querySelector<HTMLButtonElement>(".toast-action")?.focus();
      return;
    }
    const previousContinuity = this.resolvedContinuity;
    const previousState = this.continuityLoadState;
    this.continuityOperationInFlight = true;
    this.renderConversationMode();
    try {
      const resolved = await this.createAndPersistContinuityManifest(
        this.conversationSessionID,
      );
      this.resolvedContinuity = resolved;
      this.continuityLoadState = "ready";
      this.deckOrders.set(
        this.conversationAudienceID,
        [...resolved.manifest.orderedCardIDs],
      );
      this.stackIndex = 0;
      this.tabletopChoiceIndex = null;
      this.revealedCardKeys.clear();
      this.viewAllConfirmed.delete(this.conversationAudienceID);
      this.historyDetailEntryID = null;
      this.advanceCardBackPhotos();
      await this.persistConversationSession();
      this.armConversationGenerationChoreography();
      this.showToast(
        "Deck shuffled. A fresh continuity generation is ready.",
      );
    } catch (error) {
      this.resolvedContinuity = previousContinuity;
      this.continuityLoadState = previousState;
      this.showToast(
        error instanceof Error
          ? error.message
          : "The private deck could not be shuffled.",
        true,
      );
    } finally {
      this.continuityOperationInFlight = false;
      this.renderConversationMode();
      this.focusConversationStage();
    }
  }

  private moveStack(
    delta: number,
    cards: ConversationCard[],
    entranceDirection: -1 | 1 = delta < 0 ? -1 : 1,
  ): void {
    this.cancelStackMotion();
    this.clearConversationOutcomeUndo();
    this.stackIndex = (
      this.stackIndex + delta + cards.length
    ) % cards.length;
    this.stackEntranceDirection = this.reducedMotionPreferred()
      ? 0
      : entranceDirection;
    this.revealedCardKeys.clear();
    void this.persistConversationSession();
    this.renderConversationStage(cards);
    this.focusStackCard();
  }

  private reducedMotionPreferred(): boolean {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private armConversationGenerationChoreography(): void {
    this.stackEntranceDirection = (
      this.effectiveConversationLayout() === "stack"
      && !this.reducedMotionPreferred()
    ) ? 1 : 0;
    this.spreadMotionSignature = null;
  }

  private setStackCardMotion(
    card: HTMLElement,
    position: number,
  ): void {
    const rotation = Math.max(-8, Math.min(8, position / 28));
    card.style.setProperty("--card-motion-x", `${position.toFixed(3)}px`);
    card.style.setProperty("--card-motion-rotate", `${rotation.toFixed(3)}deg`);
  }

  private cancelStackMotion(): void {
    this.stackMotionGeneration += 1;
    if (this.stackMotionFrameID !== null) {
      window.cancelAnimationFrame(this.stackMotionFrameID);
      this.stackMotionFrameID = null;
    }
  }

  private animateStackCard(
    card: HTMLElement,
    initial: MotionState,
    target: number,
    onComplete?: () => void,
  ): void {
    this.cancelStackMotion();
    if (this.reducedMotionPreferred()) {
      this.setStackCardMotion(card, target);
      card.classList.remove("is-dragging", "is-entering");
      card.dataset.motionState = "idle";
      onComplete?.();
      return;
    }
    const generation = this.stackMotionGeneration;
    let state = initial;
    let previousTime = performance.now();
    card.classList.remove("is-dragging");
    card.dataset.motionState = target === 0 ? "settling" : "leaving";
    const tick = (time: number): void => {
      if (
        generation !== this.stackMotionGeneration
        || !card.isConnected
      ) return;
      state = stepSpring(
        state,
        target,
        Math.max(0, time - previousTime) / 1_000,
      );
      previousTime = time;
      this.setStackCardMotion(card, state.position);
      if (springIsAtRest(state, target)) {
        this.stackMotionFrameID = null;
        card.classList.remove("is-entering");
        card.dataset.motionState = "idle";
        onComplete?.();
        return;
      }
      this.stackMotionFrameID = window.requestAnimationFrame(tick);
    };
    this.stackMotionFrameID = window.requestAnimationFrame(tick);
  }

  private bindTableCardMotion(
    article: HTMLElement,
    face: HTMLButtonElement,
  ): void {
    let currentTilt: Tilt = { x: 0, y: 0 };
    let returnFrameID: number | null = null;
    const stopReturn = (): void => {
      if (returnFrameID === null) return;
      window.cancelAnimationFrame(returnFrameID);
      returnFrameID = null;
    };
    const setTilt = (tilt: Tilt): void => {
      currentTilt = tilt;
      face.style.setProperty("--pointer-tilt-x", `${tilt.x.toFixed(3)}deg`);
      face.style.setProperty("--pointer-tilt-y", `${tilt.y.toFixed(3)}deg`);
    };
    face.addEventListener("pointermove", (event) => {
      if (
        this.reducedMotionPreferred()
        || (event.pointerType !== "mouse" && event.pointerType !== "pen")
      ) return;
      stopReturn();
      setTilt(pointerTilt(
        event.clientX,
        event.clientY,
        face.getBoundingClientRect(),
      ));
      article.classList.add("is-pointer-tilted");
      face.dataset.motionState = "tilted";
    });
    face.addEventListener("pointerleave", () => {
      if (this.reducedMotionPreferred()) {
        setTilt({ x: 0, y: 0 });
        article.classList.remove("is-pointer-tilted");
        face.dataset.motionState = "idle";
        return;
      }
      stopReturn();
      let xState: MotionState = { position: currentTilt.x, velocity: 0 };
      let yState: MotionState = { position: currentTilt.y, velocity: 0 };
      let previousTime = performance.now();
      face.dataset.motionState = "settling";
      const tick = (time: number): void => {
        if (!face.isConnected) return;
        const elapsed = Math.max(0, time - previousTime) / 1_000;
        previousTime = time;
        xState = stepSpring(xState, 0, elapsed);
        yState = stepSpring(yState, 0, elapsed);
        setTilt({ x: xState.position, y: yState.position });
        if (springIsAtRest(xState, 0) && springIsAtRest(yState, 0)) {
          returnFrameID = null;
          article.classList.remove("is-pointer-tilted");
          face.dataset.motionState = "idle";
          return;
        }
        returnFrameID = window.requestAnimationFrame(tick);
      };
      returnFrameID = window.requestAnimationFrame(tick);
    });
  }

  private startSpreadChoreography(
    table: HTMLElement,
    shouldChoreograph: boolean,
  ): void {
    this.spreadMotionGeneration += 1;
    const generation = this.spreadMotionGeneration;
    if (!shouldChoreograph || this.reducedMotionPreferred()) {
      table.dataset.motionState = "settled";
      table.querySelectorAll(".is-dealing").forEach((card) => {
        card.classList.remove("is-dealing");
      });
      return;
    }
    table.dataset.motionState = "dealing";
    window.requestAnimationFrame(() => {
      if (
        generation !== this.spreadMotionGeneration
        || !table.isConnected
      ) return;
      table.classList.add("is-motion-ready");
      window.setTimeout(() => {
        if (
          generation !== this.spreadMotionGeneration
          || !table.isConnected
        ) return;
        table.dataset.motionState = "settled";
        table.classList.remove("is-motion-ready");
        table.querySelectorAll(".is-dealing").forEach((card) => {
          card.classList.remove("is-dealing");
        });
      }, 920);
    });
  }

  private focusStackCard(): void {
    this.focusAfterRender(
      "#stack-card, #empty-history-button, #empty-clear-button, #empty-edit-button",
    );
  }

  private focusTableCard(index: number): void {
    window.setTimeout(() => {
      const target = this.root.querySelector<HTMLElement>(
        `[data-card-index="${index}"], #empty-history-button, `
        + "#empty-clear-button, #empty-edit-button",
      );
      target?.focus();
      target?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }, 0);
  }

  private moveTableFocus(currentIndex: number, key: string): void {
    const buttons = [
      ...this.root.querySelectorAll<HTMLButtonElement>(
        "#card-table [data-card-index]",
      ),
    ];
    if (!buttons.length) return;
    if (key === "Home") {
      buttons[0].focus();
      return;
    }
    if (key === "End") {
      buttons.at(-1)?.focus();
      return;
    }

    const current = buttons.find(
      (button) => Number(button.dataset.cardIndex) === currentIndex,
    );
    if (!current) return;
    const currentBox = current.getBoundingClientRect();
    const currentCenter = {
      x: currentBox.left + currentBox.width / 2,
      y: currentBox.top + currentBox.height / 2,
    };
    const horizontal = key === "ArrowLeft" || key === "ArrowRight";
    const candidates = buttons
      .filter((button) => button !== current)
      .map((button) => {
        const box = button.getBoundingClientRect();
        const deltaX = box.left + box.width / 2 - currentCenter.x;
        const deltaY = box.top + box.height / 2 - currentCenter.y;
        const inDirection = (
          key === "ArrowLeft" ? deltaX < -1
          : key === "ArrowRight" ? deltaX > 1
          : key === "ArrowUp" ? deltaY < -1
          : deltaY > 1
        );
        return {
          button,
          inDirection,
          score: horizontal
            ? Math.abs(deltaX) + Math.abs(deltaY) * 2
            : Math.abs(deltaY) + Math.abs(deltaX) * 2,
        };
      })
      .filter(({ inDirection }) => inDirection)
      .sort((left, right) => left.score - right.score);
    candidates[0]?.button.focus();
  }

  private focusConversationStage(
    layout: ConversationLayout = this.effectiveConversationLayout(),
  ): void {
    if (layout === "stack") {
      this.focusStackCard();
    } else if (layout === "hand" || layout === "table") {
      this.focusTableCard(0);
    } else {
      this.focusAfterRender("#confirm-view-all, #hide-all");
    }
  }

  private focusAfterRender(
    selector: string,
    options?: FocusOptions,
  ): void {
    window.setTimeout(() => {
      this.root.querySelector<HTMLElement>(selector)?.focus(options);
    }, 0);
  }

  private toggleRevealed(
    key: string,
    card?: ConversationCard,
    sourceElement?: HTMLElement,
  ): boolean {
    if (
      !this.conversationSessionActive
      || this.authoritativeConversationRefreshRequired
    ) return false;
    this.clearConversationOutcomeUndo();
    this.tabletopChoiceIndex = null;
    const turningFaceDown = this.revealedCardKeys.has(key);
    if (
      !turningFaceDown
      && card
      && this.authoritativeConversationRepository
    ) {
      const assignmentID = this.assignmentIDForConversationCard(card);
      const authoritativeCard = assignmentID
        ? this.authoritativeConversationCards.get(assignmentID)
        : null;
      if (!assignmentID || !authoritativeCard) {
        this.showToast(
          "This card is not part of the accepted Conversation session.",
          true,
        );
        return false;
      }
      if (!authoritativeCard.revealed) {
        void this.revealAuthoritativeConversationCard(
          key,
          card,
          assignmentID,
          sourceElement,
        );
        return false;
      }
    }
    if (sourceElement) {
      this.armCardTurnTransition(
        key,
        turningFaceDown ? "conceal" : "reveal",
        sourceElement,
      );
    } else {
      this.cancelCardTurnTransition();
    }
    this.revealedCardKeys.clear();
    if (!turningFaceDown) {
      this.revealedCardKeys.add(key);
      if (card && !this.authoritativeConversationRepository) {
        this.recordConversationEvent(card, "revealed");
        void this.persistConversationSession();
      }
    }
    return true;
  }

  private async revealAuthoritativeConversationCard(
    key: string,
    card: ConversationCard,
    assignmentID: string,
    sourceElement?: HTMLElement,
  ): Promise<void> {
    const repository = this.authoritativeConversationRepository;
    const state = this.authoritativeConversationState;
    if (
      !repository
      || !state
      || this.authoritativeConversationRefreshRequired
      || this.conversationOutcomeUndoInFlight
    ) return;
    this.conversationOutcomeUndoInFlight = true;
    try {
      const mutation = await repository.command({
        sessionID: this.conversationSessionID,
        action: "reveal",
        assignmentID,
        targetEventID: null,
        endReason: null,
        etag: state.etag,
        idempotencyKey: `web-reveal-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      if (sourceElement?.isConnected) {
        this.armCardTurnTransition(key, "reveal", sourceElement);
      }
      this.revealedCardKeys.add(key);
      this.renderConversationMode();
      this.focusConversationStage();
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.showToast(
        error instanceof Error
          ? error.message
          : "The card was not revealed because the accepted state could not be verified.",
        true,
      );
      this.renderConversationMode();
      this.focusConversationStage();
    } finally {
      this.conversationOutcomeUndoInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private armCardTurnTransition(
    key: string,
    direction: CardTurnDirection,
    sourceElement: HTMLElement,
  ): void {
    this.cancelCardTurnTransition();
    if (this.reducedMotionPreferred() || !sourceElement.isConnected) return;
    const bounds = sourceElement.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    this.pendingCardTurn = {
      key,
      direction,
      source: {
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
      },
    };
  }

  private startPendingCardTurn(
    key: string,
    targetElement: HTMLElement,
  ): boolean {
    const transition = this.pendingCardTurn;
    if (!transition || transition.key !== key) return false;
    this.pendingCardTurn = null;
    if (this.reducedMotionPreferred() || !targetElement.isConnected) {
      return false;
    }
    const targetRect = targetElement.getBoundingClientRect();
    if (targetRect.width <= 0 || targetRect.height <= 0) return false;
    const target: CardTurnBounds = {
      left: targetRect.left,
      top: targetRect.top,
      width: targetRect.width,
      height: targetRect.height,
    };
    const generation = this.cardTurnGeneration;
    let state: MotionState = { position: 0, velocity: 0 };
    let previousTime = performance.now();
    this.activeCardTurnElement = targetElement;
    targetElement.classList.add(
      "is-card-turning",
      `is-turning-${transition.direction}`,
    );
    targetElement.dataset.motionState = `turning-${transition.direction}`;
    const applyPose = (progress: number): void => {
      const pose = cardTurnPose(
        progress,
        transition.direction,
        transition.source,
        target,
      );
      targetElement.style.setProperty(
        "--card-turn-x",
        `${pose.translateX.toFixed(3)}px`,
      );
      targetElement.style.setProperty(
        "--card-turn-y",
        `${pose.translateY.toFixed(3)}px`,
      );
      targetElement.style.setProperty(
        "--card-turn-scale-x",
        pose.scaleX.toFixed(5),
      );
      targetElement.style.setProperty(
        "--card-turn-scale-y",
        pose.scaleY.toFixed(5),
      );
      targetElement.style.setProperty(
        "--card-turn-rotate-y",
        `${pose.rotateY.toFixed(3)}deg`,
      );
      targetElement.style.setProperty(
        "--card-turn-lift",
        `${pose.lift.toFixed(3)}px`,
      );
    };
    applyPose(0);
    const tick = (time: number): void => {
      if (
        generation !== this.cardTurnGeneration
        || !targetElement.isConnected
      ) {
        if (this.activeCardTurnElement === targetElement) {
          this.activeCardTurnElement = null;
          this.cardTurnFrameID = null;
        }
        return;
      }
      state = stepSpring(
        state,
        1,
        Math.max(0, time - previousTime) / 1_000,
        CARD_TURN_SPRING,
      );
      previousTime = time;
      applyPose(state.position);
      if (springIsAtRest(state, 1, CARD_TURN_SPRING)) {
        applyPose(1);
        this.cardTurnFrameID = null;
        this.activeCardTurnElement = null;
        this.clearCardTurnStyles(targetElement);
        return;
      }
      this.cardTurnFrameID = window.requestAnimationFrame(tick);
    };
    this.cardTurnFrameID = window.requestAnimationFrame(tick);
    return true;
  }

  private cancelCardTurnTransition(): void {
    this.cardTurnGeneration += 1;
    this.pendingCardTurn = null;
    if (this.cardTurnFrameID !== null) {
      window.cancelAnimationFrame(this.cardTurnFrameID);
      this.cardTurnFrameID = null;
    }
    if (this.activeCardTurnElement) {
      this.clearCardTurnStyles(this.activeCardTurnElement);
      this.activeCardTurnElement = null;
    }
  }

  private clearCardTurnStyles(element: HTMLElement): void {
    element.classList.remove(
      "is-card-turning",
      "is-turning-reveal",
      "is-turning-conceal",
    );
    for (const property of [
      "--card-turn-x",
      "--card-turn-y",
      "--card-turn-scale-x",
      "--card-turn-scale-y",
      "--card-turn-rotate-y",
      "--card-turn-lift",
    ]) {
      element.style.removeProperty(property);
    }
    if (element.dataset.motionState?.startsWith("turning-")) {
      element.dataset.motionState = "idle";
    }
  }

  private recordConversationEvent(
    card: ConversationCard,
    action: ConversationEvent["action"],
  ): ConversationEvent | null {
    if (!this.conversationSessionActive) return null;
    const event = {
      id: crypto.randomUUID(),
      bookmarkID: card.bookmarkID,
      audienceId: card.audienceId,
      action,
      occurredAt: new Date(),
    };
    this.conversationEvents.push(event);
    this.updateConversationHistoryDisclosure();
    return event;
  }

  private updateConversationHistoryDisclosure(): void {
    const disclosure = this.root.querySelector<HTMLButtonElement>(
      "#show-history",
    );
    if (!disclosure) return;
    const count = this.conversationHistoryGroups()
      .reduce((total, group) => total + group.entries.length, 0);
    disclosure.textContent = `History${count ? ` · ${count}` : ""}`;
  }

  private openBookmarkEditor(bookmarkID: string): void {
    this.clearConversationOutcomeUndo();
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.selectedID = bookmarkID;
    const bookmark = this.bookmarks.find(({ id }) => id === bookmarkID);
    const assignment = bookmark?.audienceAssignments.find(
      ({ audienceId }) => audienceId === this.conversationAudienceID,
    );
    this.selectedPublicationID = bookmark?.libraryPublications.find(
      ({ assignmentId, revokedAt }) => (
        assignmentId === assignment?.id && revokedAt === null
      ),
    )?.id ?? null;
    this.libraryDetailMode = "edit";
    this.mode = "library";
    this.returnToConversation = true;
    this.renderApp();
    this.root.querySelector(".app-shell")?.classList.add("detail-open");
    window.setTimeout(() => {
      this.root.querySelector<HTMLTextAreaElement>("#private-notes")?.focus();
    }, 0);
  }

  private effectiveConversationLayout(): ConversationLayout {
    return window.matchMedia(mobileConversationQuery).matches
      ? "stack"
      : this.conversationLayout;
  }

  private ensureVisibleSelection(): void {
    const visible = this.visibleLibraryEntries();
    if (!visible.some(({ entry }) => (
      entry.item.id === this.selectedPublicationID
    ))) {
      const first = visible[0] ?? null;
      this.selectedID = first?.bookmark?.id ?? null;
      this.selectedPublicationID = first?.entry.item.id ?? null;
      if (first && !first.bookmark) this.libraryDetailMode = "read";
    }
  }

  private bookmarkForAssignmentID(
    assignmentID: string,
  ): { bookmark: Bookmark; assignment: AudienceAssignment } | null {
    const matches = this.bookmarks.flatMap((bookmark) => (
      bookmark.audienceAssignments.flatMap((assignment) => (
        assignment.id === assignmentID
          ? [{ bookmark, assignment }]
          : []
      ))
    ));
    return matches.length === 1 ? matches[0] : null;
  }

  private assignmentIDForConversationCard(
    card: ConversationCard,
  ): string | null {
    const bookmark = this.bookmarks.find(({ id }) => id === card.bookmarkID);
    const assignments = bookmark?.audienceAssignments.filter((assignment) => (
      assignment.audienceId === card.audienceId
      && assignment.conversationEnabled
    )) ?? [];
    return assignments.length === 1 ? assignments[0].id : null;
  }

  private localEventsFromAuthoritative(
    events: readonly AuthoritativeConversationEvent[],
  ): ConversationEvent[] {
    const localEvents: ConversationEvent[] = [];
    for (const event of events) {
      if (event.action === "corrected") {
        const targetIndex = localEvents.findIndex(
          ({ id }) => id === event.targetEventID,
        );
        if (targetIndex >= 0) localEvents.splice(targetIndex, 1);
        continue;
      }
      if (event.action === "ended" || event.assignmentID === null) continue;
      const mapped = this.bookmarkForAssignmentID(event.assignmentID);
      if (!mapped) {
        throw new Error(
          "Convoful could not match accepted Conversation history to this workspace.",
        );
      }
      localEvents.push({
        id: event.id,
        bookmarkID: mapped.bookmark.id,
        audienceId: mapped.assignment.audienceId,
        action: event.action,
        occurredAt: new Date(event.occurredAt),
      });
    }
    return localEvents;
  }

  private async ensureAuthoritativeContinuity(
    session: AuthoritativeConversationSession,
    orderedBookmarkIDs: readonly string[],
  ): Promise<void> {
    await this.hydrateContinuityManifest(true);
    if (
      this.continuityLoadState !== "ready"
      || this.resolvedContinuity?.manifest.sessionID !== session.id
    ) {
      const repository = this.continuityRepository;
      if (!repository) {
        throw new Error(
          "This browser could not prepare its private continuity cues.",
        );
      }
      const resolved = await generateContinuityManifest({
        principalUserID: this.viewerUserID,
        audienceID: session.audienceID,
        sessionID: session.id,
        orderedCardIDs: [...orderedBookmarkIDs],
      });
      await repository.save(resolved.manifest);
      this.resolvedContinuity = resolved;
      this.continuityLoadState = "ready";
    }
    this.deckOrders.set(session.audienceID, [...orderedBookmarkIDs]);
  }

  private async adoptAuthoritativeConversation(
    state: AuthoritativeConversationSessions,
  ): Promise<void> {
    const activeSessions = state.sessions.filter(
      ({ endedAt }) => endedAt === null,
    );
    if (activeSessions.length > 1) {
      throw new Error(
        "Convoful returned more than one active Conversation session.",
      );
    }
    this.authoritativeConversationState = structuredClone(state);
    this.authoritativeConversationCards.clear();
    const historyAudienceID = activeSessions[0]?.audienceID
      ?? this.conversationAudienceID;
    this.endedConversationSessions = state.sessions.flatMap((session) => (
      session.endedAt === null || session.audienceID !== historyAudienceID
        ? []
        : [{
          id: session.id,
          startedAt: session.startedAt,
          endedAt: session.endedAt,
          events: this.localEventsFromAuthoritative(session.events)
            .map((event) => ({
              ...event,
              occurredAt: event.occurredAt.toISOString(),
            })),
        }]
    ));
    const active = activeSessions[0] ?? null;
    if (!active) {
      this.bookmarks = this.bookmarks.map((bookmark) => ({
        ...bookmark,
        audienceAssignments: bookmark.audienceAssignments.map((assignment) => (
          assignment.conversationEnabled
            ? { ...assignment, conversationState: "available" }
            : assignment
        )),
      }));
      this.conversationSessionActive = false;
      this.conversationEvents.length = 0;
      this.deckOrders.clear();
      this.revealedCardKeys.clear();
      this.resolvedContinuity = null;
      this.continuityLoadState = "missing";
      return;
    }
    if (!this.audienceLabels.has(active.audienceID)) {
      throw new Error(
        "The active Conversation audience is no longer available.",
      );
    }
    const orderedCards = [...active.cards]
      .sort((left, right) => (
        left.position - right.position
        || left.assignmentID.localeCompare(right.assignmentID)
      ));
    const orderedBookmarkIDs: string[] = [];
    const seenBookmarkIDs = new Set<string>();
    for (const card of orderedCards) {
      const mapped = this.bookmarkForAssignmentID(card.assignmentID);
      if (
        !mapped
        || mapped.assignment.audienceId !== active.audienceID
        || !mapped.assignment.conversationEnabled
        || seenBookmarkIDs.has(mapped.bookmark.id)
      ) {
        throw new Error(
          "Convoful could not verify this Conversation session against the private workspace.",
        );
      }
      seenBookmarkIDs.add(mapped.bookmark.id);
      orderedBookmarkIDs.push(mapped.bookmark.id);
      this.authoritativeConversationCards.set(card.assignmentID, card);
    }
    this.bookmarks = this.bookmarks.map((bookmark) => ({
      ...bookmark,
      audienceAssignments: bookmark.audienceAssignments.map((assignment) => {
        if (
          assignment.audienceId !== active.audienceID
          || !assignment.conversationEnabled
        ) return assignment;
        const card = this.authoritativeConversationCards.get(assignment.id);
        return {
          ...assignment,
          conversationState: card?.state === "discussed"
            ? "discussed"
            : "available",
        };
      }),
    }));
    this.conversationSessionActive = true;
    this.conversationSessionID = active.id;
    this.sessionStartedAt = new Date(active.startedAt);
    this.conversationAudienceID = active.audienceID;
    this.stackIndex = 0;
    this.conversationEvents.splice(
      0,
      this.conversationEvents.length,
      ...this.localEventsFromAuthoritative(active.events),
    );
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.sessionEndConfirming = false;
    this.tabletopChoiceIndex = null;
    if (orderedBookmarkIDs.length === 0) {
      this.deckOrders.set(active.audienceID, []);
      this.resolvedContinuity = null;
      this.continuityLoadState = "missing";
    } else {
      await this.ensureAuthoritativeContinuity(active, orderedBookmarkIDs);
    }
  }

  private async loadAuthoritativeConversation(): Promise<void> {
    const repository = this.authoritativeConversationRepository;
    if (!repository) return;
    const state = await repository.load();
    await this.adoptAuthoritativeConversation(state);
  }

  private async adoptAuthoritativeMutation(
    mutation: AuthoritativeConversationMutation,
  ): Promise<void> {
    const current = this.authoritativeConversationState;
    if (!current) {
      throw new Error(
        "Refresh Conversation Cards before applying this accepted action.",
      );
    }
    const mutationSession = mutation.session;
    const existing = current.sessions.find(
      ({ id }) => id === mutationSession.id,
    );
    let session: AuthoritativeConversationSession;
    if (mutationSession.cards.length > 0 || !existing) {
      session = mutationSession;
    } else {
      session = {
        ...existing,
        ...mutationSession,
        cards: mutation.card === null
          ? existing.cards
          : existing.cards.map((card) => (
            card.assignmentID === mutation.card?.assignmentID
              ? mutation.card
              : card
          )),
        events: mutation.event === null
          ? existing.events
          : [...existing.events, mutation.event],
      };
    }
    const sessions = [
      session,
      ...current.sessions.filter(({ id }) => id !== session.id),
    ].slice(0, 20);
    await this.adoptAuthoritativeConversation({
      revision: mutation.revision,
      etag: mutation.etag,
      sessions,
      cleanupRequiredSession: current.cleanupRequiredSession?.id === session.id
        ? null
        : current.cleanupRequiredSession,
    });
  }

  private restoreConversationSession(
    session: ConversationSessionSnapshot,
  ): void {
    this.conversationSessionActive = true;
    this.conversationSessionID = session.id;
    this.sessionStartedAt = new Date(session.startedAt);
    this.conversationAudienceID = session.audienceId;
    this.conversationLayout = session.layout;
    this.stackIndex = session.stackIndex;
    this.showThemeSymbols = session.showThemeSymbols;
    this.sessionMetricsVisible = session.statsVisible;
    this.dateFilter = session.dateFilter;
    this.customDateStart = session.customDateStart;
    this.customDateEnd = session.customDateEnd;

    this.deckOrders.clear();
    for (const [audienceId, bookmarkIDs] of Object.entries(
      session.deckOrders,
    )) {
      this.deckOrders.set(audienceId, [...bookmarkIDs]);
    }

    this.selectedThemes.clear();
    session.selectedThemes.forEach((theme) => this.selectedThemes.add(theme));

    this.conversationEvents.length = 0;
    for (const event of session.events) {
      this.conversationEvents.push({
        ...event,
        occurredAt: new Date(event.occurredAt),
      });
    }
    this.pruneConversationSessionPointers();

    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.filtersOpen = false;
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.statsOpen = false;
    this.sessionLifecycleOpen = false;
    this.sessionEndConfirming = false;
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
  }

  private pruneConversationSessionPointers(): void {
    const audienceIDs = new Set(
      this.bookmarks.flatMap((bookmark) => (
        bookmark.audienceAssignments
          .filter(({ conversationEnabled }) => conversationEnabled)
          .map(({ audienceId }) => audienceId)
      )),
    );
    if (!audienceIDs.has(this.conversationAudienceID)) {
      const firstAudienceID = audienceIDs.values().next().value;
      this.conversationAudienceID = typeof firstAudienceID === "string"
        ? firstAudienceID
        : "";
    }
    for (const [audienceId, bookmarkIDs] of this.deckOrders) {
      if (!audienceIDs.has(audienceId)) {
        this.deckOrders.delete(audienceId);
        continue;
      }
      const allowedBookmarkIDs = new Set(
        conversationDeck(this.bookmarks, audienceId)
          .map(({ bookmarkID }) => bookmarkID),
      );
      this.deckOrders.set(
        audienceId,
        bookmarkIDs.filter((bookmarkID) => (
          allowedBookmarkIDs.has(bookmarkID)
        )),
      );
    }
    const retainedEvents = this.conversationEvents.filter((event) => (
      conversationDeck(this.bookmarks, event.audienceId)
        .some(({ bookmarkID }) => bookmarkID === event.bookmarkID)
    ));
    this.conversationEvents.splice(
      0,
      this.conversationEvents.length,
      ...retainedEvents,
    );
    this.endedConversationSessions = this.endedConversationSessions.map(
      (session) => ({
        ...session,
        events: session.events.filter((event) => (
          conversationDeck(this.bookmarks, event.audienceId)
            .some(({ bookmarkID }) => bookmarkID === event.bookmarkID)
        )),
      }),
    );
    const currentThemes = new Set(this.themesForCurrentAudience());
    for (const theme of this.selectedThemes) {
      if (!currentThemes.has(theme)) this.selectedThemes.delete(theme);
    }
  }

  private currentConversationSessionStats(
    cards: ConversationCard[],
  ): { available: number; discussed: number; postponed: number } {
    const audienceEvents = this.conversationSessionActive
      ? this.conversationEvents.filter(
        ({ audienceId }) => audienceId === this.conversationAudienceID,
      )
      : [];
    return {
      available: this.conversationSessionActive
        ? cards.filter(({ state }) => state !== "discussed").length
        : 0,
      discussed: audienceEvents.filter(
        ({ action }) => action === "discussed",
      ).length,
      postponed: audienceEvents.filter(
        ({ action }) => action === "postponed",
      ).length,
    };
  }

  private conversationSessionElapsedLabel(): string {
    if (this.conversationSessionActive) {
      return compactElapsedTime(this.sessionStartedAt, new Date());
    }
    const latest = this.endedConversationSessions[0] ?? null;
    return latest
      ? compactElapsedTime(
        new Date(latest.startedAt),
        new Date(latest.endedAt),
      )
      : "Ended";
  }

  private currentConversationAudienceLabel(): string {
    return this.conversationAudienceLabel(this.conversationAudienceID);
  }

  private conversationAudienceLabel(audienceID: string): string {
    return this.bookmarks.flatMap(({ audienceAssignments }) => (
      audienceAssignments
    )).find(({ audienceId }) => audienceId === audienceID)?.audienceLabel
      ?? demoAudiences.find(({ id }) => id === audienceID)?.label
      ?? "Private audience";
  }

  private async endConversationSession(): Promise<void> {
    if (this.authoritativeConversationRefreshRequired) return;
    if (!this.conversationSessionActive) return;
    if (this.authoritativeConversationRepository) {
      await this.endAuthoritativeConversationSession("completed");
      return;
    }
    this.clearConversationOutcomeUndo();
    const endedAt = new Date();
    const ended: EndedConversationSessionSnapshot = {
      id: this.conversationSessionID,
      startedAt: this.sessionStartedAt.toISOString(),
      endedAt: endedAt.toISOString(),
      events: this.conversationEvents.map((event) => ({
        ...event,
        occurredAt: event.occurredAt.toISOString(),
      })),
    };
    const bounded = [ended, ...this.endedConversationSessions]
      .slice(0, MAX_SYNTHETIC_ENDED_SESSIONS);
    while (
      bounded.length > 1
      && bounded.reduce(
        (total, session) => total + session.events.length,
        0,
      ) > 5_000
    ) {
      bounded.pop();
    }
    this.endedConversationSessions = bounded;
    this.conversationSessionActive = false;
    this.syncScreenAwakeContext();
    this.conversationEvents.length = 0;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.filtersOpen = false;
    this.statsOpen = false;
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
    this.sessionEndConfirming = false;
    const persisted = await this.persistConversationSession();
    this.renderConversationMode();
    this.focusAfterRender("#start-new-session");
    if (persisted) {
      this.showToast(
        "Session ended. Its action-only history stays in this browser tab.",
      );
    }
  }

  private async startNewConversationSession(): Promise<void> {
    if (this.conversationSessionActive) return;
    if (this.authoritativeConversationRepository) {
      await this.startAuthoritativeConversationSession();
      return;
    }
    this.clearConversationOutcomeUndo();
    const nextSessionID = crypto.randomUUID();
    let nextContinuity: ResolvedContinuityManifest | null = null;
    if (this.authoritativePlayableConversationCards().length > 0) {
      this.continuityOperationInFlight = true;
      this.renderConversationMode();
      try {
        nextContinuity = await this.createAndPersistContinuityManifest(
          nextSessionID,
        );
      } catch (error) {
        this.continuityOperationInFlight = false;
        this.renderConversationMode();
        this.focusAfterRender("#start-new-session, #stage-start-session");
        this.showToast(
          error instanceof Error
            ? error.message
            : "A new private session could not be prepared.",
          true,
        );
        return;
      }
    }
    this.conversationSessionID = nextSessionID;
    this.sessionStartedAt = new Date();
    this.conversationSessionActive = true;
    this.sessionMetricsVisible = defaultSessionMetricsVisible();
    this.syncScreenAwakeContext();
    this.conversationEvents.length = 0;
    this.deckOrders.clear();
    this.stackIndex = 0;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.clear();
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.filtersOpen = false;
    this.statsOpen = false;
    this.sessionLifecycleOpen = false;
    this.sessionEndConfirming = false;
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
    this.resolvedContinuity = nextContinuity;
    this.continuityLoadState = nextContinuity ? "ready" : "missing";
    if (nextContinuity) {
      this.deckOrders.set(
        this.conversationAudienceID,
        [...nextContinuity.manifest.orderedCardIDs],
      );
    }
    const persisted = await this.persistConversationSession();
    this.armConversationGenerationChoreography();
    this.continuityOperationInFlight = false;
    this.renderConversationMode();
    this.focusConversationStage();
    if (persisted) {
      this.showToast(
        `New ${this.currentConversationAudienceLabel()} session started.`,
      );
    }
  }

  private async endAuthoritativeConversationSession(
    reason: "completed" | "paused" | "abandoned",
    quiet = false,
  ): Promise<boolean> {
    const repository = this.authoritativeConversationRepository;
    const state = this.authoritativeConversationState;
    if (
      !repository
      || !state
      || !this.conversationSessionActive
      || this.authoritativeConversationRefreshRequired
    ) return false;
    this.clearConversationOutcomeUndo();
    this.continuityOperationInFlight = true;
    this.renderConversationMode();
    try {
      const mutation = await repository.command({
        sessionID: this.conversationSessionID,
        action: "end",
        assignmentID: null,
        targetEventID: null,
        endReason: reason,
        etag: state.etag,
        idempotencyKey: `web-end-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      this.syncScreenAwakeContext();
      this.continuityOperationInFlight = false;
      this.renderConversationMode();
      this.focusAfterRender("#start-new-session");
      if (!quiet) {
        this.showToast(
          "Session ended. Its action-only history is synced privately.",
        );
      }
      return true;
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.continuityOperationInFlight = false;
      this.showToast(
        error instanceof Error
          ? error.message
          : "The Conversation session was not ended.",
        true,
      );
      this.renderConversationMode();
      return false;
    } finally {
      this.continuityOperationInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private async cleanupUnavailableAuthoritativeConversation(): Promise<boolean> {
    const repository = this.authoritativeConversationRepository;
    const state = this.authoritativeConversationState;
    const cleanup = state?.cleanupRequiredSession;
    if (!repository || !state || !cleanup) return true;
    try {
      const mutation = await repository.command({
        sessionID: cleanup.id,
        action: "end",
        assignmentID: null,
        targetEventID: null,
        endReason: "abandoned",
        etag: state.etag,
        idempotencyKey: `web-cleanup-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      return true;
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.showToast(
        error instanceof Error
          ? error.message
          : "The unavailable prior session could not be closed safely.",
        true,
      );
      return false;
    }
  }

  private async startAuthoritativeConversationSession(): Promise<boolean> {
    const repository = this.authoritativeConversationRepository;
    if (
      !repository
      || this.conversationSessionActive
      || this.authoritativeConversationRefreshRequired
    ) return false;
    this.clearConversationOutcomeUndo();
    this.continuityOperationInFlight = true;
    this.renderConversationMode();
    try {
      if (!await this.cleanupUnavailableAuthoritativeConversation()) {
        return false;
      }
      const state = this.authoritativeConversationState;
      if (!state) {
        throw new Error("Refresh Conversation Cards before starting.");
      }
      const cards = presentConversationDeck(
        filterConversationCards(
          this.authoritativePlayableConversationCards(),
          this.conversationFilters(),
        ),
        this.conversationDeckPreferences(),
      );
      const assignmentIDs = cards.map((card) => {
        const assignmentID = this.assignmentIDForConversationCard(card);
        if (!assignmentID) {
          throw new Error(
            "This deck includes a card without a verified audience assignment.",
          );
        }
        return assignmentID;
      });
      if (assignmentIDs.length === 0) {
        throw new Error(
          "There are no cards in the current audience and filters to start.",
        );
      }
      const mutation = await repository.start({
        audienceID: this.conversationAudienceID,
        assignmentIDs,
        etag: state.etag,
        idempotencyKey: `web-start-${crypto.randomUUID()}`,
      });
      await this.adoptAuthoritativeMutation(mutation);
      this.sessionMetricsVisible = defaultSessionMetricsVisible();
      this.armConversationGenerationChoreography();
      this.syncScreenAwakeContext();
      this.continuityOperationInFlight = false;
      this.renderConversationMode();
      this.focusConversationStage();
      this.showToast(
        `New ${this.currentConversationAudienceLabel()} session started and synced.`,
      );
      return true;
    } catch (error) {
      await this.loadAuthoritativeConversation().catch(() => {});
      this.continuityOperationInFlight = false;
      this.showToast(
        error instanceof Error
          ? error.message
          : "A new private Conversation session could not start.",
        true,
      );
      this.renderConversationMode();
      this.focusAfterRender("#start-new-session, #stage-start-session");
      return false;
    } finally {
      this.continuityOperationInFlight = false;
      this.refreshAuthoritativeConversationOnReturn();
    }
  }

  private async restartAuthoritativeConversation(): Promise<void> {
    if (
      !this.conversationSessionActive
      || this.continuityOperationInFlight
      || this.conversationOutcomeUndo
      || this.conversationOutcomeUndoInFlight
    ) return;
    const ended = await this.endAuthoritativeConversationSession("paused", true);
    if (!ended) return;
    await this.startAuthoritativeConversationSession();
  }

  private conversationSessionSnapshot(): ConversationSessionSnapshot | null {
    if (!this.conversationSessionActive) return null;
    return {
      id: this.conversationSessionID,
      startedAt: this.sessionStartedAt.toISOString(),
      audienceId: this.conversationAudienceID,
      deckOrders: Object.fromEntries(
        [...this.deckOrders.entries()]
          .map(([audienceId, bookmarkIDs]) => [
            audienceId,
            [...bookmarkIDs],
          ]),
      ),
      events: this.conversationEvents.map((event) => ({
        ...event,
        occurredAt: event.occurredAt.toISOString(),
      })),
      selectedThemes: [...this.selectedThemes],
      dateFilter: this.dateFilter,
      customDateStart: this.customDateStart,
      customDateEnd: this.customDateEnd,
      showThemeSymbols: this.showThemeSymbols,
      statsVisible: this.sessionMetricsVisible,
      layout: this.conversationLayout,
      stackIndex: this.stackIndex,
    };
  }

  private async persistConversationSession(): Promise<boolean> {
    if (!this.conversationSessionRepository) return true;
    try {
      await this.conversationSessionRepository.save(
        this.conversationSessionSnapshot(),
        this.endedConversationSessions,
      );
      return true;
    } catch {
      this.showToast(
        "This synthetic Conversation session could not be saved in this tab.",
        true,
      );
      return false;
    }
  }

  private conversationCardForEvent(
    event: ConversationEvent,
  ): ConversationCard | null {
    return conversationDeck(this.bookmarks, event.audienceId)
      .find(({ bookmarkID }) => bookmarkID === event.bookmarkID) ?? null;
  }

  private conversationHistoryEntries(): ConversationHistoryEntry[] {
    return this.conversationHistoryGroups()
      .flatMap(({ entries }) => entries);
  }

  private conversationHistoryGroups(): ConversationHistoryGroup[] {
    const groups: ConversationHistoryGroup[] = [];
    if (this.conversationSessionActive) {
      const entries = this.conversationHistoryEntriesForEvents(
        this.conversationSessionID,
        this.conversationEvents,
      );
      if (entries.length > 0) {
        groups.push({
          sessionID: this.conversationSessionID,
          startedAt: this.sessionStartedAt,
          endedAt: null,
          active: true,
          entries,
        });
      }
    }
    for (const session of this.endedConversationSessions) {
      const events = session.events.map((event) => ({
        ...event,
        occurredAt: new Date(event.occurredAt),
      }));
      const entries = this.conversationHistoryEntriesForEvents(
        session.id,
        events,
      );
      if (entries.length === 0) continue;
      groups.push({
        sessionID: session.id,
        startedAt: new Date(session.startedAt),
        endedAt: new Date(session.endedAt),
        active: false,
        entries,
      });
    }
    return groups;
  }

  private conversationHistoryEntriesForEvents(
    sessionID: string,
    events: readonly ConversationEvent[],
  ): ConversationHistoryEntry[] {
    const chronological = events
      .map((event, feedIndex) => ({ event, feedIndex }))
      .filter(({ event }) => (
        event.audienceId === this.conversationAudienceID
      ))
      .sort((left, right) => (
        left.event.occurredAt.valueOf() - right.event.occurredAt.valueOf()
        || left.feedIndex - right.feedIndex
      ));
    const entries: ConversationHistoryEntry[] = [];
    for (const { event, feedIndex } of chronological) {
      const card = this.conversationCardForEvent(event);
      if (!card) continue;
      if (event.action === "revealed") {
        entries.push({
          id: `${sessionID}:${event.id}`,
          card,
          event,
          feedIndex,
        });
        continue;
      }
      const pendingRevealIndex = entries.findLastIndex((entry) => (
        entry.card.bookmarkID === event.bookmarkID
        && entry.event.action === "revealed"
      ));
      if (pendingRevealIndex >= 0) {
        entries[pendingRevealIndex] = {
          ...entries[pendingRevealIndex],
          card,
          event,
          feedIndex,
        };
      } else {
        entries.push({
          id: `${sessionID}:${event.id}`,
          card,
          event,
          feedIndex,
        });
      }
    }
    return entries.sort((left, right) => (
      right.event.occurredAt.valueOf() - left.event.occurredAt.valueOf()
      || right.feedIndex - left.feedIndex
    ));
  }

  private startSessionClock(): void {
    if (this.sessionClockID !== null) return;
    this.sessionClockID = window.setInterval(() => {
      if (!this.conversationSessionActive) return;
      const elapsed = this.conversationSessionElapsedLabel();
      const strip = this.root.querySelector<HTMLElement>("#stat-session");
      if (strip) strip.textContent = elapsed;
      this.root.querySelectorAll<HTMLElement>("[data-session-elapsed]")
        .forEach((element) => {
          element.textContent = elapsed;
        });
    }, 30_000);
  }

  private bindGlobalKeyboard(): void {
    if (this.keyboardBound) return;
    window.addEventListener("keydown", this.handleKeyboard);
    window.addEventListener(
      "blur",
      this.markAuthoritativeConversationReturnRefresh,
    );
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("pagehide", this.handlePageHide);
    window.addEventListener("focus", this.handleWindowFocus);
    window.addEventListener("pageshow", this.handlePageShow);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.conversationViewport = window.matchMedia(mobileConversationQuery);
    this.conversationViewport.addEventListener(
      "change",
      this.handleConversationViewportChange,
    );
    this.tabletopViewport = window.matchMedia(compactConversationQuery);
    this.tabletopViewport.addEventListener(
      "change",
      this.handleTabletopViewportChange,
    );
    this.libraryFilterViewport = window.matchMedia(compactConversationQuery);
    this.libraryFilterViewport.addEventListener(
      "change",
      this.handleLibraryFilterViewportChange,
    );
    this.keyboardBound = true;
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden) this.markAuthoritativeConversationReturnRefresh();
    if (!document.hidden) {
      this.refreshAuthoritativeConversationOnReturn();
      this.checkForCurrentWebVersion();
    }
    this.syncScreenAwakeContext();
  };

  private readonly handleWindowFocus = (): void => {
    this.refreshAuthoritativeConversationOnReturn();
    this.checkForCurrentWebVersion();
  };

  private readonly handlePageShow = (): void => {
    this.refreshAuthoritativeConversationOnReturn();
    this.checkForCurrentWebVersion();
  };

  private refreshAuthoritativeConversationOnReturn(): void {
    if (
      !this.authoritativeConversationRepository
      || !this.authoritativeConversationRefreshRequired
      || this.authoritativeConversationRefreshInFlight
      || this.continuityOperationInFlight
      || this.conversationOutcomeUndoInFlight
      || document.hidden
    ) return;
    void this.performAuthoritativeConversationReturnRefresh();
  }

  private async performAuthoritativeConversationReturnRefresh(): Promise<void> {
    this.authoritativeConversationRefreshInFlight = true;
    if (this.mode === "conversation") this.renderConversationMode();
    let failure = false;
    try {
      await this.loadAuthoritativeConversation();
      this.authoritativeConversationRefreshRequired = false;
      this.syncScreenAwakeContext();
    } catch {
      failure = true;
    } finally {
      this.authoritativeConversationRefreshInFlight = false;
      if (this.mode === "conversation") this.renderConversationMode();
    }
    if (failure) {
      this.showToast(
        "Convoful could not refresh the accepted private Conversation. "
          + "Every prompt stayed concealed; try again.",
        true,
      );
    }
  }

  private readonly handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    void this.screenAwake.disable();
    if (!this.bookmarkDrafts.size) return;
    event.preventDefault();
    event.returnValue = "";
  };

  private readonly handleConversationViewportChange = (
    event: MediaQueryListEvent,
  ): void => {
    if (this.mode !== "conversation") return;
    const cardBackPhotoDialog = this.root.querySelector<HTMLDialogElement>(
      "#card-back-photo-dialog",
    );
    const preserveCardBackPhotoDialog = cardBackPhotoDialog?.open ?? false;
    const dialogFocusLabel = (
      preserveCardBackPhotoDialog
      && cardBackPhotoDialog?.contains(document.activeElement)
    )
      ? (document.activeElement as HTMLElement | null)
        ?.getAttribute("aria-label")
      : null;
    this.clearConversationOutcomeUndo();
    if (event.matches) this.conversationLayout = "stack";
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.delete(this.conversationAudienceID);
    this.renderConversationMode();
    if (preserveCardBackPhotoDialog) {
      this.renderCardBackPhotoLibrary();
      const replacement = this.root.querySelector<HTMLDialogElement>(
        "#card-back-photo-dialog",
      );
      replacement?.showModal();
      const focusTarget = dialogFocusLabel
        ? [...(replacement?.querySelectorAll<HTMLElement>("[aria-label]") ?? [])]
          .find((element) => (
            element.getAttribute("aria-label") === dialogFocusLabel
          ))
        : null;
      (focusTarget ?? replacement?.querySelector<HTMLElement>(
        "#close-card-back-photos",
      ))?.focus();
    }
    if (!preserveCardBackPhotoDialog) {
      this.focusConversationStage(this.effectiveConversationLayout());
    }
  };

  private readonly handleTabletopViewportChange = (
    event: MediaQueryListEvent,
  ): void => {
    if (
      !event.matches
      || this.mode !== "conversation"
      || !this.tabletopMode
    ) return;
    this.clearConversationOutcomeUndo();
    const activeCardIndex = Number(
      (document.activeElement as HTMLElement | null)?.dataset.cardIndex ?? 0,
    );
    this.tabletopMode = false;
    this.tabletopChoiceIndex = null;
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.delete(this.conversationAudienceID);
    this.historyDetailEntryID = null;
    this.renderConversationMode();
    this.focusTableCard(
      Number.isSafeInteger(activeCardIndex) && activeCardIndex >= 0
        ? activeCardIndex
        : 0,
    );
  };

  private readonly handleLibraryFilterViewportChange = (
    event: MediaQueryListEvent,
  ): void => {
    if (this.mode !== "library") return;
    const toggle = this.root.querySelector<HTMLButtonElement>(
      "#library-filter-toggle",
    );
    const controls = this.root.querySelector<HTMLElement>(
      "#library-filter-controls",
    );
    const activeElement = document.activeElement;
    const focusInsideControls = Boolean(
      controls && activeElement && controls.contains(activeElement),
    );
    const focusOnToggle = (
      activeElement === toggle || this.libraryFilterToggleFocused
    );
    if (event.matches && focusInsideControls) {
      this.libraryFiltersOpen = true;
    }
    this.updateLibraryFilterDisclosure();
    if (!event.matches && focusOnToggle) {
      this.libraryFilterToggleFocused = false;
      this.focusAfterRender("#audience-filter");
    }
  };

  private readonly markAuthoritativeConversationReturnRefresh = (): void => {
    if (this.authoritativeConversationRepository) {
      this.authoritativeConversationRefreshRequired = true;
    }
    this.concealPrivateConversation();
  };

  private readonly concealPrivateConversation = (): void => {
    if (this.mode !== "conversation") return;
    this.clearConversationOutcomeUndo();
    this.cancelCardTurnTransition();
    const cardBackPhotoDialog = this.root.querySelector<HTMLDialogElement>(
      "#card-back-photo-dialog",
    );
    const hadPrivateSurface = (
      this.revealedCardKeys.size > 0
      || this.viewAllConfirmed.has(this.conversationAudienceID)
      || this.historyOpen
      || cardBackPhotoDialog?.open
    );
    if (!hadPrivateSurface) return;
    if (cardBackPhotoDialog?.open) cardBackPhotoDialog.close();
    this.revealedCardKeys.clear();
    this.viewAllConfirmed.delete(this.conversationAudienceID);
    this.historyOpen = false;
    this.historyDetailEntryID = null;
    this.renderConversationMode();
  };

  private readonly handleKeyboard = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    const isEditing = target?.matches("input, textarea, select, summary");
    const quickCaptureOpen = this.root.querySelector<HTMLDialogElement>(
      "#quick-capture-dialog",
    )?.open ?? false;
    const newCardOpen = this.root.querySelector<HTMLDialogElement>(
      "#new-card-dialog",
    )?.open ?? false;
    const cardBackPhotoLibraryOpen = this.root.querySelector<HTMLDialogElement>(
      "#card-back-photo-dialog",
    )?.open ?? false;
    const keyboardHelpOpen = this.root.querySelector<HTMLDialogElement>(
      "#keyboard-help-dialog",
    )?.open ?? false;
    const ownerBookmarksOpen = this.root.querySelector<HTMLDialogElement>(
      "#owner-bookmark-dialog",
    )?.open ?? false;
    if (
      quickCaptureOpen
      || newCardOpen
      || cardBackPhotoLibraryOpen
      || keyboardHelpOpen
      || ownerBookmarksOpen
    ) return;
    if (
      !isEditing
      && event.key === "?"
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey
    ) {
      event.preventDefault();
      this.openKeyboardHelp(
        target?.isConnected
          ? target
          : this.root.querySelector<HTMLButtonElement>("#keyboard-help")!,
      );
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
      const form = this.root.querySelector<HTMLFormElement>("#bookmark-form");
      if (form) {
        event.preventDefault();
        const save = form.querySelector<HTMLButtonElement>(".save-button");
        if (!save?.disabled) form.requestSubmit(save);
      }
      return;
    }
    if (isEditing) return;

    if (this.mode === "library") {
      if (
        event.key.toLowerCase() === "n"
        && !event.metaKey
        && !event.ctrlKey
        && !event.altKey
      ) {
        event.preventDefault();
        this.openNewCardComposer();
        return;
      }
      if (
        event.key.toLowerCase() === "b"
        && !event.metaKey
        && !event.ctrlKey
        && !event.altKey
      ) {
        event.preventDefault();
        this.openOwnerBookmarkManager();
        return;
      }
      if (
        event.key === "Escape"
        && this.libraryFiltersOpen
        && window.matchMedia(compactConversationQuery).matches
      ) {
        event.preventDefault();
        this.libraryFiltersOpen = false;
        this.updateLibraryFilterDisclosure();
        this.focusAfterRender("#library-filter-toggle");
        return;
      }
      if (
        event.key.toLowerCase() === "c"
        && this.demo
        && !event.metaKey
        && !event.ctrlKey
        && !event.altKey
      ) {
        event.preventDefault();
        this.openQuickCapture();
        return;
      }
      if (
        event.key === "/"
        && !event.metaKey
        && !event.ctrlKey
        && !event.altKey
      ) {
        event.preventDefault();
        this.root.querySelector<HTMLInputElement>("#library-search")?.focus();
        return;
      }
      if (event.key.toLowerCase() === "e" && this.selectedID) {
        event.preventDefault();
        if (this.libraryDetailMode === "read") {
          this.showEditorDetail();
        } else {
          this.root.querySelector<HTMLTextAreaElement>("#private-notes")?.focus();
        }
        return;
      }
      if (
        event.key.toLowerCase() === "r"
        && this.root.querySelector("#show-reading-view")
      ) {
        event.preventDefault();
        this.showReadingDetail();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const visible = this.visibleLibraryEntries();
        if (!visible.length) return;
        event.preventDefault();
        const current = visible.findIndex(({ entry }) => (
          entry.item.id === this.selectedPublicationID
        ));
        const delta = event.key === "ArrowDown" ? 1 : -1;
        const next = Math.min(Math.max(current + delta, 0), visible.length - 1);
        const nextCandidate = visible[next];
        this.selectedID = nextCandidate.bookmark?.id ?? null;
        this.selectedPublicationID = nextCandidate.entry.item.id;
        if (!nextCandidate.bookmark) this.libraryDetailMode = "read";
        this.renderLibrary();
        this.root.querySelector<HTMLButtonElement>(
          `[data-publication-id="${this.selectedPublicationID}"]`,
        )?.focus();
      }
      return;
    }

    if (
      event.key.toLowerCase() === "u"
      && this.conversationOutcomeUndo
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey
    ) {
      event.preventDefault();
      void this.undoConversationOutcome(this.conversationOutcomeUndo);
      return;
    }
    if (event.key === "Escape" && this.sessionLifecycleOpen) {
      event.preventDefault();
      if (this.sessionEndConfirming) {
        this.sessionEndConfirming = false;
        this.renderConversationMode();
        this.focusAfterRender("#end-session");
      } else {
        this.sessionLifecycleOpen = false;
        this.renderConversationMode();
        this.focusAfterRender("#manage-conversation-session");
      }
      return;
    }
    if (event.key === "Escape" && this.historyOpen) {
      event.preventDefault();
      if (this.historyDetailEntryID !== null) {
        const selectedIndex = this.conversationHistoryEntries()
          .filter(({ event: historyEvent }) => (
            this.historyFilter === "all"
            || historyEvent.action === this.historyFilter
          ))
          .findIndex(({ id }) => id === this.historyDetailEntryID);
        this.historyDetailEntryID = null;
        this.renderConversationMode();
        this.focusAfterRender(`[data-history-index="${selectedIndex}"]`);
      } else {
        this.historyOpen = false;
        this.renderConversationMode();
        this.focusAfterRender("#show-history");
      }
      return;
    }
    if (this.authoritativeConversationRefreshRequired) return;
    if (!this.conversationSessionActive) return;
    const cards = this.orderedConversationDeck();
    if (!cards.length) return;
    if (event.key === "Escape") {
      const hadVisiblePrompt = (
        this.revealedCardKeys.size > 0
        || this.viewAllConfirmed.has(this.conversationAudienceID)
      );
      if (hadVisiblePrompt) {
        event.preventDefault();
        const layout = this.effectiveConversationLayout();
        const tableIndex = target?.dataset.cardIndex;
        const revealedKey = this.revealedCardKeys.values().next().value;
        const sourceElement = layout === "stack"
          ? this.root.querySelector<HTMLElement>("#stack-card")
          : layout === "hand" || layout === "table"
          ? this.root.querySelector<HTMLElement>(
            ".table-card.is-revealed .table-card-face",
          )
          : null;
        const animateSingleConceal = (
          typeof revealedKey === "string"
          && sourceElement !== null
          && layout !== "all"
        );
        if (animateSingleConceal) {
          this.toggleRevealed(revealedKey, undefined, sourceElement);
        } else {
          this.cancelCardTurnTransition();
          this.revealedCardKeys.clear();
        }
        this.viewAllConfirmed.delete(this.conversationAudienceID);
        if (animateSingleConceal) {
          this.renderConversationStage(cards, true);
        } else {
          this.renderConversationMode();
        }
        if (layout === "stack") {
          this.focusStackCard();
        } else if (layout === "hand" || layout === "table") {
          this.focusTableCard(Number(tableIndex ?? 0));
        } else if (layout === "all") {
          this.focusConversationStage("all");
        }
        return;
      }
      if (
        this.tabletopMode
        && (
          this.effectiveConversationLayout() === "hand"
          || this.effectiveConversationLayout() === "table"
        )
      ) {
        event.preventDefault();
        const roomViewToggle = this.effectiveConversationLayout() === "hand"
          ? "#toggle-room-view"
          : "#toggle-tabletop";
        this.tabletopMode = false;
        this.tabletopChoiceIndex = null;
        this.renderConversationMode();
        this.focusAfterRender(roomViewToggle);
      }
      return;
    }
    if (event.key.toLowerCase() === "s") {
      event.preventDefault();
      void this.shuffleDeck();
      return;
    }
    const revealedScrollableCard = (
      target?.matches(".ritual-card.is-revealed")
      || (
        target?.matches(".table-card-face")
        && Boolean(target.closest(".table-card.is-revealed"))
      )
    );
    if (
      (event.key === "PageDown" || event.key === "PageUp")
      && revealedScrollableCard
      && target
      && target.scrollHeight > target.clientHeight
    ) {
      event.preventDefault();
      const direction = event.key === "PageDown" ? 1 : -1;
      target.scrollTop += direction * Math.max(target.clientHeight * 0.8, 44);
      return;
    }
    const layout = this.effectiveConversationLayout();
    if (
      event.key.toLowerCase() === "d"
      && layout === "hand"
      && this.revealedCardKeys.size === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey
    ) {
      event.preventDefault();
      this.dealAnotherHand(cards);
      return;
    }
    if (
      event.key.toLowerCase() === "r"
      && (layout === "hand" || layout === "table")
      && this.revealedCardKeys.size === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.altKey
    ) {
      event.preventDefault();
      this.chooseSpreadCard(cards, layout === "hand" ? 3 : 12);
      return;
    }
    if (
      (layout === "hand" || layout === "table")
      && target?.dataset.cardIndex !== undefined
      && [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ].includes(event.key)
    ) {
      event.preventDefault();
      this.moveTableFocus(Number(target.dataset.cardIndex), event.key);
      return;
    }
    if (layout !== "stack") return;
    if (event.key === " " || event.key === "Enter") {
      if (target?.matches("button, a")) return;
      event.preventDefault();
      const card = cards[clampIndex(this.stackIndex, cards.length)];
      const stackCard = this.root.querySelector<HTMLElement>("#stack-card");
      if (!stackCard) return;
      if (!this.toggleRevealed(cardKey(card), card, stackCard)) return;
      this.renderConversationStage(cards, true);
      this.focusStackCard();
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      this.moveStack(event.key === "ArrowRight" ? 1 : -1, cards);
    }
  };

  private clearConversationOutcomeUndo(): void {
    if (!this.conversationOutcomeUndo) return;
    this.conversationOutcomeUndo = null;
    this.dismissToast(false);
  }

  private dismissToast(notify = true): void {
    const toast = this.root.querySelector<HTMLElement>("#toast");
    if (this.toastTimeoutID !== null) {
      window.clearTimeout(this.toastTimeoutID);
      this.toastTimeoutID = null;
    }
    toast?.classList.remove("is-visible", "is-actionable");
    toast?.replaceChildren();
    const onDismiss = this.toastOnDismiss;
    this.toastOnDismiss = null;
    if (notify) onDismiss?.();
  }

  private showToast(
    message: string,
    error = false,
    action?: ToastAction,
  ): void {
    const toast = this.root.querySelector<HTMLElement>("#toast");
    if (!toast) return;
    this.dismissToast();
    const text = document.createElement("span");
    text.className = "toast-message";
    text.textContent = message;
    toast.replaceChildren(text);
    if (action) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "toast-action";
      button.textContent = action.label;
      if (action.keyboardShortcut) {
        button.setAttribute("aria-keyshortcuts", action.keyboardShortcut);
      }
      button.addEventListener("click", () => {
        this.dismissToast(false);
        action.onActivate();
      });
      toast.append(button);
    }
    toast.classList.toggle("is-error", error);
    toast.classList.toggle("is-actionable", Boolean(action));
    toast.classList.add("is-visible");
    this.toastOnDismiss = action?.onDismiss ?? null;
    if (!action) {
      this.toastTimeoutID = window.setTimeout(
        () => this.dismissToast(),
        2_800,
      );
    }
  }

  private celebrateDiscussed(): void {
    const layer = this.root.querySelector<HTMLElement>("#celebration-layer");
    if (!layer) return;
    if (this.celebrationTimeoutID !== null) {
      window.clearTimeout(this.celebrationTimeoutID);
    }
    layer.replaceChildren();
    const burst = document.createElement("div");
    burst.className = "celebration-burst";
    if (this.root.querySelector("#empty-history-button")) {
      burst.classList.add("is-session-complete");
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const seal = document.createElement("span");
      seal.className = "celebration-seal";
      seal.textContent = "✓";
      burst.append(seal);
    } else {
      for (let index = 0; index < 14; index += 1) {
        const angle = (index * 360) / 14 - 90;
        const radians = angle * (Math.PI / 180);
        const distance = 82 + (index % 2 === 0 ? 35 : 8);
        const particle = document.createElement("span");
        particle.className = index % 3 === 0
          ? "celebration-particle celebration-spark"
          : "celebration-particle";
        particle.style.setProperty("--burst-x", `${Math.cos(radians) * distance}px`);
        particle.style.setProperty("--burst-y", `${Math.sin(radians) * distance}px`);
        particle.style.setProperty("--burst-rotation", `${index * 31}deg`);
        burst.append(particle);
      }
    }
    layer.append(burst);
    this.celebrationTimeoutID = window.setTimeout(() => {
      layer.replaceChildren();
      this.celebrationTimeoutID = null;
    }, 900);
  }
}

async function loadFixtureReceipts(
  repository: LibraryReceiptRepository,
  bookmarks: Bookmark[],
): Promise<LibraryReceipt[]> {
  const publicationIDs = bookmarks.flatMap((bookmark) => {
    const libraryAssignmentIDs = new Set(
      bookmark.audienceAssignments
        .filter(({ libraryEnabled }) => libraryEnabled)
        .map(({ id }) => id),
    );
    return bookmark.libraryPublications
      .filter((publication) => (
        publication.revokedAt === null
        && libraryAssignmentIDs.has(publication.assignmentId)
      ))
      .map(({ id }) => id);
  });
  const receipts = await Promise.all(
    publicationIDs.map((publicationID) => (
      repository.loadReceipt(publicationID)
    )),
  );
  return receipts.filter(
    (receipt): receipt is LibraryReceipt => receipt !== null,
  );
}

function replaceLibraryReceipt(
  receipts: LibraryReceipt[],
  replacement: LibraryReceipt,
): LibraryReceipt[] {
  const remaining = receipts.filter((receipt) => !(
    receipt.publicationId === replacement.publicationId
    && receipt.userId === replacement.userId
  ));
  return [...remaining, structuredClone(replacement)];
}

function bookmarkEditsMatch(
  original: Bookmark,
  edits: BookmarkEdits,
): boolean {
  if (
    validatePrivateSummaryTransition(
      original.privateHumanSummary,
      edits.privateHumanSummary,
    )
    || validatePrivateCardMedia(edits.privateCardMedia)
  ) {
    return false;
  }
  const selectedPublication = original.libraryPublications.find(
    ({ id, revokedAt }) => (
      id === edits.publicationID && revokedAt === null
    ),
  );
  const updated = applyBookmarkEdits(
    original,
    edits,
    selectedPublication?.updatedAt ?? original.createdAt,
  );
  return (
    updated.title === original.title
    && updated.sourceLabel === original.sourceLabel
    && updated.sourceURL === original.sourceURL
    && updated.primaryTheme === original.primaryTheme
    && JSON.stringify(updated.privateCardMedia ?? [])
      === JSON.stringify(original.privateCardMedia ?? [])
    && edits.sharedTitle.trim() === (selectedPublication?.sharedTitle ?? "")
    && edits.sharedSourceLabel.trim()
      === (selectedPublication?.sharedSourceLabel ?? "")
    && (safeExternalURL(edits.sharedURL) ?? "")
      === (selectedPublication?.sharedURL ?? "")
    && edits.sharedNotes.trim() === (selectedPublication?.sharedNotes ?? "")
    && updated.privateNotes === original.privateNotes
    && updated.privateHumanSummary === original.privateHumanSummary
    && JSON.stringify(updated.triageAudit ?? null)
      === JSON.stringify(original.triageAudit ?? null)
    && edits.format === (selectedPublication?.format ?? original.format)
    && edits.lane === (selectedPublication?.lane ?? original.lane)
    && edits.videoDurationSeconds
      === (
        selectedPublication
          ? selectedPublication.videoDurationSeconds
          : original.videoDurationSeconds
      )
    && JSON.stringify(updated.keywords) === JSON.stringify(original.keywords)
    && JSON.stringify(updated.tags) === JSON.stringify(original.tags)
    && audienceAssignmentSignature(updated.audienceAssignments)
      === audienceAssignmentSignature(original.audienceAssignments)
  );
}

function audienceAssignmentSignature(
  assignments: AudienceAssignment[],
): string {
  return JSON.stringify(
    assignments
      .map((assignment) => ({
        id: assignment.id,
        audienceId: assignment.audienceId,
        conversationEnabled: assignment.conversationEnabled,
        libraryEnabled: assignment.libraryEnabled,
        isPrimary: assignment.isPrimary,
        conversationState: assignment.conversationState,
      }))
      .sort((left, right) => left.audienceId.localeCompare(right.audienceId)),
  );
}

function renderCardBack(
  button: HTMLButtonElement,
  index: number,
  audienceLabel: string,
  theme: string,
  showThemeSymbol: boolean,
  photoURL: string | null,
  continuityCode: string,
): void {
  button.replaceChildren();
  button.classList.toggle("has-card-back-photo", photoURL !== null);
  if (photoURL) {
    const photo = document.createElement("img");
    photo.className = "card-back-photo";
    photo.src = photoURL;
    photo.alt = "";
    photo.setAttribute("aria-hidden", "true");
    photo.draggable = false;
    photo.style.objectPosition = `${20 + (index % 3) * 30}% center`;
    button.append(photo);
  }
  const symbol = document.createElement("span");
  symbol.className = "card-back-symbol";
  symbol.setAttribute("aria-hidden", "true");
  symbol.textContent = showThemeSymbol
    ? themeSymbol(theme)
    : PRODUCT.brandMark;
  const label = document.createElement("span");
  label.className = "card-back-label";
  label.setAttribute("aria-hidden", "true");
  label.textContent = `${audienceLabel} · card ${index + 1}`;
  button.append(symbol, label, continuityCue(continuityCode));
}

function cardBackAriaLabel(
  action: string,
  theme: string,
  showThemeSymbol: boolean,
  hasPhoto: boolean,
  continuityCode: string,
): string {
  const photo = hasPhoto ? " Shuffled personal photo background." : "";
  const card = showThemeSymbol
    ? `${action}.${photo} ${humanizeConversationTheme(theme)} theme hint.`
    : `${action}.${photo} Theme hidden.`;
  return `${card} ${continuityAccessibleCopy(continuityCode)}`;
}

function renderRevealedCardFace(
  button: HTMLButtonElement,
  card: ConversationCard,
  privateMediaObjectURLs: ReadonlyMap<string, string>,
  large = false,
  continuityCode = "",
): void {
  button.replaceChildren();
  button.classList.remove("has-card-back-photo");
  const image = privateCardImage(card, privateMediaObjectURLs);
  button.classList.toggle("has-media", Boolean(image));
  const metadata = document.createElement("span");
  metadata.className = "revealed-metadata";
  const eyebrow = document.createElement("span");
  eyebrow.className = "revealed-theme";
  eyebrow.textContent = humanizeConversationTheme(card.theme);
  const date = document.createElement("span");
  date.className = "revealed-date";
  date.textContent = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(new Date(card.createdAt));
  metadata.append(eyebrow, date, continuityCue(continuityCode));
  const title = document.createElement(large ? "h2" : "h3");
  title.textContent = card.title;
  const supporting = document.createElement("span");
  supporting.className = "revealed-supporting";
  supporting.textContent = card.supportingText || card.sourceLabel;
  const hint = document.createElement("span");
  hint.className = "turn-hint";
  hint.textContent = "Tap to turn face down";
  button.append(metadata);
  if (image) {
    const media = document.createElement("img");
    media.className = "revealed-media";
    media.src = image.url;
    media.alt = "";
    media.setAttribute("aria-hidden", "true");
    media.decoding = "async";
    button.append(media);
  }
  button.append(title, supporting, hint);
}

function privateCardImage(
  card: ConversationCard,
  privateMediaObjectURLs: ReadonlyMap<string, string>,
): { url: string; alt: string } | null {
  const image = card.privateCardMedia.find(({ kind }) => kind === "image");
  if (!image) return null;
  const url = privateMediaObjectURLs.get(image.url)
    ?? safeSameOriginMediaURL(image.url, window.location.origin);
  if (!url) return null;
  return {
    url,
    alt: image.alt.trim() || "Private card image",
  };
}

function revealedCardAriaLabel(
  card: ConversationCard,
  prefix: string,
  action: string,
  privateMediaObjectURLs: ReadonlyMap<string, string>,
  continuityCode: string,
): string {
  const image = privateCardImage(card, privateMediaObjectURLs);
  return [
    prefix,
    image ? `Private card image: ${image.alt}` : "",
    card.title,
    continuityAccessibleCopy(continuityCode),
    action,
  ].filter(Boolean).join(" ");
}

function continuityCue(code: string): HTMLSpanElement {
  const cue = document.createElement("span");
  cue.className = "continuity-cue";
  cue.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.textContent = "Continuity";
  const value = document.createElement("strong");
  value.textContent = code;
  cue.append(label, value);
  return cue;
}

function continuityAccessibleCopy(code: string): string {
  return `Continuity code ${code}. `
    + "The same code stays with this card after reveal.";
}

function labeledControl(
  type: "checkbox" | "radio",
  id: string,
  text: string,
  checked: boolean,
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = document.createElement("label");
  label.htmlFor = id;
  const input = document.createElement("input");
  input.id = id;
  input.type = type;
  input.checked = checked;
  const copy = document.createElement("span");
  copy.textContent = text;
  label.append(input, copy);
  return { label, input };
}

function labeledTextInput(
  id: string,
  text: string,
  value: string,
  type: "text" | "url" = "text",
): { label: HTMLLabelElement; input: HTMLInputElement } {
  const label = document.createElement("label");
  label.htmlFor = id;
  label.textContent = text;
  const input = document.createElement("input");
  input.id = id;
  input.type = type;
  input.value = value;
  label.append(input);
  return { label, input };
}

function primaryAudienceRadio(
  audienceId: string,
  checked: boolean,
): HTMLLabelElement {
  const wrapper = document.createElement("label");
  wrapper.className = "primary-audience-radio";
  const input = document.createElement("input");
  input.type = "radio";
  input.name = "primary-audience";
  input.value = audienceId;
  input.checked = checked;
  input.dataset.primaryAudience = "";
  input.setAttribute("aria-label", "Default audience");
  const visual = document.createElement("span");
  visual.setAttribute("aria-hidden", "true");
  const accessible = document.createElement("span");
  accessible.className = "sr-only";
  accessible.textContent = "Default";
  wrapper.append(input, visual, accessible);
  return wrapper;
}

function destinationCheckbox(
  audienceId: string,
  destination: "conversation" | "library",
  label: string,
  checked: boolean,
): HTMLLabelElement {
  const wrapper = document.createElement("label");
  wrapper.className = "destination-check";
  const input = document.createElement("input");
  input.type = "checkbox";
  input.checked = checked;
  input.dataset.destination = destination;
  input.name = `${audienceId}-${destination}`;
  input.setAttribute("aria-label", label);
  const visual = document.createElement("span");
  visual.setAttribute("aria-hidden", "true");
  const accessible = document.createElement("span");
  accessible.className = "sr-only";
  accessible.textContent = destination === "conversation" ? "Cards" : "Library";
  wrapper.append(input, visual, accessible);
  return wrapper;
}

function actionButton(
  label: string,
  action: string,
  compact: boolean,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.dataset.cardAction = action;
  button.className = compact ? "card-action compact" : "card-action";
  button.textContent = label;
  return button;
}

function sourceLink(card: ConversationCard): HTMLAnchorElement | null {
  const safeSourceURL = safeExternalURL(card.sourceURL);
  if (!safeSourceURL) return null;
  const link = document.createElement("a");
  link.href = safeSourceURL;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.className = "card-source-link";
  link.setAttribute(
    "aria-label",
    `Open original source from ${card.sourceLabel} in a new tab`,
  );
  link.textContent = `${card.sourceLabel} ↗`;
  return link;
}

function uniqueOptions(values: Array<[string, string]>): Array<[string, string]> {
  return [...new Map(values).entries()]
    .sort((left, right) => left[1].localeCompare(right[1]));
}

function option(value: string, label: string): HTMLOptionElement {
  const result = document.createElement("option");
  result.value = value;
  result.textContent = label;
  return result;
}

function setText(root: HTMLElement, selector: string, value: string): void {
  const element = root.querySelector<HTMLElement>(selector);
  if (element) element.textContent = value;
}

function opaqueDOMKey(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

function setValue(root: HTMLElement, selector: string, value: string): void {
  const element = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);
  if (element) element.value = value;
}

function setSelectValue(
  root: HTMLElement,
  selector: string,
  value: string,
): void {
  const element = root.querySelector<HTMLSelectElement>(selector);
  if (element) element.value = value;
}

function humanizeStatus(status: LibraryStatus): string {
  switch (status) {
    case "open": return "Open";
    case "in_progress": return "In progress";
    case "finished": return "Finished";
    case "dismissed": return "Dismissed";
    default: return "New";
  }
}

function humanImageType(mimeType: string): string {
  return mimeType
    .trim()
    .replace(/^image\//i, "")
    .replace("jpeg", "JPEG")
    .replace("png", "PNG")
    .replace("webp", "WebP")
    .replace("gif", "GIF")
    .replace("avif", "AVIF")
    .replace("heic", "HEIC")
    .replace("heif", "HEIF");
}

function humanFileSize(byteCount: number): string {
  if (byteCount < 1024) return `${byteCount} B`;
  if (byteCount < 1024 * 1024) {
    return `${(byteCount / 1024).toFixed(1)} KB`;
  }
  return `${(byteCount / (1024 * 1024)).toFixed(1)} MB`;
}

function humanizeTriageConfidence(
  confidence: LibraryTriageConfidence,
): string {
  switch (confidence) {
    case "high": return "High-confidence source signal";
    case "medium": return "Source-host suggestion";
    case "requires_review": return "Human review required";
  }
}

function videoDurationLabel(seconds: number | undefined): string | null {
  if (seconds === undefined) return null;
  if (seconds < 60) return `${seconds} sec video`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder
    ? `${minutes} min ${remainder} sec video`
    : `${minutes} min video`;
}

function videoDurationMinutesInput(
  seconds: number | undefined,
): string {
  return seconds === undefined ? "" : String(seconds / 60);
}

function parseVideoDurationSeconds(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const minutes = Number(value);
  return Number.isFinite(minutes)
    ? Math.round(minutes * 60)
    : Number.NaN;
}

function parseLongVideoThresholdSeconds(value: string): number | null {
  const minutes = Number(value);
  return (
    Number.isInteger(minutes)
    && minutes > 0
  )
    ? minutes * 60
    : null;
}

function progressHelp(status: LibraryStatus): string {
  switch (status) {
    case "new":
      return "Next: Open or Dismissed.";
    case "open":
      return "Next: In progress, Finished, or Dismissed.";
    case "in_progress":
      return "Next: keep In progress, Finish, or Dismiss.";
    case "finished":
      return "Finished is terminal for this recipient.";
    case "dismissed":
      return "Dismissed is terminal for this recipient.";
  }
}

function receiptActionLabel(
  current: LibraryStatus,
  next: LibraryStatus,
): string {
  switch (next) {
    case "open":
      return "Open";
    case "in_progress":
      return current === "in_progress" ? "Update progress" : "Start progress";
    case "finished":
      return "Finish";
    case "dismissed":
      return "Dismiss";
    case "new":
      return "New";
  }
}

function humanizeHistoryAction(
  action: ConversationEvent["action"],
): string {
  switch (action) {
    case "discussed": return "Discussed";
    case "postponed": return "Postponed";
    default: return "Revealed";
  }
}

function themeSymbol(theme: string): string {
  const index = [...theme].reduce(
    (sum, character) => sum + character.charCodeAt(0),
    0,
  ) % cardBackSymbols.length;
  return cardBackSymbols[index];
}

function dateInputValue(date: Date): string {
  const local = new Date(date.valueOf() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function compactElapsedTime(start: Date, now: Date): string {
  const minutes = Math.max(
    0,
    Math.floor((now.valueOf() - start.valueOf()) / 60_000),
  );
  if (minutes < 1) return "Now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
}

function formatSessionTime(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function formatSessionDate(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function cardKey(card: ConversationCard): string {
  return `${card.audienceId}:${card.bookmarkID}`;
}

function clampIndex(index: number, length: number): number {
  if (!length) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}
